import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { CityData, NavEdge, NavNode } from '../../types/map';
import { GameState, Card, Item, MovementBuff, InformantTip, SpotterDevice, CombatEncounter, RoundResult, Player, TaxiPath, Signal } from '../../types/game';
import { initializeGameState } from '../../engine/gameState';
import { MapRenderer, TransitType, HoverInfo, MovementMode } from '../map/MapRenderer';
import { TransitFilter } from '../map/TransitFilter';
import { PathResult, findPathBetweenPoints } from '../../engine/pathfinding';
import { findNearestNode, findNearestNodes } from '../../engine/pathfinding';
import { findNearbyBoardingOptions, advanceStopIndex, getRideSignalStrength, findLineById, getLineSegmentBetweenStops } from '../../engine/transit';
import { shuffleDeck, drawHand, CARD_TEMPLATES, createCard } from '../../data/cards';
import { ITEMS } from '../../data/items';
import { PASSIVE_TEMPLATES, PassiveBucket, hasPassive } from '../../data/passives';
import {
  applyPassivesToCard,
  getPlayerHandSize,
  getIntelDurationBonus,
  computePassiveSignalMultiplier,
  getInventoryCap,
  applyHeadhunterPostIntelPlay,
  resetHeadhunterDiscounts,
} from '../../engine/passiveEffects';
import { WireTap, AcousticPing, BearingRead, ReconStrip, StakeoutDevice, SurveillanceVan } from '../../types/game';
import { saveMidRound } from '../../engine/runState';
import { playEndTurn, playKillSound, playWarning } from '../../audio';
import { buildZoneLookup, zoneAtPoint } from '../../pipeline/processor';
import { generateMovementSignal, generateIdleSignal, revealAndAgeSignals, calculateExposureFromEdges, applyExposureDiminishing, SIGNAL_MAX_AGE, findNearestEdgeToPosition, computeMovementSignalStrength } from '../../engine/signal';
import { playerCanSeeAgent, agentCanSeePlayer, checkPlayerMidPathDetection, bearingDeg, isInVisionCone } from '../../engine/detection';
import type { AgentWorkerRequest, AgentWorkerResponse } from '../../engine/agentWorker';
import {
  determineCombatScenario,
  canAmbushWith,
  resolveAmbush,
  resolveAmbushed,
  resolveConfrontationWithChoice,
  resolveSniped,
  applyCombatItemEffects,
  applyCombatPlayerEffects,
} from '../../engine/combat';
import { tuning } from '../../data/tuning';
import {
  isFieldcraftEffectCard,
  executeFieldcraftEffect,
  applyDoubleTap,
  routePlayedCard,
  resolveChainDiscard,
  resolveAdaptDiscard,
  resolveCopyPick,
  resolveDoubleTapPick,
  resolveRecallPick,
  resolveStashPick,
  resolveFieldModifyPick,
  resolveOverclockPick,
  resolveImprovisePick,
  PendingCardAction,
} from '../../engine/cardEffects';
import { TuningPanel } from '../ui/TuningPanel';
import { AgentAIPanel } from '../ui/AgentAIPanel';
import { agentHasPassive } from '../../data/agentPassives';
import { TutorialScreen } from './TutorialScreen';
import { TargetPanel } from '../ui/TargetPanel';
import { CardPicker } from '../ui/CardPicker';
import { ItemIcon, getCategoryColor } from '../ui/ItemIcon';
import { CardCategoryIcon } from '../ui/CardCategoryIcon';
import { theme } from '../../styles/theme';
import { StreetViewThumbnail } from '../ui/StreetViewThumbnail';
import { AgentTurnLog } from '../../types/game';

/**
 * Calculate the bearing (degrees, 0=north, 90=east) from a movement path.
 * Uses the last segment of the path for the direction of arrival.
 */
function calcPathBearing(
  coordinates: [number, number][],
  startPos: { lat: number; lng: number },
): number {
  // Use the last 2 points of the path for direction of arrival
  let fromLat: number, fromLng: number, toLat: number, toLng: number;

  if (coordinates.length >= 2) {
    const from = coordinates[coordinates.length - 2];
    const to = coordinates[coordinates.length - 1];
    fromLng = from[0]; fromLat = from[1];
    toLng = to[0]; toLat = to[1];
  } else if (coordinates.length === 1) {
    fromLat = startPos.lat; fromLng = startPos.lng;
    toLng = coordinates[0][0]; toLat = coordinates[0][1];
  } else {
    return 0;
  }

  const dLng = ((toLng - fromLng) * Math.PI) / 180;
  const lat1 = (fromLat * Math.PI) / 180;
  const lat2 = (toLat * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const bearingRad = Math.atan2(y, x);
  return ((bearingRad * 180) / Math.PI + 360) % 360;
}

/**
 * Variant tags for the message overlay above the hand. The variant drives
 * the background/border/text color so the player can parse the category
 * of a message at a glance:
 *   - 'agent'   → red    (enemy agent related — spotting, combat, contact)
 *   - 'intel'   → purple (intel card results, surveillance feedback)
 *   - 'general' → blue   (generic feedback — movement, cards, items, etc.)
 */
type NotificationVariant = 'agent' | 'intel' | 'general';

/**
/**
 * Convert a bearing in degrees (0=N, 90=E) to a rough compass octant string
 * for player-facing notifications. Used by the Bearing intel card.
 */
function compassOctant(deg: number): string {
  const d = ((deg % 360) + 360) % 360;
  const octants = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return octants[Math.round(d / 45) % 8];
}

/**
 * Compute the world position (lat/lng) at a given distance-along a polyline.
 * `coords` is the polyline vertices, `distAlong` is the precomputed
 * cumulative-distance array (same length as coords, first entry is 0).
 * Used by Surveillance Van to interpolate the van's position as it walks
 * its patrol route each turn.
 */
function positionAtDistAlong(
  coords: [number, number][],
  distAlong: number[],
  targetDist: number,
): { lat: number; lng: number } {
  if (coords.length === 0) return { lat: 0, lng: 0 };
  if (coords.length === 1) return { lat: coords[0][1], lng: coords[0][0] };
  const total = distAlong[distAlong.length - 1];
  const clamped = Math.max(0, Math.min(total, targetDist));
  // Linear scan — polylines here are small (dozens of vertices)
  let i = 1;
  while (i < distAlong.length && distAlong[i] < clamped) i++;
  if (i >= distAlong.length) {
    const last = coords[coords.length - 1];
    return { lat: last[1], lng: last[0] };
  }
  const segStart = distAlong[i - 1];
  const segLen = distAlong[i] - segStart;
  const t = segLen > 0 ? (clamped - segStart) / segLen : 0;
  const [lng1, lat1] = coords[i - 1];
  const [lng2, lat2] = coords[i];
  return { lat: lat1 + (lat2 - lat1) * t, lng: lng1 + (lng2 - lng1) * t };
}

/**
 * Build the cumulative-distance array for a polyline. distAlong[i] is the
 * total meters from coords[0] to coords[i] along the polyline.
 */
function buildDistAlong(coords: [number, number][]): number[] {
  const out: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const [lng1, lat1] = coords[i - 1];
    const [lng2, lat2] = coords[i];
    const dLat = (lat2 - lat1) * 111000;
    const dLng = (lng2 - lng1) * 111000 * Math.cos((lat1 * Math.PI) / 180);
    out.push(out[i - 1] + Math.sqrt(dLat * dLat + dLng * dLng));
  }
  return out;
}

/**
 * Advance a surveillance van along its ping-pong route by `step` meters,
 * bouncing off either endpoint mid-turn using any leftover step budget.
 * Returns the new currentDist, direction, world position, AND the sub-
 * polyline traversed during this step (in traversal order), suitable for
 * marker animation. Never mutates its inputs.
 *
 * The animation path includes intermediate points whenever the van bounces
 * off an endpoint, so a turn that crosses an endpoint renders as a smooth
 * out-and-back instead of cutting across.
 */
function advanceVanAlongRoute(
  routeCoords: [number, number][],
  distAlong: number[],
  totalLength: number,
  currentDist: number,
  direction: 1 | -1,
  step: number,
): {
  currentDist: number;
  direction: 1 | -1;
  position: { lat: number; lng: number };
  animPath: [number, number][];
} {
  if (totalLength === 0) {
    const p = positionAtDistAlong(routeCoords, distAlong, 0);
    return { currentDist: 0, direction, position: p, animPath: [[p.lng, p.lat]] };
  }

  // Sample the route between two distance values (respecting direction of
  // travel) to build a smooth animation polyline. Walks through routeCoords
  // in order if forward, reverse if backward.
  const samplePath = (fromDist: number, toDist: number, dir: 1 | -1): [number, number][] => {
    const out: [number, number][] = [];
    const start = positionAtDistAlong(routeCoords, distAlong, fromDist);
    out.push([start.lng, start.lat]);
    if (dir === 1) {
      for (let i = 0; i < distAlong.length; i++) {
        if (distAlong[i] > fromDist && distAlong[i] < toDist) out.push(routeCoords[i]);
      }
    } else {
      for (let i = distAlong.length - 1; i >= 0; i--) {
        if (distAlong[i] < fromDist && distAlong[i] > toDist) out.push(routeCoords[i]);
      }
    }
    const end = positionAtDistAlong(routeCoords, distAlong, toDist);
    out.push([end.lng, end.lat]);
    return out;
  };

  let dist = currentDist;
  let dir = direction;
  let remaining = step;
  // Accumulate the full path as we walk (potentially through bounces).
  const animPath: [number, number][] = [];
  const startPos = positionAtDistAlong(routeCoords, distAlong, dist);
  animPath.push([startPos.lng, startPos.lat]);
  let bounces = 0;
  while (remaining > 0 && bounces < 10) {
    if (dir === 1) {
      const toEnd = totalLength - dist;
      if (remaining <= toEnd) {
        const nextDist = dist + remaining;
        const seg = samplePath(dist, nextDist, 1);
        for (let i = 1; i < seg.length; i++) animPath.push(seg[i]);
        dist = nextDist;
        remaining = 0;
      } else {
        const seg = samplePath(dist, totalLength, 1);
        for (let i = 1; i < seg.length; i++) animPath.push(seg[i]);
        dist = totalLength;
        remaining -= toEnd;
        dir = -1;
        bounces++;
      }
    } else {
      const toStart = dist;
      if (remaining <= toStart) {
        const nextDist = dist - remaining;
        const seg = samplePath(dist, nextDist, -1);
        for (let i = 1; i < seg.length; i++) animPath.push(seg[i]);
        dist = nextDist;
        remaining = 0;
      } else {
        const seg = samplePath(dist, 0, -1);
        for (let i = 1; i < seg.length; i++) animPath.push(seg[i]);
        dist = 0;
        remaining -= toStart;
        dir = 1;
        bounces++;
      }
    }
  }
  return {
    currentDist: dist,
    direction: dir,
    position: positionAtDistAlong(routeCoords, distAlong, dist),
    animPath,
  };
}

/*
 * Consume up to `budget` meters off the start of a taxi path. Returns the
 * consumed slice (coords + proxy edges + distance) and the remaining path.
 * If the whole path fits within the budget, returns `remaining: null` so
 * the caller knows the ride is complete.
 *
 * Coordinate walking is exact; edge walking is approximated by taking
 * whole edges off the front until their cumulative length hits the budget.
 * Close enough for signal/exposure accounting on a multi-turn ride.
 */
function sliceTaxiPath(path: TaxiPath, budget: number): {
  consumedCoords: [number, number][];
  consumedEdges: NavEdgeLike[];
  consumedDistance: number;
  remaining: TaxiPath | null;
  endPos: { lat: number; lng: number };
} {
  const coords = path.coordinates;
  if (coords.length < 2) {
    const lastCoord = coords[coords.length - 1] ?? [0, 0];
    return {
      consumedCoords: [...coords],
      consumedEdges: [],
      consumedDistance: 0,
      remaining: null,
      endPos: { lat: lastCoord[1], lng: lastCoord[0] },
    };
  }

  // --- Walk coordinates to find the exact endpoint at `budget` meters ---
  let accumulated = 0;
  const consumedCoords: [number, number][] = [coords[0]];
  let endLng = coords[coords.length - 1][0];
  let endLat = coords[coords.length - 1][1];
  let lastConsumedIdx = coords.length - 1; // inclusive upper bound of consumed coords from the old path
  let sliceComplete = false;

  for (let i = 0; i < coords.length - 1; i++) {
    const [lng1, lat1] = coords[i];
    const [lng2, lat2] = coords[i + 1];
    const dLat = (lat2 - lat1) * 111000;
    const dLng = (lng2 - lng1) * 111000 * Math.cos(lat1 * Math.PI / 180);
    const segLen = Math.sqrt(dLat * dLat + dLng * dLng);

    if (accumulated + segLen >= budget) {
      const t = segLen > 0 ? (budget - accumulated) / segLen : 0;
      endLng = lng1 + (lng2 - lng1) * t;
      endLat = lat1 + (lat2 - lat1) * t;
      consumedCoords.push([endLng, endLat]);
      accumulated = budget;
      lastConsumedIdx = i; // the segment we cut on started at coords[i]
      sliceComplete = true;
      break;
    }
    accumulated += segLen;
    consumedCoords.push(coords[i + 1]);
  }

  // --- Approximate edge consumption ---
  // Take whole edges off the front until their cumulative length exceeds the
  // consumed distance. Partial edges stay in `remaining` as a simplification.
  let edgeDistAccumulated = 0;
  let consumedEdgeCount = 0;
  for (let i = 0; i < path.edges.length; i++) {
    if (edgeDistAccumulated + path.edges[i].length > accumulated) break;
    edgeDistAccumulated += path.edges[i].length;
    consumedEdgeCount++;
  }
  const consumedEdges = path.edges.slice(0, consumedEdgeCount);

  if (!sliceComplete) {
    // We ran out of path before burning the budget — ride is complete.
    return {
      consumedCoords,
      consumedEdges: path.edges,
      consumedDistance: accumulated,
      remaining: null,
      endPos: { lat: endLat, lng: endLng },
    };
  }

  // Build the remaining path: interpolated start point + rest of old coords.
  const remainingCoords: [number, number][] = [[endLng, endLat]];
  for (let j = lastConsumedIdx + 1; j < coords.length; j++) {
    remainingCoords.push(coords[j]);
  }
  const remainingEdges = path.edges.slice(consumedEdgeCount);
  const remainingTotalDistance = Math.max(0, path.totalDistance - accumulated);

  // A slice that lands exactly on a coord junction can produce a remaining
  // path whose coords start with a duplicate (the interpolated endpoint AND
  // the next original coord are identical). Over multiple turns, this can
  // leave behind a degenerate "stub" path — coords like [last, last] with
  // totalDistance 0 but length >= 2 and an edge still in the list.
  //
  // Without this guard, the ride doesn't terminate on the turn it actually
  // arrives: isInTaxi stays true, the route line renders as a zero-length
  // stub, and movement cards stay blocked for one extra wasted turn.
  // Threshold <1m covers FP drift from the walked-vs-edge-length mismatch.
  const isEffectivelyDone = remainingEdges.length === 0
    || remainingCoords.length < 2
    || remainingTotalDistance < 1;

  return {
    consumedCoords,
    consumedEdges,
    consumedDistance: accumulated,
    remaining: isEffectivelyDone
      ? null
      : {
          coordinates: remainingCoords,
          edges: remainingEdges,
          totalDistance: remainingTotalDistance,
        },
    endPos: { lat: endLat, lng: endLng },
  };
}

// Minimum NavEdge shape the slicer uses. Lets the helper live above the
// component without importing the full map types — they're wide and the
// engine layer already validates them.
type NavEdgeLike = TaxiPath['edges'][number];

interface GameScreenProps {
  cityData: CityData;
  onBack?: () => void;
  onRoundComplete?: (result: RoundResult, earnedMarks: number, collectedItems: Item[], playerDeck: Card[], safehouseCards?: Card[], agentCodename?: string) => void;
  onSaveAndExit?: (gameState: GameState) => void;
  runState?: import('../../types/run').RunState | null;
}

export function GameScreen({ cityData, onBack, onRoundComplete, onSaveAndExit, runState }: GameScreenProps) {
  // Derive the current map-node type. Used to gate:
  //   - Extraction cards (only on 'extraction' nodes or practice mode)
  //   - Extraction POI rendering (same)
  //   - Round-end-on-kill behaviour (suppressed on extraction nodes: the
  //     round must end via the extraction point, not the kill)
  const currentNodeType: string | null = (() => {
    if (!runState?.runMap || !runState.currentNodeId) return null;
    const n = runState.runMap.nodes.find(x => x.id === runState.currentNodeId);
    return n?.type ?? null;
  })();
  const isExtractionRound = currentNodeType === 'extraction';
  // Round-end-on-kill gating: kills end standard rounds, but on extraction
  // rounds the agent's death only clears the threat — the player still has
  // to reach the extraction point. Practice mode (no runState) never ends
  // on extraction because there's no node; keeps the original behaviour.
  const killEndsRound = !isExtractionRound;
  // Stable ref so callbacks created with empty dep arrays (advanceCombatPhase
  // etc.) read the current flag instead of a stale closure value.
  const killEndsRoundRef = useRef(killEndsRound);
  killEndsRoundRef.current = killEndsRound;

  // Initialize game state: restore mid-round save if available, otherwise fresh init
  const [gameState, setGameState] = useState<GameState>(() => {
    if (runState?.midRoundSave?.gameState) {
      return runState.midRoundSave.gameState;
    }
    const gs = initializeGameState(cityData, runState?.deck, runState);
    // Hit / Elite / Boss rounds have no extraction objective — strip the
    // extraction points from gameState so MapRenderer doesn't draw the
    // green exit markers for a route the player can't (and shouldn't)
    // take. Extraction-op nodes keep them; practice mode keeps them.
    if (runState && !isExtractionRound) {
      return { ...gs, extractions: [] };
    }
    return gs;
  });
  const [, setTuningVersion] = useState(0);

  // Card selection & movement mode
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [remainingRange, setRemainingRange] = useState<number | null>(null);

  // Signal visibility — always on (toggle removed), age fixed at 3
  const showEnemySignals = true;
  const signalMaxAge = 3;
  const [counterSurveillanceActive, setCounterSurveillanceActive] = useState(false);
  const [showOwnSignals, setShowOwnSignals] = useState(false);

  // Pile overlay (deck or discard viewer)
  const [pileOverlay, setPileOverlay] = useState<'deck' | 'discard' | null>(null);

  // Scavenge dialog — shows the item found at a pickup point
  const [scavengeDialog, setScavengeDialog] = useState<{
    placedItemId: string;
    item: import('../../types/game').Item;
    poiName: string;
    category: string;
    pickupType: import('../../types/game').PickupType;
  } | null>(null);

  // Inventory action menu — which slot index is open (null = none)
  const [itemMenuIndex, setItemMenuIndex] = useState<number | null>(null);
  // Item used animation
  const [itemUsedAnim, setItemUsedAnim] = useState<{ name: string; iconId: string; key: number; slotIndex: number } | null>(null);
  const itemAnimKeyRef = useRef(0);

  // Pending fieldcraft card IDs — shown at lower opacity until next card played
  const [pendingFieldcraftIds, setPendingFieldcraftIds] = useState<Set<string>>(new Set());

  // In-game notification banner (replaces browser alert())
  const [gameNotification, setGameNotification] = useState<{ text: string; variant: NotificationVariant } | null>(null);

  /**
   * Show a temporary message in the message overlay above the hand.
   *
   * Variant drives the color:
   *  - 'agent'   → red   (anything about the enemy agent: visual contact,
   *                       combat prerequisites, ambush prompts)
   *  - 'intel'   → purple (intel card results: Counter-Surveillance,
   *                        District Scan, Acoustic Ping, Trace Route,
   *                        Wire Tap, Misdirect, Radio Scanner)
   *  - 'general' → blue  (everything else — card feedback, movement
   *                       errors, item usage, transit, safehouse, etc.)
   */
  const notify = useCallback((
    text: string,
    variant: NotificationVariant = 'general',
    duration: number = 3000,
  ) => {
    setGameNotification({ text, variant });
    window.setTimeout(() => {
      setGameNotification(prev => (prev && prev.text === text ? null : prev));
    }, duration);
  }, []);

  // Burner phone placement mode — waiting for map click to place distraction signal
  const [burnerPhoneMode, setBurnerPhoneMode] = useState(false);
  // Slot index of the burner phone being placed — used on commit so the
  // right stack is consumed when the player places the decoy somewhere.
  const [burnerPhoneSlotIndex, setBurnerPhoneSlotIndex] = useState<number | null>(null);
  // Jammer placement mode — same pattern as Burner Phone. Committed zone
  // lives on player.jammerPosition/jammerTurnsLeft; only ONE jammer zone
  // is active at a time (placing a new one replaces the old).
  const [jammerMode, setJammerMode] = useState(false);
  const [jammerSlotIndex, setJammerSlotIndex] = useState<number | null>(null);

  // Combat encounter state
  const [combatEncounter, setCombatEncounter] = useState<CombatEncounter | null>(null);

  // Stable ref for gameState so advanceCombatPhase can read player/agent
  // without being re-created on every state change (otherwise the
  // 2-second auto-advance timer in the useEffect below keeps resetting
  // because its dep changes, and the combat modal never opens).
  const gameStateRefForCombat = useRef(gameState);
  gameStateRefForCombat.current = gameState;

  // Advance combat from reveal phase to action/outcome phase.
  // Stable reference — uses a ref for gameState so the useEffect below
  // doesn't reset the timer on unrelated state changes.
  const advanceCombatPhase = useCallback(() => {
    setCombatEncounter(prev => {
      if (!prev || prev.phase !== 'reveal') return prev;
      const gs = gameStateRefForCombat.current;

      // If the player already selected a weapon (item-use trigger), auto-resolve
      if (prev.preSelectedWeapon) {
        if (prev.scenario === 'ambush') {
          const enc = resolveAmbush(prev.preSelectedWeapon, false);
          if (enc.outcome === 'player-kills') {
            if (killEndsRoundRef.current) {
              setTimeout(() => setGameState(cur => ({ ...cur, roundResult: 'kill' })), 0);
            } else {
              // Extraction op: kill clears the threat but the round only
              // ends at the extraction point. Mark the agent dead so AI
              // pauses and combat can't re-trigger.
              setTimeout(() => setGameState(cur => ({ ...cur, agent: { ...cur.agent, isDead: true } })), 0);
            }
          }
          return { ...enc, revealText: prev.revealText };
        } else if (prev.scenario === 'confrontation') {
          const enc = resolveConfrontationWithChoice(
            prev.preSelectedWeapon, false, 0,
            gs.player, gs.agent,
          );
          if (enc.vestExhausted || enc.flashbangUsed || enc.hardTargetUsed) {
            setTimeout(() => setGameState(cur => ({
              ...cur,
              player: applyCombatPlayerEffects(cur.player, enc),
            })), 0);
          }
          if (enc.outcome === 'player-kills') {
            if (killEndsRoundRef.current) {
              setTimeout(() => setGameState(cur => ({ ...cur, roundResult: 'kill' })), 0);
            } else {
              setTimeout(() => setGameState(cur => ({ ...cur, agent: { ...cur.agent, isDead: true } })), 0);
            }
          } else if (enc.outcome === 'player-dies') {
            setTimeout(() => setGameState(cur => ({ ...cur, roundResult: 'death' })), 0);
          }
          return { ...enc, revealText: prev.revealText };
        }
      }

      // Ambushed + agent has weapon → auto-resolve via resolveAmbushed
      // (nothing meaningful the player can choose: vest/flashbang check
      // is automatic, a gun or knife in the agent's hand makes weapon
      // choice irrelevant). Unarmed-agent ambushes still show the weapon
      // picker so the player can decide how to react — the click
      // handler for each weapon routes to resolveAmbushed to get the
      // proper competitive-roll path.
      if (prev.scenario === 'ambushed') {
        const agentHasWeapon = prev.agentWeapon &&
          (prev.agentWeapon.id.startsWith('pistol') || prev.agentWeapon.id.startsWith('sniper') || prev.agentWeapon.id.startsWith('combat-knife'));
        if (agentHasWeapon) {
          const encounter = resolveAmbushed(gs.player, gs.agent);
          if (encounter.vestExhausted || encounter.flashbangUsed || encounter.hardTargetUsed) {
            setTimeout(() => setGameState(cur => ({
              ...cur,
              player: applyCombatPlayerEffects(cur.player, encounter),
            })), 0);
          }
          if (encounter.outcome === 'player-dies') {
            setTimeout(() => setGameState(cur => ({ ...cur, roundResult: 'death' })), 0);
          }
          return { ...encounter, revealText: prev.revealText };
        }
      }

      // All other cases: show weapon choice
      return { ...prev, phase: 'action' };
    });
  }, []);

  // Auto-advance from reveal phase after 2s. Reveal only renders as a
  // message overlay (not the full combat modal) so the player can still
  // see the agent on the map while registering that contact has been made.
  // 2 seconds is long enough to read the banner and parse the agent's
  // position, short enough that the player doesn't go clicking other
  // things in the interim.
  useEffect(() => {
    if (combatEncounter?.phase !== 'reveal') return;
    const timer = setTimeout(advanceCombatPhase, 2000);
    return () => clearTimeout(timer);
  }, [combatEncounter?.phase, advanceCombatPhase]);

  // Play kill sound when combat resolves to an outcome with a kill or death.
  // The weapon that dealt the killing blow determines the audio cue.
  // Also stamps a 1-turn combat-retrigger cooldown on the player when they
  // survive a contact (escape or vest-save) — without it, a second move
  // past the agent immediately triggers another confrontation and the
  // player can't break contact. Cooldown decrements at end-of-turn.
  const prevCombatOutcomeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const outcome = combatEncounter?.outcome;
    if (outcome && outcome !== prevCombatOutcomeRef.current) {
      if (outcome === 'player-kills') {
        // Player's weapon killed the agent
        playKillSound(combatEncounter?.playerWeapon?.id);
      } else if (outcome === 'player-dies') {
        // Agent's weapon killed the player
        playKillSound(combatEncounter?.agentWeapon?.id);
      } else if (outcome === 'player-escapes' || outcome === 'player-saved') {
        // Survived a confrontation — grant one turn of immunity from
        // combat re-triggering on the next move.
        setGameState(gs => ({
          ...gs,
          player: { ...gs.player, combatCooldownTurnsLeft: 1 },
        }));
      }
    }
    prevCombatOutcomeRef.current = outcome;
  }, [combatEncounter?.outcome, combatEncounter?.playerWeapon?.id, combatEncounter?.agentWeapon?.id]);

  // Fieldcraft card effect — pending UI action (card picker modal)
  const [pendingAction, setPendingAction] = useState<PendingCardAction | null>(null);

  // Decoy size selection modal
  const [decoySelectOpen, setDecoySelectOpen] = useState(false);

  // Taxi destination selection: when the Taxi card is played we enter a
  // map-click mode (taxiSelectMode = true). Clicking the map sets a pending
  // destination + route preview which the player then confirms in a modal.
  const [taxiSelectMode, setTaxiSelectMode] = useState(false);
  // Store the full Card object (not just its id) so handleTaxiConfirm doesn't
  // need to do an id lookup inside the setGameState updater. An earlier
  // version that looked up by id silently failed when the lookup returned
  // undefined — the state never updated, the card stayed in hand, but the
  // notification still fired, leaving the player confused about whether
  // the taxi was actually hailed.
  const [taxiPendingRoute, setTaxiPendingRoute] = useState<{
    card: Card;
    path: TaxiPath;
    destinationLabel: string;
  } | null>(null);
  const [decoyRadius, setDecoyRadius] = useState<number>(tuning.decoyRadius);

  // Test mode — reveals all hidden info for debugging (Ctrl+T)
  const [testMode, setTestMode] = useState(false);
  const [teleportMode, setTeleportMode] = useState(false);
  const [boardTransitOpen, setBoardTransitOpen] = useState(false);
  // Stage Exit: when the card is played, we open a line/direction picker
  // modeled on the transit-boarding dialog. The pending card id is consumed
  // on confirm. Setting stageExitPendingCardId to null (from CANCEL) puts
  // the card back in hand unchanged.
  const [stageExitPendingCardId, setStageExitPendingCardId] = useState<string | null>(null);
  // Recon Plane placement mode + pending placement (set on click-drag-release,
  // confirmed via a banner button, then committed to gameState.reconStrips).
  const [reconPlaneMode, setReconPlaneMode] = useState(false);
  const [reconPlanePending, setReconPlanePending] = useState<{
    center: { lat: number; lng: number };
    angleDeg: number;
    lengthM: number;
    widthM: number;
    cardId: string;
  } | null>(null);
  // Stakeout — two-click placement. First click picks a commercial building
  // (stored in stakeoutPendingBuilding). Second click anywhere sets the cone
  // facing as the bearing from the building centroid to the click point, then
  // commits. Escape/right-click or re-click the card to cancel.
  const [stakeoutMode, setStakeoutMode] = useState(false);
  const [stakeoutPendingBuilding, setStakeoutPendingBuilding] = useState<{
    position: { lat: number; lng: number };
    buildingFeatureId: number;
    cardId: string;
  } | null>(null);
  // Alert triggered when a Stakeout first spots the agent — reuses the Spotter
  // alert modal pattern. Set once on new-entry; cleared by the player.
  const [stakeoutAlert, setStakeoutAlert] = useState<StakeoutDevice | null>(null);
  // Surveillance Van: hold-and-drag placement. The MapRenderer handles the
  // waypoint-tracked preview and passes back the final coord list on mouse
  // up. We just hold the mode flag, the selected card id, and a live length
  // preview for the top-of-screen banner.
  const [surveillanceVanMode, setSurveillanceVanMode] = useState(false);
  const [surveillanceVanPreview, setSurveillanceVanPreview] = useState<{ distance: number; overBudget: boolean } | null>(null);
  // Alert triggered when a Surveillance Van first spots the agent.
  const [surveillanceVanAlert, setSurveillanceVanAlert] = useState<SurveillanceVan | null>(null);
  // Signal pulse — transient ring chart that appears next to the player marker
  // after any passive signal is generated from the player's actual position
  // (movement, idle, taxi advance, transit). Contains the signal strength
  // (0-1) and the position at which it was generated. The MapRenderer
  // consumes this as a prop and renders a fading SVG overlay for ~2 seconds.
  // The `id` field is monotonically incremented so repeat signals at the
  // same position still retrigger the fade-in animation.
  const [signalPulse, setSignalPulse] = useState<{
    strength: number;
    lat: number;
    lng: number;
    id: number;
    /** Optional exposure delta this signal event added. When present, the
     * pulse renders a "+N EXP" line under the SIGNAL readout so the
     * player can eyeball the cost of the move they just committed. */
    exposureGain?: number;
  } | null>(null);
  const signalPulseIdRef = useRef(0);
  const showSignalPulse = useCallback((strength: number, lat: number, lng: number, exposureGain?: number) => {
    signalPulseIdRef.current += 1;
    setSignalPulse({ strength, lat, lng, id: signalPulseIdRef.current, exposureGain });
  }, []);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [recenterTarget, setRecenterTarget] = useState<{ lat: number; lng: number } | null>(null);
  // Optional zoom override for the next recenter. Set when Move/Shortcut is selected
  // so the map snaps into a comfortable placement view. Left null for "normal" recenters
  // (the recenter button, alert banners) — those preserve whatever zoom the player had.
  const [recenterZoom, setRecenterZoom] = useState<number | null>(null);
  /** Zoom level used when a movement card enters placement — medium-close, enough
   * context to see a 300-500m placement radius around the player. */
  const MOVEMENT_PLACEMENT_ZOOM = 16;
  // Clear the one-shot zoom override whenever nothing is selected. Many code
  // paths deselect the card (complete, cancel, switch, AP exhausted) — this
  // single effect covers them all so the next manual recenter (button, banner
  // click) doesn't inherit the placement zoom we set for movement.
  useEffect(() => {
    if (selectedCardId === null) setRecenterZoom(null);
  }, [selectedCardId]);

  /** Snap the camera to the player and zoom into a comfortable placement view.
   * Called when a path/segment card enters placement mode (Move, Shortcut,
   * Misdirect, Overwatch, Doppelgänger, Stage Exit) — all cards where the
   * placement originates from the player's position. */
  const recenterForPlacement = useCallback(() => {
    setRecenterTarget(null); // null target = fly to player position
    setRecenterZoom(MOVEMENT_PLACEMENT_ZOOM);
    setRecenterTrigger(n => n + 1);
  }, []);
  const [spotterAlert, setSpotterAlert] = useState<SpotterDevice | null>(null);
  const [wireTapAlert, setWireTapAlert] = useState<WireTap | null>(null);
  // Cold Opening first-turn reveal dialog — shown once at round start
  // when the player has the passive. "Show me" button flies the camera
  // to the agent's revealed position.
  const [coldOpenAlert, setColdOpenAlert] = useState<{ lat: number; lng: number } | null>(null);
  // Traffic Analysis hit alert — shown when the card successfully reads the
  // agent's named road. Stores the road name for display and a center point
  // on the highlighted road so the "show me" button has somewhere to fly to.
  const [trafficAnalysisAlert, setTrafficAnalysisAlert] = useState<
    { roadName: string; center: { lat: number; lng: number } } | null
  >(null);
  const [informantAlert, setInformantAlert] = useState<
    { poiName: string; streetName: string; center: { lat: number; lng: number } } | null
  >(null);
  // Safehouse draft — collapsed to a single pick of ANY category (was
  // previously two phases: fieldcraft then tactics-intel). The `phase`
  // field is kept for type compatibility with older state shapes but
  // only 'any' is used now.
  const [safehouseDraft, setSafehouseDraft] = useState<{
    phase: 'any';
    options: Card[];
  } | null>(null);
  // When entering a safehouse we open a dossier dialog FIRST — shows the
  // agent's now-revealed loadout (face, weapon, passives, deck). The
  // pre-rolled draft options wait here until the player dismisses the
  // dossier; then they flow into `safehouseDraft`.
  const [pendingSafehouseDraft, setPendingSafehouseDraft] = useState<Card[] | null>(null);
  const safehouseCardsPickedRef = useRef<Card[]>([]);

  // Track whether street view thumbnail is expanded (to push stat bars right)
  const [streetViewExpanded, setStreetViewExpanded] = useState(false);

  // Free hand mode — lets user pick exactly which cards are in their hand
  const [freeHandMode, setFreeHandMode] = useState(false);
  const [freeHandPanel, setFreeHandPanel] = useState(false);

  // Alt movement mode — shows multiple route options on click instead of hover-to-preview
  const [altMovementMode, setAltMovementMode] = useState(false);

  // Agent AI panel open state
  const [agentAIPanelOpen, setAgentAIPanelOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [lightMode, setLightMode] = useState(false);

  // Light mode: invert the entire page and counter-invert the map canvas
  // so map tiles render correctly. Quick global toggle without touching
  // every inline style.
  useEffect(() => {
    document.documentElement.classList.toggle('light-mode', lightMode);
    return () => { document.documentElement.classList.remove('light-mode'); };
  }, [lightMode]);
  // Tuning panel open state
  const [tuningPanelOpen, setTuningPanelOpen] = useState(false);
  // Agent movement animation path — set when agent moves and is visible
  const [agentAnimPath, setAgentAnimPath] = useState<[number, number][] | null>(null);
  // Alt movement routes count — for showing hint
  const [altRoutesCount, setAltRoutesCount] = useState(0);

  // End-turn button hover flash — briefly applied when Enter triggers the
  // end turn, so keyboard use gets the same visual feedback as a mouse click.
  const [endTurnFlash, setEndTurnFlash] = useState(false);

  // Agent AI turn logs — accumulated for the Agent AI Test Panel
  const agentTurnLogsRef = useRef<AgentTurnLog[]>([]);
  // Track movement paths for round-end debrief (full road-level coords, not just endpoints)
  const playerPathRef = useRef<[number, number][]>([]);
  const agentPathRef = useRef<[number, number][]>([[gameState.agent.position.lng, gameState.agent.position.lat]]);
  // Per-turn agent movement coords for Trace Route card (stores last N turns of full road paths)
  const agentMovementHistoryRef = useRef<Array<{ turn: number; coords: [number, number][] }>>([]);
  const [agentTurnLogs, setAgentTurnLogs] = useState<AgentTurnLog[]>([]);

  // Auto-save every turn + play end-turn sound when turn advances
  const prevTurnRef = useRef(gameState.turn);
  useEffect(() => {
    if (gameState.turn > prevTurnRef.current) {
      playEndTurn();
    }
    prevTurnRef.current = gameState.turn;
    if (!runState || gameState.roundResult) return;
    saveMidRound(runState, gameState, cityData.name, cityData.center.lat, cityData.center.lng);
  }, [gameState.turn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stage Exit path aging: once the turn has advanced past the one it was
  // played on + 1 (i.e. the end of the following turn has passed), clear
  // the yellow line. Doing this as a dedicated turn-change effect rather
  // than inside the big end-of-turn reducer side-steps any ordering
  // questions about when exactly the reducer's state commit vs. the agent
  // worker's onmessage setGameState land.
  useEffect(() => {
    const t = gameState.player.stageExitPathTurn;
    if (t == null) return;
    if (gameState.turn > t + 1) {
      setGameState(prev => ({
        ...prev,
        player: { ...prev.player, stageExitPath: null, stageExitPathTurn: null },
      }));
    }
  }, [gameState.turn, gameState.player.stageExitPathTurn]);

  // Misdirect path aging: same two-turn persistence as Stage Exit. The
  // yellow fake-movement line stays visible during the turn it was played
  // AND the following turn, then clears at end of N+1. Dedicated effect
  // so timing is independent of the mega end-of-turn reducer.
  useEffect(() => {
    const t = gameState.player.misdirectPathTurn;
    if (t == null) return;
    if (gameState.turn > t + 1) {
      setGameState(prev => ({
        ...prev,
        player: { ...prev.player, misdirectPath: null, misdirectPathTurn: null },
      }));
    }
  }, [gameState.turn, gameState.player.misdirectPathTurn]);


  // Agent AI Web Worker — runs heavy AI computation off the main thread
  const agentWorkerRef = useRef<Worker | null>(null);
  // Guards against React 18 strict-mode double-invoking the handleEndTurn
  // state updater, which would otherwise dispatch TWO agent turn worker
  // requests with different random shuffles and apply whichever one happened
  // to finish later. We record the turn we've dispatched for here and also
  // the turn we've applied results for, so both ends are idempotent.
  const lastDispatchedAgentTurnRef = useRef<number>(-1);
  const lastAppliedAgentTurnRef = useRef<number>(-1);
  const agentWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track how many times the worker has been auto-respawned due to onerror,
  // so we can bail out of the recovery loop if it keeps failing (would indicate
  // a persistent module-level error in the worker, e.g. a broken import).
  const workerErrorCountRef = useRef(0);
  useEffect(() => {
    const spawn = (): Worker => {
      const w = new Worker(
        new URL('../../engine/agentWorker.ts', import.meta.url),
        { type: 'module' },
      );
      w.onerror = (ev) => {
        // `ev` is often a synthetic Event without a useful message. Log what
        // we can (message, filename, lineno, colno) so the console at least
        // points at the offending module.
        const e = ev as ErrorEvent;
        console.error('[AgentWorker] Worker error:', e.message || '(no message)', {
          filename: e.filename, lineno: e.lineno, colno: e.colno, error: e.error,
        });
        // Terminate the dead worker and spawn a fresh one, provided we
        // haven't hit the retry ceiling (prevents a tight respawn loop when
        // the worker module itself is broken).
        workerErrorCountRef.current++;
        if (workerErrorCountRef.current > 5) {
          console.error('[AgentWorker] Giving up after 5 consecutive crashes — agent AI disabled for this session.');
          return;
        }
        try { w.terminate(); } catch { /* no-op */ }
        // Clear any in-flight watchdog — it would try to recover against
        // the dead worker ref; we replace that ref now.
        if (agentWatchdogRef.current) { clearTimeout(agentWatchdogRef.current); agentWatchdogRef.current = null; }
        const fresh = spawn();
        agentWorkerRef.current = fresh;
        // If there's an outstanding turn that was dispatched but never got a
        // response, we need to advance the lastAppliedAgentTurnRef so the
        // game doesn't wedge. The fresh worker won't have the old request,
        // so fast-forward "applied" to match "dispatched" — this effectively
        // skips one agent turn rather than freezing forever.
        if (lastDispatchedAgentTurnRef.current > lastAppliedAgentTurnRef.current) {
          lastAppliedAgentTurnRef.current = lastDispatchedAgentTurnRef.current;
        }
      };
      return w;
    };
    const worker = spawn();
    agentWorkerRef.current = worker;
    return () => { try { worker.terminate(); } catch { /* no-op */ } };
  }, []);

  // When test mode is toggled on, immediately refill AP
  useEffect(() => {
    if (testMode) {
      setGameState(prev => ({
        ...prev,
        player: { ...prev.player, ap: 99 },
      }));
    }
  }, [testMode]);

  const virtualCardRef = useRef<Card | null>(null);
  const selectedCard = useMemo(() => {
    if (!selectedCardId) { virtualCardRef.current = null; return null; }
    const handCard = gameState.player.hand.find(c => c.id === selectedCardId);
    if (handCard) { virtualCardRef.current = null; return handCard; }
    // Check if it's a virtual card (stored in ref from handleCardClick)
    if (virtualCardRef.current?.id === selectedCardId) return virtualCardRef.current;
    return null;
  }, [selectedCardId, gameState.player.hand]);

  // --- Hand animation state ---
  // Track entering cards (newly added to hand) and exiting cards (just removed from hand).
  const prevHandIdsRef = useRef<Set<string>>(new Set());
  const [enteringCardIds, setEnteringCardIds] = useState<Set<string>>(new Set());
  const [exitingCards, setExitingCards] = useState<Array<{ card: Card; left: number; top: number; width: number; height: number }>>([]);

  // Cache card positions (updated by callback refs) so we can show exit animations
  // at the card's original spot after it's been removed from the hand.
  const cardPositionsRef = useRef<Map<string, { left: number; top: number; width: number; height: number }>>(new Map());
  const setCardRef = useCallback((id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      const r = el.getBoundingClientRect();
      cardPositionsRef.current.set(id, { left: r.left, top: r.top, width: r.width, height: r.height });
    }
    // Do NOT clear on unmount — we need the last-known position for the exit overlay.
  }, []);

  // Cache the most-recently-seen card data per ID.
  const lastKnownHandCardsRef = useRef<Map<string, Card>>(new Map());
  useEffect(() => {
    for (const c of gameState.player.hand) {
      lastKnownHandCardsRef.current.set(c.id, c);
    }
  }, [gameState.player.hand]);

  useEffect(() => {
    const currentIds = new Set(gameState.player.hand.map(c => c.id));
    const prev = prevHandIdsRef.current;

    // Cards removed from hand → animate out at their last DOM position
    const removedIds = Array.from(prev).filter(id => !currentIds.has(id));
    if (removedIds.length > 0) {
      const toExit: typeof exitingCards = [];
      for (const id of removedIds) {
        const card = lastKnownHandCardsRef.current.get(id);
        const pos = cardPositionsRef.current.get(id);
        if (card && pos) toExit.push({ card, ...pos });
      }
      if (toExit.length > 0) {
        setExitingCards(curr => [...curr, ...toExit]);
        const ids = new Set(toExit.map(e => e.card.id));
        setTimeout(() => {
          setExitingCards(curr => curr.filter(e => !ids.has(e.card.id)));
          // Also clear cached positions for removed cards
          for (const id of ids) cardPositionsRef.current.delete(id);
        }, 320);
      }
    }

    // Cards new to hand → animate in with stagger
    const addedIds = Array.from(currentIds).filter(id => !prev.has(id));
    if (addedIds.length > 0) {
      setEnteringCardIds(curr => {
        const next = new Set(curr);
        for (const id of addedIds) next.add(id);
        return next;
      });
      const toCleanup = new Set(addedIds);
      setTimeout(() => {
        setEnteringCardIds(curr => {
          const next = new Set(curr);
          for (const id of toCleanup) next.delete(id);
          return next;
        });
      }, 800);
    }

    prevHandIdsRef.current = currentIds;
  }, [gameState.player.hand]);

  const movementMode: MovementMode | null = useMemo(() => {
    if (!selectedCard || !selectedCard.movementRange) return null;
    let baseRange = remainingRange ?? selectedCard.movementRange;
    // Apply Sprint buff — double movement range
    if (remainingRange === null) {
      for (const buff of gameState.player.activeBuffs) {
        if (buff.rangeMultiplier) baseRange *= buff.rangeMultiplier;
      }
    }
    // Vehicle doubles movement range
    if (gameState.player.isInVehicle && remainingRange === null) {
      baseRange *= 2;
    }
    if (baseRange <= 0) return null;
    return { card: selectedCard, maxRange: baseRange, isShortcut: !!selectedCard.isShortcut, isVehicle: gameState.player.isInVehicle };
  }, [selectedCard, remainingRange, gameState.player.activeBuffs, gameState.player.isInVehicle]);

  // Sweep mode — selected card is a sweep card waiting for map placement (trivial check, no memo needed)
  const sweepMode = (selectedCard && selectedCard.sweepRadius)
    ? { card: selectedCard, radius: selectedCard.sweepRadius }
    : null;

  // Movement preview info — signal/exposure feedback while drawing a path
  const [movementPreview, setMovementPreview] = useState<{ distance: number; signalStrength: number; exposureGain: number; nearFootpath: boolean } | null>(null);
  // Live distance readout while placing a Doppelgänger path — mirrors the
  // Move card's movementPreview shape but only tracks distance (there's no
  // exposure / signal cost since the player doesn't actually move).
  const [doppelgangerPreview, setDoppelgangerPreview] = useState<{ distance: number; overRange: boolean } | null>(null);
  // Same pattern for Misdirect and Overwatch placements so the player sees
  // the live segment length as they aim.
  const [misdirectPreview, setMisdirectPreview] = useState<{ distance: number; overRange: boolean } | null>(null);
  const [overwatchPreview, setOverwatchPreview] = useState<{ distance: number } | null>(null);

  // Zone lookup — precompute for zone-at-point queries
  const zonePolygons = useMemo(() => buildZoneLookup(cityData.layers.zones), [cityData]);

  // Check if player is on a residential street (for Hotwire playability)
  // Optimized: early-exit with bounding box filter to avoid scanning all edges
  const playerNearestRoadClass = useMemo(() => {
    const pos = gameState.player.position;
    // ~50m bounding box in coordinate space
    const threshold = 0.0005;
    let bestDist = Infinity;
    let bestClass = '' as string;
    for (const edge of cityData.navigation.edges) {
      // Quick bounding box pre-filter: skip edges far from player
      const geom = edge.geometry;
      let inRange = false;
      for (let i = 0; i < geom.length; i++) {
        if (Math.abs(geom[i][1] - pos.lat) < threshold && Math.abs(geom[i][0] - pos.lng) < threshold) {
          inRange = true;
          break;
        }
      }
      if (!inRange) continue;

      for (const [lng, lat] of geom) {
        const d = (lat - pos.lat) ** 2 + (lng - pos.lng) ** 2;
        if (d < bestDist) {
          bestDist = d;
          bestClass = edge.classification;
        }
      }
    }
    return bestClass;
  }, [gameState.player.position.lat, gameState.player.position.lng, cityData.navigation.edges]);

  const playerOnResidentialStreet = playerNearestRoadClass === 'residential';
  const playerOnMajorOrMinorRoad = playerNearestRoadClass === 'major' || playerNearestRoadClass === 'minor';

  // Check if player is in a commercial zone (Hotwire blocked near commercial)
  const playerInCommercialZone = useMemo(() => {
    const pos = gameState.player.position;
    const zone = zoneAtPoint(pos.lng, pos.lat, zonePolygons);
    return zone === 'commercial';
  }, [gameState.player.position, zonePolygons]);

  // Taxi playability: major/minor is a clear yes. But a residential
  // street that INTERSECTS a major/minor is also fine — you can walk a few
  // steps and flag the cab down on the main road. We look for any edge in
  // the small proximity window (~50m) classified major/minor, even if the
  // nearest one happens to be residential. Mirrors the real-world "corner
  // of X and Y" affordance that the original nearest-edge lookup missed.
  const playerNearMajorOrMinorRoad = useMemo(() => {
    const pos = gameState.player.position;
    const threshold = 0.0005; // ~50m, same window as playerNearestRoadClass
    for (const edge of cityData.navigation.edges) {
      if (edge.classification !== 'major' && edge.classification !== 'minor') continue;
      const geom = edge.geometry;
      for (let i = 0; i < geom.length; i++) {
        if (Math.abs(geom[i][1] - pos.lat) < threshold && Math.abs(geom[i][0] - pos.lng) < threshold) {
          return true;
        }
      }
    }
    return false;
  }, [gameState.player.position.lat, gameState.player.position.lng, cityData.navigation.edges]);

  const taxiCanBeHailed = playerOnMajorOrMinorRoad || playerNearMajorOrMinorRoad;

  // Nearby item pickups — uncollected items within 50m of the player
  const SCAVENGE_RANGE = 50; // meters
  const nearbyItems = useMemo(() => {
    const pos = gameState.player.position;
    return gameState.placedItems.filter(pi => {
      if (pi.collected) return false;
      const dLat = (pi.poi.lat - pos.lat) * 111000;
      const dLng = (pi.poi.lng - pos.lng) * 111000 * Math.cos((pos.lat * Math.PI) / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng) <= SCAVENGE_RANGE;
    });
  }, [gameState.player.position.lat, gameState.player.position.lng, gameState.placedItems]);

  // Active Malware count — junk cards currently living anywhere in the agent's
  // card system (deck, hand, or discard). Used for the Malware tooltip counter
  // and the MALWARE status chip.
  const activeMalwareCount = useMemo(() => {
    const a = gameState.agent;
    const count = (arr: typeof a.deck) => arr.filter(c => c.isJunk).length;
    return count(a.deck) + count(a.hand) + count(a.discard);
  }, [gameState.agent]);

  // Nearby transit boarding options — lines with a stop within transitBoardRange
  const nearbyBoardingOptions = useMemo(() => {
    if (gameState.player.isInTransit || gameState.player.isInVehicle || gameState.player.isInBuilding || gameState.player.isElevated) return [];
    return findNearbyBoardingOptions(gameState.player.position, cityData.layers.transit.lines);
  }, [gameState.player.position.lat, gameState.player.position.lng, gameState.player.isInTransit, gameState.player.isInVehicle, gameState.player.isInBuilding, gameState.player.isElevated, cityData.layers.transit.lines]);

  // Transit types with nearby boarding stops (for highlighting transit filter buttons)
  const nearbyTransitTypes = useMemo(() => {
    const types = new Set<TransitType>();
    for (const opt of nearbyBoardingOptions) {
      types.add(opt.line.type as TransitType);
    }
    return types;
  }, [nearbyBoardingOptions]);

  // Nearby passphrases — within 50m, not collected
  const PASSPHRASE_RANGE = 50;
  const nearbyPassphrases = useMemo(() => {
    const pos = gameState.player.position;
    return gameState.passphrases.filter(p => {
      if (p.collected) return false;
      const dLat = (p.lat - pos.lat) * 111000;
      const dLng = (p.lng - pos.lng) * 111000 * Math.cos((pos.lat * Math.PI) / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng) <= PASSPHRASE_RANGE;
    });
  }, [gameState.player.position.lat, gameState.player.position.lng, gameState.passphrases]);

  // Passphrase collection progress
  const passphrasesCollected = useMemo(() =>
    gameState.passphrases.filter(p => p.collected).length,
    [gameState.passphrases]
  );
  const passphrasesRequired = 2;

  // Nearby safehouses — within 50m, not visited, player not in building/vehicle/transit
  const SAFEHOUSE_RANGE = 50;
  const nearbySafehouses = useMemo(() => {
    if (gameState.player.isInBuilding || gameState.player.isInVehicle || gameState.player.isInTransit || gameState.player.isElevated || gameState.player.isInSafehouse) return [];
    const pos = gameState.player.position;
    return gameState.safehouses.filter(sh => {
      if (sh.visited) return false;
      const dLat = (sh.lat - pos.lat) * 111000;
      const dLng = (sh.lng - pos.lng) * 111000 * Math.cos((pos.lat * Math.PI) / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng) <= SAFEHOUSE_RANGE;
    });
  }, [gameState.player.position.lat, gameState.player.position.lng, gameState.safehouses, gameState.player.isInBuilding, gameState.player.isInVehicle, gameState.player.isInTransit, gameState.player.isElevated, gameState.player.isInSafehouse]);

  // Lockpick rooftop access — show virtual card if player has a lockpick, isn't already elevated/in building,
  // and doesn't already have a real Rooftop Access card in hand
  const canLockpickRooftop = useMemo(() => {
    if (gameState.player.isElevated || gameState.player.isInBuilding || gameState.player.isInVehicle || gameState.player.isInTransit) return false;
    const hasLockpick = gameState.player.items.some(i => i.name === 'Lock Pick Set');
    if (!hasLockpick) return false;
    const hasRealCard = gameState.player.hand.some(c => c.name === 'Rooftop Access');
    if (hasRealCard) return false;
    // Check if any commercial building is within 50m
    const COMMERCIAL_TYPES = ['commercial', 'retail', 'office', 'industrial', 'warehouse', 'public', 'civic', 'government', 'church', 'cathedral', 'chapel', 'mosque', 'synagogue', 'temple', 'hospital', 'hotel', 'school', 'university', 'train_station', 'supermarket', 'mall', 'basilica'];
    const pos = gameState.player.position;
    const cosLat = Math.cos(pos.lat * Math.PI / 180);
    const buildings = cityData.layers.buildings.features;
    for (let i = 0; i < buildings.length; i++) {
      const f = buildings[i];
      if (!f.geometry || f.geometry.type !== 'Polygon') continue;
      const bType = (f.properties as any)?.type || 'yes';
      // Check building type or fall back to zone
      let isCommercial = COMMERCIAL_TYPES.includes(bType);
      if (!isCommercial && bType === 'yes') {
        // Check zone at building position
        const ring = (f.geometry as GeoJSON.Polygon).coordinates[0];
        if (ring && ring.length > 0) {
          const zone = zoneAtPoint(ring[0][0], ring[0][1], zonePolygons);
          if (zone === 'commercial') isCommercial = true;
        }
      }
      if (!isCommercial) continue;
      // Distance check
      const ring = (f.geometry as GeoJSON.Polygon).coordinates[0];
      if (!ring || ring.length === 0) continue;
      const dLat = (ring[0][1] - pos.lat) * 111000;
      const dLng = (ring[0][0] - pos.lng) * 111000 * cosLat;
      if (Math.sqrt(dLat * dLat + dLng * dLng) <= 50) return true;
    }
    return false;
  }, [gameState.player.isElevated, gameState.player.isInBuilding, gameState.player.isInVehicle, gameState.player.isInTransit, gameState.player.items, gameState.player.hand, gameState.player.position.lat, gameState.player.position.lng, cityData.layers.buildings, zonePolygons]);

  // Nearby extraction points — within extract range (50m)
  const nearbyExtractions = useMemo(() => {
    const pos = gameState.player.position;
    return gameState.extractions.filter(ext => {
      const dLat = (ext.lat - pos.lat) * 111000;
      const dLng = (ext.lng - pos.lng) * 111000 * Math.cos((pos.lat * Math.PI) / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng) <= tuning.extractRange;
    });
  }, [gameState.player.position.lat, gameState.player.position.lng, gameState.extractions]);

  // Extraction proximity — within reveal range (300m) of any extraction
  const inExtractionZone = useMemo(() => {
    const pos = gameState.player.position;
    return gameState.extractions.some(ext => {
      const dLat = (ext.lat - pos.lat) * 111000;
      const dLng = (ext.lng - pos.lng) * 111000 * Math.cos((pos.lat * Math.PI) / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng) <= tuning.extractRevealRange;
    });
  }, [gameState.player.position.lat, gameState.player.position.lng, gameState.extractions]);

  // Hide mode — selected card is Hide Inside (simple comparison, no memo needed)
  const hideMode = selectedCard?.name === 'Hide Inside';

  // Rooftop mode — selected card is Rooftop Access
  const rooftopMode = selectedCard?.name === 'Rooftop Access';

  // Decoy mode — selected card is Decoy AND size has been selected (modal closed)
  const decoyMode = selectedCard?.name === 'Decoy' && !decoySelectOpen;

  // Deselect the card. If a partial move was used (remainingRange is set),
  // the card has already been played — discard it and deduct AP.
  const deselect = useCallback(() => {
    if (remainingRange !== null && selectedCard) {
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          ...routePlayedCard(prev.player, selectedCard),
          ap: prev.player.ap - selectedCard.apCost,
        },
      }));
    }
    setSelectedCardId(null);
    setRemainingRange(null);
  }, [remainingRange, selectedCard]);

  // Scavenge — open dialog showing what item was found (must be before handleCardClick)
  const handleScavenge = useCallback((placedItemId: string) => {
    const pi = gameState.placedItems.find(p => p.id === placedItemId);
    if (!pi || pi.collected) return;
    setScavengeDialog({
      placedItemId: pi.id,
      item: pi.item,
      poiName: pi.poi.name,
      category: (pi.poi.category as string).charAt(0).toUpperCase() + (pi.poi.category as string).slice(1),
      pickupType: pi.pickupType ?? 'mixed',
    });
  }, [gameState.placedItems]);

  const handleCardClick = useCallback((card: Card) => {
    if (gameState.player.ap < card.apCost) return; // Not enough AP

    // Contingency: decrement the counter when a 0-AP tactics card is played.
    // If stacked ×2, playing one discounted card leaves 1 charge remaining.
    if (card._contingencyApplied) {
      setGameState(prev => ({
        ...prev,
        player: { ...prev.player, contingencyActive: Math.max(0, prev.player.contingencyActive - 1) },
      }));
    }

    // If there are active fieldcraft buffs and this is NOT a move card or another fieldcraft,
    // the buffs are wasted (consumed without effect). "If the next card played is a Move card..."
    if (gameState.player.activeBuffs.length > 0 && !card.movementRange && !card.isFieldcraft) {
      // Move pending fieldcraft cards from hand to discard
      const pendingIds = pendingFieldcraftIds;
      setPendingFieldcraftIds(new Set());
      setGameState(prev => {
        const pendingCards = prev.player.hand.filter(c => pendingIds.has(c.id));
        return {
          ...prev,
          player: {
            ...prev.player,
            activeBuffs: [],
            hand: prev.player.hand.filter(c => !pendingIds.has(c.id)),
            discard: [...prev.player.discard, ...pendingCards],
          },
        };
      });
    }

    // Scavenge: virtual card — open the scavenge dialog
    if (card.name === 'Scavenge' && card.id.startsWith('scavenge-')) {
      const placedItemId = card.id.replace('scavenge-', '');
      handleScavenge(placedItemId);
      return;
    }

    // Extract: virtual card — attempt extraction at nearby exit
    if (card.name === 'Extract' && card.id.startsWith('extract-')) {
      // Check passphrase requirement first
      if (passphrasesCollected < passphrasesRequired) {
        notify(
          `Need ${passphrasesRequired - passphrasesCollected} more passphrase${passphrasesRequired - passphrasesCollected !== 1 ? 's' : ''} to extract. Collect dead drops on the map.`,
          'general',
          4000,
        );
        return;
      }
      const exposurePct = gameState.player.exposure / tuning.exposureMax;
      const threshold = tuning.extractMaxExposurePct;
      if (exposurePct > threshold) {
        notify(
          `Extraction failed — exposure too high (${Math.round(exposurePct * 100)}%). Must be below ${Math.round(threshold * 100)}% to extract cleanly.`,
          'general',
          4000,
        );
        return;
      }
      // Success — player escapes
      setGameState(prev => ({ ...prev, roundResult: 'escape' }));
      return;
    }

    // Ping junk (agent Ping passive): play the card to suppress the ping.
    // Costs 1 AP, just removes the card from hand (doesn't route into deck/
    // discard — the end-of-turn reducer sweeps unremoved pings out via its
    // pingStillInHand check anyway).
    if (card.name === 'Ping' && card.isJunk) {
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          hand: prev.player.hand.filter(c => c.id !== card.id),
          ap: prev.player.ap - card.apCost,
        },
      }));
      notify('Ping suppressed — agent signal lost.', 'intel');
      return;
    }

    // Passphrase collection: virtual card — pick up a dead drop
    if (card.name === 'Collect Passphrase' && card.id.startsWith('passphrase-card-')) {
      const passphraseId = card.id.replace('passphrase-card-', '');
      const pp = gameState.passphrases.find(p => p.id === passphraseId);
      if (!pp || pp.collected) return;
      // Generate moderate signal on collection
      const collectSignal: import('../../types/game').Signal = {
        id: `signal-passphrase-${Date.now()}`,
        position: { lat: pp.lat, lng: pp.lng },
        actualPosition: { lat: pp.lat, lng: pp.lng },
        radius: 150,
        strength: 0.5,
        age: 0,
        turnGenerated: gameState.turn,
        source: 'player',
        type: 'passive',
        revealed: true,
      };
      setGameState(prev => ({
        ...prev,
        passphrases: prev.passphrases.map(p => p.id === passphraseId ? { ...p, collected: true } : p),
        signals: [...prev.signals, collectSignal],
      }));
      notify(`Passphrase collected: ${pp.word}`, 'general');
      return;
    }

    // Can't move while hiding in a building
    if (gameState.player.isInBuilding && card.movementRange) return;
    if (gameState.player.isInTransit && card.movementRange) return;

    // Counter-Surveillance: instant play — reveals your own signal +
    // reduces 100 exposure + force-reveals last turn's enemy passive
    // signals. Since the player is always blind-by-default now,
    // awareness-gated reveals leave a lot of enemy passive signals
    // invisible on low-exposure turns — this gives Counter-Surveillance
    // a second job that matches its "see what's hunting you" flavor.
    if (card.name === 'Counter-Surveillance') {
      setCounterSurveillanceActive(true);
      setShowOwnSignals(true);
      setGameState(prev => {
        let player = {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          exposure: Math.max(0, prev.player.exposure - 100),
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        };
        const dtResult = applyDoubleTap(player, card);
        if (dtResult) player = dtResult;
        player = applyHeadhunterPostIntelPlay(player, runState);
        // Force-reveal agent passive signals from the prior turn. Own
        // signal reveal still flows through showOwnSignals / the normal
        // reveal pipeline; this only touches enemy passives.
        const lastTurn = prev.turn - 1;
        const signals = prev.signals.map(s =>
          s.source === 'agent' && s.type === 'passive' && s.turnGenerated === lastTurn
            ? { ...s, revealed: true }
            : s
        );
        return { ...prev, player, signals };
      });
      notify('Counter-Surveillance: Signal footprint revealed. Last turn\'s enemy signals exposed. −100 exposure.', 'intel');
      return;
    }

    // Sweep card: toggle selection (placement mode handled by MapRenderer)
    if (card.sweepRadius) {
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
      } else {
        setSelectedCardId(card.id);
      }
      return;
    }

    // Hide Inside: toggle selection (building selection handled by MapRenderer)
    if (card.name === 'Hide Inside') {
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
      } else {
        setSelectedCardId(card.id);
      }
      return;
    }

    // Rooftop Access: toggle selection (building selection handled by MapRenderer, same as Hide Inside)
    if (card.name === 'Rooftop Access') {
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
      } else {
        // Store virtual card in ref so selectedCard memo can find it
        if (card.id.startsWith('lockpick-rooftop-')) virtualCardRef.current = card;
        setSelectedCardId(card.id);
      }
      return;
    }

    // Instinct: instant play — 360° vision cone for 1 turn + 5% Prep
    if (card.name === 'Instinct') {
      setGameState(prev => {
        const routed = routePlayedCard(prev.player, card);
        let player = {
          ...prev.player,
          visionConeWidth: 360,
          instinctTurnsLeft: tuning.instinctDuration,
          ...routed,
          ap: prev.player.ap - card.apCost,
          // Stack on top of routePlayedCard's already-Sharpen-aware prep
          // numbers so Instinct + Sharpen active = +15% this play.
          prepBonus: routed.prepBonus + 0.05,
          prepBuiltThisTurn: routed.prepBuiltThisTurn + 0.05,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        };
        const dtResult = applyDoubleTap(player, card);
        if (dtResult) player = dtResult;
        return { ...prev, player };
      });
      return;
    }

    // Ghost: instant play — negates ALL player signal for this turn.
    // Retroactively strips any player-source signals already generated this
    // turn (movement, Move-then-Ghost sequence, etc.) AND prevents any
    // further ones (idle signal at end-of-turn is gated on ghostActive;
    // movement signals in the move handlers zero effectiveSignalMod). Only
    // current-turn signals get filtered — older signals remain in their
    // natural fade window.
    if (card.name === 'Ghost') {
      setGameState(prev => {
        let player = {
          ...prev.player,
          ghostActive: true,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        };
        const dtResult = applyDoubleTap(player, card);
        if (dtResult) player = dtResult;
        const signals = prev.signals.filter(
          s => !(s.source === 'player' && s.turnGenerated === prev.turn)
        );
        return { ...prev, player, signals };
      });
      notify('Ghost: all signal negated for this turn.', 'general');
      return;
    }

    // Static: instant play — this turn's signal will be displaced to a
    // random street within 200m of the player's actual position.
    if (card.name === 'Static') {
      setGameState(prev => {
        let player = {
          ...prev.player,
          staticActive: true,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        };
        const dtResult = applyDoubleTap(player, card);
        if (dtResult) player = dtResult;
        return { ...prev, player };
      });
      notify('Static: your next signal will be displaced to a nearby street.', 'general');
      return;
    }

    // Dead Man's Switch: instant play — capture the player's signal this
    // turn and set it to replay 3 turns later at this spot. Caches the
    // cached signal into player.deadMansSwitch. The actual signal capture
    // happens at end-of-turn when we know what signal (movement or idle)
    // was generated; here we just set the state flag with a fire turn.
    if (card.name === 'Dead Man\'s Switch') {
      setGameState(prev => {
        let player: Player = {
          ...prev.player,
          // Placeholder cachedSignal: the real one is written at end-of-turn
          // when we know what signal the player actually generated.
          deadMansSwitch: {
            cachedSignal: null as unknown as Signal, // will be filled at end-of-turn
            playedOnTurn: prev.turn,
            fireOnTurn: prev.turn + 3,
            deployed: false,
          },
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        };
        const dtResult = applyDoubleTap(player, card);
        if (dtResult) player = dtResult;
        return { ...prev, player };
      });
      notify('Dead Man\'s Switch armed. Your signal will replay in 3 turns.', 'general');
      return;
    }

    // Overwatch: enter placement mode. Player's position is the fixed
    // origin; hover on the map picks the direction the segment walks
    // along the road network. Click confirms. Requires pistol or sniper
    // in inventory — we validate here so the player gets the error up
    // front instead of after aiming.
    if (card.name === 'Overwatch') {
      const weapon = gameState.player.items.find(i =>
        i.id.startsWith('pistol') || i.id.startsWith('sniper-rifle')
      );
      if (!weapon) {
        notify('Overwatch requires a pistol or sniper rifle in inventory.', 'general');
        return;
      }
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
      } else {
        setSelectedCardId(card.id);
        virtualCardRef.current = card;
        // Placement segment is anchored at player's position — snap camera in.
        recenterForPlacement();
      }
      return;
    }

    // Doppelgänger: enter placement mode (map-click selects target, then
    // pathfind an 800m path from player to that point).
    if (card.name === 'Doppelgänger' || card.name === 'Doppelganger') {
      // Toggle selection: clicking again while selected cancels
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
      } else {
        setSelectedCardId(card.id);
        virtualCardRef.current = card;
        // Path pathfinds from player — snap camera in for comfortable target picking.
        recenterForPlacement();
      }
      return;
    }

    // Hotwire: enter a vehicle on a residential street, not in commercial zone
    if (card.name === 'Hotwire') {
      if (!playerOnResidentialStreet || playerInCommercialZone) {
        notify('Must be on a residential street away from commercial zones to hotwire.', 'general');
        return;
      }

      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          isInVehicle: true,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
        },
      }));
      return;
    }

    // Taxi: enter destination selection mode. Must be on a major or minor
    // road touching a commercial district. The card isn't consumed until
    // the player confirms a destination in the modal that follows.
    if (card.name === 'Taxi') {
      if (gameState.player.isInTaxi || gameState.player.isInVehicle || gameState.player.isInTransit) {
        notify('Already moving by vehicle or transit.', 'general');
        return;
      }
      if (!taxiCanBeHailed) {
        notify('Must be on a major or minor road to hail a taxi.', 'general');
        return;
      }
      if (selectedCardId === card.id && taxiSelectMode) {
        // Toggle off
        setTaxiSelectMode(false);
        setSelectedCardId(null);
      } else {
        setTaxiSelectMode(true);
        setSelectedCardId(card.id);
      }
      return;
    }

    // Disguise: instant play. Sets disguiseTurnsLeft and decrements at turn end.
    if (card.name === 'Disguise') {
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          disguiseTurnsLeft: tuning.disguiseDuration,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          lastPlayedMovement: false,
        },
      }));
      notify(`Disguise active — ${tuning.disguiseDuration} turns of reduced signal in commercial/major.`, 'general');
      return;
    }

    // Malware: shuffle a junk card into the agent's deck. Stacks.
    if (card.name === 'Malware') {
      setGameState(prev => {
        const junkCard = createCard('agentJunkMalware');
        // Insert at a random position in the agent's deck so it doesn't
        // always draw first next turn.
        const deck = [...prev.agent.deck];
        const insertAt = Math.floor(Math.random() * (deck.length + 1));
        deck.splice(insertAt, 0, junkCard);
        return {
          ...prev,
          player: {
            ...prev.player,
            ...routePlayedCard(prev.player, card),
            ap: prev.player.ap - card.apCost,
            cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
            lastPlayedMovement: false,
          },
          agent: { ...prev.agent, deck },
        };
      });
      notify('Malware injected — a junk card is now in the agent\'s deck.', 'general');
      return;
    }

    // Stage Exit: must be near a transit stop. Opens a line/direction picker
    // (parallel to BOARD TRANSIT). The actual effect — fake signals + belief
    // commit — runs in handleStageExitConfirm once the player picks.
    if (card.name === 'Stage Exit') {
      if (nearbyBoardingOptions.length === 0) {
        notify('Must be near a transit stop to stage an exit.', 'general');
        return;
      }
      // Click again to toggle the picker closed.
      if (stageExitPendingCardId === card.id) {
        setStageExitPendingCardId(null);
        setSelectedCardId(null);
      } else {
        setStageExitPendingCardId(card.id);
        setSelectedCardId(card.id);
        // Transit boarding originates at the player's current stop — snap in
        // so the destination picker has comfortable context.
        recenterForPlacement();
      }
      return;
    }

    // Double Agent: burn card that primes a flag on the player. Consumed on
    // the next Informant-type intel the agent plays (see agentAI.ts).
    if (card.name === 'Double Agent') {
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          doubleAgentPrimed: true,
          hand: prev.player.hand.filter(c => c.id !== card.id),
          burnPile: [...prev.player.burnPile, card],
          ap: prev.player.ap - card.apCost,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          lastPlayedMovement: false,
        },
      }));
      notify('Double Agent primed — next enemy Informant play points to a false POI.', 'general');
      return;
    }

    // Low Profile: immediately drop 300 exposure AND block any further exposure
    // gain for the rest of this turn. Flag is read at every exposure-gain site
    // and cleared in the end-of-turn reducer.
    if (card.name === 'Low Profile') {
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          exposure: Math.max(0, prev.player.exposure - 300),
          lowProfileActive: true,
          ap: prev.player.ap - card.apCost,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          lastPlayedMovement: false,
        },
      }));
      notify('Low Profile — −300 exposure. No more exposure this turn.', 'general');
      return;
    }

    // Parade Route: primes the next agent turn to run at half movement range.
    // Flag is read by agentAI (executeAgentCard's Move/Shortcut branch) and
    // cleared in the onmessage handler after the worker returns.
    if (card.name === 'Parade Route') {
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          paradeRoutePrimed: true,
          ap: prev.player.ap - card.apCost,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          lastPlayedMovement: false,
        },
      }));
      notify('Parade Route — agent will run into a parade next turn (½ movement).', 'general');
      return;
    }

    // Stop and Frisk: primes the next agent turn to skip all Move/Shortcut
    // card plays. Flag is read by agentAI's card selection filter and cleared
    // in the onmessage handler after the worker returns.
    if (card.name === 'Stop and Frisk') {
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          stopAndFriskPrimed: true,
          ap: prev.player.ap - card.apCost,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          lastPlayedMovement: false,
        },
      }));
      notify('Stop and Frisk — local police will stop the agent next turn. No moves.', 'general');
      return;
    }

    // Decoy: open size selection modal first
    if (card.name === 'Decoy') {
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
        setDecoySelectOpen(false);
      } else {
        setDecoySelectOpen(true);
        setSelectedCardId(card.id);
      }
      return;
    }

    // Fieldcraft effect cards — handled by the card effects engine
    if (isFieldcraftEffectCard(card)) {
      // Opportunist gate: must play immediately after a movement card
      if (card.name === 'Opportunist' && !gameState.player.lastPlayedMovement) {
        notify('Opportunist: Must play immediately after a movement card.', 'general');
        return;
      }
      // Operational Integrity gate: must be only card played this turn
      if (card.name === 'Operational Integrity' && gameState.player.cardsPlayedThisTurn > 0) {
        notify('Operational Integrity: Must be the only card played this turn.', 'general');
        return;
      }
      // Patience gate: must be only card played this turn
      if (card.name === 'Patience' && gameState.player.cardsPlayedThisTurn > 0) {
        notify('Patience: Can only be played if no other card was played this turn.', 'general');
        return;
      }

      // Two-call pattern for fieldcraft cards:
      //
      //   1. A functional setState reducer runs executeFieldcraftEffect on
      //      the freshest `prev` state. This is what actually commits the
      //      card play. Using `prev` (not closure-captured `gameState`)
      //      means a concurrent update like the "player-sees-agent" effect
      //      pinning seenAgentTurnsLeft=3 doesn't get clobbered when we
      //      replace state. Root-cause fix for the "red dot disappears when
      //      playing Detour" bug.
      //
      //   2. A synchronous call to executeFieldcraftEffect(gameState, card)
      //      gives us the `pendingAction` + `notification` to dispatch
      //      immediately. These are state-shape-independent (the pending
      //      action's `cardPlayed` is just the card, the picker reads cards
      //      out of the live hand at render time), so computing them on the
      //      closure snapshot is safe even though the reducer will see a
      //      slightly different prev. The previous version of this block
      //      tried to capture the result inside the reducer and read it
      //      after setGameState — but React's setState is asynchronous, so
      //      the if-check ran before the reducer, the capture was null,
      //      and no pickers ever opened.
      const previewResult = executeFieldcraftEffect(gameState, card);
      setGameState(prev => executeFieldcraftEffect(prev, card).newState);
      if (previewResult.notification) {
        notify(previewResult.notification, 'general', 4000);
      }
      if (previewResult.pendingAction) {
        setPendingAction(previewResult.pendingAction);
      }
      return;
    }

    // Fieldcraft buff cards (Quick Step, Sprint, Fade) — instant play, buffs next Move card
    // Card stays in hand at lower opacity until consumed
    if (card.isFieldcraft) {
      const buff: MovementBuff = {
        sourceCardName: card.name,
        signalModifier: card.buffSignalModifier,
        rangeMultiplier: card.buffRangeMultiplier,
        signalCondition: card.buffSignalCondition,
      };
      setPendingFieldcraftIds(prev => new Set([...prev, card.id]));
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          activeBuffs: [...prev.player.activeBuffs, buff],
          ap: prev.player.ap - card.apCost,
        },
      }));
      return;
    }

    // Informant: instant play — find a POI near agent and the agent's street.
    // We compute the tip INSIDE the setGameState reducer using `prev.agent.position`
    // instead of reading `gameState.agent.position` from the outer closure.
    // handleCardClick is a useCallback whose deps don't include gameState.agent.*,
    // so the closure-captured agent position is stale across turns — which
    // caused a second Informant play to reuse the first tip's POI and street
    // (the agent had moved but the handler didn't know).
    if (card.name === 'Informant') {
      let capturedTip: InformantTip | null = null;
      setGameState(prev => {
        const agentPos = prev.agent.position;

        // Find POIs within range of the CURRENT agent position.
        const poisInRange = cityData.pois
          .map(poi => {
            const dLat = (poi.lat - agentPos.lat) * 111000;
            const dLng = (poi.lng - agentPos.lng) * 111000 * Math.cos((agentPos.lat * Math.PI) / 180);
            return { poi, dist: Math.sqrt(dLat * dLat + dLng * dLng) };
          })
          .filter(p => p.dist >= tuning.informantMinRange && p.dist <= tuning.informantMaxRange)
          .sort((a, b) => a.dist - b.dist);

        // Pick a POI — prefer one in range, else use closest overall
        let chosenPoi: typeof poisInRange[0];
        if (poisInRange.length > 0) {
          chosenPoi = poisInRange[Math.floor(Math.random() * Math.min(3, poisInRange.length))];
        } else {
          const allPois = cityData.pois.map(poi => {
            const dLat = (poi.lat - agentPos.lat) * 111000;
            const dLng = (poi.lng - agentPos.lng) * 111000 * Math.cos((agentPos.lat * Math.PI) / 180);
            return { poi, dist: Math.sqrt(dLat * dLat + dLng * dLng) };
          }).sort((a, b) => a.dist - b.dist);
          chosenPoi = allPois[0];
        }

        // Find nearest street name to the CURRENT agent position
        let streetName = 'Unknown Street';
        if (cityData.layers.streets.features.length > 0) {
          let bestDist = Infinity;
          for (const feature of cityData.layers.streets.features) {
            const name = (feature.properties as any)?.name;
            if (!name) continue;
            const geom = feature.geometry;
            if (geom.type !== 'LineString') continue;
            for (const coord of (geom as GeoJSON.LineString).coordinates) {
              const dLat = (coord[1] - agentPos.lat) * 111000;
              const dLng = (coord[0] - agentPos.lng) * 111000 * Math.cos((agentPos.lat * Math.PI) / 180);
              const d = dLat * dLat + dLng * dLng;
              if (d < bestDist) {
                bestDist = d;
                streetName = name;
              }
            }
          }
        }

        const tip: InformantTip = {
          id: `informant-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          poiName: chosenPoi.poi.name || chosenPoi.poi.category,
          poiPosition: { lat: chosenPoi.poi.lat, lng: chosenPoi.poi.lng },
          streetName,
          turnCreated: prev.turn,
        };
        capturedTip = tip;

        let player: Player = {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
        };
        player = applyHeadhunterPostIntelPlay(player, runState);
        return {
          ...prev,
          informantTips: [...prev.informantTips, tip],
          player,
        };
      });
      if (capturedTip) {
        const tip = capturedTip as InformantTip;
        setTimeout(() => setInformantAlert({
          poiName: tip.poiName,
          streetName: tip.streetName,
          center: { lat: tip.poiPosition.lat, lng: tip.poiPosition.lng },
        }), 0);
      }
      return;
    }

    // Spotter: instant play — place surveillance at player's position
    if (card.name === 'Spotter') {
      const spotter: SpotterDevice = {
        id: `spotter-${Date.now()}`,
        position: { ...gameState.player.position },
        radius: tuning.spotterRadius,
        turnsRemaining: -1, // indefinite until agent enters radius
        turnPlaced: gameState.turn,
        agentRevealed: false,
      };
      setGameState(prev => {
        let player: Player = {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
        };
        player = applyHeadhunterPostIntelPlay(player, runState);
        return {
          ...prev,
          spotters: [...prev.spotters, spotter],
          player,
        };
      });
      return;
    }

    // District Scan: instant play — find which neighborhood the agent is in
    if (card.name === 'District Scan') {
      // Look up agent position against neighborhood data.
      // OSM neighborhoods are usually point nodes, sometimes polygons.
      // Strategy: first try point-in-polygon, then fall back to nearest named point.
      const agentPos = gameState.agent.position;
      let foundName: string | null = null;

      // Pass 1: check polygons (rare but precise)
      for (const feature of cityData.layers.neighborhoods.features) {
        if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') continue;
        const name = (feature.properties as Record<string, unknown>)?.name as string | undefined;
        if (!name) continue;
        const rings = feature.geometry.type === 'Polygon'
          ? [feature.geometry.coordinates[0]]
          : feature.geometry.coordinates.map(p => p[0]);
        for (const ring of rings) {
          let inside = false;
          for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const [xi, yi] = ring[i]; const [xj, yj] = ring[j];
            if ((yi > agentPos.lat) !== (yj > agentPos.lat) &&
              agentPos.lng < (xj - xi) * (agentPos.lat - yi) / (yj - yi) + xi) {
              inside = !inside;
            }
          }
          if (inside) { foundName = name; break; }
        }
        if (foundName) break;
      }

      // Pass 2: if no polygon match, find the nearest named neighborhood point
      if (!foundName) {
        let bestDist = Infinity;
        for (const feature of cityData.layers.neighborhoods.features) {
          const name = (feature.properties as Record<string, unknown>)?.name as string | undefined;
          if (!name) continue;
          let fLat: number, fLng: number;
          if (feature.geometry.type === 'Point') {
            [fLng, fLat] = feature.geometry.coordinates as [number, number];
          } else if (feature.geometry.type === 'Polygon') {
            // Use centroid of first ring
            const ring = feature.geometry.coordinates[0];
            let sLat = 0, sLng = 0;
            for (const [lng, lat] of ring) { sLat += lat; sLng += lng; }
            fLat = sLat / ring.length; fLng = sLng / ring.length;
          } else continue;
          const dLat = (fLat - agentPos.lat) * 111000;
          const dLng = (fLng - agentPos.lng) * 111000 * Math.cos((agentPos.lat * Math.PI) / 180);
          const d = Math.sqrt(dLat * dLat + dLng * dLng);
          if (d < bestDist) {
            bestDist = d;
            foundName = name;
          }
        }
      }
      setGameState(prev => ({
        ...prev,
        districtScanName: foundName ?? 'Unknown area',
        districtScanTurn: prev.turn,
        player: applyHeadhunterPostIntelPlay({
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        }, runState),
      }));
      notify(`District Scan: Agent is near ${foundName ?? 'an unknown area'}`, 'intel', 4000);
      return;
    }

    // Acoustic Ping: instant play — reveal distance to agent as a ring
    if (card.name === 'Acoustic Ping') {
      const agentPos = gameState.agent.position;
      const playerPos = gameState.player.position;
      const dLat = (agentPos.lat - playerPos.lat) * 111000;
      const dLng = (agentPos.lng - playerPos.lng) * 111000 * Math.cos((playerPos.lat * Math.PI) / 180);
      const distM = Math.round(Math.sqrt(dLat * dLat + dLng * dLng));
      const ping: AcousticPing = {
        id: `ping-${Date.now()}`,
        position: { ...playerPos },
        distanceM: distM,
        turn: gameState.turn,
      };
      setGameState(prev => ({
        ...prev,
        acousticPings: [...prev.acousticPings, ping],
        player: applyHeadhunterPostIntelPlay({
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        }, runState),
      }));
      notify(`Acoustic Ping: Agent is ${distM}m away`, 'intel', 4000);
      return;
    }

    // Traffic Analysis: instant play — find the agent's nearest drivable
    // edge. If the road is classified major/minor AND has a name (i.e. an
    // OSM primary/secondary/tertiary), highlight every edge sharing the
    // same roadId for the current turn. Otherwise show a message overlay.
    if (card.name === 'Traffic Analysis') {
      const agentPos = gameState.agent.position;
      const nearestEdge = findNearestEdgeToPosition(agentPos.lat, agentPos.lng, cityData.navigation.edges);
      const onNamedArterial =
        nearestEdge &&
        (nearestEdge.classification === 'major' || nearestEdge.classification === 'minor') &&
        nearestEdge.name &&
        nearestEdge.roadId;
      if (!onNamedArterial) {
        // Still consume the card and AP — the read itself is the cost, not
        // the result. Same way Acoustic Ping works if the agent is offline.
        setGameState(prev => ({
          ...prev,
          player: applyHeadhunterPostIntelPlay({
            ...prev.player,
            ...routePlayedCard(prev.player, card),
            ap: prev.player.ap - card.apCost,
            lastPlayedMovement: false,
            cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          }, runState),
        }));
        notify('Traffic Analysis: agent is not on a major or minor road.', 'intel', 4000);
        return;
      }
      // Collect every edge that shares the nearest edge's roadId.
      const roadId = nearestEdge.roadId;
      const roadName = nearestEdge.name!;
      const roadEdges = cityData.navigation.edges.filter(e => e.roadId === roadId);
      const edgeCoordGroups: [number, number][][] = roadEdges.map(e => e.geometry);
      // Compute the center of the highlighted road so "SHOW ME" can fly the
      // camera to a reasonable anchor. We use the bbox midpoint of all
      // vertex coords across every edge in the road — cheap and robust for
      // both short dead-end roads and long arterials.
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      for (const group of edgeCoordGroups) {
        for (const [lng, lat] of group) {
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
          if (lng < minLng) minLng = lng;
          if (lng > maxLng) maxLng = lng;
        }
      }
      const roadCenter = {
        lat: (minLat + maxLat) / 2,
        lng: (minLng + maxLng) / 2,
      };
      setGameState(prev => ({
        ...prev,
        player: applyHeadhunterPostIntelPlay({
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          trafficAnalysisEdges: edgeCoordGroups,
          trafficAnalysisRoadName: roadName,
        }, runState),
      }));
      // Fire the alert modal AFTER the state commit so the highlight renders
      // behind it. setTimeout(0) matches the pattern used by spotter/stakeout
      // alerts and avoids setState-in-render warnings.
      setTimeout(() => setTrafficAnalysisAlert({ roadName, center: roadCenter }), 0);
      notify(`Traffic Analysis: agent is on ${roadName}.`, 'intel', 4000);
      return;
    }

    // Bearing: instant play — snapshot the direction from the player to the
    // agent at this exact moment. The line is static: it doesn't track the
    // agent as it moves. Multiple Bearing reads from different player
    // positions intersect, enabling triangulation without exact-position intel.
    if (card.name === 'Bearing') {
      const agentPos = gameState.agent.position;
      const playerPos = gameState.player.position;
      const read: BearingRead = {
        id: `bearing-${Date.now()}`,
        origin: { ...playerPos },
        bearingDeg: bearingDeg(playerPos, agentPos),
        turn: gameState.turn,
      };
      setGameState(prev => ({
        ...prev,
        bearings: [...prev.bearings, read],
        player: applyHeadhunterPostIntelPlay({
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        }, runState),
      }));
      notify(`Bearing: agent is to the ${compassOctant(read.bearingDeg)}. Move, read again, triangulate.`, 'intel', 4000);
      return;
    }

    // Recon Plane: enter placement mode. Click-drag-release on the map to
    // set center + angle. The placement isn't committed until the player
    // clicks CONFIRM on the banner that appears after release.
    if (card.name === 'Recon Plane') {
      if (reconPlaneMode && selectedCardId === card.id) {
        // Toggle off
        setReconPlaneMode(false);
        setReconPlanePending(null);
        setSelectedCardId(null);
      } else {
        setReconPlaneMode(true);
        setSelectedCardId(card.id);
        setReconPlanePending(null);
      }
      return;
    }

    // Stakeout: two-click placement. First click picks a commercial building,
    // second click anywhere sets the cone facing (bearing from building to
    // click). Card isn't consumed until second click commits.
    if (card.name === 'Stakeout') {
      if (stakeoutMode && selectedCardId === card.id) {
        setStakeoutMode(false);
        setStakeoutPendingBuilding(null);
        setSelectedCardId(null);
      } else {
        setStakeoutMode(true);
        setSelectedCardId(card.id);
        setStakeoutPendingBuilding(null);
      }
      return;
    }

    // Surveillance Van: hold-and-drag placement. The MapRenderer handles the
    // drag and passes back a locked-in drivable route on release. Card is
    // consumed by handleSurveillanceVanRoutePlace.
    if (card.name === 'Surveillance Van') {
      if (surveillanceVanMode && selectedCardId === card.id) {
        setSurveillanceVanMode(false);
        setSurveillanceVanPreview(null);
        setSelectedCardId(null);
      } else {
        setSurveillanceVanMode(true);
        setSelectedCardId(card.id);
        setSurveillanceVanPreview(null);
      }
      return;
    }

    // Trace Route: instant play — reveal the agent's movement path from 2+ turns ago.
    // Deliberately excludes the LAST turn's movement so it doesn't reveal the
    // agent's current location — only shows where they've been, not where they are.
    if (card.name === 'Trace Route') {
      const history = agentMovementHistoryRef.current;
      // Skip the most recent entry (current/last turn) — show the one before that
      const olderEntries = history.length >= 2 ? history.slice(0, -1) : [];
      const newPositions: Array<{ lat: number; lng: number; turn: number }> = [];
      for (const entry of olderEntries) {
        for (const [lng, lat] of entry.coords) {
          newPositions.push({ lat, lng, turn: entry.turn });
        }
      }
      if (newPositions.length === 0) {
        notify('Trace Route: Not enough movement history yet — try again in a couple turns.', 'general');
        return;
      }
      setGameState(prev => {
        const existingPositions = prev.traceRouteTrail?.positions ?? [];
        return {
          ...prev,
          traceRouteTrail: { positions: [...existingPositions, ...newPositions], turn: prev.turn },
          player: applyHeadhunterPostIntelPlay({
            ...prev.player,
            ...routePlayedCard(prev.player, card),
            ap: prev.player.ap - card.apCost,
            lastPlayedMovement: false,
            cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          }, runState),
        };
      });
      notify('Trace Route: Agent\'s older movement path revealed.', 'intel');
      return;
    }

    // Wire Tap: toggle selection mode — click a street segment to place
    if (card.name === 'Wire Tap') {
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
      } else {
        setSelectedCardId(card.id);
      }
      return;
    }

    // Misdirect: toggle selection — click map to draw a fake movement path (up to 300m)
    if (card.name === 'Misdirect') {
      if (selectedCardId === card.id) {
        setSelectedCardId(null);
      } else {
        setSelectedCardId(card.id);
        // Fake path originates at player's position — snap camera in.
        recenterForPlacement();
      }
      return;
    }

    // Improvised Strike — if no target visible, build +10% bonus for future ambush.
    // If target visible in ambush position, attack with accumulated bonus.
    // Rooftop exception: agentCanSeePlayer doesn't treat isElevated as
    // hidden (the player's gameplay position stays on the ground for
    // vision checks), so a player on a rooftop who's in the agent's cone
    // gets flagged "agent sees you too" and the ambush branch is blocked.
    // Treat elevation as "unseen from the agent's POV" for this card —
    // a rooftop throw-down is still an ambush, not a confrontation.
    if (card.name === 'Improvised Strike') {
      const canSee = playerCanSeeAgent(gameState.player, gameState.agent);
      const agentCanSee = gameState.player.isElevated
        ? false
        : agentCanSeePlayer(gameState.agent, gameState.player);
      if (!canSee) {
        // No target — prep mode: +10% to future improvised strike chance.
        // Stack on top of Sharpen's routePlayedCard bump so Improvised
        // Strike in prep mode with Sharpen active = +20% this play.
        setGameState(prev => {
          const routed = routePlayedCard(prev.player, card);
          return {
            ...prev,
            player: {
              ...prev.player,
              ...routed,
              ap: prev.player.ap - card.apCost,
              prepBonus: routed.prepBonus + 0.1,
              prepBuiltThisTurn: routed.prepBuiltThisTurn + 0.1,
            },
          };
        });
        return;
      }
      if (canSee && agentCanSee) {
        notify('The agent sees you too — this isn\'t an ambush. Use a weapon for a confrontation.', 'agent', 4000);
        return;
      }
      // Rooftop block: Improvised Strike is melee — you can't reach a
      // ground-level target from a roof. Descend first. Prep-build path
      // above remains allowed, so players can still stack prep on a roof
      // and cash it in once they descend.
      if (gameState.player.isElevated) {
        notify('Cannot strike from a rooftop. Descend to engage in melee.', 'general', 4000);
        return;
      }
      // Ambush with improvised strike — use accumulated bonus. Pass
      // agent so resolveAmbush can factor in the agent's weapon (armed
      // defender bonus) and their own built prep. Pass player so the
      // resolver can check for a flashbang (the only sanctioned escape
      // route when the strike fails).
      const encounter = resolveAmbush(null, true, gameState.player.prepBonus, gameState.agent, gameState.player);
      setCombatEncounter({ ...encounter, revealText: 'You strike from the shadows.' });
      setGameState(prev => {
        // Apply item consumption (flashbang on escape, vest on save — vest
        // isn't produced in ambush today but route it anyway for symmetry).
        const consumedItems = (encounter.flashbangUsed || encounter.vestExhausted)
          ? applyCombatItemEffects(prev.player.items, encounter)
          : prev.player.items;
        const nextPlayer = {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          items: consumedItems,
        };
        const roundFlag =
          encounter.outcome === 'player-kills' && killEndsRoundRef.current ? { roundResult: 'kill' as const } :
          encounter.outcome === 'player-dies' ? { roundResult: 'death' as const } :
          {};
        // Extraction op: kill clears the threat but the round continues.
        // Mark the agent dead so AI/detection/combat all stop.
        const agentFlag = encounter.outcome === 'player-kills' && !killEndsRoundRef.current
          ? { agent: { ...prev.agent, isDead: true } }
          : {};
        return { ...prev, player: nextPlayer, ...agentFlag, ...roundFlag };
      });
      return;
    }

    if (!card.movementRange) return;
    if (selectedCardId === card.id) {
      // Toggling off the same card — if partial move was used, discard it
      deselect();
    } else {
      // Selecting a different card — if previous had partial move, discard it first
      if (remainingRange !== null && selectedCard) {
        setGameState(prev => ({
          ...prev,
          player: {
            ...prev.player,
            ...routePlayedCard(prev.player, selectedCard),
            ap: prev.player.ap - selectedCard.apCost,
          },
        }));
      }
      setRemainingRange(null);
      setSelectedCardId(card.id);
      // Snap camera to player + zoom in for comfortable path tracing. Applies
      // to both Move and Shortcut (both have movementRange). See
      // recenterForPlacement for the full list of cards that use this.
      recenterForPlacement();
    }
  }, [gameState.player.ap, gameState.player.isInBuilding, gameState.player.isInTransit, gameState.player.isInVehicle, gameState.player.isElevated, gameState.player.activeBuffs, gameState.player.lastPlayedMovement, gameState.player.cardsPlayedThisTurn, selectedCardId, deselect, remainingRange, selectedCard, handleScavenge, recenterForPlacement]);

  const handleMovementComplete = useCallback((path: PathResult) => {
    if (!selectedCard) return;

    setGameState(prev => {
      // --- Mid-path detection: check if player spots/is spotted during movement ---
      const midDetection = checkPlayerMidPathDetection(path, prev.player, prev.agent);
      let effectivePath = path;

      if (midDetection) {
        // Truncate the path at the detection point
        const truncCoords: [number, number][] = [];
        for (let i = 0; i <= midDetection.truncateAtSegment; i++) {
          truncCoords.push(path.coordinates[i]);
        }
        truncCoords.push(midDetection.truncatedCoord);

        // Truncate edges: only include edges up to the truncation segment
        const truncEdges = path.edges.slice(0, midDetection.truncateAtSegment + 1);
        // Truncate nodes similarly
        const truncNodes = path.nodes.slice(0, midDetection.truncateAtSegment + 2);

        // Recalculate total distance for truncated path
        let truncDist = 0;
        for (let i = 0; i < truncCoords.length - 1; i++) {
          const [lng1, lat1] = truncCoords[i];
          const [lng2, lat2] = truncCoords[i + 1];
          const dLat = (lat2 - lat1) * 111000;
          const dLng = (lng2 - lng1) * 111000 * Math.cos((lat1 * Math.PI) / 180);
          truncDist += Math.sqrt(dLat * dLat + dLng * dLng);
        }

        effectivePath = {
          coordinates: truncCoords,
          edges: truncEdges,
          nodes: truncNodes,
          totalDistance: truncDist,
        };

        // Determine detection scenario
        const scenario = determineCombatScenario(midDetection.playerSeesAgent, midDetection.agentSeesPlayer, prev.agent, prev.player);

        if (scenario === 'ambush') {
          // Player sees agent but agent doesn't see player — DON'T force combat.
          // Just reveal the agent and let the player decide whether to engage.
          // Don't truncate the path — let them complete their move.
          effectivePath = path; // restore full path
          // Set "enemy seen" timer so agent shows on map
          setTimeout(() => {
            playWarning();
            setGameState(gs => ({
              ...gs,
              player: { ...gs.player, seenAgentTurnsLeft: 3 },
            }));
            notify('You spotted the agent! They don\'t see you.', 'agent');
          }, 0);
        } else if (scenario && prev.player.combatCooldownTurnsLeft > 0) {
          // Just survived an encounter — give one turn of amnesty so the
          // player can break contact instead of being re-engaged on the
          // next step. Restore full path and notify.
          effectivePath = path;
          setTimeout(() => {
            notify('You slipped past — no time to re-engage.', 'agent');
          }, 0);
        } else if (scenario) {
          // Confrontation or ambushed — truncate path and trigger combat
          const agentWeapon = prev.agent.equipment.find(e => e.category === 'weapon');
          const revealText = scenario === 'confrontation' ? 'Eyes locked. The agent sees you too.'
            : 'The agent has spotted you!';
          setTimeout(() => {
            playWarning();
            setCombatEncounter({ scenario, phase: 'reveal', agentWeapon, revealText });
          }, 0);
        }

        console.log(`[Detection] Player movement — ${scenario}`);
      }

      const lastCoord = effectivePath.coordinates[effectivePath.coordinates.length - 1];
      const endLat = lastCoord[1];
      const endLng = lastCoord[0];

      // Use the last node in the path as the graph anchor.
      // If the path ended mid-edge past this node, find whichever
      // end of that last edge is closest to the final position.
      let nearestId: string;
      if (effectivePath.nodes.length > 0) {
        const lastPathNode = effectivePath.nodes[effectivePath.nodes.length - 1];
        // Check if there's a closer node (the other end of the final edge)
        if (effectivePath.edges.length > 0) {
          const lastEdge = effectivePath.edges[effectivePath.edges.length - 1];
          const otherNodeId = lastEdge.from === lastPathNode.id ? lastEdge.to : lastEdge.from;
          const otherNode = cityData.navigation.nodes.find(n => n.id === otherNodeId);
          if (otherNode) {
            const dLast = (lastPathNode.lat - endLat) ** 2 + (lastPathNode.lng - endLng) ** 2;
            const dOther = (otherNode.lat - endLat) ** 2 + (otherNode.lng - endLng) ** 2;
            nearestId = dOther < dLast ? otherNodeId : lastPathNode.id;
          } else {
            nearestId = lastPathNode.id;
          }
        } else {
          nearestId = lastPathNode.id;
        }
      } else {
        const fallback = findNearestNode(endLat, endLng, cityData.navigation.nodes);
        nearestId = fallback?.id ?? prev.player.nearestNodeId;
      }

      // Remove the played move card AND any pending fieldcraft cards from hand.
      // Route the played card through routePlayedCard so Double Tap copies
      // (isBurn: true) correctly land in the burn pile instead of discard;
      // otherwise they shuffle back into the deck as permanent 0 AP copies.
      // Pending fieldcraft cards are never isBurn in practice, so they just go
      // to discard — but we still route them through the helper for safety.
      const pendingIds = pendingFieldcraftIds;
      const pendingCards = prev.player.hand.filter(c => pendingIds.has(c.id));
      const newHand = prev.player.hand.filter(c => c.id !== selectedCard.id && !pendingIds.has(c.id));
      let newDiscard: typeof prev.player.discard = [...prev.player.discard];
      let newBurnPile: typeof prev.player.burnPile = [...prev.player.burnPile];
      if (selectedCard.isBurn) newBurnPile.push(selectedCard);
      else newDiscard.push(selectedCard);
      for (const pc of pendingCards) {
        if (pc.isBurn) newBurnPile.push(pc);
        else newDiscard.push(pc);
      }
      const newAp = prev.player.ap - selectedCard.apCost;

      // Accumulate exposure from zones traversed (with diminishing returns).
      // Low Profile: all exposure gain from this movement is zeroed out
      // (both zone exposure and road-crossing bonus).
      const lowProfileBlocked = prev.player.lowProfileActive;
      let rawExpGain = lowProfileBlocked ? 0 : calculateExposureFromEdges(effectivePath.edges);
      // Vehicle on major roads: double exposure
      if (prev.player.isInVehicle) {
        const majorLength = effectivePath.edges
          .filter(e => e.classification === 'major')
          .reduce((sum, e) => sum + e.length, 0);
        if (majorLength > 0) rawExpGain *= 2;
      }
      // Disguise: 50% less exposure while active
      if (prev.player.disguiseTurnsLeft > 0) rawExpGain *= tuning.disguiseExposureMultiplier;
      const expGain = applyExposureDiminishing(prev.player.exposure, rawExpGain);
      let newExposure = Math.min(tuning.exposureMax, prev.player.exposure + expGain);
      // +35 exposure per major road crossed, +25 per minor road crossed during movement
      // Disguise also halves these road-crossing penalties. Low Profile zeroes
      // them entirely.
      const disguiseActive = prev.player.disguiseTurnsLeft > 0;
      const crossingMult = lowProfileBlocked ? 0 : (disguiseActive ? tuning.disguiseExposureMultiplier : 1);
      for (const edge of effectivePath.edges) {
        if (edge.classification === 'major') newExposure = Math.min(tuning.exposureMax, newExposure + 35 * crossingMult);
        else if (edge.classification === 'minor') newExposure = Math.min(tuning.exposureMax, newExposure + 25 * crossingMult);
      }

      // Calculate effective signal modifier from card + active fieldcraft buffs
      let effectiveSignalMod = selectedCard.signalModifier ?? 1;
      const endPos = { lat: endLat, lng: endLng };

      for (const buff of prev.player.activeBuffs) {
        if (buff.signalCondition === 'residential-zone') {
          // Fade: only zeroes signal if ending in residential zone
          const endZone = zoneAtPoint(endLng, endLat, zonePolygons);
          if (endZone === 'residential') {
            effectiveSignalMod = buff.signalModifier ?? 0;
          }
          // else: buff wasted, normal signal
        } else if (buff.signalModifier !== undefined) {
          effectiveSignalMod *= buff.signalModifier;
        }
      }

      // Vehicle on major roads: double signal
      if (prev.player.isInVehicle) {
        const majorLength = effectivePath.edges
          .filter(e => e.classification === 'major')
          .reduce((sum, e) => sum + e.length, 0);
        if (majorLength > 0) effectiveSignalMod *= 2;
      }

      // Ghost: zero all player signal for the rest of this turn
      if (prev.player.ghostActive) effectiveSignalMod = 0;
      // Cold Exit passive: zero signal for a few turns after leaving a safehouse
      if (prev.player.coldExitTurnsLeft > 0) effectiveSignalMod = 0;
      // Jammer: zero signal if player's end position is inside jammer zone
      if (prev.player.jammerTurnsLeft > 0 && prev.player.jammerPosition) {
        const jdLat = (endPos.lat - prev.player.jammerPosition.lat) * 111000;
        const jdLng = (endPos.lng - prev.player.jammerPosition.lng) * 111000 * Math.cos(endPos.lat * Math.PI / 180);
        if (Math.sqrt(jdLat * jdLat + jdLng * jdLng) <= prev.player.jammerRadius) effectiveSignalMod = 0;
      }

      // Disguise: proportional 75% signal reduction on commercial/major segments.
      if (prev.player.disguiseTurnsLeft > 0 && effectivePath.edges.length > 0) {
        const totalLen = effectivePath.edges.reduce((s, e) => s + e.length, 0);
        if (totalLen > 0) {
          const maskedLen = effectivePath.edges
            .filter(e => e.zone === 'commercial' || e.classification === 'major')
            .reduce((s, e) => s + e.length, 0);
          const fraction = maskedLen / totalLen;
          const reduction = fraction * (1 - tuning.disguiseSignalMultiplier);
          effectiveSignalMod *= 1 - reduction;
        }
      }

      // Background-bucket passives stack multiplicatively onto the card modifier.
      effectiveSignalMod *= computePassiveSignalMultiplier({
        edges: effectivePath.edges,
        isInVehicle: prev.player.isInVehicle,
        isFirstSignalThisTurn: prev.player.signalsGeneratedThisTurn === 0,
        origin: 'movement',
      }, runState);

      // Generate signal from this movement (exposure amplifies it)
      const signal = generateMovementSignal(
        endPos,
        effectivePath.edges,
        'player',
        prev.turn,
        effectiveSignalMod,
        newExposure,
      );

      // Auto-rotate vision cone to face direction of travel
      const newFacing = calcPathBearing(effectivePath.coordinates, prev.player.position);

      let player = {
        ...prev.player,
        position: endPos,
        nearestNodeId: nearestId,
        hand: newHand,
        discard: newDiscard,
        burnPile: newBurnPile,
        ap: newAp,
        facing: newFacing,
        exposure: newExposure,
        activeBuffs: [] as MovementBuff[], // consume all buffs after move
        lastPlayedMovement: true,
        cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        // Track for First Mover Advantage — any signal attempt counts, even if
        // it ended up below threshold. This matches the player's intuition that
        // "moving" used their first-action discount.
        signalsGeneratedThisTurn: prev.player.signalsGeneratedThisTurn + 1,
      };
      // Double Tap: if active, duplicate the movement card into hand
      if (selectedCard) {
        const dtResult = applyDoubleTap(player, selectedCard);
        if (dtResult) player = dtResult;
      }
      // Store road-level coords for debrief
      if (playerPathRef.current.length === 0) {
        playerPathRef.current.push(...effectivePath.coordinates);
      } else {
        playerPathRef.current.push(...effectivePath.coordinates.slice(1));
      }

      return {
        ...prev,
        signals: signal ? [...prev.signals, signal] : prev.signals,
        player,
      };
    });

    // Signal pulse — compute the final strength with the same helper used
    // by the map preview so the pulse number always matches what the
    // banner showed. All of this runs OUTSIDE the setGameState callback
    // and uses `path` (the handler argument) directly so variables aren't
    // accidentally closure-scoped to the updater.
    // Note: Shortcut card moves have zero edges (they cut through buildings)
    // but still need to show a pulse — the helper will return 0 strength
    // for them, which renders as "SIGNAL DARK".
    if (path.coordinates.length >= 2) {
      const lastCoord2 = path.coordinates[path.coordinates.length - 1];
      const pulseLat = lastCoord2[1];
      const pulseLng = lastCoord2[0];
      // Mirror Low Profile's exposure block so the signal-pulse post-move
      // exposure matches what the state reducer actually committed.
      const lpBlocked = gameState.player.lowProfileActive;
      const rawExpGain = lpBlocked ? 0 : calculateExposureFromEdges(path.edges);
      const expGain = applyExposureDiminishing(gameState.player.exposure, rawExpGain);
      const crossingMult = lpBlocked
        ? 0
        : (gameState.player.disguiseTurnsLeft > 0 ? tuning.disguiseExposureMultiplier : 1);
      let postMoveExposure = Math.min(tuning.exposureMax, gameState.player.exposure + expGain);
      for (const edge of path.edges) {
        if (edge.classification === 'major') postMoveExposure = Math.min(tuning.exposureMax, postMoveExposure + 35 * crossingMult);
        else if (edge.classification === 'minor') postMoveExposure = Math.min(tuning.exposureMax, postMoveExposure + 25 * crossingMult);
      }
      const passiveMult = computePassiveSignalMultiplier(
        {
          edges: path.edges,
          isInVehicle: gameState.player.isInVehicle,
          isFirstSignalThisTurn: gameState.player.signalsGeneratedThisTurn === 0,
          origin: 'movement',
        },
        runState,
      );
      const endZone = zoneAtPoint(pulseLng, pulseLat, zonePolygons);
      const { finalStrength } = computeMovementSignalStrength({
        edges: path.edges,
        cardSignalModifier: selectedCard.signalModifier ?? 1,
        activeBuffs: gameState.player.activeBuffs,
        isInVehicle: gameState.player.isInVehicle,
        ghostActive: gameState.player.ghostActive,
        coldExitTurnsLeft: gameState.player.coldExitTurnsLeft,
        jammerTurnsLeft: gameState.player.jammerTurnsLeft,
        jammerPosition: gameState.player.jammerPosition,
        jammerRadius: gameState.player.jammerRadius,
        disguiseTurnsLeft: gameState.player.disguiseTurnsLeft,
        endPos: { lat: pulseLat, lng: pulseLng },
        endZone,
        exposure: postMoveExposure,
        passiveMultiplier: passiveMult,
      });
      const pulseExpGain = Math.max(0, postMoveExposure - gameState.player.exposure);
      showSignalPulse(finalStrength, pulseLat, pulseLng, pulseExpGain);
    }

    setSelectedCardId(null);
    setRemainingRange(null);
    setPendingFieldcraftIds(new Set());
  }, [selectedCard, cityData.navigation.nodes, cityData.navigation.edges, gameState.player, zonePolygons, pendingFieldcraftIds, runState, showSignalPulse]);

  const handleMovementPartial = useCallback((path: PathResult) => {
    if (!selectedCard) return;

    // Use buffed range for first partial move (Sprint doubles range, vehicle doubles range)
    let baseRange = selectedCard.movementRange!;
    if (remainingRange === null) {
      for (const buff of gameState.player.activeBuffs) {
        if (buff.rangeMultiplier) baseRange *= buff.rangeMultiplier;
      }
      if (gameState.player.isInVehicle) baseRange *= 2;
    }
    const currentRange = remainingRange ?? baseRange;
    const newRemaining = Math.max(0, currentRange - path.totalDistance);

    // Check mid-path detection before state update (need it outside setGameState)
    const midDetection = checkPlayerMidPathDetection(path, gameState.player, gameState.agent);

    setGameState(prev => {
      let effectivePath: PathResult = path;

      if (midDetection) {
        const truncCoords: [number, number][] = [];
        for (let i = 0; i <= midDetection.truncateAtSegment; i++) {
          truncCoords.push(path.coordinates[i]);
        }
        truncCoords.push(midDetection.truncatedCoord);
        const truncEdges = path.edges.slice(0, midDetection.truncateAtSegment + 1);
        const truncNodes = path.nodes.slice(0, midDetection.truncateAtSegment + 2);
        let truncDist = 0;
        for (let i = 0; i < truncCoords.length - 1; i++) {
          const [lng1, lat1] = truncCoords[i];
          const [lng2, lat2] = truncCoords[i + 1];
          const dLat2 = (lat2 - lat1) * 111000;
          const dLng2 = (lng2 - lng1) * 111000 * Math.cos((lat1 * Math.PI) / 180);
          truncDist += Math.sqrt(dLat2 * dLat2 + dLng2 * dLng2);
        }
        effectivePath = { coordinates: truncCoords, edges: truncEdges, nodes: truncNodes, totalDistance: truncDist };

        const scenario = determineCombatScenario(midDetection.playerSeesAgent, midDetection.agentSeesPlayer, prev.agent, prev.player);

        if (scenario === 'ambush') {
          // Player sees agent but agent doesn't see player — don't force combat
          effectivePath = path; // restore full path
          setTimeout(() => {
            playWarning();
            setGameState(gs => ({
              ...gs,
              player: { ...gs.player, seenAgentTurnsLeft: 3 },
            }));
            notify('You spotted the agent! They don\'t see you.', 'agent');
          }, 0);
        } else if (scenario && prev.player.combatCooldownTurnsLeft > 0) {
          // Post-survive amnesty — see comment on the parallel check above.
          setTimeout(() => {
            notify('You slipped past — no time to re-engage.', 'agent');
          }, 0);
        } else if (scenario) {
          const agentWeapon = prev.agent.equipment.find(e => e.category === 'weapon');
          const revealText = scenario === 'confrontation' ? 'Eyes locked. The agent sees you too.'
            : 'The agent has spotted you!';
          setTimeout(() => {
            playWarning();
            setCombatEncounter({ scenario, phase: 'reveal', agentWeapon, revealText });
          }, 0);
        }
      }

      const lastCoord = effectivePath.coordinates[effectivePath.coordinates.length - 1];
      const endLat = lastCoord[1];
      const endLng = lastCoord[0];

      let nearestId: string;
      if (effectivePath.nodes.length > 0) {
        const lastPathNode = effectivePath.nodes[effectivePath.nodes.length - 1];
        if (effectivePath.edges.length > 0) {
          const lastEdge = effectivePath.edges[effectivePath.edges.length - 1];
          const otherNodeId = lastEdge.from === lastPathNode.id ? lastEdge.to : lastEdge.from;
          const otherNode = cityData.navigation.nodes.find(n => n.id === otherNodeId);
          if (otherNode) {
            const dLast = (lastPathNode.lat - endLat) ** 2 + (lastPathNode.lng - endLng) ** 2;
            const dOther = (otherNode.lat - endLat) ** 2 + (otherNode.lng - endLng) ** 2;
            nearestId = dOther < dLast ? otherNodeId : lastPathNode.id;
          } else {
            nearestId = lastPathNode.id;
          }
        } else {
          nearestId = lastPathNode.id;
        }
      } else {
        const fallback = findNearestNode(endLat, endLng, cityData.navigation.nodes);
        nearestId = fallback?.id ?? prev.player.nearestNodeId;
      }

      // Accumulate exposure from zones traversed (with diminishing returns).
      // Low Profile: zero exposure gain this turn.
      const lowProfileBlockedPartial = prev.player.lowProfileActive;
      let rawExpGainPartial = lowProfileBlockedPartial ? 0 : calculateExposureFromEdges(effectivePath.edges);
      if (prev.player.isInVehicle) {
        const majorLen = effectivePath.edges.filter(e => e.classification === 'major').reduce((s, e) => s + e.length, 0);
        if (majorLen > 0) rawExpGainPartial *= 2;
      }
      // Disguise: 50% less exposure while active
      if (prev.player.disguiseTurnsLeft > 0) rawExpGainPartial *= tuning.disguiseExposureMultiplier;
      const expGainPartial = applyExposureDiminishing(prev.player.exposure, rawExpGainPartial);
      let newExposure = Math.min(tuning.exposureMax, prev.player.exposure + expGainPartial);
      // +35 exposure per major road crossed, +25 per minor road crossed during movement.
      // Disguise halves these crossing penalties too; Low Profile zeroes them.
      {
        const crossingMult = lowProfileBlockedPartial
          ? 0
          : (prev.player.disguiseTurnsLeft > 0 ? tuning.disguiseExposureMultiplier : 1);
        for (const edge of effectivePath.edges) {
          if (edge.classification === 'major') newExposure = Math.min(tuning.exposureMax, newExposure + 35 * crossingMult);
          else if (edge.classification === 'minor') newExposure = Math.min(tuning.exposureMax, newExposure + 25 * crossingMult);
        }
      }

      // Calculate effective signal modifier from card + active fieldcraft buffs
      let effectiveSignalMod = selectedCard.signalModifier ?? 1;
      const endPos = { lat: endLat, lng: endLng };

      for (const buff of prev.player.activeBuffs) {
        if (buff.signalCondition === 'residential-zone') {
          const endZone = zoneAtPoint(endLng, endLat, zonePolygons);
          if (endZone === 'residential') {
            effectiveSignalMod = buff.signalModifier ?? 0;
          }
        } else if (buff.signalModifier !== undefined) {
          effectiveSignalMod *= buff.signalModifier;
        }
      }

      // Vehicle on major roads: double signal
      if (prev.player.isInVehicle) {
        const majorLen = effectivePath.edges.filter(e => e.classification === 'major').reduce((s, e) => s + e.length, 0);
        if (majorLen > 0) effectiveSignalMod *= 2;
      }

      // Ghost: zero all player signal for the rest of this turn
      if (prev.player.ghostActive) effectiveSignalMod = 0;
      // Cold Exit passive: zero signal for a few turns after leaving a safehouse
      if (prev.player.coldExitTurnsLeft > 0) effectiveSignalMod = 0;
      // Jammer: zero signal if player's end position is inside jammer zone
      if (prev.player.jammerTurnsLeft > 0 && prev.player.jammerPosition) {
        const jdLat = (endPos.lat - prev.player.jammerPosition.lat) * 111000;
        const jdLng = (endPos.lng - prev.player.jammerPosition.lng) * 111000 * Math.cos(endPos.lat * Math.PI / 180);
        if (Math.sqrt(jdLat * jdLat + jdLng * jdLng) <= prev.player.jammerRadius) effectiveSignalMod = 0;
      }

      // Disguise: proportional 75% signal reduction on commercial/major segments.
      if (prev.player.disguiseTurnsLeft > 0 && effectivePath.edges.length > 0) {
        const totalLen = effectivePath.edges.reduce((s, e) => s + e.length, 0);
        if (totalLen > 0) {
          const maskedLen = effectivePath.edges
            .filter(e => e.zone === 'commercial' || e.classification === 'major')
            .reduce((s, e) => s + e.length, 0);
          const fraction = maskedLen / totalLen;
          const reduction = fraction * (1 - tuning.disguiseSignalMultiplier);
          effectiveSignalMod *= 1 - reduction;
        }
      }

      // Background-bucket passives stack multiplicatively onto the card modifier.
      effectiveSignalMod *= computePassiveSignalMultiplier({
        edges: effectivePath.edges,
        isInVehicle: prev.player.isInVehicle,
        isFirstSignalThisTurn: prev.player.signalsGeneratedThisTurn === 0,
        origin: 'movement',
      }, runState);

      const signal = generateMovementSignal(
        endPos,
        effectivePath.edges,
        'player',
        prev.turn,
        effectiveSignalMod,
        newExposure,
      );

      // Auto-rotate vision cone to face direction of travel
      const newFacing = calcPathBearing(effectivePath.coordinates, prev.player.position);

      // Store road-level coords for debrief
      if (playerPathRef.current.length === 0) {
        playerPathRef.current.push(...effectivePath.coordinates);
      } else {
        playerPathRef.current.push(...effectivePath.coordinates.slice(1));
      }

      return {
        ...prev,
        signals: signal ? [...prev.signals, signal] : prev.signals,
        player: {
          ...prev.player,
          position: endPos,
          nearestNodeId: nearestId,
          exposure: newExposure,
          facing: newFacing,
          signalsGeneratedThisTurn: prev.player.signalsGeneratedThisTurn + 1,
        },
      };
    });

    // If mid-path detection happened, consume the card and stop movement.
    // Route the played card through routePlayedCard so DT burn-copies land
    // in the burn pile, not discard. Pending fieldcraft buffs are treated
    // the same way for safety — none of them are isBurn in practice.
    if (midDetection) {
      const pendingIds = pendingFieldcraftIds;
      setGameState(prev => {
        const pendingCards = prev.player.hand.filter(c => pendingIds.has(c.id));
        const routed = routePlayedCard(prev.player, selectedCard!);
        const nextDiscard = [...routed.discard];
        const nextBurnPile = [...routed.burnPile];
        for (const pc of pendingCards) {
          if (pc.isBurn) nextBurnPile.push(pc);
          else nextDiscard.push(pc);
        }
        return {
          ...prev,
          player: {
            ...prev.player,
            hand: routed.hand.filter(c => !pendingIds.has(c.id)),
            discard: nextDiscard,
            burnPile: nextBurnPile,
            ap: prev.player.ap - selectedCard!.apCost,
            activeBuffs: [],
          },
        };
      });
      setSelectedCardId(null);
      setRemainingRange(null);
      setPendingFieldcraftIds(new Set());
      return;
    }

    // If remaining range is very small, finish the card
    if (newRemaining < 10) {
      // Consume the card and deduct AP; route via routePlayedCard so burn-copies
      // land in the burn pile. Move pending fieldcraft buffs similarly.
      const pendingIds = pendingFieldcraftIds;
      setGameState(prev => {
        const pendingCards = prev.player.hand.filter(c => pendingIds.has(c.id));
        const routed = routePlayedCard(prev.player, selectedCard);
        const nextDiscard = [...routed.discard];
        const nextBurnPile = [...routed.burnPile];
        for (const pc of pendingCards) {
          if (pc.isBurn) nextBurnPile.push(pc);
          else nextDiscard.push(pc);
        }
        return {
          ...prev,
          player: {
            ...prev.player,
            hand: routed.hand.filter(c => !pendingIds.has(c.id)),
            discard: nextDiscard,
            burnPile: nextBurnPile,
            ap: prev.player.ap - selectedCard.apCost,
            activeBuffs: [],
          },
        };
      });
      setSelectedCardId(null);
      setRemainingRange(null);
      setPendingFieldcraftIds(new Set());
    } else {
      // Keep card active with reduced range
      setRemainingRange(newRemaining);
    }

    // Signal pulse — see note in handleMovementComplete. Use the path
    // argument (not the truncated effectivePath) since mid-path detection
    // doesn't change the signal that would have been emitted.
    if (path.coordinates.length >= 2) {
      const endLat2 = path.coordinates[path.coordinates.length - 1][1];
      const endLng2 = path.coordinates[path.coordinates.length - 1][0];
      // Mirror Low Profile's exposure block for the partial-move signal pulse.
      const lpBlockedPartial = gameState.player.lowProfileActive;
      const rawExpGain = lpBlockedPartial ? 0 : calculateExposureFromEdges(path.edges);
      const expGain = applyExposureDiminishing(gameState.player.exposure, rawExpGain);
      const crossingMult = lpBlockedPartial
        ? 0
        : (gameState.player.disguiseTurnsLeft > 0 ? tuning.disguiseExposureMultiplier : 1);
      let postMoveExposure = Math.min(tuning.exposureMax, gameState.player.exposure + expGain);
      for (const edge of path.edges) {
        if (edge.classification === 'major') postMoveExposure = Math.min(tuning.exposureMax, postMoveExposure + 35 * crossingMult);
        else if (edge.classification === 'minor') postMoveExposure = Math.min(tuning.exposureMax, postMoveExposure + 25 * crossingMult);
      }
      const passiveMult = computePassiveSignalMultiplier(
        {
          edges: path.edges,
          isInVehicle: gameState.player.isInVehicle,
          isFirstSignalThisTurn: gameState.player.signalsGeneratedThisTurn === 0,
          origin: 'movement',
        },
        runState,
      );
      const endZone = zoneAtPoint(endLng2, endLat2, zonePolygons);
      const { finalStrength } = computeMovementSignalStrength({
        edges: path.edges,
        cardSignalModifier: selectedCard.signalModifier ?? 1,
        activeBuffs: gameState.player.activeBuffs,
        isInVehicle: gameState.player.isInVehicle,
        ghostActive: gameState.player.ghostActive,
        coldExitTurnsLeft: gameState.player.coldExitTurnsLeft,
        jammerTurnsLeft: gameState.player.jammerTurnsLeft,
        jammerPosition: gameState.player.jammerPosition,
        jammerRadius: gameState.player.jammerRadius,
        disguiseTurnsLeft: gameState.player.disguiseTurnsLeft,
        endPos: { lat: endLat2, lng: endLng2 },
        endZone,
        exposure: postMoveExposure,
        passiveMultiplier: passiveMult,
      });
      const pulseExpGainPartial = Math.max(0, postMoveExposure - gameState.player.exposure);
      showSignalPulse(finalStrength, endLat2, endLng2, pulseExpGainPartial);
    }
  }, [selectedCard, remainingRange, cityData.navigation.nodes, zonePolygons, runState, gameState.player, showSignalPulse]);

  const handleMovementCancel = useCallback(() => {
    deselect();
  }, [deselect]);

  // Hide Inside / Rooftop Access: player selects a building
  const handleHideSelect = useCallback((buildingFeatureId: number, buildingInfo?: { centroid: { lat: number; lng: number }; levels: number | null }) => {
    if (!selectedCard) return;

    // Rooftop Access — climb to rooftop (no hiding, sets isElevated)
    if (selectedCard.name === 'Rooftop Access') {
      const rooftopPos = buildingInfo?.centroid ?? gameState.player.position;
      const levels = buildingInfo?.levels ?? null;
      const isLockpickVersion = selectedCard.id.startsWith('lockpick-rooftop-');
      setGameState(prev => {
        let newItems = prev.player.items;
        let newHand = prev.player.hand;
        let newDiscard = prev.player.discard;
        if (isLockpickVersion) {
          // Consume the lockpick item
          const lockpickIdx = newItems.findIndex(i => i.name === 'Lock Pick Set');
          if (lockpickIdx >= 0) {
            newItems = [...newItems];
            newItems.splice(lockpickIdx, 1);
          }
          // Virtual card — don't discard to deck
        } else {
          // Real card — remove from hand, add to discard
          newHand = newHand.filter(c => c.id !== selectedCard.id);
          newDiscard = [...newDiscard, selectedCard];
        }
        return {
          ...prev,
          player: {
            ...prev.player,
            isElevated: true,
            rooftopPosition: rooftopPos,
            rooftopBuildingLevels: levels,
            hidingBuildingId: buildingFeatureId,
            hand: newHand,
            discard: newDiscard,
            items: newItems,
            ap: prev.player.ap - selectedCard.apCost,
          },
        };
      });
      setSelectedCardId(null);
      return;
    }

    // Check if the selected building is residential by its actual building type tag
    const COMMERCIAL_BUILDING_TYPES = ['commercial', 'retail', 'office', 'industrial', 'warehouse', 'public', 'civic', 'government', 'church', 'cathedral', 'chapel', 'mosque', 'synagogue', 'temple', 'hospital', 'hotel', 'school', 'university', 'train_station', 'supermarket', 'mall', 'basilica'];
    const buildingFeature = cityData.layers.buildings.features[buildingFeatureId];
    const buildingType = (buildingFeature?.properties as any)?.type || 'yes';
    const isBuildingCommercial = COMMERCIAL_BUILDING_TYPES.includes(buildingType);

    // If building type is generic ('yes'), fall back to zone check
    const playerPos = gameState.player.position;
    const zone = zoneAtPoint(playerPos.lng, playerPos.lat, zonePolygons);
    const needsLockpick = !isBuildingCommercial && (buildingType !== 'yes' || zone === 'residential');

    if (needsLockpick) {
      const lockpickIdx = gameState.player.items.findIndex(
        i => i.name === 'Lock Pick Set'
      );
      if (lockpickIdx === -1) {
        // No lockpick — can't enter residential buildings
        // Deselect the card and show in-game notification
        setSelectedCardId(null);
        notify('You need a Lock Pick to enter a residential building.', 'general');
        return;
      }
      // Consume the lockpick
      setGameState(prev => {
        const newItems = [...prev.player.items];
        newItems.splice(lockpickIdx, 1);
        return {
          ...prev,
          player: {
            ...prev.player,
            isInBuilding: true,
            hidingBuildingId: buildingFeatureId,
            hidingTurnsLeft: tuning.hideDuration,
            ...routePlayedCard(prev.player, selectedCard),
            ap: prev.player.ap - selectedCard.apCost,
            items: newItems,
          },
        };
      });
    } else {
      // Non-residential zones — enter freely
      setGameState(prev => ({
        ...prev,
        player: {
          ...prev.player,
          isInBuilding: true,
          hidingBuildingId: buildingFeatureId,
          hidingTurnsLeft: tuning.hideDuration,
          ...routePlayedCard(prev.player, selectedCard),
          ap: prev.player.ap - selectedCard.apCost,
        },
      }));
    }
    setSelectedCardId(null);
  }, [selectedCard, gameState.player.position, gameState.player.items, zonePolygons]);

  // Decoy placement: player clicks map to place a fake signal
  const handleDecoyPlace = useCallback((lat: number, lng: number) => {
    if (!selectedCard || selectedCard.name !== 'Decoy') return;

    // Use player-selected radius
    const DECOY_STRENGTH = tuning.decoyStrength;
    const DECOY_RADIUS = decoyRadius;

    const decoySignal = {
      id: `signal-decoy-${gameState.signals.length}`,
      position: { lat, lng },
      actualPosition: { lat, lng },
      radius: DECOY_RADIUS,
      strength: DECOY_STRENGTH,
      age: 0,
      turnGenerated: gameState.turn,
      source: 'decoy' as const,
      type: 'passive' as const,
      revealed: true, // visible immediately
      neighborhoodName: 'decoy', // tag for hover label
    };

    setGameState(prev => ({
      ...prev,
      signals: [...prev.signals, decoySignal],
      player: {
        ...prev.player,
        ...routePlayedCard(prev.player, selectedCard),
        ap: prev.player.ap - selectedCard.apCost,
      },
    }));
    setSelectedCardId(null);
  }, [selectedCard, gameState.turn, gameState.signals.length, decoyRadius]);

  // Burner Phone placement: player clicks a point on the map to drop
  // the decoy signal. Unlike Decoy, the radius is fixed (tuning.
  // burnerPhoneRadius) — this is the "free Decoy without radius select"
  // reframe. Consumes the specific slot the user chose to use.
  const handleBurnerPhonePlace = useCallback((lat: number, lng: number) => {
    if (!burnerPhoneMode || burnerPhoneSlotIndex === null) return;
    setGameState(prev => {
      const slot = burnerPhoneSlotIndex;
      if (slot < 0 || slot >= prev.player.items.length) return prev;
      const item = prev.player.items[slot];
      if (!item.id.startsWith('burner-phone')) return prev;
      const newItems = [...prev.player.items];
      newItems.splice(slot, 1);
      const burnerSignal: import('../../types/game').Signal = {
        id: `signal-burner-${Date.now()}`,
        position: { lat, lng },
        actualPosition: { lat, lng },
        radius: tuning.burnerPhoneRadius,
        strength: tuning.burnerPhoneStrength,
        age: 0,
        turnGenerated: prev.turn,
        source: 'decoy' as const,
        type: 'passive' as const,
        revealed: true,
        neighborhoodName: 'burner-phone',
      };
      return {
        ...prev,
        signals: [...prev.signals, burnerSignal],
        player: { ...prev.player, items: newItems },
      };
    });
    setBurnerPhoneMode(false);
    setBurnerPhoneSlotIndex(null);
    notify('Burner Phone dropped — decoy signal broadcasting at target.', 'intel');
  }, [burnerPhoneMode, burnerPhoneSlotIndex]);

  // Jammer placement: click on map to drop the signal-suppression zone.
  // Fixed 350m radius, 3-turn duration. Absorbs ANY signal (player or
  // agent) whose origin is inside the zone — see the onmessage handler
  // for the agent-side filtering.
  const handleJammerPlace = useCallback((lat: number, lng: number) => {
    if (!jammerMode || jammerSlotIndex === null) return;
    setGameState(prev => {
      const slot = jammerSlotIndex;
      if (slot < 0 || slot >= prev.player.items.length) return prev;
      const item = prev.player.items[slot];
      if (!item.id.startsWith('jammer')) return prev;
      const newItems = [...prev.player.items];
      newItems.splice(slot, 1);
      return {
        ...prev,
        player: {
          ...prev.player,
          items: newItems,
          jammerTurnsLeft: 3,
          jammerPosition: { lat, lng },
          jammerRadius: 350,
        },
      };
    });
    setJammerMode(false);
    setJammerSlotIndex(null);
    notify('Jammer deployed — 350m suppression zone active for 3 turns.', 'intel');
  }, [jammerMode, jammerSlotIndex]);

  // Take the scavenged item (optionally after dropping one to make room)
  const handleScavengeTake = useCallback(() => {
    if (!scavengeDialog) return;
    setGameState(prev => {
      const idx = prev.placedItems.findIndex(pi => pi.id === scavengeDialog.placedItemId);
      if (idx === -1) return prev;
      if (prev.player.items.length >= getInventoryCap(runState)) return prev; // shouldn't happen — UI prevents it

      const newPlacedItems = [...prev.placedItems];
      newPlacedItems[idx] = { ...newPlacedItems[idx], collected: true };

      return {
        ...prev,
        placedItems: newPlacedItems,
        player: {
          ...prev.player,
          items: [...prev.player.items, scavengeDialog.item],
        },
      };
    });
    setScavengeDialog(null);
  }, [scavengeDialog]);

  // Drop an inventory item to make room during scavenge
  const handleScavengeDrop = useCallback((dropIndex: number) => {
    setGameState(prev => {
      const newItems = [...prev.player.items];
      newItems.splice(dropIndex, 1);
      return {
        ...prev,
        player: { ...prev.player, items: newItems },
      };
    });
  }, []);

  // Leave — close scavenge dialog without taking (item stays on map)
  const handleScavengeLeave = useCallback(() => {
    setScavengeDialog(null);
  }, []);

  // Trigger item-used animation for consumables
  const triggerItemUsedAnim = useCallback((item: Item, slotIdx: number) => {
    itemAnimKeyRef.current++;
    setItemUsedAnim({ name: item.name, iconId: item.id.replace(/-[a-z0-9]{6}$/, '').replace(/-loadout$/, ''), key: itemAnimKeyRef.current, slotIndex: slotIdx });
    setTimeout(() => setItemUsedAnim(null), 1500);
  }, []);

  // Inventory — use an item
  const handleItemUse = useCallback((slotIndex: number) => {
    const item = gameState.player.items[slotIndex];
    if (!item) return;

    // Strip the trailing random suffix, then also strip the "-loadout"
    // marker that buildStartingInventory adds so starter items ("pistol-
    // loadout-abc123", "combat-knife-loadout-abc123", etc.) hit the right
    // switch cases below. Without the second replace, a loadout pistol's
    // baseId is "pistol-loadout", which matches no case — clicking AMBUSH
    // silently does nothing and the weapon looks broken.
    const baseId = item.id
      .replace(/-[a-z0-9]{6}$/, '')
      .replace(/-loadout$/, '');

    // Check AP cost
    if (item.apCost > 0 && gameState.player.ap < item.apCost) return;

    // Trigger item-used animation for consumable items (before setGameState to avoid Strict Mode double-fire)
    // Radio Scanner is NOT in this list anymore — it's now a held-inventory
    // passive, always on while in the player's items, no explicit play action.
    const CONSUMABLE_IDS = ['stimulant', 'burner-phone', 'jammer', 'flashbang'];
    if (CONSUMABLE_IDS.includes(baseId)) {
      triggerItemUsedAnim(item, slotIndex);
    }

    setGameState(prev => {
      const newItems = [...prev.player.items];
      let apDelta = item.apCost;
      let extraState: Partial<typeof prev.player> = {};

      switch (baseId) {
        case 'stimulant':
          // +2 AP this turn, consumed
          extraState = {};
          apDelta = 0; // free to use
          newItems.splice(slotIndex, 1);
          setItemMenuIndex(null);
          return {
            ...prev,
            player: { ...prev.player, items: newItems, ap: prev.player.ap + 2 },
          };

        case 'burner-phone': {
          // Enter placement mode — player picks where to drop the decoy.
          // Item is consumed on commit (handleBurnerPhonePlace), not here.
          // Previously this placed the signal at the player's position
          // immediately; making it map-placeable turns it into a proper
          // free-Decoy-without-radius-select item.
          setBurnerPhoneMode(true);
          setBurnerPhoneSlotIndex(slotIndex);
          setItemMenuIndex(null);
          apDelta = 0; // AP is deducted on commit
          void extraState;
          return prev;
        }
        case 'burner-phone-legacy-unused': {
          // Unreachable — kept so the outer switch structure compiles if
          // we ever need the immediate-place variant back.
          const burnerSignal: import('../../types/game').Signal = {
            id: `signal-burner-${Date.now()}`,
            position: { ...prev.player.position },
            actualPosition: { ...prev.player.position },
            radius: tuning.burnerPhoneRadius,
            strength: tuning.burnerPhoneStrength,
            age: 0,
            turnGenerated: prev.turn,
            source: 'decoy' as const,
            type: 'passive' as const,
            revealed: true,
            neighborhoodName: 'burner-phone',
          };
          newItems.splice(slotIndex, 1);
          setItemMenuIndex(null);
          return {
            ...prev,
            signals: [...prev.signals, burnerSignal],
            player: { ...prev.player, items: newItems },
          };
        }

        case 'jammer': {
          // Enter placement mode — player picks where to drop the jammer.
          // Consumption + zone commit happens on handleJammerPlace. The
          // zone is now agnostic: it suppresses ANY signal (player or
          // agent) generated inside its radius, not just the player's
          // signal while standing in it. That makes jammers useful as
          // ambush zones or to plant in the agent's path.
          setJammerMode(true);
          setJammerSlotIndex(slotIndex);
          setItemMenuIndex(null);
          apDelta = 0;
          void extraState;
          return prev;
        }

        // Radio Scanner case removed — the item is now a held-inventory
        // passive, so there's no play action to consume it. See items.ts
        // and MapRenderer.tsx scannerActive check.

        case 'flashbang':
          // Break engagement — consume
          newItems.splice(slotIndex, 1);
          break;

        case 'bulletproof-vest':
          // Passive — absorbs hit, don't consume on "use"
          break;

        case 'binoculars':
          // Passive reusable — extend vision range (no consume)
          break;

        case 'lock-pick':
          // Passive — used automatically by Hide Inside, no active use
          break;

        case 'pistol':
        case 'sniper-rifle':
        case 'combat-knife': {
          // Combat — check if we have an ambush opportunity
          const canSeeAgent = playerCanSeeAgent(prev.player, prev.agent);
          const agentCanSee = agentCanSeePlayer(prev.agent, prev.player);
          if (!canSeeAgent) {
            notify('No target in sight — you need visual contact to attack.', 'agent');
            return prev;
          }
          if (canSeeAgent && !agentCanSee) {
            // Ambush — player already chose weapon, go through reveal then auto-resolve
            const check = canAmbushWith(item, prev.player);
            if (!check.valid) {
              notify(check.reason ?? 'Cannot use this weapon here.', 'agent');
              return prev;
            }
            setTimeout(() => {
              setCombatEncounter({
                scenario: 'ambush',
                phase: 'reveal',
                agentWeapon: prev.agent.equipment.find(e => e.category === 'weapon'),
                preSelectedWeapon: item,
                revealText: 'You take aim...',
              });
            }, 0);
            return prev;
          }
          if (canSeeAgent && agentCanSee) {
            // Confrontation — player chose weapon, go through reveal then resolve
            setTimeout(() => {
              setCombatEncounter({
                scenario: 'confrontation',
                phase: 'reveal',
                agentWeapon: prev.agent.equipment.find(e => e.category === 'weapon'),
                preSelectedWeapon: item,
                revealText: 'Eyes locked. The agent sees you too.',
              });
            }, 0);
            return prev;
          }
          break;
        }

        default:
          break;
      }

      return {
        ...prev,
        player: {
          ...prev.player,
          items: newItems,
          ap: prev.player.ap - apDelta,
          ...extraState,
        },
      };
    });

    setItemMenuIndex(null);
  }, [gameState.player.items, gameState.player.ap]);

  // Inventory — drop an item
  const handleItemDrop = useCallback((slotIndex: number) => {
    setGameState(prev => {
      const newItems = [...prev.player.items];
      newItems.splice(slotIndex, 1);
      return { ...prev, player: { ...prev.player, items: newItems } };
    });
    setItemMenuIndex(null);
  }, []);

  // Tuning panel changed — apply card tuning to existing cards in game state
  const handleTuningChange = useCallback(() => {
    setTuningVersion(v => v + 1);

    // Build a name→key lookup from CARD_TEMPLATES
    const nameToKey = new Map<string, string>();
    for (const [key, tpl] of Object.entries(CARD_TEMPLATES)) {
      nameToKey.set(tpl.name, key);
    }

    // Apply card tuning overrides (and passive card-cost reductions) to all
    // cards in hand/deck/discard. Passives are re-applied on every tuning
    // change so passive-adjusted costs don't get clobbered by a tuning reset.
    setGameState(prev => {
      const applyTuning = (card: Card): Card => {
        const templateKey = nameToKey.get(card.name);
        if (!templateKey) return card;
        const ct = tuning.cards[templateKey];
        if (!ct) return card;
        const withTuning: Card = {
          ...card,
          apCost: ct.apCost,
          ...(ct.movementRange !== undefined ? { movementRange: ct.movementRange } : {}),
          ...(ct.signalModifier !== undefined ? { signalModifier: ct.signalModifier } : {}),
          ...(ct.sweepRadius !== undefined ? { sweepRadius: ct.sweepRadius } : {}),
        };
        return applyPassivesToCard(withTuning, runState);
      };
      return {
        ...prev,
        player: {
          ...prev.player,
          hand: prev.player.hand.map(applyTuning),
          deck: prev.player.deck.map(applyTuning),
          discard: prev.player.discard.map(applyTuning),
          visionConeWidth: prev.player.instinctTurnsLeft > 0 ? 360 : tuning.visionConeAngle,
          maxAp: tuning.playerMaxAp,
        },
      };
    });
  }, [runState]);

  // Facing change — player drags the vision cone handle
  const handleFacingChange = useCallback((degrees: number) => {
    setGameState(prev => ({
      ...prev,
      player: { ...prev.player, facing: degrees },
    }));
  }, []);

  // Exit building early (or descend from rooftop)
  const handleExitBuilding = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        isInBuilding: false,
        isElevated: false,
        rooftopPosition: null,
        rooftopBuildingLevels: null,
        hidingBuildingId: null,
        hidingTurnsLeft: 0,
      },
    }));
  }, []);

  // Transit stops can snap to disconnected subway/tram nodes. Find a
  // well-connected street node within 500m so the player can walk after
  // exiting/boarding. Falls back to any connected node in the candidate
  // set (no distance cap) before falling back to raw nearest — this
  // handles deep-underground subway lines (Osaka was the motivating case)
  // where the station centroid is hundreds of meters from the surface
  // street network. 500m is wide enough that real subway stops almost
  // always find a connected node inside the cap; the wider search budget
  // (150 candidates) catches outliers that used to strand the player.
  const findConnectedNode = useCallback((lat: number, lng: number) => {
    const candidates = findNearestNodes(lat, lng, cityData.navigation.nodes, 150);
    for (const node of candidates) {
      if (node.edges.length >= 2) {
        const dLat = (node.lat - lat) * 111000;
        const dLng = (node.lng - lng) * 111000 * Math.cos((lat * Math.PI) / 180);
        const dist = Math.sqrt(dLat * dLat + dLng * dLng);
        if (dist <= 500) return node;
      }
    }
    // Second pass: any connected node, regardless of distance. Preferable
    // to handing back a disconnected candidate[0] that traps the player.
    for (const node of candidates) {
      if (node.edges.length >= 2) return node;
    }
    return candidates[0] ?? null;
  }, [cityData.navigation.nodes]);

  // Board a transit line — snap player to the stop and start riding in the chosen direction.
  // Generates a strong boarding signal. 0 AP.
  const handleBoardTransit = useCallback((lineId: string, stopIndex: number, direction: 'forward' | 'backward') => {
    const line = findLineById(cityData.layers.transit.lines, lineId);
    if (!line || stopIndex < 0 || stopIndex >= line.stops.length) return;
    const stop = line.stops[stopIndex];

    setGameState(prev => {
      const nearest = findConnectedNode(stop.lat, stop.lng);
      // Subway Surfer passive OR active Disguise: masks the agent's
      // ground-truth knowledge of transit boardings (knownPlayerTransit).
      // The station-ping signal itself is kept at full strength — the bug
      // that was making Subway Surfer ineffective wasn't about signals,
      // it was agent.knownPlayerTransit being re-populated every end-of-turn
      // by the refresh at line ~5127. That leak is now gated separately.
      const transitMasked = hasPassive(runState, 'subwaySurfer') || prev.player.disguiseTurnsLeft > 0;
      // Strong boarding signal at the stop — agent belief-updater treats
      // this via the normal signal-integration path.
      const boardSignal: import('../../types/game').Signal = {
        id: `signal-transit-board-${Date.now()}`,
        position: { lat: stop.lat, lng: stop.lng },
        actualPosition: { lat: stop.lat, lng: stop.lng },
        radius: 100,
        strength: tuning.transitBoardSignal,
        age: 0,
        turnGenerated: prev.turn,
        source: 'player',
        type: 'passive',
        revealed: true,
      };
      return {
        ...prev,
        signals: [...prev.signals, boardSignal],
        player: {
          ...prev.player,
          position: { lat: stop.lat, lng: stop.lng },
          nearestNodeId: nearest?.id ?? prev.player.nearestNodeId,
          exposure: Math.min(tuning.exposureMax, prev.player.exposure + 100),
          isInTransit: true,
          transitLineId: lineId,
          transitDirection: direction,
          transitStopIndex: stopIndex,
        },
        // Ground-truth: tell the agent the player just boarded (unless masked).
        agent: transitMasked
          ? prev.agent
          : {
              ...prev.agent,
              knownPlayerTransit: { lineId, direction, stopIndex },
            },
      };
    });
    setBoardTransitOpen(false);
    notify(`Boarded ${line.name} — ${direction}. Strong signal at station.`, 'general');
  }, [cityData, findConnectedNode, runState]);

  // Exit transit — drop off at the current stop and generate strong exit signal. 0 AP.
  const handleExitTransit = useCallback(() => {
    setGameState(prev => {
      const line = prev.player.transitLineId ? findLineById(cityData.layers.transit.lines, prev.player.transitLineId) : null;
      const stop = line && prev.player.transitStopIndex >= 0 && prev.player.transitStopIndex < line.stops.length
        ? line.stops[prev.player.transitStopIndex]
        : null;
      // Subway Surfer or active Disguise: masks the agent's ground-truth
      // knownPlayerTransit (preserved in the return below via transitMasked)
      // but does NOT suppress the exit-ping signal itself. The fundamental
      // Subway Surfer leak was the per-turn newKnownPlayerTransit refresh
      // in the end-of-turn block, not the signal — so signal stays full.
      const transitMasked = hasPassive(runState, 'subwaySurfer') || prev.player.disguiseTurnsLeft > 0;
      const signals = [...prev.signals];
      if (stop) {
        signals.push({
          id: `signal-transit-exit-${Date.now()}`,
          position: { lat: stop.lat, lng: stop.lng },
          actualPosition: { lat: stop.lat, lng: stop.lng },
          radius: 100,
          strength: tuning.transitBoardSignal,
          age: 0,
          turnGenerated: prev.turn,
          source: 'player',
          type: 'passive',
          revealed: true,
        });
      }
      // Snap to a connected street node so the player can walk after exit
      const connected = stop ? findConnectedNode(stop.lat, stop.lng) : null;
      const exitPos = connected
        ? { lat: connected.lat, lng: connected.lng }
        : prev.player.position;
      return {
        ...prev,
        signals,
        player: {
          ...prev.player,
          position: exitPos,
          nearestNodeId: connected?.id ?? prev.player.nearestNodeId,
          exposure: Math.min(tuning.exposureMax, prev.player.exposure + 100),
          isInTransit: false,
          transitLineId: null,
          transitDirection: null,
          transitStopIndex: 0,
        },
        // Ground-truth: player exited transit — clear agent's tracking (unless masked).
        agent: transitMasked
          ? prev.agent
          : {
              ...prev.agent,
              knownPlayerTransit: null,
            },
      };
    });
  }, [cityData, findConnectedNode, runState]);

  // Stage Exit confirm — chosen line + direction from the picker. Emits a
  // fake boarding signal at the start stop and a fake exit signal at the
  // adjacent stop in the chosen direction, then commits the agent's belief
  // to the fake exit with high confidence. Player stays put. Consumes the
  // pending card and its AP.
  const handleStageExitConfirm = useCallback((lineId: string, stopIndex: number, direction: 'forward' | 'backward') => {
    const line = findLineById(cityData.layers.transit.lines, lineId);
    if (!line) return;
    const nextIdx = direction === 'forward' ? stopIndex + 1 : stopIndex - 1;
    if (nextIdx < 0 || nextIdx >= line.stops.length) {
      notify('No stop in that direction.', 'general');
      return;
    }
    const startStop = line.stops[stopIndex];
    const endStop = line.stops[nextIdx];

    // Compute the line polyline between the two stops so the map can draw a
    // yellow line along the fake ride. Falls back to a straight segment if
    // the line has no geometry.
    const segment = getLineSegmentBetweenStops(line, stopIndex, nextIdx) ?? [
      [startStop.lng, startStop.lat],
      [endStop.lng, endStop.lat],
    ];

    setGameState(prev => {
      const pendingId = stageExitPendingCardId;
      if (!pendingId) return prev;
      const card = prev.player.hand.find(c => c.id === pendingId);
      if (!card) return prev;

      const boardSignal: import('../../types/game').Signal = {
        id: `signal-stage-board-${Date.now()}`,
        position: { lat: startStop.lat, lng: startStop.lng },
        actualPosition: { lat: startStop.lat, lng: startStop.lng },
        radius: 100,
        strength: tuning.transitBoardSignal,
        age: 0,
        turnGenerated: prev.turn,
        source: 'player',
        type: 'passive',
        revealed: true,
      };
      const exitSignal: import('../../types/game').Signal = {
        id: `signal-stage-exit-${Date.now()}`,
        position: { lat: endStop.lat, lng: endStop.lng },
        actualPosition: { lat: endStop.lat, lng: endStop.lng },
        radius: 100,
        strength: tuning.transitBoardSignal,
        age: 0,
        turnGenerated: prev.turn,
        source: 'player',
        type: 'passive',
        revealed: true,
      };
      return {
        ...prev,
        signals: [...prev.signals, boardSignal, exitSignal],
        player: {
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
          lastPlayedMovement: false,
          stageExitPath: segment,
          // Record the turn this was played on so the turn-change effect in
          // GameScreen can clear it once the following turn ends. Players
          // want the yellow line visible during both the turn they played
          // Stage Exit AND the following turn — it's their visual reminder
          // of the false intel the agent is chasing.
          stageExitPathTurn: prev.turn,
        },
        agent: {
          ...prev.agent,
          belief: {
            ...prev.agent.belief,
            position: { lat: endStop.lat, lng: endStop.lng },
            confidence: 0.85,
            radius: 150,
            lastUpdated: prev.turn,
          },
          // Clear any real transit ground-truth — the fake exit replaces it.
          knownPlayerTransit: null,
        },
      };
    });
    notify(`Stage Exit: agent believes you rode to ${endStop.name ?? 'the next stop'}.`, 'general');
    setStageExitPendingCardId(null);
    setSelectedCardId(null);
  }, [cityData, stageExitPendingCardId]);

  // Recon Plane: click-drag-release handler from MapRenderer. Stores the
  // pending placement — doesn't commit until the player clicks CONFIRM.
  const handleReconPlanePlace = useCallback((center: { lat: number; lng: number }, angleDeg: number) => {
    if (!selectedCard || selectedCard.name !== 'Recon Plane') return;
    // Compute strip length to span the full map diagonal (plus overshoot).
    const b = cityData.bounds;
    const dLatM = (b.north - b.south) * 111000;
    const dLngM = (b.east - b.west) * 111000 * Math.cos(((b.north + b.south) / 2) * Math.PI / 180);
    const lengthM = 2 * Math.sqrt(dLatM * dLatM + dLngM * dLngM);
    setReconPlanePending({
      center,
      angleDeg,
      lengthM,
      widthM: 300,
      cardId: selectedCard.id,
    });
  }, [selectedCard, cityData.bounds]);

  // Recon Plane confirm — run the yes/no check, store the strip in gameState,
  // consume the card and AP, exit placement mode.
  const handleReconPlaneConfirm = useCallback(() => {
    if (!reconPlanePending) return;

    // Compute hit up front so we can notify + store the same value.
    // Perpendicular distance from agent to the strip's center line.
    // Width axis (perpendicular to length, CCW): (-cos θ, sin θ).
    // Length axis: (sin θ, cos θ).
    const theta = (reconPlanePending.angleDeg * Math.PI) / 180;
    const pE = -Math.cos(theta);
    const pN = Math.sin(theta);
    const lE = Math.sin(theta);
    const lN = Math.cos(theta);
    const ap = gameState.agent.position;
    const dE = (ap.lng - reconPlanePending.center.lng) * 111000 * Math.cos((reconPlanePending.center.lat * Math.PI) / 180);
    const dN = (ap.lat - reconPlanePending.center.lat) * 111000;
    const perpDist = Math.abs(dE * pE + dN * pN);
    const parallelDist = Math.abs(dE * lE + dN * lN);
    const hit = perpDist <= reconPlanePending.widthM / 2 && parallelDist <= reconPlanePending.lengthM / 2;

    setGameState(prev => {
      const card = prev.player.hand.find(c => c.id === reconPlanePending.cardId);
      if (!card) return prev;
      const strip: ReconStrip = {
        id: `recon-${Date.now()}`,
        center: reconPlanePending.center,
        angleDeg: reconPlanePending.angleDeg,
        widthM: reconPlanePending.widthM,
        lengthM: reconPlanePending.lengthM,
        hit,
        turn: prev.turn,
      };
      return {
        ...prev,
        reconStrips: [...prev.reconStrips, strip],
        player: applyHeadhunterPostIntelPlay({
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        }, runState),
      };
    });
    // Feedback is the on-map strip itself — red with ⚠ CONTACT label if hit,
    // gray with NO MATCH label if miss. Same pattern as Sweep. No notify needed.
    setReconPlaneMode(false);
    setReconPlanePending(null);
    setSelectedCardId(null);
  }, [reconPlanePending, runState, gameState.agent.position]);

  const handleReconPlaneCancel = useCallback(() => {
    setReconPlaneMode(false);
    setReconPlanePending(null);
    setSelectedCardId(null);
  }, []);

  // Stakeout building selection — first click in two-click placement. Receives
  // the centroid of the clicked commercial building and its feature index.
  // MapRenderer's stakeout placement useEffect gates this to commercial
  // buildings only (same filter as Rooftop Access).
  const handleStakeoutBuildingSelect = useCallback((buildingFeatureId: number, centroid: { lat: number; lng: number }) => {
    if (!selectedCard || selectedCard.name !== 'Stakeout') return;
    setStakeoutPendingBuilding({
      position: centroid,
      buildingFeatureId,
      cardId: selectedCard.id,
    });
  }, [selectedCard]);

  // Stakeout angle commit — second click. Computes the bearing from the chosen
  // building centroid to the click point, creates the StakeoutDevice, consumes
  // the card and AP, and exits placement mode.
  const handleStakeoutAngleCommit = useCallback((clickLat: number, clickLng: number) => {
    if (!stakeoutPendingBuilding) return;
    const facing = bearingDeg(stakeoutPendingBuilding.position, { lat: clickLat, lng: clickLng });
    setGameState(prev => {
      const card = prev.player.hand.find(c => c.id === stakeoutPendingBuilding.cardId);
      if (!card) return prev;
      const stakeout: StakeoutDevice = {
        id: `stakeout-${Date.now()}`,
        position: stakeoutPendingBuilding.position,
        buildingFeatureId: stakeoutPendingBuilding.buildingFeatureId,
        facingDeg: facing,
        coneAngleDeg: tuning.stakeoutConeAngle,
        coneRangeM: tuning.stakeoutConeRange,
        turnPlaced: prev.turn,
        agentInCone: false,
      };
      return {
        ...prev,
        stakeouts: [...prev.stakeouts, stakeout],
        player: applyHeadhunterPostIntelPlay({
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        }, runState),
      };
    });
    notify('Stakeout deployed — a comrade is now watching that rooftop.', 'intel', 4000);
    setStakeoutMode(false);
    setStakeoutPendingBuilding(null);
    setSelectedCardId(null);
  }, [stakeoutPendingBuilding, runState]);

  // Enter safehouse — snap to position, mark visited, open draft, hide player
  const handleEnterSafehouse = useCallback((safehouseId: string) => {
    const sh = gameState.safehouses.find(s => s.id === safehouseId);
    if (!sh) return;
    const exposurePct = gameState.player.exposure / tuning.exposureMax;
    if (exposurePct > 0.5) {
      notify('Exposure too high to enter safehouse. Must be below 50%.', 'general');
      return;
    }
    const connected = findConnectedNode(sh.lat, sh.lng);
    setGameState(prev => ({
      ...prev,
      safehouses: prev.safehouses.map(s => s.id === safehouseId ? { ...s, visited: true } : s),
      // Entering any safehouse reveals the agent's dossier for the round.
      // The Target panel reads this flag to switch from silhouette to
      // full loadout view (face, weapon, passives, deck composition).
      targetRevealed: true,
      player: {
        ...prev.player,
        position: connected ? { lat: connected.lat, lng: connected.lng } : { lat: sh.lat, lng: sh.lng },
        nearestNodeId: connected?.id ?? prev.player.nearestNodeId,
        isInSafehouse: true,
        isInBuilding: true,
        safehousePosition: { lat: sh.lat, lng: sh.lng },
        safehouseTurnsLeft: 3,
        hidingTurnsLeft: 4,
      },
    }));
    // Roll the draft options NOW, but stash them in pendingSafehouseDraft
    // while the agent dossier dialog is up. Dialog dismissal promotes the
    // pending set into the actual draft state.
    const anyKeys = Object.entries(CARD_TEMPLATES)
      .filter(([, t]) => t.category !== 'junk')
      .map(([k]) => k);
    const shuffled = [...anyKeys].sort(() => Math.random() - 0.5);
    const picks = shuffled.slice(0, 3).map(k => ({ ...CARD_TEMPLATES[k], id: `draft-${k}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }));
    setPendingSafehouseDraft(picks);
  }, [gameState.safehouses, gameState.player.exposure, findConnectedNode]);

  // Dismiss the safehouse dossier dialog: flush pending draft into the
  // real draft state so the pick UI opens next.
  const handleSafehouseDossierContinue = useCallback(() => {
    if (pendingSafehouseDraft) {
      setSafehouseDraft({ phase: 'any', options: pendingSafehouseDraft });
      setPendingSafehouseDraft(null);
    }
  }, [pendingSafehouseDraft]);

  const handleSafehousePick = useCallback((card: Card | null) => {
    if (!safehouseDraft) return;
    if (card) {
      // Bake passive card-cost reductions into the card before it enters the deck.
      // Mark it as extract-required so the hand UI displays an EXTRACT keyword
      // badge — the player keeps this card permanently only if they survive
      // to the extraction, so they need to know what's at stake.
      const passiveCard: Card = {
        ...applyPassivesToCard(card, runState),
        isExtractRequired: true,
      };
      // Add to deck
      setGameState(prev => ({
        ...prev,
        player: { ...prev.player, deck: [...prev.player.deck, passiveCard] },
      }));
      // Track for post-extraction summary
      safehouseCardsPickedRef.current = [...safehouseCardsPickedRef.current, passiveCard];
    }
    // Single-pick flow — close the draft whether the player chose a card
    // or skipped. No more phase advancement.
    setSafehouseDraft(null);
  }, [safehouseDraft, runState]);

  // Exit safehouse — reset exposure to 0, clear building state
  const handleExitSafehouse = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        isInSafehouse: false,
        isInBuilding: false,
        safehouseTurnsLeft: 0,
        safehousePosition: null,
        hidingTurnsLeft: 0,
        hidingBuildingId: null,
        exposure: 0,
        // Cold Exit passive: 3 turns of zero-signal grace after leaving a safehouse.
        coldExitTurnsLeft: hasPassive(runState, 'coldExit') ? 3 : prev.player.coldExitTurnsLeft,
      },
    }));
  }, [runState]);

  // Misdirect placement: player clicks map to draw a fake movement path (300m)
  // Generates a real signal at the endpoint as if the player walked there.
  const handleMisdirectPlace = useCallback((lat: number, lng: number) => {
    if (!selectedCard || selectedCard.name !== 'Misdirect') return;
    const playerPos = gameState.player.position;
    const MISDIRECT_RANGE = 300;
    // Try within range first, then try longer and truncate
    let path = findPathBetweenPoints(
      playerPos.lat, playerPos.lng,
      lat, lng,
      cityData.navigation.nodes,
      cityData.navigation.edges,
      MISDIRECT_RANGE,
    );
    if (!path || path.coordinates.length < 2) {
      // Over range — find longer path and truncate to 300m
      const longPath = findPathBetweenPoints(
        playerPos.lat, playerPos.lng,
        lat, lng,
        cityData.navigation.nodes,
        cityData.navigation.edges,
        MISDIRECT_RANGE * 5,
      );
      if (longPath && longPath.coordinates.length >= 2) {
        // Truncate to 300m
        let accumulated = 0;
        let truncIdx = longPath.coordinates.length - 1;
        for (let i = 0; i < longPath.coordinates.length - 1; i++) {
          const [lng1, lat1] = longPath.coordinates[i];
          const [lng2, lat2] = longPath.coordinates[i + 1];
          const dLat = (lat2 - lat1) * 111000;
          const dLng = (lng2 - lng1) * 111000 * Math.cos(lat1 * Math.PI / 180);
          accumulated += Math.sqrt(dLat * dLat + dLng * dLng);
          if (accumulated >= MISDIRECT_RANGE) { truncIdx = i + 1; break; }
        }
        path = {
          ...longPath,
          coordinates: longPath.coordinates.slice(0, truncIdx + 1),
          edges: longPath.edges.slice(0, truncIdx),
          totalDistance: Math.min(longPath.totalDistance, MISDIRECT_RANGE),
        };
      }
    }
    if (!path || path.coordinates.length < 2) {
      notify('No reachable path. Click closer to a road.', 'general', 2000);
      return;
    }
    // The fake signal appears at the end of the path
    const endCoord = path.coordinates[path.coordinates.length - 1];
    const fakeEndPos = { lat: endCoord[1], lng: endCoord[0] };
    // Method Actor boosts misdirect strength (+25%); other Background passives
    // do not apply to misdirects because the fake signal is not "you" moving.
    const misdirectMod = computePassiveSignalMultiplier({
      edges: path.edges,
      isInVehicle: gameState.player.isInVehicle,
      isFirstSignalThisTurn: gameState.player.signalsGeneratedThisTurn === 0,
      origin: 'misdirect',
    }, runState);
    // Generate signal as if the player actually moved there
    const fakeSignal = generateMovementSignal(
      fakeEndPos,
      path.edges,
      'player',
      gameState.turn,
      1 * misdirectMod, // full signal strength × passive modifiers
      gameState.player.exposure,
    );
    setGameState(prev => ({
      ...prev,
      signals: fakeSignal ? [...prev.signals, fakeSignal] : prev.signals,
      player: {
        ...prev.player,
        ...routePlayedCard(prev.player, selectedCard),
        ap: prev.player.ap - selectedCard.apCost,
        lastPlayedMovement: false,
        cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        misdirectPath: path.coordinates,
        // Record the turn the path was laid down so the turn-watcher effect
        // can expire it at end of turn N+1 (same pattern as Stage Exit).
        misdirectPathTurn: prev.turn,
        signalsGeneratedThisTurn: prev.player.signalsGeneratedThisTurn + 1,
      },
    }));
    setSelectedCardId(null);
    notify('Misdirect: fake movement signal deployed.', 'intel');
  }, [selectedCard, gameState.player.position, gameState.turn, gameState.player.exposure, gameState.player.isInVehicle, gameState.player.signalsGeneratedThisTurn, cityData.navigation.nodes, cityData.navigation.edges, runState]);

  // Doppelgänger placement: click on map → pathfind up to 800m from player,
  // store the path + set 4 turns remaining. Card is BURN (one-shot per round).
  const handleDoppelgangerPlace = useCallback((lat: number, lng: number) => {
    if (!selectedCard || (selectedCard.name !== 'Doppelgänger' && selectedCard.name !== 'Doppelganger')) return;
    const playerPos = gameState.player.position;
    const DOPPELGANGER_RANGE = 800;
    // Find path (long search budget, then truncate to 800m)
    const longPath = findPathBetweenPoints(
      playerPos.lat, playerPos.lng,
      lat, lng,
      cityData.navigation.nodes,
      cityData.navigation.edges,
      DOPPELGANGER_RANGE * 3,
    );
    if (!longPath || longPath.coordinates.length < 2) {
      notify('Could not pathfind a route. Click closer to a road.', 'general', 2000);
      return;
    }
    // Truncate at 800m
    let accumulated = 0;
    let truncIdx = longPath.coordinates.length - 1;
    for (let i = 0; i < longPath.coordinates.length - 1; i++) {
      const [lng1, lat1] = longPath.coordinates[i];
      const [lng2, lat2] = longPath.coordinates[i + 1];
      const dLat = (lat2 - lat1) * 111000;
      const dLng = (lng2 - lng1) * 111000 * Math.cos(lat1 * Math.PI / 180);
      accumulated += Math.sqrt(dLat * dLat + dLng * dLng);
      if (accumulated >= DOPPELGANGER_RANGE) { truncIdx = i + 1; break; }
    }
    const pathCoords = longPath.coordinates.slice(0, truncIdx + 1);
    const pathEdges = longPath.edges.slice(0, truncIdx).map(e => e.id);
    if (pathCoords.length < 2) {
      notify('Path too short.', 'general', 2000);
      return;
    }
    setGameState(prev => ({
      ...prev,
      doppelganger: {
        pathCoords,
        pathEdges,
        currentPos: { lat: pathCoords[0][1], lng: pathCoords[0][0] },
        distanceWalked: 0,
        direction: 1,
        turnsLeft: 4,
      },
      player: {
        ...prev.player,
        ...routePlayedCard(prev.player, selectedCard),
        ap: prev.player.ap - selectedCard.apCost,
        lastPlayedMovement: false,
        cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
      },
    }));
    setSelectedCardId(null);
    notify('Doppelgänger deployed. The decoy will walk the path for 4 turns.', 'intel');
  }, [selectedCard, gameState.player.position, cityData.navigation.nodes, cityData.navigation.edges]);

  // Overwatch placement: start from the player's nearest road node and walk
  // up to 300m along the road network toward the cursor, picking the
  // adjacent edge at each step whose "other end" most aligns with the
  // cursor bearing. Same-roadId preference keeps the segment on the named
  // street you started on when possible; if that street dead-ends the
  // walker bails rather than swerving onto a perpendicular road.
  const handleOverwatchPlace = useCallback((lat: number, lng: number) => {
    if (!selectedCard || selectedCard.name !== 'Overwatch') return;
    const weapon = gameState.player.items.find(i =>
      i.id.startsWith('pistol') || i.id.startsWith('sniper-rifle')
    );
    if (!weapon) {
      notify('Overwatch requires a pistol or sniper rifle in inventory.', 'general');
      return;
    }
    const playerPos = gameState.player.position;
    const startNodeId = gameState.player.nearestNodeId;
    const nodeMap = new Map<string, NavNode>();
    for (const n of cityData.navigation.nodes) nodeMap.set(n.id, n);
    const edgeMap = new Map<string, NavEdge>();
    for (const e of cityData.navigation.edges) edgeMap.set(e.id, e);
    const startNode = nodeMap.get(startNodeId);
    if (!startNode) {
      notify('No road nearby to set up overwatch.', 'general');
      return;
    }

    const SEGMENT_M = 300;
    // Anchor the segment at the player's EXACT position. Previously this
    // snapped visually to the nearest road node, which read as a jumpy
    // offset from the player icon — the player sees their dot somewhere
    // and the overwatch line starting 10–30m away. Prepending playerPos
    // means the rendered line runs from the player out to the road and
    // along the named street.
    const collected: [number, number][] = [[playerPos.lng, playerPos.lat], [startNode.lng, startNode.lat]];
    let currentNodeId: string = startNodeId;
    let remaining = SEGMENT_M;
    let targetRoadId: string | null = null;
    const visitedEdges = new Set<string>();
    const visitedNodes = new Set<string>();
    visitedNodes.add(currentNodeId);

    while (remaining > 0) {
      const curNode = nodeMap.get(currentNodeId);
      if (!curNode) break;
      // Cursor direction vector from current node, normalized
      const cursorDLat = lat - curNode.lat;
      const cursorDLng = lng - curNode.lng;
      const cursorMag = Math.sqrt(cursorDLat * cursorDLat + cursorDLng * cursorDLng);
      if (cursorMag === 0) break;
      const cursorLatN = cursorDLat / cursorMag;
      const cursorLngN = cursorDLng / cursorMag;

      let candidateEdges = curNode.edges
        .map(id => edgeMap.get(id))
        .filter((e): e is NavEdge => !!e && !visitedEdges.has(e.id) && e.classification !== 'footpath');
      // Same-road restriction: once we have a roadId locked in, STAY on
      // that named street. If the road terminates, stop the segment here
      // rather than bending around a corner onto an intersecting road —
      // mirrors the Wire Tap collectEdges behavior so the overwatch line
      // represents a single named street, not a multi-road polyline.
      if (targetRoadId) {
        const sameRoad = candidateEdges.filter(e => (e.roadId ?? e.id) === targetRoadId);
        if (sameRoad.length > 0) candidateEdges = sameRoad;
        else break; // same-road chain ended — terminate the segment
      }
      if (candidateEdges.length === 0) break;

      // Pick edge whose far endpoint best aligns with cursor bearing.
      let best: NavEdge | null = null;
      let bestDot = -Infinity;
      let bestOtherId: string | null = null;
      for (const e of candidateEdges) {
        const otherId = e.from === currentNodeId ? e.to : e.from;
        if (visitedNodes.has(otherId)) continue;
        const otherNode = nodeMap.get(otherId);
        if (!otherNode) continue;
        const edgeDLat = otherNode.lat - curNode.lat;
        const edgeDLng = otherNode.lng - curNode.lng;
        const edgeMag = Math.sqrt(edgeDLat * edgeDLat + edgeDLng * edgeDLng);
        if (edgeMag === 0) continue;
        const dot = (edgeDLat / edgeMag) * cursorLatN + (edgeDLng / edgeMag) * cursorLngN;
        if (dot > bestDot) { bestDot = dot; best = e; bestOtherId = otherId; }
      }
      // Never walk backward: require positive alignment with cursor
      if (!best || !bestOtherId || bestDot <= 0) break;

      visitedEdges.add(best.id);
      if (!targetRoadId) targetRoadId = best.roadId ?? best.id;

      // Append the edge geometry oriented from currentNode → other. The
      // raw geometry is indexed from edge.from → edge.to, so if we're
      // traveling in the reverse direction we walk it backward.
      const geom = best.geometry;
      const oriented = best.from === currentNodeId ? geom : [...geom].reverse();
      // Skip the first point because it equals the last point we pushed.
      for (let i = 1; i < oriented.length; i++) collected.push(oriented[i]);

      remaining -= best.length;
      currentNodeId = bestOtherId;
      visitedNodes.add(currentNodeId);
    }

    if (collected.length < 2) {
      notify('Could not set up overwatch segment.', 'general', 2000);
      return;
    }

    // Avoid referencing a stale closure: grab the card before setState
    const playedCard = selectedCard;
    setGameState(prev => {
      let player: Player = {
        ...prev.player,
        overwatch: {
          segmentCoords: collected,
          turnsLeft: 3,
          weapon,
        },
        ...routePlayedCard(prev.player, playedCard),
        ap: prev.player.ap - playedCard.apCost,
        lastPlayedMovement: false,
        cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
      };
      const dtResult = applyDoubleTap(player, playedCard);
      if (dtResult) player = dtResult;
      return { ...prev, player };
    });
    setSelectedCardId(null);
    notify(`Overwatch deployed with ${weapon.name}. Holding for up to 3 turns.`, 'general');
  }, [selectedCard, gameState.player.position, gameState.player.items, gameState.player.nearestNodeId, cityData.navigation.nodes, cityData.navigation.edges]);

  // Drivable edges: everything except footpaths. Cached per cityData because the
  // network is large and we don't want to re-filter on every click.
  const drivableEdges = useMemo(
    () => cityData.navigation.edges.filter(e => e.classification !== 'footpath'),
    [cityData.navigation.edges],
  );

  // Surveillance Van — receive the finalized drag-drawn route from the map.
  // MapRenderer already snapped waypoints to drivable nodes and enforced the
  // length budget on release; we just build the van device and consume the
  // card + AP.
  const handleSurveillanceVanRoutePlace = useCallback((coords: [number, number][]) => {
    if (coords.length < 2) return;
    if (!selectedCard || selectedCard.name !== 'Surveillance Van') return;
    const cardId = selectedCard.id;
    const distAlong = buildDistAlong(coords);
    const totalLength = distAlong[distAlong.length - 1];
    const startPos = { lat: coords[0][1], lng: coords[0][0] };

    setGameState(prev => {
      const card = prev.player.hand.find(c => c.id === cardId);
      if (!card) return prev;
      const van: SurveillanceVan = {
        id: `van-${Date.now()}`,
        routeCoords: coords,
        distAlong,
        totalLength,
        currentDist: 0,
        direction: 1,
        position: startPos,
        prevPosition: null,
        animPath: null,
        visionRangeM: tuning.surveillanceVanVisionRange,
        stepPerTurn: tuning.surveillanceVanStepPerTurn,
        turnPlaced: prev.turn,
        agentInCone: false,
      };
      return {
        ...prev,
        surveillanceVans: [...prev.surveillanceVans, van],
        player: applyHeadhunterPostIntelPlay({
          ...prev.player,
          ...routePlayedCard(prev.player, card),
          ap: prev.player.ap - card.apCost,
          lastPlayedMovement: false,
          cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        }, runState),
      };
    });
    notify(`Surveillance van deployed — ${Math.round(totalLength)}m patrol route.`, 'intel', 4000);
    setSurveillanceVanMode(false);
    setSurveillanceVanPreview(null);
    setSelectedCardId(null);
  }, [selectedCard, runState]);

  // Taxi destination placement: pathfind a road-only route from the player's
  // current position to the clicked point. On success, store a pending route
  // and open the confirm modal. The card isn't consumed until confirm.
  const handleTaxiPlace = useCallback((lat: number, lng: number) => {
    if (!selectedCard || selectedCard.name !== 'Taxi') return;
    const pos = gameState.player.position;

    // Allow very long paths — taxis don't have the single-turn range budget
    // that movement cards do. 20km is plenty for any 4km map.
    const path = findPathBetweenPoints(
      pos.lat, pos.lng,
      lat, lng,
      cityData.navigation.nodes,
      drivableEdges,
      20_000,
    );
    if (!path || path.coordinates.length < 2) {
      notify('No drivable route to that point. Try a point closer to a road.', 'general', 2500);
      return;
    }

    // Build a short human label for the destination — street name if possible,
    // else a distance summary.
    let destinationLabel = `${Math.round(path.totalDistance)}m away`;
    // Snap to nearest edge to pick up the street name, if any.
    const endEdge = path.edges[path.edges.length - 1];
    const endEdgeName = (endEdge as unknown as { name?: string } | undefined)?.name;
    if (endEdgeName) destinationLabel = `${endEdgeName} · ${Math.round(path.totalDistance)}m`;

    setTaxiPendingRoute({
      card: selectedCard,
      path: {
        coordinates: path.coordinates,
        edges: path.edges,
        totalDistance: path.totalDistance,
      },
      destinationLabel,
    });
    // Exit select mode once we have a candidate — the modal takes over.
    setTaxiSelectMode(false);
  }, [selectedCard, gameState.player.position, cityData.navigation.nodes, drivableEdges]);

  // Taxi confirm: consume the card, AP, and seed the player with the full path.
  // We use the card object stored in taxiPendingRoute directly — no id lookup
  // inside the reducer, so there's no silent-no-op failure mode if the
  // player's hand reference has been swapped out (e.g. by handleTuningChange)
  // between placement and confirm.
  const handleTaxiConfirm = useCallback(() => {
    if (!taxiPendingRoute) return;
    const { card, path } = taxiPendingRoute;
    setGameState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        ...routePlayedCard(prev.player, card),
        ap: prev.player.ap - card.apCost,
        isInTaxi: true,
        taxiRemainingPath: path,
        cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
        lastPlayedMovement: false,
      },
    }));
    setTaxiPendingRoute(null);
    setSelectedCardId(null);
    notify(`Taxi hailed — ${Math.round(path.totalDistance)}m ride at ${tuning.taxiSpeedPerTurn}m per turn.`, 'general');
  }, [taxiPendingRoute]);

  // Taxi cancel: dismiss the pending route without consuming the card.
  const handleTaxiCancel = useCallback(() => {
    setTaxiPendingRoute(null);
    setTaxiSelectMode(false);
    setSelectedCardId(null);
  }, []);

  // Taxi exit: stops the ride at the current position without advancing.
  const handleExitTaxi = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        isInTaxi: false,
        taxiRemainingPath: null,
      },
    }));
  }, []);

  // Wire Tap placement: place on nearest edge, extend 150m each direction along that road
  const handleWireTapPlace = useCallback((lat: number, lng: number) => {
    if (!selectedCard || selectedCard.name !== 'Wire Tap') return;
    const edges = cityData.navigation.edges;
    const nodes = cityData.navigation.nodes;
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // Find nearest edge to click
    let bestEdge: typeof edges[0] | null = null;
    let bestDist = Infinity;
    for (const edge of edges) {
      if (!edge.geometry || edge.geometry.length < 2) continue;
      for (let i = 0; i < edge.geometry.length - 1; i++) {
        const [lng1, lat1] = edge.geometry[i];
        const [lng2, lat2] = edge.geometry[i + 1];
        const midLat = (lat1 + lat2) / 2;
        const midLng = (lng1 + lng2) / 2;
        const dLat = (midLat - lat) * 111000;
        const dLng = (midLng - lng) * 111000 * Math.cos((lat * Math.PI) / 180);
        const d = Math.sqrt(dLat * dLat + dLng * dLng);
        if (d < bestDist && d < 80) { bestDist = d; bestEdge = edge; }
      }
    }
    if (!bestEdge) {
      notify('No street found nearby. Click closer to a road.', 'general', 2000);
      return;
    }

    // Walk along connected edges 150m each direction from click point.
    // Stop at dead ends, when distance is exhausted, OR when the name/roadId
    // would change (so the tap stays on ONE named road instead of taking a
    // shorter straight-line path through an intersection onto a different
    // street). For unnamed edges the logic falls back to the old
    // "most-aligned" dot product heuristic.
    const WIRE_TAP_RANGE = 150; // meters each direction
    const targetRoadId = bestEdge.roadId;
    const collectEdges = (startNodeId: string, maxDist: number): typeof edges[0][] => {
      const result: typeof edges[0][] = [];
      let currentNodeId = startNodeId;
      let remaining = maxDist;
      const visitedEdges = new Set<string>();
      const visitedNodes = new Set<string>();
      while (remaining > 0) {
        visitedNodes.add(currentNodeId);
        // Candidates at this node: no footpaths, not-yet-visited. When the
        // starting edge has a roadId, FURTHER restrict to edges that share
        // the same roadId — this is the "stay on this named road" filter.
        let candidates = edges.filter(e =>
          !visitedEdges.has(e.id) &&
          e.classification !== 'footpath' &&
          (e.from === currentNodeId || e.to === currentNodeId)
        );
        if (targetRoadId) {
          const sameRoad = candidates.filter(e => e.roadId === targetRoadId);
          if (sameRoad.length > 0) candidates = sameRoad;
          else break; // same-road chain has ended — stop here
        }
        if (candidates.length === 0) break;
        let next = candidates[0];
        if (candidates.length > 1 && result.length > 0) {
          // Prefer the edge most aligned with the direction we came from
          // (handles Y-forks on a single named road, where both branches
          // share the same roadId but only one continues "straight").
          const prevNode = result.length > 0 ? (result[result.length - 1].from === currentNodeId ? result[result.length - 1].to : result[result.length - 1].from) : null;
          if (prevNode) {
            const prevN = nodeMap.get(prevNode);
            const curN = nodeMap.get(currentNodeId);
            if (prevN && curN) {
              const dirLat = curN.lat - prevN.lat;
              const dirLng = curN.lng - prevN.lng;
              let bestDot = -Infinity;
              for (const c of candidates) {
                const otherNodeId = c.from === currentNodeId ? c.to : c.from;
                if (visitedNodes.has(otherNodeId)) continue;
                const otherN = nodeMap.get(otherNodeId);
                if (!otherN) continue;
                const cDirLat = otherN.lat - curN.lat;
                const cDirLng = otherN.lng - curN.lng;
                const dot = dirLat * cDirLat + dirLng * cDirLng;
                if (dot > bestDot) { bestDot = dot; next = c; }
              }
            }
          }
        }
        const nextNodeId = next.from === currentNodeId ? next.to : next.from;
        if (visitedNodes.has(nextNodeId)) break;
        visitedEdges.add(next.id);
        result.push(next);
        remaining -= next.length;
        currentNodeId = nextNodeId;
      }
      return result;
    };

    const edgesForward = collectEdges(bestEdge.to, WIRE_TAP_RANGE - bestEdge.length / 2);
    const edgesBackward = collectEdges(bestEdge.from, WIRE_TAP_RANGE - bestEdge.length / 2);
    const allEdges = [bestEdge, ...edgesForward, ...edgesBackward];

    // Deduplicate
    const seen = new Set<string>();
    const uniqueEdges = allEdges.filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true; });

    const allCoords: [number, number][] = [];
    const edgeCoordGroups: [number, number][][] = [];
    for (const edge of uniqueEdges) {
      if (edge.geometry && edge.geometry.length >= 2) {
        edgeCoordGroups.push(edge.geometry);
        allCoords.push(...edge.geometry);
      }
    }
    const tap: WireTap = {
      id: `wiretap-${Date.now()}`,
      edgeIds: uniqueEdges.map(e => e.id),
      coordinates: allCoords,
      edgeCoordGroups,
      triggered: false,
      triggeredTurn: null,
    };
    setGameState(prev => ({
      ...prev,
      wireTaps: [...prev.wireTaps, tap],
      player: applyHeadhunterPostIntelPlay({
        ...prev.player,
        ...routePlayedCard(prev.player, selectedCard),
        ap: prev.player.ap - selectedCard.apCost,
        lastPlayedMovement: false,
        cardsPlayedThisTurn: prev.player.cardsPlayedThisTurn + 1,
      }, runState),
    }));
    setSelectedCardId(null);
    notify('Wire Tap placed. Waiting for agent to cross...', 'intel');
  }, [selectedCard, cityData.navigation.edges, cityData.navigation.nodes, runState]);

  // Sweep placement: player clicks map to place scan zone
  const handleSweepPlace = useCallback((lat: number, lng: number) => {
    if (!selectedCard || !selectedCard.sweepRadius) return;

    // Check if agent OR any decoy is within sweep radius
    const haversine = (lat1: number, lng1: number, lat2: number, lng2: number) => {
      const R = 6371000;
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLng = ((lng2 - lng1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const agentPos = gameState.agent.position;
    const distToAgent = haversine(lat, lng, agentPos.lat, agentPos.lng);
    const agentHit = distToAgent <= selectedCard.sweepRadius;

    // Sweep only hits real contacts — decoys and burner-phone fakes are ignored.
    const isHit = agentHit;

    let signalCounter = gameState.signals.length;
    // Persistent Surveillance: sweep results linger on the map one extra turn.
    const durationBonus = getIntelDurationBonus(runState);
    const sweepSignal = {
      id: `signal-sweep-${signalCounter}`,
      position: { lat, lng },
      actualPosition: { lat, lng },
      radius: selectedCard.sweepRadius,
      strength: 1,
      age: 0,
      turnGenerated: gameState.turn,
      source: 'player' as const,
      type: 'sweep' as const,
      revealed: true, // sweep results are immediately visible to the player
      sweepResult: isHit ? 'success' as const : 'no-match' as const,
      ...(durationBonus > 0 ? { maxAgeOverride: SIGNAL_MAX_AGE + durationBonus } : {}),
    };

    setGameState(prev => ({
      ...prev,
      signals: [...prev.signals, sweepSignal],
      player: applyHeadhunterPostIntelPlay({
        ...prev.player,
        ...routePlayedCard(prev.player, selectedCard),
        ap: prev.player.ap - selectedCard.apCost,
      }, runState),
    }));
    setSelectedCardId(null);
  }, [selectedCard, gameState.agent.position, gameState.turn, gameState.signals.length, runState]);

  // Movement signal pulses are fired explicitly from handleMovementComplete
  // and handleMovementPartial (see firePlayerSignalPulse below) so the pulse
  // value always matches the strength computed by computeMovementSignalStrength.
  // Taxi/transit/idle signals can be added later via their own dispatch sites.

  // Escape key cancels card selection
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        if (itemMenuIndex !== null) {
          setItemMenuIndex(null);
        } else if (pileOverlay) {
          setPileOverlay(null);
        } else if (burnerPhoneMode) {
          setBurnerPhoneMode(false);
          setBurnerPhoneSlotIndex(null);
        } else if (jammerMode) {
          setJammerMode(false);
          setJammerSlotIndex(null);
        } else if (decoySelectOpen) {
          setDecoySelectOpen(false);
          setSelectedCardId(null);
        } else if (reconPlaneMode || reconPlanePending) {
          // Recon Plane: exit placement mode and discard any pending placement.
          setReconPlaneMode(false);
          setReconPlanePending(null);
          setSelectedCardId(null);
        } else if (stakeoutMode || stakeoutPendingBuilding) {
          // Stakeout: exit placement and discard the building selection.
          setStakeoutMode(false);
          setStakeoutPendingBuilding(null);
          setSelectedCardId(null);
        } else if (surveillanceVanMode) {
          // Surveillance Van: exit placement and discard any in-progress drag.
          setSurveillanceVanMode(false);
          setSurveillanceVanPreview(null);
          setSelectedCardId(null);
        } else if (selectedCardId) {
          deselect();
        }
      }
      // Ctrl+T toggles test mode
      if (e.code === 'KeyT' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setTestMode(prev => !prev);
      }
    };
    // Right-click also cancels card selection (same as Escape)
    const onContextMenu = (e: MouseEvent) => {
      if (selectedCardId || decoySelectOpen || burnerPhoneMode || jammerMode || reconPlaneMode || reconPlanePending || stakeoutMode || stakeoutPendingBuilding || surveillanceVanMode) {
        e.preventDefault();
        if (decoySelectOpen) {
          setDecoySelectOpen(false);
          setSelectedCardId(null);
        } else if (burnerPhoneMode) {
          setBurnerPhoneMode(false);
          setBurnerPhoneSlotIndex(null);
        } else if (jammerMode) {
          setJammerMode(false);
          setJammerSlotIndex(null);
        } else if (reconPlaneMode || reconPlanePending) {
          setReconPlaneMode(false);
          setReconPlanePending(null);
          setSelectedCardId(null);
        } else if (stakeoutMode || stakeoutPendingBuilding) {
          setStakeoutMode(false);
          setStakeoutPendingBuilding(null);
          setSelectedCardId(null);
        } else if (surveillanceVanMode) {
          setSurveillanceVanMode(false);
          setSurveillanceVanPreview(null);
          setSelectedCardId(null);
        } else if (selectedCardId) {
          deselect();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('contextmenu', onContextMenu);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('contextmenu', onContextMenu);
    };
  }, [selectedCardId, deselect, pileOverlay, itemMenuIndex, reconPlaneMode, reconPlanePending, stakeoutMode, stakeoutPendingBuilding, surveillanceVanMode]);

  const handleEndTurn = useCallback(() => {
    setSelectedCardId(null);
    setRemainingRange(null);
    setCounterSurveillanceActive(false);
    setShowOwnSignals(false);
    setPendingFieldcraftIds(new Set());
    setPendingAction(null);

    // Note: player road-level path is recorded in handleMovementComplete/handleMovementPartial.
    // Don't push raw position here — it creates disconnected lines from road-level coords.

    // Phase 1: Update player state synchronously, then dispatch agent AI to Web Worker
    setGameState(prev => {
      // --- Separate retained cards (Stash + Hidden Pocket) from cards to discard ---
      const retainedIds = new Set(prev.player.retainedCardIds);
      // Hidden Pocket passive: if the player ends the turn with exactly 1 card in hand,
      // retain it instead of discarding. Applied by adding its id to the retention set.
      if (hasPassive(runState, 'hiddenPocket') && prev.player.hand.length === 1) {
        retainedIds.add(prev.player.hand[0].id);
      }
      // Headhunter: when the chain resets at turn end, restore any retained
      // intel cards that are still sitting at a discounted cost so they don't
      // carry the discount into next turn with no chain active.
      const retainedCards = resetHeadhunterDiscounts(
        prev.player.hand.filter(c => retainedIds.has(c.id)),
      );
      const discardableHand = prev.player.hand.filter(c => !retainedIds.has(c.id));

      // Discard non-retained hand cards. Ephemeral cards (unplayed Double Tap
      // copies) burn at end of turn because they're not supposed to outlive
      // the turn they were created on. Regular `isBurn` cards — Double Agent,
      // Ghost, Cold Exit, Jammer, Contingency, Malware — only burn when
      // ACTUALLY PLAYED; unplayed copies in hand discard normally so the card
      // comes back around next cycle.
      // Ping passive penalty: if a Ping junk card is still in the
      // discardable hand (player didn't spend 1 AP to play/discard it),
      // the agent gets the player's exact position for 2 turns.
      const pingStillInHand = discardableHand.some(c => c.name === 'Ping' && c.isJunk);

      const burnThisTurn = discardableHand.filter(c => c.isEphemeral);
      // Remove Ping junk cards from the discard pool — they don't enter
      // the deck cycle, they just vanish after triggering (or not).
      const nonBurnDiscardable = discardableHand.filter(c => !c.isEphemeral && !(c.name === 'Ping' && c.isJunk));
      // Headhunter: strip any lingering discount flag from cards moving to
      // discard/deck pools so future draws don't inherit the stale discount.
      const newDiscard = resetHeadhunterDiscounts(
        [...prev.player.discard, ...nonBurnDiscardable],
      );

      // Shuffle discard back into deck if deck is too small to draw
      let newDeck = resetHeadhunterDiscounts([...prev.player.deck]);
      // Base hand size respects Capacious passive (+1)
      const baseHand = getPlayerHandSize(tuning.handSize, runState);
      // Op Integrity: +3 additional cards on top of the normal hand size
      const drawCount = prev.player.operationalIntegrityActive ? baseHand + 3 : baseHand;
      if (newDeck.length < drawCount) {
        newDeck = shuffleDeck([...newDeck, ...newDiscard]);
        newDiscard.length = 0;
      }

      // Draw new hand (Operational Integrity: draw 5 instead of normal)
      let { hand, remaining } = drawHand(newDeck, drawCount);

      // Add retained cards back to hand
      hand = [...retainedCards, ...hand];

      // Vehicle guarantee: always at least 1 move card in hand when in a vehicle
      if (prev.player.isInVehicle) {
        const hasMoveCard = hand.some(c => !!c.movementRange);
        if (!hasMoveCard) {
          const moveIdx = remaining.findIndex(c => !!c.movementRange);
          if (moveIdx >= 0) {
            const moveCard = remaining[moveIdx];
            const swapIdx = hand.findIndex(c => !c.movementRange);
            if (swapIdx >= 0) {
              const swappedCard = hand[swapIdx];
              hand = [...hand];
              hand[swapIdx] = moveCard;
              remaining = [...remaining];
              remaining[moveIdx] = swappedCard;
            }
          }
        }
      }

      // Hiding: if player has hiding turns left, suppress signal completely
      const isHiding = prev.player.hidingTurnsLeft > 0;

      // If the player didn't move this turn and isn't hiding, generate idle signal
      let signals = [...prev.signals];
      const playerMovedThisTurn = prev.signals.some(
        s => s.source === 'player' && s.turnGenerated === prev.turn,
      );
      // Check if player is inside jammer zone
      const inJammerZone = prev.player.jammerTurnsLeft > 0 && prev.player.jammerPosition != null && (() => {
        const dLat = (prev.player.position.lat - prev.player.jammerPosition!.lat) * 111000;
        const dLng = (prev.player.position.lng - prev.player.jammerPosition!.lng) * 111000 * Math.cos(prev.player.position.lat * Math.PI / 180);
        return Math.sqrt(dLat * dLat + dLng * dLng) <= prev.player.jammerRadius;
      })();

      if (!playerMovedThisTurn && !isHiding && !prev.player.ghostActive && !inJammerZone && prev.player.coldExitTurnsLeft === 0) {
        // Background passives apply to idle signal too — resolve the nearest
        // edge to the player so zone-gated passives (Bystander/Neighbor) know
        // whether to activate.
        const idleNearestEdge = findNearestEdgeToPosition(
          prev.player.position.lat,
          prev.player.position.lng,
          cityData.navigation.edges,
        );
        const idleMod = computePassiveSignalMultiplier({
          nearestEdge: idleNearestEdge,
          isInVehicle: prev.player.isInVehicle,
          isFirstSignalThisTurn: prev.player.signalsGeneratedThisTurn === 0,
          origin: 'idle',
        }, runState);
        const idleSignal = generateIdleSignal(
          prev.player.position,
          cityData.navigation.edges,
          'player',
          prev.turn,
          1 * idleMod,
          prev.player.exposure,
        );
        if (idleSignal) {
          signals = [...signals, idleSignal];
        }
      }

      // ── Static: displace this turn's player signal(s) to a random street
      // within 200m of the player's real position. Fools belief + Sweep.
      if (prev.player.staticActive) {
        const playerLat = prev.player.position.lat;
        const playerLng = prev.player.position.lng;
        const latRad = (playerLat * Math.PI) / 180;
        // Collect all nav nodes within 200m
        const candidates: NavNode[] = [];
        for (const n of cityData.navigation.nodes) {
          const dLat = (n.lat - playerLat) * 111000;
          const dLng = (n.lng - playerLng) * 111000 * Math.cos(latRad);
          if (Math.sqrt(dLat * dLat + dLng * dLng) <= 200) candidates.push(n);
        }
        if (candidates.length > 0) {
          const chosen = candidates[Math.floor(Math.random() * candidates.length)];
          const displacedPos = { lat: chosen.lat, lng: chosen.lng };
          // Rewrite every player-source signal created this turn
          signals = signals.map(s => {
            if (s.source === 'player' && s.turnGenerated === prev.turn) {
              return { ...s, position: { ...displacedPos }, actualPosition: { ...displacedPos } };
            }
            return s;
          });
        }
      }

      // ── Dead Man's Switch (capture phase): if the player armed a Dead
      // Man's Switch THIS turn, cache whichever signal they generated so
      // we can replay it 3 turns later. Runs after Static so the cached
      // signal is the displaced one if Static was also played.
      let dmsNextState = prev.player.deadMansSwitch;
      if (dmsNextState && dmsNextState.playedOnTurn === prev.turn && !dmsNextState.cachedSignal) {
        const thisTurnSignals = signals.filter(s => s.source === 'player' && s.turnGenerated === prev.turn);
        if (thisTurnSignals.length > 0) {
          // Cache the strongest one (most useful to replay)
          const strongest = thisTurnSignals.reduce((best, s) => s.strength > best.strength ? s : best);
          dmsNextState = { ...dmsNextState, cachedSignal: strongest };
        } else {
          // Player generated nothing this turn (Ghost, Hiding, etc.) — fabricate
          // an idle signal at current position so the card still fires later.
          const idleNearestEdge = findNearestEdgeToPosition(
            prev.player.position.lat,
            prev.player.position.lng,
            cityData.navigation.edges,
          );
          const fallback = generateIdleSignal(
            prev.player.position,
            cityData.navigation.edges,
            'player',
            prev.turn,
            1,
            prev.player.exposure,
          );
          if (fallback) dmsNextState = { ...dmsNextState, cachedSignal: fallback };
          else dmsNextState = null; // could not fabricate — cancel the switch
          void idleNearestEdge;
        }
      }

      // Dead Man's Switch fire phase runs below after newTurn is declared.

      // ── Doppelgänger movement: advance the decoy 200m along its path and
      // generate a real-equivalent signal at its new position. The signal's
      // source is 'player' so the agent's belief system treats it the same
      // as a real player movement signal. Decoy oscillates: on reaching
      // either endpoint before its timer expires, direction flips so it
      // walks back along the same path. Only dissipates when turnsLeft
      // runs out (or when the agent engages — handled elsewhere).
      let nextDoppelganger = prev.doppelganger;
      if (nextDoppelganger && nextDoppelganger.turnsLeft > 0) {
        const DOPPEL_STEP_M = 200;
        const coords = nextDoppelganger.pathCoords;
        // Cumulative distances along the path from start → end.
        const cum: number[] = [0];
        for (let i = 1; i < coords.length; i++) {
          const [lng1, lat1] = coords[i - 1];
          const [lng2, lat2] = coords[i];
          const dLat = (lat2 - lat1) * 111000;
          const dLng = (lng2 - lng1) * 111000 * Math.cos(lat1 * Math.PI / 180);
          cum.push(cum[i - 1] + Math.sqrt(dLat * dLat + dLng * dLng));
        }
        const totalLen = cum[cum.length - 1];
        const startM = nextDoppelganger.distanceWalked;
        let direction = nextDoppelganger.direction;
        // Walk DOPPEL_STEP_M meters this turn, bouncing off either end
        // if the step would overshoot. At a bounce point we "spend" the
        // remaining step in the opposite direction so a single turn can
        // cross through an endpoint (e.g. at endpoint with 50m of step
        // left, we walk 50m back).
        let posM = startM;
        let remaining = DOPPEL_STEP_M;
        while (remaining > 0 && totalLen > 0) {
          const ahead = direction === 1 ? (totalLen - posM) : posM;
          if (ahead <= 0) {
            // Already at a boundary → flip and continue.
            direction = (direction === 1 ? -1 : 1) as 1 | -1;
            continue;
          }
          const step = Math.min(ahead, remaining);
          posM += step * direction;
          remaining -= step;
          // If we just landed on a boundary with more step to spend, flip.
          if (remaining > 0) {
            direction = (direction === 1 ? -1 : 1) as 1 | -1;
          }
        }
        const endM = Math.max(0, Math.min(totalLen, posM));
        // Interpolate coordinate at endM.
        let newPos: { lat: number; lng: number };
        if (endM >= totalLen) {
          newPos = { lat: coords[coords.length - 1][1], lng: coords[coords.length - 1][0] };
        } else if (endM <= 0) {
          newPos = { lat: coords[0][1], lng: coords[0][0] };
        } else {
          let idx = 1;
          while (idx < cum.length && cum[idx] < endM) idx++;
          const segStart = cum[idx - 1];
          const segLen = cum[idx] - segStart;
          const t = segLen > 0 ? (endM - segStart) / segLen : 0;
          const [lng1, lat1] = coords[idx - 1];
          const [lng2, lat2] = coords[idx];
          newPos = { lat: lat1 + (lat2 - lat1) * t, lng: lng1 + (lng2 - lng1) * t };
        }
        // Edges touched during this step — for signal zone weighting.
        // Picks edges starting from the decoy's current position along
        // the direction of travel, roughly DOPPEL_STEP_M worth.
        const edgeMap = new Map<string, NavEdge>();
        for (const e of cityData.navigation.edges) edgeMap.set(e.id, e);
        const traversedEdges: NavEdge[] = [];
        for (const eid of nextDoppelganger.pathEdges) {
          const e = edgeMap.get(eid);
          if (e) traversedEdges.push(e);
          if (traversedEdges.reduce((sum, ed) => sum + ed.length, 0) >= DOPPEL_STEP_M) break;
        }
        const doppelSignal = traversedEdges.length > 0
          ? generateMovementSignal(
              newPos,
              traversedEdges,
              'player',
              prev.turn,
              1,
              prev.player.exposure,
            )
          : generateIdleSignal(
              newPos,
              cityData.navigation.edges,
              'player',
              prev.turn,
              1,
              prev.player.exposure,
            );
        if (doppelSignal) {
          signals = [...signals, doppelSignal];
        }
        const newTurnsLeft = nextDoppelganger.turnsLeft - 1;
        if (newTurnsLeft <= 0) {
          nextDoppelganger = null; // timed out
        } else {
          nextDoppelganger = {
            ...nextDoppelganger,
            currentPos: newPos,
            distanceWalked: endM,
            direction,
            turnsLeft: newTurnsLeft,
          };
        }
      }

      // --- Transit riding: advance one stop, generate riding signal, accumulate exposure ---
      let transitPosition: { lat: number; lng: number } | null = null;
      let transitNodeId: string | null = null;
      let newTransitStopIndex = prev.player.transitStopIndex;
      let newTransitDirection = prev.player.transitDirection;
      let transitExposureGain = 0;
      let transitSignalEvent: { strength: number; position: { lat: number; lng: number } } | null = null;
      if (prev.player.isInTransit && prev.player.transitLineId && prev.player.transitDirection) {
        const line = findLineById(cityData.layers.transit.lines, prev.player.transitLineId);
        if (line && line.stops.length > 1) {
          const adv = advanceStopIndex(
            prev.player.transitStopIndex,
            prev.player.transitDirection,
            line,
            cityData.bounds,
          );
          newTransitStopIndex = adv.nextIndex;
          newTransitDirection = adv.newDirection;
          const stop = line.stops[newTransitStopIndex];
          transitPosition = { lat: stop.lat, lng: stop.lng };
          const near = findConnectedNode(stop.lat, stop.lng);
          transitNodeId = near?.id ?? null;
          transitExposureGain = tuning.transitExposurePerTurn;
          transitSignalEvent = {
            strength: getRideSignalStrength(line.type),
            position: transitPosition,
          };
          // Debrief path: append the rail geometry for the segment the
          // player just rode so the post-round trail traces the actual
          // route instead of jumping straight across the map between
          // stations. Segment index uses the smaller of (from, to) since
          // line.geometry[i] spans stops[i] -> stops[i+1]. On backward
          // travel we reverse so the trail still reads start -> end.
          const fromIdx = prev.player.transitStopIndex;
          const toIdx = newTransitStopIndex;
          const segIdx = Math.min(fromIdx, toIdx);
          const segGeom = line.geometry?.[segIdx];
          if (segGeom && segGeom.length >= 2) {
            const oriented = fromIdx < toIdx ? segGeom : [...segGeom].reverse();
            if (playerPathRef.current.length === 0) {
              playerPathRef.current.push(...oriented);
            } else {
              playerPathRef.current.push(...oriented.slice(1));
            }
          } else {
            // Fallback: no geometry available → at least connect the
            // station points so the debrief isn't disconnected.
            if (playerPathRef.current.length === 0) {
              playerPathRef.current.push([prev.player.position.lng, prev.player.position.lat]);
            }
            playerPathRef.current.push([stop.lng, stop.lat]);
          }
          if (adv.shouldReverse) {
            notify(`Reached end of ${line.name} — reversing direction next turn.`, 'general');
          }
        }
      }

      // --- Taxi auto-advance: slice tuning.taxiSpeedPerTurn off the remaining path ---
      // Runs after transit so transit and taxi can't stack in the same state.
      let taxiPosition: { lat: number; lng: number } | null = null;
      let taxiNodeId: string | null = null;
      let taxiExposureGain = 0;
      let newTaxiRemainingPath: TaxiPath | null = prev.player.taxiRemainingPath;
      let stillInTaxi = prev.player.isInTaxi;
      if (prev.player.isInTaxi && prev.player.taxiRemainingPath) {
        const slice = sliceTaxiPath(prev.player.taxiRemainingPath, tuning.taxiSpeedPerTurn);
        taxiPosition = slice.endPos;
        const nearNode = findConnectedNode(slice.endPos.lat, slice.endPos.lng);
        taxiNodeId = nearNode?.id ?? null;
        newTaxiRemainingPath = slice.remaining;
        // Exit automatically if we've consumed the whole route.
        if (!slice.remaining) {
          stillInTaxi = false;
          notify('Taxi arrived at destination.', 'general');
        }

        // Generate a single movement signal for the traversed slice. Apply
        // taxi's louder-than-walking multiplier plus disguise/background
        // passives on top.
        const taxiEdges = slice.consumedEdges;
        let taxiSignalMod = tuning.taxiSignalMultiplier;
        // Disguise: proportional 75% reduction on commercial/major segments.
        if (prev.player.disguiseTurnsLeft > 0 && taxiEdges.length > 0) {
          const totalLen = taxiEdges.reduce((s, e) => s + e.length, 0);
          if (totalLen > 0) {
            const maskedLen = taxiEdges
              .filter(e => e.zone === 'commercial' || e.classification === 'major')
              .reduce((s, e) => s + e.length, 0);
            const fraction = maskedLen / totalLen;
            const reduction = fraction * (1 - tuning.disguiseSignalMultiplier);
            taxiSignalMod *= 1 - reduction;
          }
        }
        taxiSignalMod *= computePassiveSignalMultiplier({
          edges: taxiEdges,
          isInVehicle: false,
          isFirstSignalThisTurn: prev.player.signalsGeneratedThisTurn === 0,
          origin: 'movement',
        }, runState);

        const taxiSignal = taxiEdges.length > 0
          ? generateMovementSignal(
              slice.endPos,
              taxiEdges,
              'player',
              prev.turn,
              taxiSignalMod,
              prev.player.exposure,
            )
          : null;
        if (taxiSignal) signals = [...signals, taxiSignal];

        // Exposure for the slice, applying taxi + disguise multipliers.
        let rawTaxiExp = calculateExposureFromEdges(taxiEdges);
        rawTaxiExp *= tuning.taxiExposureMultiplier;
        if (prev.player.disguiseTurnsLeft > 0) rawTaxiExp *= tuning.disguiseExposureMultiplier;
        taxiExposureGain = applyExposureDiminishing(prev.player.exposure, rawTaxiExp);
      }

      // Apply transit exposure then decay (so transit still nets positive).
      // Taxi exposure is added here too — same semantics, different source.
      const exposureAfterTransit = Math.min(
        tuning.exposureMax,
        prev.player.exposure + transitExposureGain + taxiExposureGain,
      );
      // Hide Inside bonus: every turn spent hiding drops an extra 100
      // exposure on top of the normal per-turn decay. Skipped if the
      // player's already in a safehouse because safehouse exit resets
      // exposure to 0 anyway — double-dipping would be invisible.
      const hidingDecayBonus = prev.player.hidingTurnsLeft > 0 && !prev.player.isInSafehouse ? 100 : 0;
      const decayedExposure = Math.max(0, exposureAfterTransit - tuning.exposureDecayPerTurn - hidingDecayBonus);

      // Add transit riding signal (at new position, weakened by line type)
      if (transitSignalEvent) {
        signals = [...signals, {
          id: `signal-transit-ride-${Date.now()}`,
          position: transitSignalEvent.position,
          actualPosition: transitSignalEvent.position,
          radius: 200,
          strength: transitSignalEvent.strength,
          age: 0,
          turnGenerated: prev.turn,
          source: 'player',
          type: 'passive',
          revealed: true,
        }];
      }

      // --- Disguise decrement ---
      const newDisguiseTurns = Math.max(0, prev.player.disguiseTurnsLeft - 1);

      // Decrement safehouse turns; if done, auto-exit (exposure → 0)
      const newSafehouseTurns = Math.max(0, prev.player.safehouseTurnsLeft - 1);
      const safehouseExiting = prev.player.isInSafehouse && newSafehouseTurns === 0;

      // Decrement hiding turns; if done, exit building
      const newHidingTurns = safehouseExiting ? 0 : Math.max(0, prev.player.hidingTurnsLeft - 1);

      // Decrement instinct turns; if done, restore normal vision cone
      const newInstinctTurns = Math.max(0, prev.player.instinctTurnsLeft - 1);
      const newVisionConeWidth = newInstinctTurns > 0 ? 360 : tuning.visionConeAngle;

      // Decrement "enemy seen" timer
      const newSeenAgentTurns = Math.max(0, prev.player.seenAgentTurnsLeft - 1);

      // Decrement radio scanner and jammer timers
      const newScannerTurns = Math.max(0, prev.player.radioScannerTurnsLeft - 1);
      const newJammerTurns = Math.max(0, prev.player.jammerTurnsLeft - 1);

      // Age informant tips — remove expired ones
      const newTurn = prev.turn + 1;
      const newInformantTips = prev.informantTips.filter(
        t => newTurn - t.turnCreated < tuning.informantTipDuration
      );

      // ── Dead Man's Switch (fire phase): if the scheduled fire turn equals
      // newTurn, inject the cached signal retimed to the new turn and flag
      // deployed=true so the status chip shows DEPLOYED for one turn. After
      // the deployed turn, dmsNextState is cleared entirely.
      if (dmsNextState && dmsNextState.cachedSignal && dmsNextState.fireOnTurn === newTurn) {
        const replay: Signal = {
          ...dmsNextState.cachedSignal,
          id: `deadmans-${newTurn}-${Math.random().toString(36).slice(2, 6)}`,
          turnGenerated: newTurn,
          age: 0,
          revealed: false,
        };
        signals = [...signals, replay];
        dmsNextState = { ...dmsNextState, deployed: true };
      } else if (dmsNextState && dmsNextState.deployed) {
        // One turn after DEPLOYED → clear state entirely
        dmsNextState = null;
      }

      // --- Contingency: N Tactics cards drawn cost 0 AP (stacking counter). ---
      // Apply discount to up to N tactics cards in the new hand. Each
      // discounted card is marked so playing it decrements the counter.
      const contingencyCharges = prev.player.contingencyActive;
      if (contingencyCharges > 0) {
        hand = [...hand];
        let applied = 0;
        for (let ci = 0; ci < hand.length && applied < contingencyCharges; ci++) {
          if (hand[ci].category === 'tactics' && !hand[ci]._contingencyApplied) {
            hand[ci] = {
              ...hand[ci],
              originalApCost: hand[ci].originalApCost ?? hand[ci].apCost,
              apCost: 0,
              _contingencyApplied: true,
            };
            applied++;
          }
        }
      }

      // --- Calculate new AP: maxAp (includes permanent patience bonus) + bonusApNextTurn ---
      // Op Integrity grants +2 AP on top of normal AP for the next turn
      const opIntBonus = prev.player.operationalIntegrityActive ? 2 : 0;
      const nextTurnAp = testMode ? 99 : prev.player.maxAp + prev.player.bonusApNextTurn + opIntBonus;

      // Update agent's knowledge of player transit state (ground truth).
      // Non-null while the player is riding — reflects their new stop after
      // this turn's advance. Gated by Subway Surfer / active Disguise: when
      // masked the agent does NOT get ground-truth transit intel and the
      // field stays null. This is the ACTUAL leak that was making Subway
      // Surfer look ineffective — agentAI.ts uses knownPlayerTransit to
      // directly snap belief to the exact stop (lines ~1841-1851), and this
      // per-turn refresh was re-populating the field every turn regardless
      // of the passive, overwriting the null we set at board time.
      const transitMaskedEndOfTurn = hasPassive(runState, 'subwaySurfer') || prev.player.disguiseTurnsLeft > 0;
      const newKnownPlayerTransit = prev.player.isInTransit && !transitMaskedEndOfTurn
        ? { lineId: prev.player.transitLineId!, direction: newTransitDirection!, stopIndex: newTransitStopIndex }
        : null;

      // Advance surveillance vans along their patrol routes. Each van moves
      // stepPerTurn meters, ping-ponging at endpoints (mid-turn turnaround
      // using leftover budget). prevPosition + animPath are captured here
      // so the post-agent-turn detection can sweep the van's traversed
      // segment, and MapRenderer can animate the marker along animPath.
      const advancedVans = prev.surveillanceVans.map(v => {
        const { currentDist, direction, position, animPath } = advanceVanAlongRoute(
          v.routeCoords, v.distAlong, v.totalLength,
          v.currentDist, v.direction, v.stepPerTurn,
        );
        return { ...v, currentDist, direction, position, prevPosition: v.position, animPath };
      });

      // Ping passive penalty: player didn't discard the Ping junk card in
      // time — set detection to 2 turns so the agent gets the player's
      // exact position via the detection broadcast system in agentAI.ts.
      const pingDetectedTurns = pingStillInHand ? 2 : 0;

      // Build the pre-agent state with updated player + signals
      const preAgentState: GameState = {
        ...prev,
        turn: newTurn,
        phase: 'observe',
        signals,
        informantTips: newInformantTips,
        surveillanceVans: advancedVans,
        doppelganger: nextDoppelganger,
        player: {
          ...prev.player,
          hand,
          deck: remaining,
          discard: newDiscard,
          burnPile: [...prev.player.burnPile, ...burnThisTurn],
          ap: nextTurnAp,
          maxAp: prev.player.maxAp, // preserve Patience bonus
          position: taxiPosition ?? transitPosition ?? prev.player.position,
          nearestNodeId: taxiNodeId ?? transitNodeId ?? prev.player.nearestNodeId,
          isInTaxi: stillInTaxi,
          taxiRemainingPath: newTaxiRemainingPath,
          disguiseTurnsLeft: newDisguiseTurns,
          transitStopIndex: newTransitStopIndex,
          transitDirection: newTransitDirection,
          hidingTurnsLeft: newHidingTurns,
          isInBuilding: newHidingTurns > 0 || (prev.player.isInSafehouse && !safehouseExiting),
          isInSafehouse: prev.player.isInSafehouse && !safehouseExiting,
          safehouseTurnsLeft: newSafehouseTurns,
          // Cold Exit: grant 3 turns of grace on auto-exit; otherwise tick down existing grace.
          coldExitTurnsLeft: safehouseExiting && hasPassive(runState, 'coldExit')
            ? 3
            : Math.max(0, prev.player.coldExitTurnsLeft - 1),
          exposure: safehouseExiting ? 0 : decayedExposure,
          hidingBuildingId: newHidingTurns > 0 ? prev.player.hidingBuildingId : null,
          instinctTurnsLeft: newInstinctTurns,
          seenAgentTurnsLeft: newSeenAgentTurns,
          combatCooldownTurnsLeft: Math.max(0, prev.player.combatCooldownTurnsLeft - 1),
          radioScannerTurnsLeft: newScannerTurns,
          jammerTurnsLeft: newJammerTurns,
          jammerPosition: newJammerTurns > 0 ? prev.player.jammerPosition : null,
          ghostActive: false,
          // Low Profile is a single-turn effect; it ends when the turn does.
          // (Parade Route / Stop and Frisk are cleared AFTER the agent turn
          //  runs, inside the onmessage handler, not here.)
          lowProfileActive: false,
          // Static is a single-turn effect — cleared at end of turn.
          staticActive: false,
          // Dead Man's Switch persists across turns; threaded above.
          deadMansSwitch: dmsNextState,
          // Overwatch: decrement turnsLeft each turn end; clear at 0
          overwatch: prev.player.overwatch
            ? (prev.player.overwatch.turnsLeft - 1 > 0
                ? { ...prev.player.overwatch, turnsLeft: prev.player.overwatch.turnsLeft - 1 }
                : null)
            : null,
          // Doppelgänger reveal: decrement turns so the agent dot fades
          // back out after 3 turns following a doppelgänger engagement.
          doppelgangerRevealTurnsLeft: Math.max(0, prev.player.doppelgangerRevealTurnsLeft - 1),
          misdirectActive: false,
          // misdirectPath / misdirectPathTurn are managed by a dedicated
          // turn-watcher effect (see GameScreen init block). The effect
          // clears them once gameState.turn has advanced past pathTurn + 1
          // — i.e. the path stays visible through the play turn AND the
          // following turn, then disappears at end of turn N+1.
          trafficAnalysisEdges: null,
          trafficAnalysisRoadName: null,
          prepBuiltThisTurn: 0,
          adrenalineApThisTurn: 0,
          signalsGeneratedThisTurn: 0,
          // Ping penalty: if the player failed to discard the Ping junk
          // card, set detection to 2 turns so the agent gets their position.
          ...(pingDetectedTurns > 0 ? { detectedTurnsRemaining: pingDetectedTurns } : {}),
          visionConeWidth: newVisionConeWidth,
          activeBuffs: [],
          // Stash persists until the card is actually played. Keep only IDs
          // whose cards are still in the hand we just computed (i.e., cards
          // the player explicitly chose to retain and haven't played yet).
          // Hidden Pocket's auto-retained card is one-turn only, so we filter
          // it out by intersecting with the explicit retainedCardIds rather
          // than the full retainedIds set that includes the Hidden Pocket add.
          retainedCardIds: retainedCards
            .filter(c => prev.player.retainedCardIds.includes(c.id))
            .map(c => c.id),
          bonusApNextTurn: 0,
          coldReadActive: false,
          intelChainActive: false, // Headhunter: chain window closes at end of turn
          doubleTapActive: false,
          contingencyActive: contingencyCharges,
          operationalIntegrityActive: false,
          cardsPlayedThisTurn: 0,
          sharpenActive: false,
          lastPlayedMovement: false,
        },
        agent: {
          ...prev.agent,
          knownPlayerTransit: newKnownPlayerTransit,
        },
      };

      // ── Debug: Log player state for this turn ──
      {
        const buf = ((window as any).__agentDebugLogs as string[] | undefined) ?? [];
        const p = preAgentState.player;
        const pSigs = signals.filter(s => s.source === 'player' && s.turnGenerated === prev.turn);
        const cardsPlayed = prev.player.cardsPlayedThisTurn;
        const distToAgent = Math.round(
          Math.sqrt(
            ((p.position.lat - prev.agent.position.lat) * 111000) ** 2 +
            ((p.position.lng - prev.agent.position.lng) * 111000 * Math.cos(p.position.lat * Math.PI / 180)) ** 2
          )
        );
        buf.push(`[T${newTurn}] 🎮 PLAYER: pos=(${p.position.lat.toFixed(4)},${p.position.lng.toFixed(4)}), exposure=${p.exposure.toFixed(0)}/${tuning.exposureMax}, hiding=${p.hidingTurnsLeft > 0}, inBuilding=${p.isInBuilding}, cardsPlayed=${cardsPlayed}, distToAgent=${distToAgent}m`);
        if (pSigs.length > 0) {
          for (const sig of pSigs) {
            buf.push(`[T${newTurn}] 🎮 PLAYER SIGNAL: str=${sig.strength.toFixed(2)}, radius=${Math.round(sig.radius ?? 0)}m, pos=(${sig.position.lat.toFixed(4)},${sig.position.lng.toFixed(4)})`);
          }
        } else {
          buf.push(`[T${newTurn}] 🎮 PLAYER SIGNAL: none this turn${isHiding ? ' (hiding)' : ''}`);
        }
        if (buf.length > 500) buf.splice(0, buf.length - 500);
        (window as any).__agentDebugLogs = buf;
      }

      // Dispatch agent AI to Web Worker (async — results applied in Phase 2)
      // Agent sees pre-decrement hiding/instinct state so these effects cover the
      // agent turn that fires right after the player ends their turn.
      const keepHiding = prev.player.hidingTurnsLeft > 0 && newHidingTurns === 0;
      const keepInstinct = prev.player.instinctTurnsLeft > 0 && newInstinctTurns === 0;
      const agentGameState: GameState = (keepHiding || keepInstinct)
        ? {
            ...preAgentState,
            player: {
              ...preAgentState.player,
              ...(keepHiding ? {
                hidingTurnsLeft: 1,
                isInBuilding: true,
                hidingBuildingId: prev.player.hidingBuildingId,
              } : {}),
              ...(keepInstinct ? {
                instinctTurnsLeft: 1,
                visionConeWidth: 360,
              } : {}),
            },
          }
        : preAgentState;
      const worker = agentWorkerRef.current;
      // Extraction-op kill: agent is dead. Skip the entire AI turn —
      // no movement, no signals, no card play. The worker would crash
      // anyway since dead agents have stale state, and players need
      // a clear no-pressure window to walk to extraction.
      if (preAgentState.agent.isDead) {
        return preAgentState;
      }
      if (worker) {
        // Strict-mode guard: skip the dispatch if this updater was already
        // called for this turn (React invokes state updaters twice in dev
        // to detect impurity, and worker.postMessage IS a side effect).
        if (lastDispatchedAgentTurnRef.current === newTurn) {
          // eslint-disable-next-line no-console
          console.log(`[AgentWorker] Skipping dispatch for turn ${newTurn} (already dispatched — strict-mode double invoke)`);
          return preAgentState;
        }
        lastDispatchedAgentTurnRef.current = newTurn;

        const request: AgentWorkerRequest = {
          type: 'runAgentTurn',
          gameState: agentGameState,
          cityData,
        };
        worker.onmessage = (e: MessageEvent<AgentWorkerResponse>) => {
          if (e.data.type !== 'agentTurnResult') return;
          // Clear the watchdog timer — worker responded successfully
          if (agentWatchdogRef.current) { clearTimeout(agentWatchdogRef.current); agentWatchdogRef.current = null; }
          const { agent: updatedAgent, signals: agentSignals, log: agentLog, debugLogs, movementCoords } = e.data;
          // Diagnostic: confirm the worker came back and report what it decided.
          // If the agent is stuck standing still, the "moved" flag here tells
          // us whether the worker produced movementCoords at all. Remove once
          // the current stuck-agent investigation lands.
          // eslint-disable-next-line no-console
          console.log(
            `[AgentWorker] Response: turn=${agentLog?.turn}, movementCoords=${movementCoords?.length ?? 0}, ` +
            `agentPos=(${updatedAgent.position.lat.toFixed(4)},${updatedAgent.position.lng.toFixed(4)}), ` +
            `cardsPlayed=${agentLog?.cardsPlayed.length ?? 0}, ` +
            `stateAfter=${agentLog?.stateAfter}, ` +
            `belief=(${agentLog?.beliefAfter.position.lat.toFixed(4)},${agentLog?.beliefAfter.position.lng.toFixed(4)}) conf=${agentLog?.beliefAfter.confidence.toFixed(2)}, ` +
            `debugLen=${debugLogs?.length ?? 0}`,
          );

          // Strict-mode guard: the dispatch guard above catches the common
          // case, but if a stray duplicate response ever does come through
          // (e.g. if the dispatch guard is moved later), drop anything we've
          // already applied for this turn.
          if (agentLog && lastAppliedAgentTurnRef.current === agentLog.turn) {
            return;
          }
          if (agentLog) lastAppliedAgentTurnRef.current = agentLog.turn;

          // ── Doppelgänger path-intercept check ──
          // Walk the agent's full movement path this turn looking for the
          // first point within 40m of the active doppelgänger. If found,
          // the agent stops there, attacks the decoy, and the path is
          // truncated for animation, debrief, and the agent's committed
          // position. Replaces the old endpoint-only check which missed
          // any engagement that ended further down the road.
          //
          // Reading `gameStateRefForCombat.current` (not the stale closure
          // `gameState`) so we get the freshest doppelgänger data at the
          // moment the worker response lands.
          let interceptedCoords: [number, number][] | null = null;
          let interceptedPosition: { lat: number; lng: number } | null = null;
          {
            const freshGs = gameStateRefForCombat.current;
            const dopp = freshGs.doppelganger;
            if (dopp && movementCoords && movementCoords.length > 0) {
              const dpLat = dopp.currentPos.lat;
              const dpLng = dopp.currentPos.lng;
              const latRad = (dpLat * Math.PI) / 180;
              const INTERCEPT_M = 40;
              for (let i = 0; i < movementCoords.length; i++) {
                const [lng, lat] = movementCoords[i];
                const dLat = (lat - dpLat) * 111000;
                const dLng = (lng - dpLng) * 111000 * Math.cos(latRad);
                if (Math.sqrt(dLat * dLat + dLng * dLng) < INTERCEPT_M) {
                  interceptedCoords = movementCoords.slice(0, i + 1);
                  interceptedPosition = { lat, lng };
                  break;
                }
              }
            }
          }
          // Effective agent movement for downstream use: truncated path
          // if we intercepted, otherwise the full worker-returned path.
          const effectiveCoords = interceptedCoords ?? movementCoords;
          // Also override the agent's committed final position so the
          // dot and the nearest-node line up with the intercept point
          // rather than the worker's intended endpoint.
          if (interceptedPosition) {
            updatedAgent.position = interceptedPosition;
            // Snap nearest-node to the closest nav node to the intercept
            // so post-move path logic (e.g. next-turn movement, vans)
            // doesn't see the agent "floating" between nodes.
            const nearest = findNearestNode(interceptedPosition.lat, interceptedPosition.lng, cityData.navigation.nodes);
            if (nearest) updatedAgent.nearestNodeId = nearest.id;
          }

          // Trigger agent movement animation if the agent will be visible
          if (effectiveCoords && effectiveCoords.length >= 2) {
            setAgentAnimPath(effectiveCoords);
          }

          // Record agent road-level path for debrief + Trace Route
          if (effectiveCoords && effectiveCoords.length >= 2) {
            agentPathRef.current.push(...effectiveCoords.slice(1));
            agentMovementHistoryRef.current.push({ turn: agentGameState.turn, coords: effectiveCoords });
            // Keep only last 8 turns of history
            if (agentMovementHistoryRef.current.length > 8) {
              agentMovementHistoryRef.current = agentMovementHistoryRef.current.slice(-8);
            }
          } else {
            // Fallback: agent didn't move, just record the endpoint
            agentPathRef.current.push([updatedAgent.position.lng, updatedAgent.position.lat]);
          }

          // Accumulate debug logs from worker onto window for the AI panel to read
          if (debugLogs) {
            const existing = (window as any).__agentDebugLogs as string[] | undefined;
            const newLines = debugLogs.split('\n').filter((l: string) => l.length > 0);
            const buf = existing ? [...existing, ...newLines] : newLines;
            (window as any).__agentDebugLogs = buf.length > 500 ? buf.slice(-500) : buf;
          }

          // Capture turn log for Agent AI Panel (deduplicate — Strict Mode fires worker twice)
          if (agentLog) {
            const existing = agentTurnLogsRef.current;
            const alreadyHasTurn = existing.some(l => l.turn === agentLog.turn);
            if (!alreadyHasTurn) {
              agentTurnLogsRef.current = [...existing, agentLog];
              setAgentTurnLogs(agentTurnLogsRef.current);
            }
          }

          // Phase 2: Apply agent results + detection/combat
          setGameState(gs => {
            let playerSeesAgent = playerCanSeeAgent(gs.player, updatedAgent);
            let agentSeesPlayer = agentCanSeePlayer(updatedAgent, gs.player);

            // Transit same-stop mutual detection: if both are on the same line at the
            // same stop, force confrontation regardless of vision cones.
            const playerOnLine = gs.player.isInTransit && gs.player.transitLineId;
            const agentOnLine = updatedAgent.isOnTransit && updatedAgent.transitLineId;
            if (
              playerOnLine && agentOnLine &&
              gs.player.transitLineId === updatedAgent.transitLineId &&
              gs.player.transitStopIndex === updatedAgent.transitStopIndex
            ) {
              playerSeesAgent = true;
              agentSeesPlayer = true;
            }

            // Combat resolution
            const combatScenario = determineCombatScenario(playerSeesAgent, agentSeesPlayer, updatedAgent, gs.player);
            if (combatScenario) {
              console.log(`[Combat] Scenario: ${combatScenario}`);
              const agentWeapon = updatedAgent.equipment.find(e => e.category === 'weapon');

              // Sniped: auto-resolve immediately (no weapon choice, no reveal phase)
              if (combatScenario === 'sniped') {
                // Find nearest building distance for escape check
                const bldgs = cityData.layers.buildings.features;
                let nearestBldgDist = Infinity;
                for (let bi = 0; bi < bldgs.length; bi++) {
                  const geom = bldgs[bi].geometry;
                  if (geom.type !== 'Polygon') continue;
                  const ring = (geom as GeoJSON.Polygon).coordinates[0];
                  if (!ring || ring.length < 3) continue;
                  const bLat = ring.reduce((s, c) => s + c[1], 0) / ring.length;
                  const bLng = ring.reduce((s, c) => s + c[0], 0) / ring.length;
                  const d = Math.sqrt(((bLat - gs.player.position.lat) * 111000) ** 2 +
                    ((bLng - gs.player.position.lng) * 111000 * Math.cos(gs.player.position.lat * Math.PI / 180)) ** 2);
                  if (d < nearestBldgDist) nearestBldgDist = d;
                }
                const snipedEncounter = resolveSniped(gs.player, updatedAgent, nearestBldgDist);
                setTimeout(() => {
                  playWarning();
                  setCombatEncounter({ ...snipedEncounter, revealText: 'A shot from above.' });
                  if (snipedEncounter.outcome === 'player-dies') {
                    setGameState(cur => ({ ...cur, roundResult: 'death' }));
                  } else if (snipedEncounter.vestExhausted || snipedEncounter.hardTargetUsed) {
                    setGameState(cur => ({
                      ...cur,
                      player: applyCombatPlayerEffects(cur.player, snipedEncounter),
                    }));
                  }
                }, 0);
              } else {
                const revealText = combatScenario === 'ambush'
                  ? 'You spotted the agent. They don\'t see you.'
                  : combatScenario === 'confrontation'
                  ? 'Eyes locked. The agent sees you too.'
                  : 'The agent has spotted you!';
                setTimeout(() => {
                  playWarning();
                  setCombatEncounter({
                    scenario: combatScenario,
                    phase: 'reveal',
                    agentWeapon,
                    revealText,
                  });
                }, 0);
              }
            }

            // Detection broadcasting
            const hasBinocularsOnRoof = gs.player.isElevated && gs.player.items.some(i => i.id.startsWith('binoculars'));
            const spotDuration = hasBinocularsOnRoof ? tuning.rooftopBinocularRevealDuration : 3;
            const agentDetectedTurns = playerSeesAgent ? spotDuration
              : Math.max(0, (updatedAgent.detectedTurnsRemaining ?? 0) - 1);
            const nearExtraction = gs.extractions.some(ext => {
              const dLat = (ext.lat - gs.player.position.lat) * 111000;
              const dLng = (ext.lng - gs.player.position.lng) * 111000 * Math.cos((gs.player.position.lat * Math.PI) / 180);
              return Math.sqrt(dLat * dLat + dLng * dLng) <= tuning.extractRevealRange;
            });
            const playerDetectedTurns = (agentSeesPlayer || nearExtraction) ? 3
              : Math.max(0, gs.player.detectedTurnsRemaining - 1);
            updatedAgent.detectedTurnsRemaining = agentDetectedTurns;
            // Cold Opening cone-only reveal decrements each turn independently
            // of the full-detection timer. Once it hits 0 the cone hint is gone.
            updatedAgent.coneOnlyRevealTurnsLeft = Math.max(
              0,
              (updatedAgent.coneOnlyRevealTurnsLeft ?? 0) - 1,
            );

            // Jammer absorption: any signal emitted INSIDE the active
            // jammer zone this turn gets dropped before reveal/age. Only
            // new this-turn agent signals need filtering here — player
            // signals inside the zone were already zeroed at generation
            // time by the jammer check in the move handlers, so they
            // never entered the array. Older signals aren't touched —
            // the jammer only eats current emissions.
            let jammerFiltered = agentSignals;
            if (gs.player.jammerTurnsLeft > 0 && gs.player.jammerPosition) {
              const jp = gs.player.jammerPosition;
              const jr = gs.player.jammerRadius;
              const latRad = (jp.lat * Math.PI) / 180;
              jammerFiltered = agentSignals.filter(s => {
                if (s.source !== 'agent') return true;
                if (s.turnGenerated !== gs.turn) return true;
                const dLat = (s.actualPosition.lat - jp.lat) * 111000;
                const dLng = (s.actualPosition.lng - jp.lng) * 111000 * Math.cos(latRad);
                return Math.sqrt(dLat * dLat + dLng * dLng) > jr;
              });
            }
            // Reveal & age signals
            let finalSignals = revealAndAgeSignals(jammerFiltered, gs.turn, 'player');
            if (testMode) {
              finalSignals = finalSignals.filter(s => s.source !== 'player' || s.turnGenerated === gs.turn - 1);
            }

            // Process spotters
            const haversineDist = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
              const dLat2 = (a.lat - b.lat) * 111000;
              const dLng2 = (a.lng - b.lng) * 111000 * Math.cos((a.lat * Math.PI) / 180);
              return Math.sqrt(dLat2 * dLat2 + dLng2 * dLng2);
            };

            // Check pass-through: did any spotter's radius intersect the agent's path?
            const passThroughDetected = (spotter: SpotterDevice) => {
              if (updatedAgent.position && haversineDist(updatedAgent.position, spotter.position) <= spotter.radius) return true;
              if (!movementCoords || movementCoords.length === 0) return false;
              for (const [lng, lat] of movementCoords) {
                if (haversineDist({ lat, lng }, spotter.position) <= spotter.radius) return true;
              }
              return false;
            };
            const newlyTriggered: SpotterDevice[] = [];
            const newSpotters = gs.spotters
              .map(s => {
                const justRevealed = !s.agentRevealed && passThroughDetected(s);
                const wasRevealed = s.agentRevealed;
                if (justRevealed) {
                  // Persistent Surveillance passive extends spotter reveal duration.
                  const updated = {
                    ...s,
                    agentRevealed: true,
                    turnsRemaining: tuning.spotterDuration + getIntelDurationBonus(runState),
                  };
                  newlyTriggered.push(updated);
                  return updated;
                } else if (wasRevealed) {
                  return { ...s, turnsRemaining: s.turnsRemaining - 1 };
                }
                return s;
              })
              .filter(s => s.turnsRemaining !== 0);

            // Queue a spotter-alert dialog for the player if any spotter was just tripped.
            if (newlyTriggered.length > 0) {
              setTimeout(() => { playWarning(); setSpotterAlert(newlyTriggered[0]); }, 0);
            }

            // Wire Tap detection: check if agent's movement path crossed any tapped edge
            const newlyTriggeredTaps: WireTap[] = [];
            // Persistent Surveillance: triggered taps linger one extra turn before expiring.
            const wireTapLifespan = 3 + getIntelDurationBonus(runState);
            const newWireTaps = gs.wireTaps
              .filter(tap => {
                if (tap.triggered && tap.triggeredTurn != null && gs.turn - tap.triggeredTurn >= wireTapLifespan) return false;
                return true;
              })
              .map(tap => {
              if (tap.triggered) return tap;
              const checkProximity = (lng: number, lat: number) => {
                for (let ci = 0; ci < tap.coordinates.length; ci++) {
                  const [eLng, eLat] = tap.coordinates[ci];
                  const dLat = (lat - eLat) * 111000;
                  const dLng = (lng - eLng) * 111000 * Math.cos((lat * Math.PI) / 180);
                  if (Math.sqrt(dLat * dLat + dLng * dLng) < 30) return true;
                }
                return false;
              };
              // Check movement path
              if (movementCoords && movementCoords.length >= 2) {
                for (const [lng, lat] of movementCoords) {
                  if (checkProximity(lng, lat)) {
                    updatedAgent.detectedTurnsRemaining = Math.max(updatedAgent.detectedTurnsRemaining, 2);
                    const triggered = { ...tap, triggered: true, triggeredTurn: gs.turn };
                    newlyTriggeredTaps.push(triggered);
                    return triggered;
                  }
                }
              }
              // Check final position
              if (checkProximity(updatedAgent.position.lng, updatedAgent.position.lat)) {
                updatedAgent.detectedTurnsRemaining = Math.max(updatedAgent.detectedTurnsRemaining, 2);
                const triggered = { ...tap, triggered: true, triggeredTurn: gs.turn };
                newlyTriggeredTaps.push(triggered);
                return triggered;
              }
              return tap;
            });

            // Queue wire tap alert dialog
            if (newlyTriggeredTaps.length > 0) {
              setTimeout(() => { playWarning(); setWireTapAlert(newlyTriggeredTaps[0]); }, 0);
            }

            // Overwatch detection: if the agent's movement path crosses the
            // overwatch segment, trigger an ambush using the player's weapon.
            // Uses the same proximity threshold as Wire Tap (30m).
            const ow = gs.player.overwatch;
            let overwatchTriggered = false;
            if (ow && ow.segmentCoords.length > 0 && !combatEncounter) {
              const segmentCrossed = (lat: number, lng: number) => {
                for (const [sLng, sLat] of ow.segmentCoords) {
                  const dLat = (lat - sLat) * 111000;
                  const dLng = (lng - sLng) * 111000 * Math.cos((lat * Math.PI) / 180);
                  if (Math.sqrt(dLat * dLat + dLng * dLng) < 30) return true;
                }
                return false;
              };
              let hit = false;
              if (movementCoords && movementCoords.length > 0) {
                for (const [lng, lat] of movementCoords) {
                  if (segmentCrossed(lat, lng)) { hit = true; break; }
                }
              }
              if (!hit && segmentCrossed(updatedAgent.position.lat, updatedAgent.position.lng)) hit = true;
              if (hit) {
                overwatchTriggered = true;
                setTimeout(() => {
                  playWarning();
                  setCombatEncounter({
                    scenario: 'ambush',
                    phase: 'reveal',
                    agentWeapon: updatedAgent.equipment.find(e => e.category === 'weapon'),
                    preSelectedWeapon: ow.weapon,
                    revealText: 'OVERWATCH TRIGGERED — you have the shot.',
                  });
                }, 0);
              }
            }
            void overwatchTriggered;

            // Stakeout detection: for each deployed stakeout, check if the
            // agent passed through its vision cone this turn OR is currently
            // inside it. Same pass-through treatment as Spotter — we walk
            // every point along movementCoords and test each. Missing this
            // would let any long-move skip through an unoccupied cone.
            //
            // - passedThrough: agent visited the cone at some point this turn
            //   (triggers "enemy seen" and the alert modal, refreshes the
            //   3-turn fade timer to max)
            // - agentInCone: agent ended the turn inside the cone (also
            //   pins the fade timer so it doesn't tick down next turn)
            //
            // passedThrough == true AND agentInCone == false means: the
            // alert fires this turn, the 3-turn fade starts immediately at 3
            // next turn and counts down normally.
            const newlyTriggeredStakeouts: StakeoutDevice[] = [];
            let anyStakeoutSeesAgent = false;
            let anyStakeoutPassThrough = false;
            const newStakeouts = gs.stakeouts.map(s => {
              const endInCone = isInVisionCone(
                s.position, s.facingDeg, s.coneAngleDeg, s.coneRangeM,
                updatedAgent.position,
              );
              let passedThrough = endInCone;
              if (!passedThrough && movementCoords && movementCoords.length > 0) {
                for (const [lng, lat] of movementCoords) {
                  if (isInVisionCone(s.position, s.facingDeg, s.coneAngleDeg, s.coneRangeM, { lat, lng })) {
                    passedThrough = true;
                    break;
                  }
                }
              }
              if (endInCone) anyStakeoutSeesAgent = true;
              if (passedThrough) anyStakeoutPassThrough = true;
              // New entry — either pass-through or lingering. Fire alert.
              if (passedThrough && !s.agentInCone) {
                const updated = { ...s, agentInCone: endInCone };
                newlyTriggeredStakeouts.push(updated);
                return updated;
              }
              // Still in cone at end of turn — keep the pin active.
              if (endInCone) {
                return s.agentInCone ? s : { ...s, agentInCone: true };
              }
              // Was in cone last turn, now out — release the pin so the
              // normal decrement starts next turn.
              if (s.agentInCone) {
                return { ...s, agentInCone: false };
              }
              return s;
            });
            if (newlyTriggeredStakeouts.length > 0) {
              setTimeout(() => { playWarning(); setStakeoutAlert(newlyTriggeredStakeouts[0]); }, 0);
            }

            // Surveillance Van detection: sample the van's ACTUAL traversed
            // route (animPath) and check every vertex's cone against every
            // agent sample (move path + final position). animPath is the
            // sub-polyline the van walked this turn, including bounce-back
            // points at endpoints, so this truly catches the agent anywhere
            // the van drove past — not just the start, middle, or end.
            //
            // This is O(agent_samples × van_samples) per van, ~100 ops on
            // typical inputs. Trivially fast.
            const newlyTriggeredVans: SurveillanceVan[] = [];
            let anyVanSawAgent = false;
            const vanHitsAgent = (v: SurveillanceVan) => {
              // Build the list of van positions to sweep. Always include the
              // current position; add every animPath vertex if the van moved
              // this turn. animPath vertices are ~route-edge length apart
              // (typically well under 2× cone radius), so consecutive circles
              // fully cover the capsule between them with no gaps.
              const sweepPositions: Array<{ lat: number; lng: number }> = [];
              if (v.animPath && v.animPath.length > 0) {
                for (const [lng, lat] of v.animPath) sweepPositions.push({ lat, lng });
              } else {
                sweepPositions.push(v.position);
              }
              const r = v.visionRangeM;
              const checkPoint = (lat: number, lng: number) => {
                for (const p of sweepPositions) {
                  const dLat = (lat - p.lat) * 111000;
                  const dLng = (lng - p.lng) * 111000 * Math.cos((p.lat * Math.PI) / 180);
                  if (Math.sqrt(dLat * dLat + dLng * dLng) <= r) return true;
                }
                return false;
              };
              if (checkPoint(updatedAgent.position.lat, updatedAgent.position.lng)) return true;
              if (movementCoords && movementCoords.length > 0) {
                for (const [lng, lat] of movementCoords) {
                  if (checkPoint(lat, lng)) return true;
                }
              }
              return false;
            };
            const newVans = gs.surveillanceVans.map(v => {
              const sees = vanHitsAgent(v);
              if (sees) anyVanSawAgent = true;
              if (sees && !v.agentInCone) {
                const updated = { ...v, agentInCone: true };
                newlyTriggeredVans.push(updated);
                return updated;
              }
              if (!sees && v.agentInCone) {
                return { ...v, agentInCone: false };
              }
              return v;
            });
            if (newlyTriggeredVans.length > 0) {
              setTimeout(() => { playWarning(); setSurveillanceVanAlert(newlyTriggeredVans[0]); }, 0);
            }

            // ── Doppelgänger engagement: the path-intercept check ran up
            // top (before setAgentAnimPath) and may have truncated the
            // agent's movement at the engagement point. Reflect that in
            // state here — clear the decoy, flip on the 3-turn reveal
            // timer, and fire an intel notification.
            let doppelAfter = gs.doppelganger;
            let doppelRevealTurns = gs.player.doppelgangerRevealTurnsLeft;
            if (doppelAfter && interceptedCoords) {
              doppelAfter = null;
              doppelRevealTurns = 3;
              setTimeout(() => notify('Doppelgänger engaged! Agent stopped along their path. Position revealed for 3 turns.', 'intel', 4500), 0);
            }

            return {
              ...gs,
              signals: finalSignals,
              agent: updatedAgent,
              doppelganger: doppelAfter,
              spotters: newSpotters,
              wireTaps: newWireTaps,
              stakeouts: newStakeouts,
              surveillanceVans: newVans,
              player: {
                ...gs.player,
                detected: agentSeesPlayer,
                detectedTurnsRemaining: playerDetectedTurns,
                doppelgangerRevealTurnsLeft: doppelRevealTurns,
                // Refresh "enemy seen" timer if player sees agent, any
                // stakeout currently has the agent in its cone (pauses fade),
                // any stakeout's cone was traversed mid-move (resets fade to
                // max but doesn't pause), or any van saw/pass-through the
                // agent this turn.
                seenAgentTurnsLeft: (playerSeesAgent || anyStakeoutSeesAgent || anyStakeoutPassThrough || anyVanSawAgent || doppelRevealTurns > 0) ? 3 : gs.player.seenAgentTurnsLeft,
                // Parade Route / Stop and Frisk were read by the agent worker
                // during its turn (just finished). Clear them now so they
                // don't bleed into subsequent turns.
                paradeRoutePrimed: false,
                stopAndFriskPrimed: false,
                // Ping passive: every 3 turns, inject a junk Ping card into
                // the player's hand. If they don't spend 1 AP to discard it
                // before ending their turn, the agent gets their exact position.
                // Notification fires so the player notices the injection.
                ...(agentHasPassive(updatedAgent, 'ping') && gs.turn % 3 === 0 ? (() => {
                  setTimeout(() => notify(
                    'Ping incoming — spend 1 AP to play the Ping card in your hand, or the agent gets your exact position at turn end.',
                    'agent',
                    5000,
                  ), 0);
                  return {
                    hand: [...gs.player.hand, {
                      ...CARD_TEMPLATES.playerPingJunk,
                      id: `ping-junk-${gs.turn}-${Math.random().toString(36).slice(2, 6)}`,
                    }],
                  };
                })() : {}),
              },
            };
          });
        };
        console.log(`[AgentWorker] Posting turn ${preAgentState.turn} to worker, agent@(${agentGameState.agent.position.lat.toFixed(4)},${agentGameState.agent.position.lng.toFixed(4)}), nearestNode=${agentGameState.agent.nearestNodeId}`);

        // Timeout watchdog: if the worker doesn't respond within 10 seconds,
        // terminate it, create a fresh one, and apply unchanged agent state
        // so the game doesn't freeze permanently.
        if (agentWatchdogRef.current) clearTimeout(agentWatchdogRef.current);
        agentWatchdogRef.current = setTimeout(() => {
          if (lastAppliedAgentTurnRef.current >= preAgentState.turn) return;
          console.error(`[AgentWorker] TIMEOUT: worker did not respond for turn ${preAgentState.turn} within 10s — terminating and recovering`);
          worker.terminate();
          const freshWorker = new Worker(
            new URL('../../engine/agentWorker.ts', import.meta.url),
            { type: 'module' },
          );
          freshWorker.onerror = (ev) => { console.error('[AgentWorker] Worker error:', ev.message, ev); };
          agentWorkerRef.current = freshWorker;
          setGameState(gs => ({
            ...gs,
            player: { ...gs.player, paradeRoutePrimed: false, stopAndFriskPrimed: false },
          }));
        }, 10000);

        worker.postMessage(request);
      } else {
        console.error('[AgentWorker] Worker ref is null — agent AI not running!');
      }

      // Return pre-agent state immediately (agent results arrive async via Phase 2)
      return preAgentState;
    });
  }, [cityData, cityData.navigation.edges]);

  // Enter / Return triggers the End Turn button. Blocked while any modal,
  // picker, placement mode, or text input is active so the keystroke doesn't
  // steal focus from something the player is actually typing in.
  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      // Ignore when focus is on an input / textarea / contenteditable so we
      // don't hijack typing in the tuning panel or dev search boxes.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
      }
      // Block while anything modal-ish is open
      if (pendingAction) return;
      if (safehouseDraft) return;
      if (combatEncounter) return;
      if (pileOverlay) return;
      if (decoySelectOpen) return;
      if (burnerPhoneMode) return;
      if (jammerMode) return;
      if (reconPlaneMode || reconPlanePending) return;
      if (stakeoutMode || stakeoutPendingBuilding) return;
      if (surveillanceVanMode) return;
      // Taxi confirm modal — if we don't guard this, pressing Enter to
      // confirm the taxi instead fires End Turn, which advances the turn
      // and moves the player without ever actually hailing the taxi.
      if (taxiPendingRoute) return;
      // Other dialog-ish states — alerts, scavenge pickup, transit
      // board select, etc. Each of them owns its own click interactions,
      // so Enter while any of them is open should never stealth-fire
      // end-of-turn.
      if (scavengeDialog) return;
      if (boardTransitOpen) return;
      if (spotterAlert) return;
      if (stakeoutAlert) return;
      if (surveillanceVanAlert) return;
      if (wireTapAlert) return;
      if (trafficAnalysisAlert) return;
      if (coldOpenAlert) return;
      if (gameState.roundResult) return; // round over — end screen showing
      if (tuningPanelOpen) return;
      if (agentAIPanelOpen) return;
      e.preventDefault();
      // Brief hover-state flash so the player gets visual confirmation the
      // keyboard shortcut fired. 180ms matches the feel of a quick click.
      setEndTurnFlash(true);
      window.setTimeout(() => setEndTurnFlash(false), 180);
      handleEndTurn();
    };
    window.addEventListener('keydown', onEnter);
    return () => window.removeEventListener('keydown', onEnter);
  }, [
    handleEndTurn,
    pendingAction,
    safehouseDraft,
    combatEncounter,
    pileOverlay,
    decoySelectOpen,
    burnerPhoneMode,
    reconPlaneMode,
    reconPlanePending,
    stakeoutMode,
    stakeoutPendingBuilding,
    surveillanceVanMode,
    taxiPendingRoute,
    scavengeDialog,
    boardTransitOpen,
    spotterAlert,
    stakeoutAlert,
    surveillanceVanAlert,
    wireTapAlert,
    trafficAnalysisAlert,
    coldOpenAlert,
    gameState.roundResult,
    tuningPanelOpen,
    agentAIPanelOpen,
  ]);

  // Determine which transit types exist in this city's data
  const availableTransitTypes = useMemo(() => {
    const types = new Set<TransitType>();
    for (const line of cityData.layers.transit.lines) {
      types.add(line.type as TransitType);
    }
    return types;
  }, [cityData]);

  // null = transit off, otherwise show only the selected type
  const [activeTransitType, setActiveTransitType] = useState<TransitType | null>(null);
  const [showZones, setShowZones] = useState(false);
  const [showRoads, setShowRoads] = useState(false);

  // Q key toggles zone overlay, R key toggles road overlay
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'KeyQ' && !e.repeat) setShowZones(prev => !prev);
      if (e.code === 'KeyR' && !e.repeat) setShowRoads(prev => !prev);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // F key — center map on the best available agent intel.
  // F key — center on the best available agent intel.
  // Priority: revealed agent > trace route > traffic analysis > strongest signal.
  const signalsRef = useRef(gameState.signals);
  signalsRef.current = gameState.signals;
  const agentPosRef = useRef(gameState.agent.position);
  agentPosRef.current = gameState.agent.position;
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;
  // Track whether the agent dot is visible — computed further down but
  // we need it here. Use a ref updated each render so the keydown
  // handler always reads the latest value without being in the deps.
  const agentDotVisibleRef = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'KeyF' || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;

      const gs = gameStateRef.current;

      // 1. Agent currently revealed on map → exact position
      if (agentDotVisibleRef.current) {
        const pos = agentPosRef.current;
        setRecenterTarget({ lat: pos.lat, lng: pos.lng });
        setRecenterTrigger(n => n + 1);
        return;
      }

      // Collect all candidate intel sources with a turn number so we can
      // pick the most recent one across signals AND non-signal intel.
      type IntelCandidate = { lat: number; lng: number; turn: number; strength: number; radius: number };
      const candidates: IntelCandidate[] = [];

      // Agent signals (passive, sweep, etc.)
      for (const s of signalsRef.current) {
        if (s.source === 'agent' && s.revealed) {
          candidates.push({
            lat: s.position.lat,
            lng: s.position.lng,
            turn: s.turnGenerated,
            strength: s.strength,
            radius: s.radius ?? 0,
          });
        }
      }

      // Traffic Analysis — road-level intel, not a Signal object.
      // Compute center of the highlighted road edges.
      if (gs.player.trafficAnalysisEdges && gs.player.trafficAnalysisEdges.length > 0) {
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
        for (const group of gs.player.trafficAnalysisEdges) {
          for (const [lng, lat] of group) {
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
          }
        }
        candidates.push({
          lat: (minLat + maxLat) / 2,
          lng: (minLng + maxLng) / 2,
          turn: gs.turn, // active this turn
          strength: 0.9, // high-value intel
          radius: 0, // road-level precision, no scatter
        });
      }

      // Trace Route — most recent position in the trail
      if (gs.traceRouteTrail && gs.traceRouteTrail.positions.length > 0) {
        const newest = gs.traceRouteTrail.positions[0]; // sorted most recent first
        candidates.push({
          lat: newest.lat,
          lng: newest.lng,
          turn: newest.turn,
          strength: 0.95,
          radius: 0, // exact historical position
        });
      }

      if (candidates.length === 0) return;

      // Pick best: most recent turn, then strongest within that turn
      const best = candidates.reduce((a, b) => {
        if (b.turn !== a.turn) return b.turn > a.turn ? b : a;
        return b.strength > a.strength ? b : a;
      });

      // Scatter within the signal radius for imprecise intel
      let targetLat = best.lat;
      let targetLng = best.lng;
      if (best.radius > 10) {
        const angle = Math.random() * 2 * Math.PI;
        const dist = Math.sqrt(Math.random()) * best.radius;
        targetLat += (dist * Math.cos(angle)) / 111000;
        targetLng += (dist * Math.sin(angle)) / (111000 * Math.cos(best.lat * Math.PI / 180));
      }
      setRecenterTarget({ lat: targetLat, lng: targetLng });
      setRecenterTrigger(n => n + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Trivial computation — no memo needed
  const visibleTransitTypes = activeTransitType === null
    ? new Set<TransitType>()
    : new Set([activeTransitType]);

  const handleSelectTransit = useCallback((type: TransitType | null) => {
    setActiveTransitType(type);
  }, []);

  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const latSpanKm = ((cityData.bounds.north - cityData.bounds.south) * 111).toFixed(1);
  const lngSpanKm = ((cityData.bounds.east - cityData.bounds.west) * 111 * Math.cos((cityData.center.lat * Math.PI) / 180)).toFixed(1);

  // Memoize expensive detection computations — these involve trig and position comparisons
  const spotterRevealActive = useMemo(
    () => gameState.spotters.some(s => s.agentRevealed && s.turnsRemaining > 0),
    [gameState.spotters],
  );
  const agentDetectionBroadcast = (gameState.agent.detectedTurnsRemaining ?? 0) > 0;
  // Cold Opening reveal — shows BOTH the agent's cone and their exact dot
  // marker on turn 1 so the player gets a clean first-turn read. The
  // coneOnlyRevealTurnsLeft field name is historical from when Cold Opening
  // was cone-only; it now drives a full reveal. Field is kept instead of
  // renamed because it still decrements independently of
  // detectedTurnsRemaining so Cold Opening can coexist with other reveal
  // sources without interfering with their timers.
  const coldOpeningRevealActive = (gameState.agent.coneOnlyRevealTurnsLeft ?? 0) > 0;

  // Fire the Cold Opening intel dialog exactly once per round — when the
  // passive is active AND it's turn 1 AND we haven't shown it yet for
  // this round. The ref prevents re-firing on hot-reload or when the
  // component re-renders for other reasons.
  const coldOpenAlertFiredRef = useRef(false);
  useEffect(() => {
    if (
      coldOpeningRevealActive &&
      gameState.turn === 1 &&
      !coldOpenAlertFiredRef.current
    ) {
      coldOpenAlertFiredRef.current = true;
      setColdOpenAlert({
        lat: gameState.agent.position.lat,
        lng: gameState.agent.position.lng,
      });
    }
  }, [coldOpeningRevealActive, gameState.turn, gameState.agent.position.lat, gameState.agent.position.lng]);

  // Dead agents (extraction-op clears) don't see anything and can't be
  // "seen" in a way that triggers combat. Their corpse still renders on
  // the map as a passive marker, but vision math goes flat.
  const agentIsDead = gameState.agent.isDead;
  const playerSeesAgentNow = useMemo(
    () => !agentIsDead && playerCanSeeAgent(gameState.player, gameState.agent),
    [agentIsDead, gameState.player.position.lat, gameState.player.position.lng, gameState.player.facing,
     gameState.player.visionConeWidth, gameState.player.isInBuilding, gameState.player.isElevated,
     gameState.agent.position.lat, gameState.agent.position.lng],
  );
  const agentSeesPlayerNow = useMemo(
    () => !agentIsDead && agentCanSeePlayer(gameState.agent, gameState.player),
    [agentIsDead, gameState.agent.position.lat, gameState.agent.position.lng, gameState.agent.facing,
     gameState.player.position.lat, gameState.player.position.lng, gameState.player.isInBuilding],
  );

  const wireTapRevealActive = gameState.wireTaps.some(t => t.triggered && t.triggeredTurn != null && gameState.turn - t.triggeredTurn < 3);
  // When player spots agent via vision cone mid-turn, set the "seen" timer to 3 turns
  const prevSeesRef = useRef(playerSeesAgentNow);
  useEffect(() => {
    if (playerSeesAgentNow && !prevSeesRef.current) {
      playWarning();
      setGameState(prev => ({
        ...prev,
        player: { ...prev.player, seenAgentTurnsLeft: 3 },
      }));
    }
    prevSeesRef.current = playerSeesAgentNow;
  }, [playerSeesAgentNow]);

  const seenAgentActive = gameState.player.seenAgentTurnsLeft > 0;
  // agentVisible drives the vision cone layer; agentDotVisible drives the
  // agent position marker. Cold Opening now shows BOTH — previously the
  // cone-only interpretation left the player squinting at an arrow
  // pointing nowhere. Keeping the two variables separate in case we bring
  // back a cone-only reveal mode for a future passive.
  const coldRevealAny = coldOpeningRevealActive;
  const doppelgangerRevealActive = gameState.player.doppelgangerRevealTurnsLeft > 0;
  // Dead agent (extraction-op kill): no vision cone, but the corpse stays
  // visible as a marker so the player knows where the body fell. Combat
  // can't re-trigger because playerSeesAgentNow / agentSeesPlayerNow are
  // already gated on isDead above.
  const agentVisible = !agentIsDead && (testMode || playerSeesAgentNow || seenAgentActive || spotterRevealActive || agentDetectionBroadcast || wireTapRevealActive || combatEncounter != null || coldRevealAny || doppelgangerRevealActive);
  const agentDotVisible = agentIsDead || testMode || playerSeesAgentNow || seenAgentActive || spotterRevealActive || agentDetectionBroadcast || wireTapRevealActive || combatEncounter != null || coldRevealAny || doppelgangerRevealActive;
  agentDotVisibleRef.current = agentDotVisible;
  const ambushAvailable = playerSeesAgentNow && !agentSeesPlayerNow;

  // Non-blocking ambush overlay — shows once per sighting session (agent
  // enters cone and stays visible). Dismisses automatically when the agent
  // leaves the cone. Only fires once per turn to avoid spamming the player
  // while they sweep their vision cone back and forth.
  const [ambushOverlayVisible, setAmbushOverlayVisible] = useState(false);
  const ambushShownThisTurnRef = useRef<number>(-1);
  useEffect(() => {
    if (ambushAvailable && !combatEncounter) {
      // Rooftop = sniper-only (mirrors canAmbushWith). If the player is
      // elevated without a sniper, no usable attack exists from this
      // position — don't pop the ambush prompt.
      const weapons = gameState.player.items.filter(i => i.category === 'weapon');
      const hasUsableWeapon = weapons.some(w => canAmbushWith(w, gameState.player).valid);
      const hasImpStrike =
        !gameState.player.isElevated &&
        gameState.player.hand.some(c => c.name === 'Improvised Strike');
      if ((hasUsableWeapon || hasImpStrike) && ambushShownThisTurnRef.current !== gameState.turn) {
        setAmbushOverlayVisible(true);
        ambushShownThisTurnRef.current = gameState.turn;
      }
    }
    if (!ambushAvailable) {
      setAmbushOverlayVisible(false);
    }
  }, [ambushAvailable, combatEncounter, gameState.player.items, gameState.player.hand, gameState.turn]);

  const agentFacing = gameState.agent.facing ?? 0;

  return (
    <div style={styles.container}>
      {/* Top bar — slim, info only */}
      <div style={styles.topBar}>
        {onSaveAndExit && (
          <button
            style={{
              ...styles.backButton,
              color: theme.colors.textSecondary,
              borderColor: theme.colors.border,
            }}
            onClick={() => onSaveAndExit(gameState)}
            title="Save progress and return to menu"
          >
            ← EXIT
          </button>
        )}
        {onBack && !onSaveAndExit && (
          <button style={styles.backButton} onClick={onBack}>
            ← ABORT
          </button>
        )}
        <div style={styles.topBarTitle}>
          GEO-SLEEPER
        </div>
        <div style={styles.topBarCity}>
          {cityData.name.toUpperCase()}
        </div>
        {runState && (
          <div style={{ fontSize: '10px', letterSpacing: '1px', color: theme.colors.textDim }}>
            ROUND {runState.round}
          </div>
        )}
        <div style={styles.topBarStats}>
          {lngSpanKm}×{latSpanKm}km
        </div>
        <button
          style={{
            ...styles.backButton,
            fontSize: '12px',
            fontWeight: 700,
            padding: '2px 8px',
            marginLeft: '4px',
          }}
          onClick={() => setShowTutorial(true)}
          title="Tutorial / Help"
        >
          ?
        </button>
        <button
          style={{
            ...styles.backButton,
            fontSize: '10px',
            fontWeight: 600,
            padding: '2px 8px',
            ...(lightMode ? { background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.3)' } : {}),
          }}
          onClick={() => setLightMode(prev => !prev)}
          title="Toggle light/dark mode"
        >
          {lightMode ? '☀' : '☾'}
        </button>
        {/* Alt Movement Mode toggle */}
        <button
          style={{
            ...styles.testModeButton,
            ...(altMovementMode ? styles.testModeButtonActive : {}),
            marginRight: '4px',
          }}
          onClick={() => setAltMovementMode(prev => !prev)}
          title="Alt Movement Mode"
        >
          ALT MOVE
        </button>
        {/* Tuning panel toggle */}
        <button
          style={{
            ...styles.testModeButton,
            ...(tuningPanelOpen ? styles.testModeButtonActive : {}),
            marginRight: '4px',
          }}
          onClick={() => setTuningPanelOpen(prev => !prev)}
          title="Tuning Panel"
        >
          ⚙️ TUNE
        </button>
        {/* Agent AI panel toggle */}
        <button
          style={{
            ...styles.testModeButton,
            ...(agentAIPanelOpen ? { ...styles.testModeButtonActive, background: 'rgba(255, 170, 68, 0.2)', borderColor: '#ffaa44', color: '#ffaa44' } : {}),
            marginRight: '4px',
          }}
          onClick={() => setAgentAIPanelOpen(prev => !prev)}
          title="Agent AI Test Panel"
        >
          {agentAIPanelOpen ? '🤖 AI ON' : '🤖 AI'}
        </button>
        {/* Test mode toggle */}
        <button
          style={{
            ...styles.testModeButton,
            ...(testMode ? styles.testModeButtonActive : {}),
          }}
          onClick={() => setTestMode(prev => !prev)}
          title="Toggle test mode (Ctrl+T)"
        >
          {testMode ? '⚡ TEST ON' : 'TEST'}
        </button>
        <button
          style={{
            ...styles.testModeButton,
            ...(freeHandMode ? { ...styles.testModeButtonActive, background: 'rgba(0, 229, 255, 0.2)', borderColor: '#00e5ff', color: '#00e5ff' } : {}),
            marginLeft: '4px',
          }}
          onClick={() => {
            setFreeHandMode(prev => !prev);
            setFreeHandPanel(prev => !prev);
          }}
          title="Deck editor — customize your card pool"
        >
          {freeHandMode ? '✋ DECK' : 'DECK EDIT'}
        </button>
      </div>

      {/* Passive traits row — shown whenever the run owns any passives */}
      {runState && runState.passives.length > 0 && (
        <div style={styles.passiveRow}>
          <span style={styles.passiveRowLabel}>TRAITS</span>
          {runState.passives.map(key => {
            const template = PASSIVE_TEMPLATES[key];
            if (!template) return null;
            const color = PASSIVE_BUCKET_COLORS[template.bucket];
            return (
              <span
                key={key}
                className="passive-chip"
                style={{
                  ...styles.passiveChip,
                  borderColor: `${color}60`,
                  color,
                }}
              >
                {template.name}
                <span className="passive-chip__tooltip">
                  <span className="passive-chip__tooltip-name" style={{ color, display: 'block' }}>
                    {template.name}
                  </span>
                  <span className="passive-chip__tooltip-bucket" style={{ color: `${color}99`, display: 'block' }}>
                    {template.bucket.toUpperCase()}
                  </span>
                  <span className="passive-chip__tooltip-desc" style={{ display: 'block' }}>
                    {template.description}
                  </span>
                </span>
              </span>
            );
          })}
        </div>
      )}

      {/* Map */}
      <div style={styles.mapContainer}>
        {/* OSM attribution — required by the ODbL license for any app using OSM-derived
            data. Top-left corner, kept small/muted so it doesn't compete with the game UI.
            Nothing else is anchored here; the banner stack is horizontally centered, and
            the inventory bar sits at vertical-center on the left edge. */}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.osmCredit}
        >
          © OpenStreetMap contributors
        </a>
        {/* Top banners — stacked vertically */}
        <div style={styles.bannerStack}>
          {/* Persistent status banners (hiding / rooftop) */}
          {gameState.player.isInBuilding && !hideMode && (
            <div style={{ ...styles.hideBannerInline, pointerEvents: 'auto' }}>
              <span style={styles.hideBannerCard}>HIDING</span>
              <span style={styles.hideBannerText}>
                Zero signal — {gameState.player.hidingTurnsLeft} turn{gameState.player.hidingTurnsLeft !== 1 ? 's' : ''} remaining
              </span>
            </div>
          )}
          {gameState.player.isElevated && !gameState.player.isInBuilding && !rooftopMode && (
            <div style={{ ...styles.hideBannerInline, pointerEvents: 'auto', borderColor: 'rgba(0, 229, 255, 0.4)', background: 'rgba(0, 229, 255, 0.08)' }}>
              <span style={{ ...styles.hideBannerCard, background: 'rgba(0, 229, 255, 0.2)', color: '#00e5ff' }}>ROOFTOP</span>
              <span style={styles.hideBannerText}>
                {tuning.rooftopRangeMultiplier}x vision range, {tuning.rooftopConeAngle}° cone — invisible to ground agents
              </span>
            </div>
          )}
          {/* Extraction proximity warning */}
          {inExtractionZone && !hideMode && !rooftopMode && (
            <div style={{ ...styles.hideBannerInline, pointerEvents: 'auto', borderColor: 'rgba(255, 64, 64, 0.5)', background: 'rgba(255, 64, 64, 0.08)' }}>
              <span style={{ ...styles.hideBannerCard, background: 'rgba(255, 64, 64, 0.25)', color: '#ff4040' }}>EXTRACTION ZONE</span>
              <span style={{ ...styles.hideBannerText, color: '#ff8080' }}>
                Position revealed to enemy — exposure must be below {Math.round(tuning.extractMaxExposurePct * 100)}% to extract
              </span>
            </div>
          )}
          {/* Mode banners */}
          {movementMode && (
            <div style={styles.movementBannerInline}>
              <span style={styles.movementBannerCard}>{movementMode.card.name}</span>
              <span style={styles.movementBannerText}>
                {remainingRange !== null
                  ? `${Math.round(remainingRange)}m remaining`
                  : `${movementMode.maxRange}m range`
                }
              </span>
              {movementPreview && (
                <span style={styles.movementPreviewInfo}>
                  <span style={{
                    ...styles.previewStat,
                    color: movementPreview.signalStrength < 0.15 ? '#44cc88'
                      : movementPreview.signalStrength < 0.35 ? '#88cc44'
                      : movementPreview.signalStrength < 0.55 ? '#ccaa44'
                      : movementPreview.signalStrength < 0.75 ? '#cc7744'
                      : '#cc4444',
                  }}>
                    SIGNAL {Math.round(movementPreview.signalStrength * 100)}%
                  </span>
                  <span style={{
                    ...styles.previewStat,
                    color: movementPreview.exposureGain < 20 ? '#44cc88'
                      : movementPreview.exposureGain < 60 ? '#ccaa44'
                      : '#cc4444',
                  }}>
                    +{movementPreview.exposureGain} EXP
                  </span>
                  <span style={styles.previewStat}>
                    {Math.round(movementPreview.distance)}m
                  </span>
                </span>
              )}
              <span style={styles.movementBannerKeys}>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>click</kbd> move</span>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>{navigator.platform.includes('Mac') ? '⌘' : 'ctrl'}+click</kbd> partial</span>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>shift</kbd> footpaths</span>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>esc</kbd> cancel</span>
              </span>
            </div>
          )}
          {/* Doppelgänger placement banner — mirrors the movement banner
              but shows only distance (player doesn't actually move) + the
              "truncated" state when the cursor is beyond 800m. */}
          {(selectedCard?.name === 'Doppelgänger' || selectedCard?.name === 'Doppelganger') && (
            <div style={styles.movementBannerInline}>
              <span style={styles.movementBannerCard}>DOPPELGÄNGER</span>
              <span style={styles.movementBannerText}>
                800m range — click to commit
              </span>
              {doppelgangerPreview && (
                <span style={styles.movementPreviewInfo}>
                  <span style={{
                    ...styles.previewStat,
                    color: doppelgangerPreview.overRange ? '#cc7744' : '#ffdd44',
                  }}>
                    {Math.round(doppelgangerPreview.distance)}m{doppelgangerPreview.overRange ? ' (truncated)' : ''}
                  </span>
                </span>
              )}
              <span style={styles.movementBannerKeys}>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>click</kbd> place</span>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>esc</kbd> cancel</span>
              </span>
            </div>
          )}
          {/* Misdirect placement banner — mirrors the Doppelgänger readout.
              300m range; over-range paths still commit but are truncated at
              the limit (matches how Move handles over-range clicks). */}
          {selectedCard?.name === 'Misdirect' && (
            <div style={styles.movementBannerInline}>
              <span style={styles.movementBannerCard}>MISDIRECT</span>
              <span style={styles.movementBannerText}>
                300m range — click to commit
              </span>
              {misdirectPreview && (
                <span style={styles.movementPreviewInfo}>
                  <span style={{
                    ...styles.previewStat,
                    color: misdirectPreview.overRange ? '#cc7744' : '#ffdd44',
                  }}>
                    {Math.round(misdirectPreview.distance)}m{misdirectPreview.overRange ? ' (truncated)' : ''}
                  </span>
                </span>
              )}
              <span style={styles.movementBannerKeys}>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>click</kbd> place</span>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>esc</kbd> cancel</span>
              </span>
            </div>
          )}
          {/* Overwatch placement banner — segment auto-truncates at 300m AND
              stops at the end of the current named road, so distance is
              always within cap (no "truncated" state like Misdirect). */}
          {selectedCard?.name === 'Overwatch' && (
            <div style={styles.movementBannerInline}>
              <span style={styles.movementBannerCard}>OVERWATCH</span>
              <span style={styles.movementBannerText}>
                Up to 300m along current road — click to deploy
              </span>
              {overwatchPreview && (
                <span style={styles.movementPreviewInfo}>
                  <span style={{ ...styles.previewStat, color: '#ffdd44' }}>
                    {Math.round(overwatchPreview.distance)}m
                  </span>
                </span>
              )}
              <span style={styles.movementBannerKeys}>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>click</kbd> place</span>
                <span style={styles.kbdHint}><kbd style={styles.kbd}>esc</kbd> cancel</span>
              </span>
            </div>
          )}
          {sweepMode && (
            <div style={styles.sweepBannerInline}>
              <span style={styles.sweepBannerCard}>SWEEP</span>
              <span style={styles.sweepBannerText}>
                Click anywhere to place a {sweepMode.radius >= 1000 ? `${(sweepMode.radius / 1000).toFixed(1)}km` : `${sweepMode.radius}m`} scan zone
              </span>
              <span style={styles.movementBannerEsc}>ESC to cancel</span>
            </div>
          )}
          {rooftopMode && (
            <div style={{ ...styles.hideBannerInline, borderColor: 'rgba(0, 229, 255, 0.4)', background: 'rgba(0, 229, 255, 0.08)' }}>
              <span style={{ ...styles.hideBannerCard, background: 'rgba(0, 229, 255, 0.2)', color: '#00e5ff' }}>ROOFTOP ACCESS</span>
              <span style={styles.hideBannerText}>
                Click a green commercial building within 50m to climb to rooftop
              </span>
              <span style={styles.movementBannerEsc}>ESC to cancel</span>
            </div>
          )}
          {hideMode && (() => {
            const hasLockpick = gameState.player.items.some(i => i.name === 'Lock Pick Set');
            return (
              <div style={styles.hideBannerInline}>
                <span style={styles.hideBannerCard}>HIDE INSIDE</span>
                <span style={styles.hideBannerText}>
                  Click a building within 50m. Green = free entry. {hasLockpick ? 'Yellow = uses Lock Pick.' : 'Residential buildings need a Lock Pick.'}
                </span>
                <span style={styles.movementBannerEsc}>ESC to cancel</span>
              </div>
            );
          })()}
          {/* Decoy radius selection is now a centered modal — see below */}
          {decoyMode && (
            <div style={styles.decoyBannerInline}>
              <span style={styles.decoyBannerCard}>DECOY</span>
              <span style={styles.decoyBannerText}>
                Click anywhere to place a {decoyRadius}m fake signal
              </span>
              <span style={styles.movementBannerEsc}>ESC to cancel</span>
            </div>
          )}
          {/* Game notifications are rendered below via the message overlay
              (above the hand), not inline at the top of the map. */}
          {/* Informant tip now fires a message overlay on play and a
              persistent status chip for the rest of the tip's lifetime. */}
          {/* Hover info — only when not in a targeting mode */}
          {hoverInfo && !movementMode && !sweepMode && !hideMode && !decoyMode && (
            <div style={styles.hoverBannerInline}>
              {hoverInfo.name ? (
                <>
                  <span style={styles.hoverType}>{hoverInfo.type === 'transit' ? 'LINE' : hoverInfo.type === 'stop' ? 'STOP' : 'ROAD'}</span>
                  <span style={styles.hoverName}>{hoverInfo.name}</span>
                  {hoverInfo.lineName && <span style={styles.hoverLine}>{hoverInfo.lineName}</span>}
                </>
              ) : (
                <span style={styles.hoverType}>ZONE</span>
              )}
              {hoverInfo.roadClass && (
                <span style={{
                  ...styles.hoverRoadClass,
                  color: hoverInfo.roadClass === 'major' ? '#f87171'
                    : hoverInfo.roadClass === 'minor' ? '#fb923c'
                    : hoverInfo.roadClass === 'residential' ? '#facc15'
                    : '#4ade80',
                }}>
                  <span style={styles.hoverRoadClassLabel}>{hoverInfo.roadClass.toUpperCase()}</span>
                  <span style={styles.hoverRoadClassWeight}>⚡{hoverInfo.roadSignalWeight?.toFixed(1)}</span>
                </span>
              )}
              {hoverInfo.zone && (
                <span style={styles.hoverZone}>
                  <span style={styles.hoverZoneLabel}>{hoverInfo.zone.toUpperCase()}</span>
                  <span style={{
                    ...styles.hoverZoneMultiplier,
                    color: hoverInfo.signalMultiplier === 0
                      ? theme.colors.accentGreen
                      : (hoverInfo.signalMultiplier ?? 0) >= 1.0
                        ? theme.colors.accentRed
                        : theme.colors.textSecondary,
                  }}>
                    {hoverInfo.signalMultiplier === 0 ? 'DARK' : `×${hoverInfo.signalMultiplier?.toFixed(1)}`}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
        <MapRenderer
          cityData={cityData}
          gameState={gameState}
          visibleTransitTypes={visibleTransitTypes}
          onHover={setHoverInfo}
          movementMode={movementMode}
          altMovementMode={altMovementMode}
          onMovementComplete={handleMovementComplete}
          onMovementPartial={handleMovementPartial}
          onMovementCancel={handleMovementCancel}
          onMovementPreview={setMovementPreview}
          onAltRoutesCount={setAltRoutesCount}
          sweepMode={sweepMode}
          onSweepPlace={handleSweepPlace}
          hideMode={hideMode}
          rooftopMode={rooftopMode}
          onHideSelect={handleHideSelect}
          decoyMode={decoyMode}
          decoyRadius={decoyRadius}
          onDecoyPlace={handleDecoyPlace}
          wireTapMode={selectedCard?.name === 'Wire Tap'}
          onWireTapPlace={handleWireTapPlace}
          burnerPhoneMode={burnerPhoneMode}
          burnerPhoneRadius={tuning.burnerPhoneRadius}
          onBurnerPhonePlace={handleBurnerPhonePlace}
          jammerMode={jammerMode}
          jammerRadius={350}
          onJammerPlace={handleJammerPlace}
          misdirectMode={selectedCard?.name === 'Misdirect'}
          onMisdirectPlace={handleMisdirectPlace}
          onMisdirectPreview={setMisdirectPreview}
          doppelgangerMode={selectedCard?.name === 'Doppelgänger' || selectedCard?.name === 'Doppelganger'}
          onDoppelgangerPlace={handleDoppelgangerPlace}
          onDoppelgangerPreview={setDoppelgangerPreview}
          doppelganger={gameState.doppelganger}
          overwatchMode={selectedCard?.name === 'Overwatch'}
          onOverwatchPlace={handleOverwatchPlace}
          onOverwatchPreview={setOverwatchPreview}
          overwatchSegment={gameState.player.overwatch?.segmentCoords ?? null}
          taxiSelectMode={taxiSelectMode}
          onTaxiSelectPlace={handleTaxiPlace}
          reconPlaneMode={reconPlaneMode}
          reconPlanePending={reconPlanePending}
          onReconPlanePlace={handleReconPlanePlace}
          stakeoutMode={stakeoutMode}
          stakeoutPendingBuilding={stakeoutPendingBuilding}
          onStakeoutBuildingSelect={handleStakeoutBuildingSelect}
          onStakeoutAngleCommit={handleStakeoutAngleCommit}
          surveillanceVanMode={surveillanceVanMode}
          onSurveillanceVanRoutePlace={handleSurveillanceVanRoutePlace}
          onSurveillanceVanPreview={setSurveillanceVanPreview}
          signalPulse={signalPulse}
          runState={runState}
          teleportMode={testMode && teleportMode}
          recenterTrigger={recenterTrigger}
          recenterTarget={recenterTarget}
          recenterZoom={recenterZoom}
          onTeleport={(lat, lng) => {
            const nearestNode = findNearestNode(lat, lng, cityData.navigation.nodes);
            if (!nearestNode) return;
            setGameState(prev => ({
              ...prev,
              player: {
                ...prev.player,
                position: { lat: nearestNode.lat, lng: nearestNode.lng },
                nearestNodeId: nearestNode.id,
              },
            }));
          }}
          showEnemySignals={testMode || showEnemySignals}
          showOwnSignals={testMode || showOwnSignals}
          counterSurveillanceActive={counterSurveillanceActive}
          signalMaxAge={testMode ? 99 : signalMaxAge}
          showZones={showZones}
          showRoads={showRoads}
          onFacingChange={handleFacingChange}
          agentVisible={agentVisible}
          agentDotVisible={agentDotVisible}
          agentAnimationPath={agentAnimPath}
          onAgentAnimationDone={() => setAgentAnimPath(null)}
          spotterReveal={spotterRevealActive}
          informantTips={gameState.informantTips}
          spotters={gameState.spotters}
          agentFacing={agentFacing}
          showAgentBelief={agentAIPanelOpen}
          debriefPaths={gameState.roundResult ? { playerPath: playerPathRef.current, agentPath: agentPathRef.current } : null}
        />
        <StreetViewThumbnail
          lat={gameState.player.position.lat}
          lng={gameState.player.position.lng}
          facing={gameState.player.facing}
          isInBuilding={gameState.player.isInBuilding}
          onExpandChange={setStreetViewExpanded}
        />

        {/* Exposure & Prep stat bars — positioned to the right of the street view thumbnail */}
        {(() => {
          const expPct = Math.min(100, (gameState.player.exposure / tuning.exposureMax) * 100);
          const expBarColor = expPct > 60 ? '#ff4040'
            : expPct > 30 ? '#ffaa30'
            : expPct > 15 ? '#44aaff'
            : '#555';

          const prepPct = Math.min(100, (gameState.player.prepBonus / 0.9) * 100); // 90% bonus = full bar
          const prepColor = prepPct > 60 ? '#44cc88' : prepPct > 30 ? '#ffaa44' : prepPct > 0 ? '#ff5050' : '#444';

          return (
            // Outer wrapper owns the absolute positioning and the
            // responsive `left` rule so both child panels (stat bars and
            // target dossier) move in lockstep with the street view
            // thumbnail expanding. `gap: 10px` matches the horizontal gap
            // between the mapillary thumbnail and the stat bars, keeping
            // spacing consistent across the HUD.
            <div
              className="hud-left-column"
              style={{
                position: 'absolute',
                top: '120px',
                left: streetViewExpanded ? '300px' : '160px',
                width: '220px',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                transition: 'left 0.2s ease',
              }}
            >
            <div
              className="stat-bars-overlay"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                background: 'rgba(8, 12, 20, 0.85)',
                borderRadius: '6px',
                padding: '10px 12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                // backdrop-filter on this wrapper AND on TargetPanel below
                // creates two sibling stacking contexts. Without explicit
                // z-index here, TargetPanel (later in the DOM) paints on top
                // and hides the exposure/prep bar hover tooltips that open
                // downward into the TargetPanel's area. Setting z-index:2
                // (any positive number works) pushes this stacking context
                // above TargetPanel's auto context so the tooltips are
                // visible.
                position: 'relative' as const,
                zIndex: 2,
              }}
            >
              {/* Exposure bar — explicit z-index so its tooltip paints above
                  the TargetPanel below it. Without this, the TargetPanel's
                  backdrop-filter creates a sibling stacking context that
                  wins the flex-order paint race and hides the tooltip. */}
              <div className="exposure-meter-wrapper" style={{ position: 'relative' as const, zIndex: 20 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '3px',
                }}>
                  <span style={{
                    fontSize: '9px',
                    fontFamily: theme.fonts.mono,
                    letterSpacing: '1.5px',
                    color: expBarColor,
                    fontWeight: 700,
                  }}>
                    EXPOSURE
                  </span>
                  <span style={{
                    fontSize: '8px',
                    fontFamily: theme.fonts.mono,
                    letterSpacing: '0.5px',
                    color: theme.colors.textDim,
                    fontWeight: 600,
                  }}>
                    {Math.round(expPct)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  position: 'relative' as const,
                }}>
                  <div style={{
                    width: `${expPct}%`,
                    height: '100%',
                    background: expBarColor,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease, background 0.3s ease',
                  }} />
                </div>
                {/* Hover tooltip */}
                <div className="exposure-tooltip" style={{
                  display: 'none',
                  position: 'absolute' as const,
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  padding: '10px 14px',
                  background: 'rgba(12, 16, 24, 0.97)',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '4px',
                  width: '240px',
                  zIndex: 10000,
                  pointerEvents: 'none' as const,
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: expBarColor, marginBottom: '6px', letterSpacing: '1px' }}>
                    EXPOSURE — {Math.round(expPct)}%
                  </div>
                  <div style={{ fontSize: '10px', color: '#aaa', lineHeight: '1.5' }}>
                    Exposure builds from moving through busy areas. Higher exposure makes you louder and blocks extraction. Safehouses reset exposure to zero.
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '9px', color: '#888', lineHeight: '1.6' }}>
                    <div style={{ color: '#ff5050' }}>You are blind to agent passive signals by default.</div>
                    <div style={{ color: '#44ff88', marginTop: '2px' }}>Radio Scanner, Echo Chamber, or Blown Entrance reveal them.</div>
                    <div style={{ color: '#44aaff', marginTop: '2px' }}>Intel cards (Sweep, Spotter, etc.) always work regardless.</div>
                  </div>
                </div>
              </div>

              {/* Prep bar — same z-index bump as the exposure bar above. */}
              <div className="exposure-meter-wrapper" style={{ position: 'relative' as const, zIndex: 20 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '3px',
                }}>
                  <span style={{
                    fontSize: '9px',
                    fontFamily: theme.fonts.mono,
                    letterSpacing: '1.5px',
                    color: prepColor,
                    fontWeight: 700,
                  }}>
                    PREP
                  </span>
                  <span style={{
                    fontSize: '8px',
                    fontFamily: theme.fonts.mono,
                    color: prepPct > 0 ? prepColor : '#555',
                  }}>
                    +{Math.round(gameState.player.prepBonus * 100)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${prepPct}%`,
                    height: '100%',
                    background: prepColor,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease, background 0.3s ease',
                  }} />
                </div>
                <div className="exposure-tooltip" style={{
                  display: 'none',
                  position: 'absolute', left: '110%', top: '50%', transform: 'translateY(-50%)',
                  background: 'rgba(8,12,20,0.98)', border: '1px solid rgba(255,85,85,0.3)',
                  borderRadius: '6px', padding: '10px 14px', fontFamily: theme.fonts.mono,
                  fontSize: '9px', width: '200px', zIndex: 10000, pointerEvents: 'none',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
                }}>
                  <div style={{ fontWeight: 700, color: prepColor, letterSpacing: '1px', marginBottom: '6px', fontSize: '10px' }}>
                    {/* Tooltip and inline label both report the raw bonus
                        (prepBonus × 100). prepPct is a fill ratio (bonus /
                        90% cap) used only to size the bar width — previously
                        the tooltip was reading that ratio and displaying it
                        as a percent, which read ~11 points higher than the
                        bar's own +N% readout (e.g. bar said +65%, tooltip
                        said 72%). */}
                    PREP — +{Math.round(gameState.player.prepBonus * 100)}%
                  </div>
                  <div style={{ color: theme.colors.textSecondary, lineHeight: '1.5' }}>
                    Prep improves your odds in combat encounters where the outcome is chance-based — mutual sightings, gun standoffs, and unarmed fights. The higher your prep, the more likely you win when it comes down to a roll.
                  </div>
                </div>
              </div>

              {/* Passphrase progress */}
              <div style={{ marginTop: '6px' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px',
                }}>
                  <span style={{
                    fontSize: '9px', fontFamily: theme.fonts.mono, letterSpacing: '1.5px',
                    color: '#ccddee', fontWeight: 700,
                  }}>
                    PASSPHRASE
                  </span>
                  <span style={{
                    fontSize: '8px', fontFamily: theme.fonts.mono,
                    color: passphrasesCollected >= passphrasesRequired ? '#44cc88' : '#ccddee',
                  }}>
                    {passphrasesCollected}/{passphrasesRequired}
                  </span>
                </div>
                <div style={{
                  display: 'flex', gap: '3px',
                }}>
                  {Array.from({ length: passphrasesRequired }).map((_, i) => {
                    const collected = gameState.passphrases.filter(p => p.collected);
                    const pp = i < collected.length ? collected[i] : null;
                    return (
                      <div key={i}
                        title={pp ? pp.word : 'Not collected'}
                        style={{
                          flex: 1, height: '8px',
                          background: pp ? '#ccddee' : 'rgba(255,255,255,0.06)',
                          borderRadius: '4px',
                          transition: 'background 0.3s ease',
                          cursor: pp ? 'help' : 'default',
                        }}
                      />
                    );
                  })}
                </div>
                {passphrasesCollected >= passphrasesRequired && (
                  <div style={{
                    fontSize: '7px', fontFamily: theme.fonts.mono, color: '#44cc88',
                    textAlign: 'center', marginTop: '2px', letterSpacing: '1px',
                  }}>
                    EXTRACTION READY
                  </div>
                )}
              </div>
            </div>

            {/* Target dossier — second flex child of the .hud-left-column
                wrapper. The 10px gap above comes from the wrapper's flex
                `gap`, so the spacing stays tight against the stat bars
                regardless of how their content grows (extraction-ready
                row, future extra passives, etc.). */}
            <TargetPanel agent={gameState.agent} revealed={gameState.targetRevealed} />
            </div>
          );
        })()}

        <TuningPanel onTuningChange={handleTuningChange} isOpen={tuningPanelOpen} onClose={() => setTuningPanelOpen(false)} />
        <AgentAIPanel
          gameState={gameState}
          turnLogs={agentTurnLogs}
          isOpen={agentAIPanelOpen}
          onClose={() => setAgentAIPanelOpen(false)}
          onAgentDeckChange={(newDeck, newHand, newDiscard) => {
            setGameState(prev => ({
              ...prev,
              agent: {
                ...prev.agent,
                deck: newDeck,
                hand: newHand,
                discard: newDiscard,
              },
            }));
          }}
          onResetBelief={() => {
            setGameState(prev => ({
              ...prev,
              agent: {
                ...prev.agent,
                committedTarget: null,
                belief: {
                  ...prev.agent.belief,
                  confidence: 0,
                  radius: tuning.agentBelief.maxRadius,
                  clearedAreas: [],
                  signalHistory: [],
                  inferredHeading: null,
                  inferredSpeed: null,
                },
              },
            }));
          }}
          onSetBelief={(lat, lng, confidence) => {
            setGameState(prev => ({
              ...prev,
              agent: {
                ...prev.agent,
                committedTarget: null,
                beliefSearchPath: [],
                beliefSearchCenter: null,
                beliefSearchRadius: 0,
                belief: {
                  ...prev.agent.belief,
                  position: { lat, lng },
                  confidence,
                  radius: Math.min(tuning.agentBelief.maxRadius,
                    confidence > 0.01 ? tuning.agentBelief.baseUncertainty / confidence : tuning.agentBelief.maxRadius),
                  lastUpdated: prev.turn,
                },
              },
            }));
          }}
          onTeleportAgent={(lat, lng) => {
            if (!cityData) return;
            const nearestNode = findNearestNode(lat, lng, cityData.navigation.nodes);
            setGameState(prev => ({
              ...prev,
              agent: {
                ...prev.agent,
                position: { lat, lng },
                nearestNodeId: nearestNode?.id ?? prev.agent.nearestNodeId,
                beliefSearchPath: [],
                beliefSearchCenter: null,
                beliefSearchRadius: 0,
              },
            }));
          }}
          onWeaponChange={(weaponId) => {
            setGameState(prev => {
              // Remove any existing weapon from equipment
              const nonWeapons = prev.agent.equipment.filter(e => e.category !== 'weapon');
              const newEquipment = [...nonWeapons];
              if (weaponId === 'pistol') {
                newEquipment.push({ id: 'pistol-agent', name: 'Pistol', category: 'weapon', apCost: 1, description: 'Standard issue sidearm.', range: 'short' });
              } else if (weaponId === 'sniper-rifle') {
                newEquipment.push({ id: 'sniper-rifle-agent', name: 'Sniper Rifle', category: 'weapon', apCost: 2, description: 'Long-range precision weapon.', range: 'long' });
              } else if (weaponId === 'combat-knife') {
                newEquipment.push({ id: 'combat-knife-agent', name: 'Combat Knife', category: 'weapon', apCost: 1, description: 'Melee sidearm.', range: 'melee' });
              }
              return {
                ...prev,
                agent: {
                  ...prev.agent,
                  equipment: newEquipment,
                  loadout: { ...prev.agent.loadout, weaponId: weaponId as any },
                },
              };
            });
          }}
        />
        <TransitFilter
          activeType={activeTransitType}
          availableTypes={availableTransitTypes}
          nearbyTypes={nearbyTransitTypes}
          onSelect={handleSelectTransit}
          showZones={showZones}
          onToggleZones={() => setShowZones(prev => !prev)}
          showRoads={showRoads}
          onToggleRoads={() => setShowRoads(prev => !prev)}
        />

        {/* Test mode debug panel */}
        {testMode && (
          <div style={styles.testDebugPanel}>
            <div style={styles.testDebugTitle}>TEST MODE</div>
            <button
              style={{
                width: '100%',
                padding: '6px 8px',
                marginBottom: '8px',
                background: teleportMode ? 'rgba(255, 200, 0, 0.25)' : 'rgba(12, 16, 24, 0.95)',
                border: `1px solid ${teleportMode ? '#ffc840' : theme.colors.border}`,
                borderRadius: '4px',
                color: teleportMode ? '#ffc840' : theme.colors.textPrimary,
                fontFamily: theme.fonts.mono,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                cursor: 'pointer',
              }}
              onClick={() => setTeleportMode(prev => !prev)}
              title="Click anywhere on the map to snap the player to the nearest road — no signal generated"
            >
              {teleportMode ? '🎯 TELEPORT: CLICK MAP' : 'TELEPORT PLAYER'}
            </button>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Agent State</span>
              <span style={{
                color: gameState.agent.state === 'search' ? '#888'
                  : gameState.agent.state === 'pursue' ? '#f06040'
                  : gameState.agent.state === 'engage' ? '#ff3030'
                  : '#6080ff',
                fontWeight: 700,
              }}>{gameState.agent.state.toUpperCase()}</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Agent Pos</span>
              <span>{gameState.agent.position.lat.toFixed(4)}, {gameState.agent.position.lng.toFixed(4)}</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Turns in State</span>
              <span>{gameState.agent.turnsInCurrentState}</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Belief Conf</span>
              <span>{(gameState.agent.belief.confidence * 100).toFixed(0)}%</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Agent Exposure</span>
              <span>{(gameState.agent.exposure * 100).toFixed(0)}%</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Player Exposure</span>
              <span>{Math.round(gameState.player.exposure)}/{tuning.exposureMax}</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Signals</span>
              <span>{gameState.signals.length} total</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Player Items</span>
              <span>{gameState.player.items.length}</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Deck / Discard</span>
              <span>{gameState.player.deck.length} / {gameState.player.discard.length}</span>
            </div>
            <div style={{ ...styles.testDebugRow, marginTop: '6px', borderTop: `1px solid ${theme.colors.border}`, paddingTop: '6px' }}>
              <span style={styles.testDebugLabel}>Agent Signals Received</span>
              <span>{gameState.agent.signalsReceived.length}</span>
            </div>
            <div style={styles.testDebugRow}>
              <span style={styles.testDebugLabel}>Agent Prep</span>
              <span>{(gameState.agent.prepBonus * 100).toFixed(0)}%</span>
            </div>
            {gameState.agent.belief.signalHistory.length > 0 && (
              <div style={styles.testDebugRow}>
                <span style={styles.testDebugLabel}>Last Signal Str</span>
                <span>{gameState.agent.belief.signalHistory[gameState.agent.belief.signalHistory.length - 1]?.strength.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {/* Safehouse dossier dialog — shows the agents revealed loadout
            before the card draft opens. Blocks until the player clicks
            CONTINUE so they cant miss the intel. */}
        {pendingSafehouseDraft && gameState.agent && (() => {
          const loadout = gameState.agent.loadout;
          const faceSrc = `./agent_faces/face_${String(loadout.faceId).padStart(2, '0')}.png`;
          const weaponLabel = loadout.weaponId === 'pistol' ? 'Pistol'
            : loadout.weaponId === 'sniper-rifle' ? 'Sniper Rifle'
            : loadout.weaponId === 'combat-knife' ? 'Combat Knife'
            : 'Unarmed';
          const gearLabels = loadout.defensiveItemIds.map(id =>
            id === 'flashbang' ? 'Flashbang'
            : id === 'bulletproof-vest' ? 'Bulletproof Vest'
            : id
          );
          return (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 270,
            }}>
              <div style={{
                background: 'rgba(8, 12, 20, 0.98)',
                border: '2px solid rgba(68, 204, 136, 0.6)',
                borderRadius: '8px',
                padding: '24px 28px',
                width: '480px',
                maxWidth: '90vw',
                fontFamily: theme.fonts.mono,
              }}>
                <div style={{
                  fontSize: '10px', letterSpacing: '3px', color: '#44cc88',
                  fontWeight: 700, marginBottom: '6px',
                }}>SAFEHOUSE — INTEL PACKET</div>
                <div style={{
                  fontSize: '18px', fontWeight: 700, color: '#ffffff',
                  marginBottom: '14px',
                }}>Target Dossier Acquired</div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <img src={faceSrc} alt={loadout.codename} style={{
                    width: '110px', height: '110px', objectFit: 'cover',
                    border: '1px solid rgba(255, 100, 100, 0.4)', borderRadius: '4px',
                    background: 'rgba(255, 100, 100, 0.04)',
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px', fontWeight: 700, color: '#ff8888',
                      marginBottom: '4px',
                    }}>"{loadout.codename}"</div>
                    <div style={{
                      fontSize: '9px', letterSpacing: '1.5px', color: '#888',
                      marginBottom: '10px',
                    }}>{loadout.archetype.toUpperCase()}</div>
                    <div style={{ fontSize: '11px', color: '#ccc', lineHeight: '1.6' }}>
                      <div><span style={{ color: '#888' }}>WPN</span> <span style={{ color: '#ff8844' }}>{weaponLabel}</span></div>
                      {gearLabels.length > 0 && (
                        <div><span style={{ color: '#888' }}>GEAR</span> <span style={{ color: '#f0c040' }}>{gearLabels.join(', ')}</span></div>
                      )}
                    </div>
                    {loadout.passives.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ fontSize: '9px', letterSpacing: '1.5px', color: '#aa88ff', marginBottom: '4px' }}>PASSIVES</div>
                        {loadout.passives.map(p => (
                          <div key={p.key} style={{ fontSize: '10px', color: '#ccc', lineHeight: '1.5' }}>
                            · {p.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{
                  marginTop: '20px', padding: '10px 12px',
                  background: 'rgba(68, 204, 136, 0.06)',
                  border: '1px solid rgba(68, 204, 136, 0.25)',
                  borderRadius: '4px',
                  fontSize: '10px', color: '#88ddaa', lineHeight: '1.6',
                }}>
                  Intel secured. Review their loadout, then continue to draft a card.
                </div>
                <button
                  onClick={handleSafehouseDossierContinue}
                  style={{
                    marginTop: '18px', width: '100%',
                    background: 'linear-gradient(180deg, rgba(68, 204, 136, 0.2), rgba(68, 204, 136, 0.08))',
                    border: '1px solid rgba(68, 204, 136, 0.6)',
                    color: '#88ddaa',
                    padding: '10px 16px',
                    fontFamily: theme.fonts.mono,
                    fontSize: '12px', letterSpacing: '2px', fontWeight: 700,
                    borderRadius: '4px', cursor: 'pointer',
                  }}
                >CONTINUE TO DRAFT →</button>
              </div>
            </div>
          );
        })()}

        {/* Free hand panel — pick cards for your hand */}
        {/* Board Transit modal */}
        {safehouseDraft && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 260,
          }}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(68, 204, 136, 0.5)',
              borderRadius: '8px', padding: '20px', minWidth: '380px', maxWidth: '480px',
            }}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#44cc88',
                letterSpacing: '2px', marginBottom: '4px',
              }}>SAFEHOUSE</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '10px', color: '#888', marginBottom: '14px',
              }}>
                Choose one card to add to your deck (or skip):
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {safehouseDraft.options.map((card) => {
                  const catColor =
                    card.category === 'fieldcraft' ? '#44cc88'
                    : card.category === 'tactics' ? '#ffaa44'
                    : card.category === 'intel' ? '#aa88ff'
                    : card.category === 'combat' ? '#ff5050'
                    : card.category === 'movement' ? '#44aaff'
                    : '#66ccff';
                  return (
                    <button
                      key={card.id}
                      className="hand-card"
                      style={{
                        width: '100px', height: '140px', padding: '10px 8px',
                        background: `${catColor}08`, border: `1px solid ${catColor}30`,
                        borderTop: `3px solid ${catColor}`, borderRadius: '6px',
                        cursor: 'pointer', fontFamily: theme.fonts.mono,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', textAlign: 'center', position: 'relative',
                        transition: 'all 0.15s',
                      }}
                      onClick={() => handleSafehousePick(card)}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.borderColor = catColor;
                        (e.currentTarget as HTMLElement).style.background = `${catColor}15`;
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.borderColor = `${catColor}30`;
                        (e.currentTarget as HTMLElement).style.background = `${catColor}08`;
                        (e.currentTarget as HTMLElement).style.borderTop = `3px solid ${catColor}`;
                      }}
                    >
                      <div style={{
                        fontSize: '9px', letterSpacing: '1px', color: catColor,
                        fontWeight: 700, marginBottom: '6px',
                      }}>{card.category.toUpperCase()}</div>
                      <div style={{
                        fontSize: '11px', fontWeight: 700, color: theme.colors.textPrimary,
                        marginBottom: '4px', lineHeight: '1.3',
                      }}>{card.name}</div>
                      <div style={{
                        fontSize: '9px', color: theme.colors.textSecondary,
                        letterSpacing: '1px',
                      }}>{card.apCost} AP</div>
                      <div className="card-tooltip" style={{ bottom: '110%', left: '50%', transform: 'translateX(-50%)' }}>
                        <div className="card-tooltip__header">
                          <span className="card-tooltip__name">{card.name}</span>
                          <span className="card-tooltip__cost">{card.apCost} AP</span>
                        </div>
                        <div className="card-tooltip__category">{card.category.toUpperCase()}</div>
                        <div className="card-tooltip__desc">{card.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <button
                style={{
                  width: '100%', marginTop: '10px', padding: '8px',
                  background: 'rgba(100,100,100,0.15)', border: '1px solid rgba(100,100,100,0.3)',
                  borderRadius: '3px', color: '#888', fontFamily: theme.fonts.mono,
                  fontSize: '10px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer',
                }}
                onClick={() => handleSafehousePick(null)}
              >SKIP</button>
            </div>
          </div>
        )}

        {/* Ambush prompt — shown when player spots agent and has a weapon */}
        {/* Non-blocking ambush overlay — floats at top of screen, no backdrop,
            auto-dismisses when agent leaves cone. Doesn't block map interaction. */}
        {ambushOverlayVisible && !combatEncounter && (
          <div style={{
            position: 'fixed', top: '12px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 280, pointerEvents: 'none',
            background: 'rgba(8, 12, 20, 0.92)', border: '1px solid rgba(255, 50, 50, 0.5)',
            borderRadius: '6px', padding: '10px 20px', textAlign: 'center',
            fontFamily: theme.fonts.mono, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#ff5050', letterSpacing: '2px' }}>
              AGENT SPOTTED — USE WEAPON TO AMBUSH
            </div>
          </div>
        )}

        {coldOpenAlert && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250,
          }} onClick={() => setColdOpenAlert(null)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(170, 136, 255, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '320px', maxWidth: '420px',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#aa88ff',
                letterSpacing: '2px', marginBottom: '8px',
              }}>COLD OPENING — ENEMY SPOTTED</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textPrimary, lineHeight: '1.5', marginBottom: '14px',
              }}>
                Your Cold Opening passive has revealed the agent's starting position for this turn only. Use the intel to plan your opening move.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(100,100,100,0.15)',
                    border: '1px solid rgba(100,100,100,0.3)', borderRadius: '3px',
                    color: '#888', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => setColdOpenAlert(null)}
                >CLOSE</button>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(170, 136, 255, 0.2)',
                    border: '1px solid rgba(170, 136, 255, 0.4)', borderRadius: '3px',
                    color: '#aa88ff', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => {
                    setRecenterTarget({ lat: coldOpenAlert.lat, lng: coldOpenAlert.lng });
                    setRecenterTrigger(n => n + 1);
                    setColdOpenAlert(null);
                  }}
                >SHOW ME</button>
              </div>
            </div>
          </div>
        )}

        {spotterAlert && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250,
          }} onClick={() => setSpotterAlert(null)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(255, 170, 68, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '320px', maxWidth: '420px',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#ffaa44',
                letterSpacing: '2px', marginBottom: '8px',
              }}>SPOTTER TRIGGERED</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textPrimary, lineHeight: '1.5', marginBottom: '14px',
              }}>
                An agent entered one of your surveillance zones. Their position is revealed for {tuning.spotterDuration} turn{tuning.spotterDuration !== 1 ? 's' : ''}.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(100,100,100,0.15)',
                    border: '1px solid rgba(100,100,100,0.3)', borderRadius: '3px',
                    color: '#888', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => setSpotterAlert(null)}
                >CLOSE</button>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(255, 170, 68, 0.2)',
                    border: '1px solid rgba(255, 170, 68, 0.4)', borderRadius: '3px',
                    color: '#ffaa44', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => {
                    setRecenterTarget({ lat: spotterAlert.position.lat, lng: spotterAlert.position.lng });
                    setRecenterTrigger(n => n + 1);
                    setSpotterAlert(null);
                  }}
                >SHOW ME</button>
              </div>
            </div>
          </div>
        )}

        {stakeoutAlert && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250,
          }} onClick={() => setStakeoutAlert(null)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(170, 136, 255, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '320px', maxWidth: '420px',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#aa88ff',
                letterSpacing: '2px', marginBottom: '8px',
              }}>STAKEOUT CONTACT</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textPrimary, lineHeight: '1.5', marginBottom: '14px',
              }}>
                A comrade on a rooftop spotted the agent. Their exact position is visible on the map — the 3-turn fade only begins once they leave the cone.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(100,100,100,0.15)',
                    borderWidth: '1px', borderStyle: 'solid', borderColor: 'rgba(100,100,100,0.3)',
                    borderRadius: '3px',
                    color: '#888', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => setStakeoutAlert(null)}
                >CLOSE</button>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(170, 136, 255, 0.2)',
                    borderWidth: '1px', borderStyle: 'solid', borderColor: 'rgba(170, 136, 255, 0.4)',
                    borderRadius: '3px',
                    color: '#aa88ff', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => {
                    setRecenterTarget({ lat: gameState.agent.position.lat, lng: gameState.agent.position.lng });
                    setRecenterTrigger(n => n + 1);
                    setStakeoutAlert(null);
                  }}
                >SHOW ME</button>
              </div>
            </div>
          </div>
        )}

        {surveillanceVanAlert && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250,
          }} onClick={() => setSurveillanceVanAlert(null)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(170, 136, 255, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '320px', maxWidth: '420px',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#aa88ff',
                letterSpacing: '2px', marginBottom: '8px',
              }}>SURVEILLANCE VAN CONTACT</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textPrimary, lineHeight: '1.5', marginBottom: '14px',
              }}>
                A van's 360° cone picked up the agent. Their exact position is visible on the map for 3 turns.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(100,100,100,0.15)',
                    borderWidth: '1px', borderStyle: 'solid', borderColor: 'rgba(100,100,100,0.3)',
                    borderRadius: '3px',
                    color: '#888', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => setSurveillanceVanAlert(null)}
                >CLOSE</button>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(170, 136, 255, 0.2)',
                    borderWidth: '1px', borderStyle: 'solid', borderColor: 'rgba(170, 136, 255, 0.4)',
                    borderRadius: '3px',
                    color: '#aa88ff', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => {
                    setRecenterTarget({ lat: gameState.agent.position.lat, lng: gameState.agent.position.lng });
                    setRecenterTrigger(n => n + 1);
                    setSurveillanceVanAlert(null);
                  }}
                >SHOW ME</button>
              </div>
            </div>
          </div>
        )}

        {wireTapAlert && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250,
          }} onClick={() => setWireTapAlert(null)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(255, 221, 68, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '320px', maxWidth: '420px',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#ffdd44',
                letterSpacing: '2px', marginBottom: '8px',
              }}>WIRE TAP TRIGGERED</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textPrimary, lineHeight: '1.5', marginBottom: '14px',
              }}>
                The agent crossed a tapped street segment. Their exact position and heading are revealed for 2 turns.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(100,100,100,0.15)',
                    border: '1px solid rgba(100,100,100,0.3)', borderRadius: '3px',
                    color: '#888', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => setWireTapAlert(null)}
                >CLOSE</button>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(255, 221, 68, 0.2)',
                    border: '1px solid rgba(255, 221, 68, 0.4)', borderRadius: '3px',
                    color: '#ffdd44', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => {
                    // Center on the agent's actual position (revealed by the wire tap)
                    setRecenterTarget({ lat: gameState.agent.position.lat, lng: gameState.agent.position.lng });
                    setRecenterTrigger(n => n + 1);
                    setWireTapAlert(null);
                  }}
                >SHOW ME</button>
              </div>
            </div>
          </div>
        )}

        {trafficAnalysisAlert && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250,
          }} onClick={() => setTrafficAnalysisAlert(null)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(255, 80, 80, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '320px', maxWidth: '420px',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#ff5050',
                letterSpacing: '2px', marginBottom: '8px',
              }}>TRAFFIC ANALYSIS — CONTACT</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textPrimary, lineHeight: '1.5', marginBottom: '14px',
              }}>
                The agent is currently on <span style={{ color: '#ff5050', fontWeight: 700 }}>{trafficAnalysisAlert.roadName}</span>. The full named road is highlighted in red for this turn.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(100,100,100,0.15)',
                    border: '1px solid rgba(100,100,100,0.3)', borderRadius: '3px',
                    color: '#888', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => setTrafficAnalysisAlert(null)}
                >CLOSE</button>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(255, 80, 80, 0.2)',
                    border: '1px solid rgba(255, 80, 80, 0.4)', borderRadius: '3px',
                    color: '#ff5050', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => {
                    // Fly to the center of the highlighted road. Traffic
                    // Analysis doesn't reveal exact position, so we center
                    // on the road's bbox midpoint rather than the agent.
                    setRecenterTarget({
                      lat: trafficAnalysisAlert.center.lat,
                      lng: trafficAnalysisAlert.center.lng,
                    });
                    setRecenterTrigger(n => n + 1);
                    setTrafficAnalysisAlert(null);
                  }}
                >SHOW ME</button>
              </div>
            </div>
          </div>
        )}

        {informantAlert && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250,
          }} onClick={() => setInformantAlert(null)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(0, 255, 136, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '320px', maxWidth: '420px',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#00ff88',
                letterSpacing: '2px', marginBottom: '8px',
              }}>INFORMANT TIP</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textPrimary, lineHeight: '1.5', marginBottom: '14px',
              }}>
                Agent spotted near <span style={{ color: '#00ff88', fontWeight: 700 }}>{informantAlert.poiName}</span> on <span style={{ color: '#00ff88', fontWeight: 700 }}>{informantAlert.streetName}</span>.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(100,100,100,0.15)',
                    border: '1px solid rgba(100,100,100,0.3)', borderRadius: '3px',
                    color: '#888', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => setInformantAlert(null)}
                >CLOSE</button>
                <button
                  style={{
                    flex: 1, padding: '8px', background: 'rgba(0, 255, 136, 0.2)',
                    border: '1px solid rgba(0, 255, 136, 0.4)', borderRadius: '3px',
                    color: '#00ff88', fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700,
                    letterSpacing: '1px', cursor: 'pointer',
                  }}
                  onClick={() => {
                    setRecenterTarget({ lat: informantAlert.center.lat, lng: informantAlert.center.lng });
                    setRecenterTrigger(n => n + 1);
                    setInformantAlert(null);
                  }}
                >SHOW ME</button>
              </div>
            </div>
          </div>
        )}

        {boardTransitOpen && nearbyBoardingOptions.length > 0 && !gameState.player.isInTransit && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
          }} onClick={() => setBoardTransitOpen(false)}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(170, 136, 255, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '400px', maxWidth: '560px', maxHeight: '80vh', overflowY: 'auto',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#aa88ff',
                letterSpacing: '2px', marginBottom: '4px',
              }}>BOARD TRANSIT</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '10px', color: '#888', marginBottom: '12px',
              }}>
                Pick a line and direction. Strong signal generated at station.
              </div>
              {nearbyBoardingOptions.map(opt => {
                const totalStops = opt.line.stops.length;
                const canForward = opt.stopIndex < totalStops - 1;
                const canBackward = opt.stopIndex > 0;
                const forwardNext = canForward ? opt.line.stops[opt.stopIndex + 1] : null;
                const backwardNext = canBackward ? opt.line.stops[opt.stopIndex - 1] : null;
                // Bearing from current stop to next stop (0 = north, 90 = east, CW)
                const bearingTo = (to: { lat: number; lng: number } | null) => {
                  if (!to) return 0;
                  const cur = opt.stop;
                  const dLat = (to.lat - cur.lat);
                  const dLng = (to.lng - cur.lng) * Math.cos(((cur.lat + to.lat) / 2) * Math.PI / 180);
                  return (Math.atan2(dLng, dLat) * 180) / Math.PI;
                };
                const fwdBearing = bearingTo(forwardNext);
                const bwdBearing = bearingTo(backwardNext);
                return (
                  <div key={opt.line.id} style={{
                    marginBottom: '12px', padding: '10px', background: 'rgba(170, 136, 255, 0.08)',
                    border: '1px solid rgba(170, 136, 255, 0.2)', borderRadius: '4px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div>
                        <div style={{ fontFamily: theme.fonts.mono, fontSize: '12px', fontWeight: 700, color: opt.line.color }}>
                          {opt.line.name}
                        </div>
                        <div style={{ fontFamily: theme.fonts.mono, fontSize: '9px', color: '#888' }}>
                          {opt.line.type.toUpperCase()} · {opt.stop.name} · stop {opt.stopIndex + 1}/{totalStops}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        disabled={!canForward}
                        style={{
                          flex: 1, padding: '8px', background: canForward ? 'rgba(68, 204, 136, 0.2)' : 'rgba(100,100,100,0.15)',
                          border: `1px solid ${canForward ? 'rgba(68, 204, 136, 0.4)' : 'rgba(100,100,100,0.3)'}`,
                          borderRadius: '3px', color: canForward ? '#44cc88' : '#555', fontFamily: theme.fonts.mono,
                          fontSize: '10px', fontWeight: 700, cursor: canForward ? 'pointer' : 'not-allowed',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        }}
                        onClick={() => handleBoardTransit(opt.line.id, opt.stopIndex, 'forward')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" style={{ transform: `rotate(${fwdBearing}deg)`, transition: 'none', flexShrink: 0 }}>
                          <path d="M12 2 L19 12 L13 12 L13 22 L11 22 L11 12 L5 12 Z" fill="currentColor" />
                        </svg>
                        <span style={{ textAlign: 'left', flex: 1 }}>{forwardNext ? forwardNext.name : 'end of line'}</span>
                      </button>
                      <button
                        disabled={!canBackward}
                        style={{
                          flex: 1, padding: '8px', background: canBackward ? 'rgba(68, 204, 136, 0.2)' : 'rgba(100,100,100,0.15)',
                          border: `1px solid ${canBackward ? 'rgba(68, 204, 136, 0.4)' : 'rgba(100,100,100,0.3)'}`,
                          borderRadius: '3px', color: canBackward ? '#44cc88' : '#555', fontFamily: theme.fonts.mono,
                          fontSize: '10px', fontWeight: 700, cursor: canBackward ? 'pointer' : 'not-allowed',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        }}
                        onClick={() => handleBoardTransit(opt.line.id, opt.stopIndex, 'backward')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" style={{ transform: `rotate(${bwdBearing}deg)`, transition: 'none', flexShrink: 0 }}>
                          <path d="M12 2 L19 12 L13 12 L13 22 L11 22 L11 12 L5 12 Z" fill="currentColor" />
                        </svg>
                        <span style={{ textAlign: 'left', flex: 1 }}>{backwardNext ? backwardNext.name : 'end of line'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
              <button
                style={{
                  width: '100%', marginTop: '4px', padding: '6px', background: 'rgba(100,100,100,0.15)',
                  border: '1px solid rgba(100,100,100,0.3)', borderRadius: '3px', color: '#888',
                  fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                }}
                onClick={() => setBoardTransitOpen(false)}
              >CANCEL</button>
            </div>
          </div>
        )}

        {/* Stage Exit picker — same structure as BOARD TRANSIT but fakes a ride */}
        {stageExitPendingCardId !== null && nearbyBoardingOptions.length > 0 && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
          }} onClick={() => { setStageExitPendingCardId(null); setSelectedCardId(null); }}>
            <div style={{
              background: 'rgba(8, 12, 20, 0.98)', border: '1px solid rgba(255, 136, 204, 0.5)',
              borderRadius: '8px', padding: '16px', minWidth: '400px', maxWidth: '560px', maxHeight: '80vh', overflowY: 'auto',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700, color: '#ff88cc',
                letterSpacing: '2px', marginBottom: '4px',
              }}>STAGE EXIT</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '10px', color: '#888', marginBottom: '12px',
              }}>
                Pick a line and direction. Fake boarding + fake exit at the next stop — you stay put.
              </div>
              {nearbyBoardingOptions.map(opt => {
                const totalStops = opt.line.stops.length;
                const canForward = opt.stopIndex < totalStops - 1;
                const canBackward = opt.stopIndex > 0;
                const forwardNext = canForward ? opt.line.stops[opt.stopIndex + 1] : null;
                const backwardNext = canBackward ? opt.line.stops[opt.stopIndex - 1] : null;
                const bearingTo = (to: { lat: number; lng: number } | null) => {
                  if (!to) return 0;
                  const cur = opt.stop;
                  const dLat = (to.lat - cur.lat);
                  const dLng = (to.lng - cur.lng) * Math.cos(((cur.lat + to.lat) / 2) * Math.PI / 180);
                  return (Math.atan2(dLng, dLat) * 180) / Math.PI;
                };
                const fwdBearing = bearingTo(forwardNext);
                const bwdBearing = bearingTo(backwardNext);
                return (
                  <div key={opt.line.id} style={{
                    marginBottom: '12px', padding: '10px', background: 'rgba(255, 136, 204, 0.08)',
                    border: '1px solid rgba(255, 136, 204, 0.2)', borderRadius: '4px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div>
                        <div style={{ fontFamily: theme.fonts.mono, fontSize: '12px', fontWeight: 700, color: opt.line.color }}>
                          {opt.line.name}
                        </div>
                        <div style={{ fontFamily: theme.fonts.mono, fontSize: '9px', color: '#888' }}>
                          {opt.line.type.toUpperCase()} · {opt.stop.name} · stop {opt.stopIndex + 1}/{totalStops}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        disabled={!canForward}
                        style={{
                          flex: 1, padding: '8px', background: canForward ? 'rgba(255, 136, 204, 0.2)' : 'rgba(100,100,100,0.15)',
                          border: `1px solid ${canForward ? 'rgba(255, 136, 204, 0.4)' : 'rgba(100,100,100,0.3)'}`,
                          borderRadius: '3px', color: canForward ? '#ff88cc' : '#555', fontFamily: theme.fonts.mono,
                          fontSize: '10px', fontWeight: 700, cursor: canForward ? 'pointer' : 'not-allowed',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        }}
                        onClick={() => handleStageExitConfirm(opt.line.id, opt.stopIndex, 'forward')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" style={{ transform: `rotate(${fwdBearing}deg)`, transition: 'none', flexShrink: 0 }}>
                          <path d="M12 2 L19 12 L13 12 L13 22 L11 22 L11 12 L5 12 Z" fill="currentColor" />
                        </svg>
                        <span style={{ textAlign: 'left', flex: 1 }}>{forwardNext ? forwardNext.name : 'end of line'}</span>
                      </button>
                      <button
                        disabled={!canBackward}
                        style={{
                          flex: 1, padding: '8px', background: canBackward ? 'rgba(255, 136, 204, 0.2)' : 'rgba(100,100,100,0.15)',
                          border: `1px solid ${canBackward ? 'rgba(255, 136, 204, 0.4)' : 'rgba(100,100,100,0.3)'}`,
                          borderRadius: '3px', color: canBackward ? '#ff88cc' : '#555', fontFamily: theme.fonts.mono,
                          fontSize: '10px', fontWeight: 700, cursor: canBackward ? 'pointer' : 'not-allowed',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        }}
                        onClick={() => handleStageExitConfirm(opt.line.id, opt.stopIndex, 'backward')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" style={{ transform: `rotate(${bwdBearing}deg)`, transition: 'none', flexShrink: 0 }}>
                          <path d="M12 2 L19 12 L13 12 L13 22 L11 22 L11 12 L5 12 Z" fill="currentColor" />
                        </svg>
                        <span style={{ textAlign: 'left', flex: 1 }}>{backwardNext ? backwardNext.name : 'end of line'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
              <button
                style={{
                  width: '100%', marginTop: '4px', padding: '6px', background: 'rgba(100,100,100,0.15)',
                  border: '1px solid rgba(100,100,100,0.3)', borderRadius: '3px', color: '#888',
                  fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                }}
                onClick={() => { setStageExitPendingCardId(null); setSelectedCardId(null); }}
              >CANCEL</button>
            </div>
          </div>
        )}

        {/* Exiting cards — fixed-position overlays at their last DOM spot, animating out */}
        {exitingCards.length > 0 && (
          <div style={{
            position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 100,
          }}>
            {exitingCards.map((entry) => {
              const { card, left, top, width, height } = entry;
              const isMovement = !!card.movementRange;
              return (
                <div
                  key={`exit-${card.id}`}
                  className="hand-card hand-card--exiting"
                  style={{
                    position: 'absolute',
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                  }}
                >
                  <div style={styles.cardCost}>{card.apCost}</div>
                  <div style={styles.cardName}>{card.name}</div>
                  <div style={styles.cardCategory}>{card.category.toUpperCase()}</div>
                  {isMovement && <div style={styles.cardRange}>{card.movementRange}m</div>}
                  <div style={styles.cardCategoryIcon}>
                    <CardCategoryIcon category={card.category} size={16} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {freeHandPanel && (() => {
          // Count cards across deck + hand + discard (the full pool, excluding burn pile)
          const allCards = [...gameState.player.deck, ...gameState.player.hand, ...gameState.player.discard];
          const totalCards = allCards.length;
          return (
          <div style={{
            position: 'absolute',
            top: '50px',
            right: '10px',
            width: '280px',
            maxHeight: '80vh',
            overflowY: 'auto',
            background: 'rgba(8, 12, 20, 0.95)',
            border: '1px solid rgba(0, 229, 255, 0.3)',
            borderRadius: '6px',
            padding: '10px',
            zIndex: 50,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px',
            }}>
              <span style={{
                fontFamily: theme.fonts.mono, fontSize: '11px', fontWeight: 700, color: '#00e5ff',
                letterSpacing: '2px',
              }}>DECK EDITOR</span>
              <span style={{
                fontFamily: theme.fonts.mono, fontSize: '9px', color: '#888',
              }}>{totalCards} cards</span>
            </div>
            <div style={{
              display: 'flex', gap: '4px', marginBottom: '8px',
            }}>
              <button
                style={{
                  background: 'rgba(255, 80, 80, 0.2)', border: '1px solid rgba(255, 80, 80, 0.4)',
                  borderRadius: '3px', color: '#ff5050', fontFamily: theme.fonts.mono, fontSize: '9px',
                  cursor: 'pointer', padding: '2px 8px', flex: 1,
                }}
                onClick={() => {
                  setGameState(prev => ({
                    ...prev,
                    player: { ...prev.player, deck: [], hand: [], discard: [] },
                  }));
                }}
              >CLEAR ALL</button>
              <button
                style={{
                  background: 'rgba(68, 204, 136, 0.2)', border: '1px solid rgba(68, 204, 136, 0.4)',
                  borderRadius: '3px', color: '#44cc88', fontFamily: theme.fonts.mono, fontSize: '9px',
                  cursor: 'pointer', padding: '2px 8px', flex: 1,
                }}
                onClick={() => {
                  // Shuffle all cards back into deck and redraw
                  setGameState(prev => {
                    const pool = shuffleDeck([...prev.player.deck, ...prev.player.hand, ...prev.player.discard]);
                    const { hand: newHand, remaining } = drawHand(pool, tuning.handSize);
                    return { ...prev, player: { ...prev.player, deck: remaining, hand: newHand, discard: [] } };
                  });
                }}
              >SHUFFLE & DRAW</button>
            </div>
            <div style={{
              fontFamily: theme.fonts.mono, fontSize: '8px', color: '#555', marginBottom: '6px',
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span>Deck: {gameState.player.deck.length}</span>
              <span>Hand: {gameState.player.hand.length}</span>
              <span>Discard: {gameState.player.discard.length}</span>
              <span>Burned: {gameState.player.burnPile.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {Object.entries(CARD_TEMPLATES)
                // Free-hand mode lets the player assemble their own deck — junk
                // cards are agent-only and must never land in the player's hand.
                .filter(([, template]) => template.category !== 'junk')
                .map(([key, template]) => {
                const countInDeck = allCards.filter(c => c.name === template.name).length;
                const catColor =
                  template.category === 'movement' ? '#66ccff'
                  : template.category === 'intel' ? '#aa88ff'
                  : template.category === 'tactics' ? '#ffaa44'
                  : template.category === 'combat' ? '#ff5555'
                  : '#44cc88';
                return (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 4px',
                    borderRadius: '3px',
                    background: countInDeck > 0 ? `${catColor}10` : 'transparent',
                  }}>
                    <button
                      style={{
                        width: '20px', height: '18px', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.2)',
                        background: countInDeck > 0 ? catColor : 'transparent', color: countInDeck > 0 ? '#000' : '#555',
                        fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1,
                      }}
                      onClick={() => {
                        // Remove one copy — check deck first, then discard, then hand
                        if (countInDeck > 0) {
                          setGameState(prev => {
                            let idx = prev.player.deck.findIndex(c => c.name === template.name);
                            if (idx >= 0) {
                              const newDeck = [...prev.player.deck];
                              newDeck.splice(idx, 1);
                              return { ...prev, player: { ...prev.player, deck: newDeck } };
                            }
                            idx = prev.player.discard.findIndex(c => c.name === template.name);
                            if (idx >= 0) {
                              const newDiscard = [...prev.player.discard];
                              newDiscard.splice(idx, 1);
                              return { ...prev, player: { ...prev.player, discard: newDiscard } };
                            }
                            idx = prev.player.hand.findIndex(c => c.name === template.name);
                            if (idx >= 0) {
                              const newHand = [...prev.player.hand];
                              newHand.splice(idx, 1);
                              return { ...prev, player: { ...prev.player, hand: newHand } };
                            }
                            return prev;
                          });
                        }
                      }}
                      title="Remove one from deck"
                    >
                      {countInDeck > 0 ? countInDeck : ''}
                    </button>
                    <span style={{
                      fontFamily: theme.fonts.mono, fontSize: '10px', color: countInDeck > 0 ? catColor : '#444', flex: 1,
                      cursor: 'pointer', userSelect: 'none',
                    }}
                    onClick={() => {
                      // Add one copy to draw pile (deck)
                      const card = { ...template, id: `free-${key}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` };
                      setGameState(prev => ({
                        ...prev,
                        player: { ...prev.player, deck: [...prev.player.deck, card] },
                      }));
                    }}
                    title={`Add ${template.name} to deck`}
                    >
                      {template.name}
                    </span>
                    <span style={{
                      fontFamily: theme.fonts.mono, fontSize: '8px', color: '#ffcc00', width: '14px', textAlign: 'right',
                    }}>{template.apCost}</span>
                  </div>
                );
              })}
            </div>
            <div style={{
              marginTop: '8px', fontFamily: theme.fonts.mono, fontSize: '9px', color: '#555',
              textAlign: 'center', lineHeight: '1.5',
            }}>
              Click name to add to deck · Click count to remove
            </div>

            {/* Items test section */}
            <div style={{
              marginTop: '12px',
              paddingTop: '10px',
              borderTop: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '10px', fontWeight: 700, color: '#ffcc00',
                letterSpacing: '2px', marginBottom: '6px',
              }}>ITEMS</div>
              <div style={{
                fontFamily: theme.fonts.mono, fontSize: '8px', color: '#555', marginBottom: '6px',
              }}>
                Inventory: {gameState.player.items.length}/4
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {Object.entries(ITEMS).map(([key, item]) => {
                  const catColor =
                    item.category === 'weapon' ? '#ff5555'
                    : item.category === 'tool' ? '#66ccff'
                    : item.category === 'gadget' ? '#aa88ff'
                    : '#ffaa44';
                  const inventoryFull = gameState.player.items.length >= getInventoryCap(runState);
                  return (
                    <div key={key} style={{
                      display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 4px',
                      borderRadius: '3px',
                      opacity: inventoryFull ? 0.4 : 1,
                    }}>
                      <span style={{
                        fontFamily: theme.fonts.mono, fontSize: '10px', color: catColor, flex: 1,
                        cursor: inventoryFull ? 'not-allowed' : 'pointer', userSelect: 'none',
                      }}
                      onClick={() => {
                        if (inventoryFull) return;
                        // Suffix must be exactly 6 chars [a-z0-9] so ItemIcon regex matches
                        const suffix = Math.random().toString(36).slice(2, 8).padEnd(6, '0').slice(0, 6);
                        const spawned = { ...item, id: `${item.id}-${suffix}` };
                        setGameState(prev => ({
                          ...prev,
                          player: { ...prev.player, items: [...prev.player.items, spawned] },
                        }));
                      }}
                      title={inventoryFull ? 'Inventory full (4/4)' : `Add ${item.name} to inventory`}
                      >
                        {item.name}
                      </span>
                      <span style={{
                        fontFamily: theme.fonts.mono, fontSize: '8px', color: '#666',
                        textTransform: 'uppercase', width: '50px', textAlign: 'right',
                      }}>{item.category}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{
                marginTop: '6px', fontFamily: theme.fonts.mono, fontSize: '9px', color: '#555',
                textAlign: 'center', lineHeight: '1.5',
              }}>
                Click item name to add to inventory
              </div>
            </div>
          </div>
          );
        })()}

        {/* Left side — Inventory slots */}
        <div style={styles.inventoryBar}>
          {Array.from({ length: 4 }).map((_, i) => {
            const item = gameState.player.items[i];
            const menuOpen = itemMenuIndex === i;
            const catColor = item ? getCategoryColor(item.category) : '';
            return (
              <div
                key={i}
                className="inventory-slot-wrapper"
                style={{ position: 'relative' as const }}
              >
                <div
                  style={{
                    ...styles.inventorySlot,
                    ...(item ? {
                      borderColor: catColor + '60',
                      cursor: 'pointer',
                    } : {}),
                    ...(menuOpen ? {
                      borderColor: catColor,
                      boxShadow: `0 0 8px ${catColor}40`,
                    } : {}),
                  }}
                  onClick={() => {
                    if (item) setItemMenuIndex(menuOpen ? null : i);
                  }}
                >
                  {item ? (
                    <>
                      <ItemIcon itemId={item.id} category={item.category} size={32} />
                      {item.category === 'weapon' && ambushAvailable && (
                        <div style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#ff3030',
                          boxShadow: '0 0 6px #ff3030',
                          animation: 'pulse 1.5s ease-in-out infinite',
                        }} />
                      )}
                    </>
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 28 28" style={{ opacity: 0.3 }}>
                      <rect x="1" y="1" width="26" height="26" rx="3" fill="none" stroke={theme.colors.textDim} strokeWidth="1.5" />
                      <line x1="4" y1="24" x2="24" y2="4" stroke={theme.colors.textDim} strokeWidth="1.5" />
                    </svg>
                  )}
                </div>

                {/* Hover tooltip — name + description */}
                {item && !menuOpen && (
                  <div className="inventory-tooltip" style={styles.inventoryTooltip}>
                    <div style={{ color: catColor, fontWeight: 700, fontSize: '11px', marginBottom: '2px' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '10px', color: theme.colors.textSecondary, lineHeight: '1.3' }}>
                      {item.description}
                    </div>
                    {item.apCost > 0 && (
                      <div style={{ fontSize: '9px', color: theme.colors.accentBlue, marginTop: '3px' }}>
                        Cost: {item.apCost} AP
                      </div>
                    )}
                    <div style={{ fontSize: '9px', color: theme.colors.accentGreen, marginTop: '3px' }}>
                      Extract value: {item.sellValue ?? 10} MK
                    </div>
                  </div>
                )}

                {/* Action menu — Use / Drop / Cancel */}
                {menuOpen && item && (
                  <div style={styles.itemActionMenu}>
                    <div style={{ color: catColor, fontWeight: 700, fontSize: '11px', marginBottom: '4px', textAlign: 'center' as const }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '9px', color: theme.colors.textSecondary, marginBottom: '6px', lineHeight: '1.3' }}>
                      {item.description}
                    </div>
                    <button
                      style={{
                        ...styles.itemMenuButton,
                        ...(item.apCost > gameState.player.ap ? styles.itemMenuButtonDisabled :
                            item.category === 'weapon' && ambushAvailable ? { ...styles.itemMenuButtonUse, background: 'rgba(255, 50, 50, 0.2)', borderColor: 'rgba(255, 50, 50, 0.5)', color: '#ff5050' } :
                            styles.itemMenuButtonUse),
                      }}
                      disabled={item.apCost > gameState.player.ap}
                      onClick={(e) => { e.stopPropagation(); handleItemUse(i); }}
                    >
                      {item.category === 'weapon' && ambushAvailable ? 'AMBUSH' :
                       item.category === 'weapon' && playerSeesAgentNow && agentSeesPlayerNow ? 'ATTACK' :
                       'USE'}{item.apCost > 0 ? ` (${item.apCost} AP)` : ''}
                    </button>
                    <button
                      style={{ ...styles.itemMenuButton, ...styles.itemMenuButtonDrop }}
                      onClick={(e) => { e.stopPropagation(); handleItemDrop(i); }}
                    >
                      DROP
                    </button>
                    <button
                      style={{ ...styles.itemMenuButton, ...styles.itemMenuButtonCancel }}
                      onClick={(e) => { e.stopPropagation(); setItemMenuIndex(null); }}
                    >
                      CANCEL
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {runState && (() => {
            // Projected value of items currently on the player (would become
            // unbanked marks on extract). Bank Job passive boosts extract
            // item value by 50% — mirror that multiplier here so the tooltip
            // matches what the player actually gets. Shown alongside the
            // already-earned "This round" marks so the player can eyeball
            // their total potential at a glance.
            const bankJobMultiplier = hasPassive(runState, 'bankJob') ? 1.5 : 1;
            const itemPotential = Math.floor(
              gameState.player.items.reduce((sum, item) => sum + (item.sellValue ?? 10), 0) * bankJobMultiplier,
            );
            return (
              <div style={styles.marksDisplay} className="marks-display">
                <img src="./Icons/marks.svg" alt="marks" style={{ width: '14px', height: '14px', filter: 'brightness(0) saturate(100%) invert(69%) sepia(52%) saturate(501%) hue-rotate(100deg) brightness(95%) contrast(92%)' }} />
                <span style={styles.marksValue}>{runState.marks + runState.unbankedMarks}</span>
                <div className="marks-tooltip" style={styles.marksTooltip}>
                  <div style={{ fontWeight: 700, color: theme.colors.accentGreen, letterSpacing: '1px', marginBottom: '6px', fontSize: '10px' }}>MARKS</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                    <span style={{ color: theme.colors.textSecondary }}>Banked</span>
                    <span style={{ color: theme.colors.accentGreen, fontWeight: 700 }}>{runState.marks}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                    <span style={{ color: theme.colors.textSecondary }}>This round</span>
                    <span style={{ color: theme.colors.accentYellow, fontWeight: 700 }}>{runState.unbankedMarks}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: theme.colors.textSecondary }}>Items (on extract)</span>
                    <span style={{ color: theme.colors.textSecondary, fontWeight: 700 }}>
                      +{itemPotential}{bankJobMultiplier > 1 ? ' (×1.5)' : ''}
                    </span>
                  </div>
                  <div style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: '6px', fontSize: '9px', color: theme.colors.textDim, lineHeight: '1.4' }}>
                    Spend marks at HQ to draft cards and remove cards from your deck. Extract to bank this round's earnings.
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Bottom HUD — cards, deck/discard, AP, turn, end turn */}
        <div style={styles.bottomHud}>
          {/* Deck pile + recenter button stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', pointerEvents: 'auto' }}>
            <button
              onClick={() => { setRecenterTarget(null); setRecenterTrigger(n => n + 1); }}
              title="Recenter map"
              style={{
                width: '44px', height: '32px', padding: 0,
                background: 'rgba(12, 16, 24, 0.95)',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#44aaff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.colors.border; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" fill="#44aaff" />
                <path d="M12 2 L12 6 M12 18 L12 22 M2 12 L6 12 M18 12 L22 12" stroke="#44aaff" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div style={styles.pile} onClick={() => setPileOverlay('deck')} title="View draw pile">
              <div style={styles.pileCount}>{gameState.player.deck.length}</div>
              <div style={styles.pileLabel}>DECK</div>
            </div>
          </div>

          {/* Hand cards with status effects floating above */}
          <div className="hand-wrapper" style={styles.handWrapper}>
            {/* Status effects — above the hand */}
            {(() => {
              type Effect = { label: string; color: string; description: string; duration: string };
              const turnsLabel = (n: number) => `${n} turn${n !== 1 ? 's' : ''} left`;
              const effects: Effect[] = [];
              if (gameState.player.isInSafehouse) {
                effects.push({ label: 'SAFEHOUSE', color: '#44cc88', description: 'Hidden, zero signal. Exposure resets to 0 on exit.', duration: turnsLabel(gameState.player.safehouseTurnsLeft) });
              } else if (gameState.player.hidingTurnsLeft > 0) {
                effects.push({ label: 'HIDE INSIDE', color: '#44cc88', description: 'Zero signal while inside the building. −100 extra exposure each turn spent hiding. You cannot move.', duration: turnsLabel(gameState.player.hidingTurnsLeft) });
              }
              if (gameState.player.ghostActive) {
                effects.push({ label: 'GHOST', color: '#ffaa44', description: 'All signal you give off is negated.', duration: 'Current turn' });
              }
              if (gameState.districtScanName && gameState.districtScanTurn != null && gameState.turn - gameState.districtScanTurn <= 2) {
                effects.push({ label: 'DISTRICT SCAN', color: '#aa88ff', description: `Agent is near: ${gameState.districtScanName}`, duration: 'Fading' });
              }
              // Active informant tips — persistent chip for each tip, latest first.
              // Tips are removed from gameState.informantTips by the turn-end logic
              // once they exceed tuning.informantTipDuration, so the chip reflects
              // exactly which tips are still valid on the map.
              if (gameState.informantTips.length > 0) {
                for (let i = gameState.informantTips.length - 1; i >= 0; i--) {
                  const tip = gameState.informantTips[i];
                  const age = gameState.turn - tip.turnCreated;
                  const turnsLeft = Math.max(1, tuning.informantTipDuration - age);
                  effects.push({
                    label: 'INFORMANT',
                    color: '#aa88ff',
                    description: `Agent reported near ${tip.poiName} on ${tip.streetName}.`,
                    duration: turnsLabel(turnsLeft),
                  });
                }
              }
              const activePings = gameState.acousticPings.filter(p => gameState.turn - p.turn <= 3);
              if (activePings.length > 0) {
                const latest = activePings[activePings.length - 1];
                effects.push({ label: `PING ${latest.distanceM}m`, color: '#66ccff', description: `Agent is ${latest.distanceM}m away (straight line). No direction. Multiple pings from different positions enable triangulation.`, duration: `${3 - (gameState.turn - latest.turn)} turns` });
              }
              if (gameState.player.trafficAnalysisRoadName) {
                effects.push({
                  label: `ON ${gameState.player.trafficAnalysisRoadName.toUpperCase()}`,
                  color: '#ff5050',
                  description: `Traffic Analysis: the agent is currently on ${gameState.player.trafficAnalysisRoadName}. The full road is highlighted red on the map.`,
                  duration: 'Current turn',
                });
              }
              const activeBearings = gameState.bearings.filter(b => gameState.turn - b.turn <= 3);
              if (activeBearings.length > 0) {
                const latest = activeBearings[activeBearings.length - 1];
                effects.push({ label: `BEARING ×${activeBearings.length}`, color: '#aa88ff', description: `Latest read points ${compassOctant(latest.bearingDeg)}. Each bearing is a static snapshot at cast time. Multiple reads from different positions intersect to triangulate.`, duration: `${3 - (gameState.turn - latest.turn)} turns` });
              }
              const activeStrips = gameState.reconStrips.filter(s => gameState.turn - s.turn <= 3);
              if (activeStrips.length > 0) {
                const latest = activeStrips[activeStrips.length - 1];
                const hits = activeStrips.filter(s => s.hit).length;
                effects.push({ label: `RECON ${latest.hit ? 'HIT' : 'MISS'}`, color: latest.hit ? '#ff4444' : '#888899', description: `${activeStrips.length} strip${activeStrips.length !== 1 ? 's' : ''} on map (${hits} hit${hits !== 1 ? 's' : ''}). Red strips contain the agent, gray strips don't.`, duration: `${3 - (gameState.turn - latest.turn)} turns` });
              }
              if (gameState.stakeouts.length > 0) {
                const watching = gameState.stakeouts.filter(s => s.agentInCone).length;
                effects.push({
                  label: watching > 0 ? `STAKEOUT ×${gameState.stakeouts.length} · LIVE` : `STAKEOUT ×${gameState.stakeouts.length}`,
                  color: watching > 0 ? '#ff8844' : '#aa88ff',
                  description: watching > 0
                    ? `${watching} of ${gameState.stakeouts.length} stakeout${gameState.stakeouts.length !== 1 ? 's' : ''} currently has eyes on the agent. Fade timer paused until the agent leaves every cone.`
                    : `${gameState.stakeouts.length} comrade${gameState.stakeouts.length !== 1 ? 's' : ''} watching the map. Alerts trigger if the agent enters any cone.`,
                  duration: 'Rest of round',
                });
              }
              if (gameState.surveillanceVans.length > 0) {
                const watching = gameState.surveillanceVans.filter(v => v.agentInCone).length;
                effects.push({
                  label: watching > 0 ? `SURV VAN ×${gameState.surveillanceVans.length} · LIVE` : `SURV VAN ×${gameState.surveillanceVans.length}`,
                  color: watching > 0 ? '#ff8844' : '#aa88ff',
                  description: watching > 0
                    ? `${watching} van${watching !== 1 ? 's' : ''} currently has the agent in sight.`
                    : `${gameState.surveillanceVans.length} surveillance van${gameState.surveillanceVans.length !== 1 ? 's' : ''} patrolling. 200m/turn along a fixed route with a 100m 360° cone.`,
                  duration: 'Rest of round',
                });
              }
              if (gameState.wireTaps.some(t => !t.triggered)) {
                const active = gameState.wireTaps.filter(t => !t.triggered).length;
                effects.push({ label: `WIRE TAP ×${active}`, color: '#ffdd44', description: 'Active wire taps on street segments. Waiting for agent to cross.', duration: 'Until triggered' });
              }
              if (gameState.player.overwatch) {
                const turnsLeft = gameState.player.overwatch.turnsLeft;
                const weaponName = gameState.player.overwatch.weapon.name;
                effects.push({
                  label: `OVERWATCH ×${turnsLeft}`,
                  color: '#ff5050',
                  description: `Holding with ${weaponName}. Move/Shortcut blocked. If the agent crosses the marked road segment, you ambush with your weapon.`,
                  duration: `${turnsLeft} turn${turnsLeft !== 1 ? 's' : ''}`,
                });
              }
              if (gameState.player.staticActive) {
                effects.push({
                  label: 'STATIC',
                  color: '#ffaa44',
                  description: 'The signal you give off this turn is displaced to a random nearby street. Fools belief and Sweep.',
                  duration: 'Current turn',
                });
              }
              if (gameState.player.deadMansSwitch) {
                const dms = gameState.player.deadMansSwitch;
                if (dms.deployed) {
                  effects.push({
                    label: "DEAD MAN'S — DEPLOYED",
                    color: '#44cc88',
                    description: 'The cached signal just fired at its original location.',
                    duration: 'Current turn',
                  });
                } else {
                  const turnsUntil = Math.max(1, dms.fireOnTurn - gameState.turn);
                  effects.push({
                    label: `DEAD MAN'S (${turnsUntil})`,
                    color: '#ffaa44',
                    description: 'Captured a signal. It will replay at its original location in a few turns.',
                    duration: `${turnsUntil} turn${turnsUntil !== 1 ? 's' : ''}`,
                  });
                }
              }
              if (gameState.doppelganger) {
                const turnsLeft = gameState.doppelganger.turnsLeft;
                effects.push({
                  label: `DOPPELGÄNGER ×${turnsLeft}`,
                  color: '#ffaa44',
                  description: 'Decoy walking its path, broadcasting player-like signals. If the agent engages, position is revealed.',
                  duration: `${turnsLeft} turn${turnsLeft !== 1 ? 's' : ''}`,
                });
              }
              if (gameState.player.doppelgangerRevealTurnsLeft > 0) {
                effects.push({
                  label: `AGENT TRACKED ×${gameState.player.doppelgangerRevealTurnsLeft}`,
                  color: '#44aaff',
                  description: 'Doppelgänger engagement: the agent\'s exact position is visible to you.',
                  duration: turnsLabel(gameState.player.doppelgangerRevealTurnsLeft),
                });
              }
              if (gameState.player.instinctTurnsLeft > 0) {
                effects.push({ label: 'INSTINCT', color: '#ffaa44', description: '360° vision cone — see in every direction.', duration: turnsLabel(gameState.player.instinctTurnsLeft) });
              }
              if (gameState.player.activeBuffs.length > 0) {
                const names = gameState.player.activeBuffs.map(b => b.sourceCardName).join(' + ');
                effects.push({ label: 'MOVE BUFF', color: '#44cc88', description: `${names} — applies to your next Move card.`, duration: 'Until next Move' });
              }
              if (gameState.player.isInVehicle) {
                effects.push({ label: 'IN VEHICLE', color: '#00e5ff', description: '2× movement range. Major roads generate 2× signal & exposure.', duration: 'Until you exit' });
              }
              if (gameState.player.isInTaxi && gameState.player.taxiRemainingPath) {
                const remaining = gameState.player.taxiRemainingPath.totalDistance;
                const turnsLeft = Math.max(1, Math.ceil(remaining / tuning.taxiSpeedPerTurn));
                effects.push({ label: 'IN TAXI', color: '#ffcc33', description: `Auto-driving ${tuning.taxiSpeedPerTurn}m/turn along roads. ~${Math.round(remaining)}m left. Generates more signal than walking, less than a hotwired car.`, duration: `${turnsLeft} turn${turnsLeft !== 1 ? 's' : ''}` });
              }
              if (gameState.player.disguiseTurnsLeft > 0) {
                effects.push({ label: 'DISGUISE', color: '#ffaa44', description: '75% less signal in commercial zones and on major roads, 50% less exposure build. Transit boardings read as normal movement (no ground-truth to the agent).', duration: turnsLabel(gameState.player.disguiseTurnsLeft) });
              }
              if (activeMalwareCount > 0) {
                effects.push({ label: `MALWARE ×${activeMalwareCount}`, color: '#ffaa44', description: `${activeMalwareCount} junk card${activeMalwareCount !== 1 ? 's' : ''} living in the agent's deck. Each one occupies a hand slot when drawn and burns a turn of the agent's attention.`, duration: 'Rest of round' });
              }
              if (gameState.player.doubleAgentPrimed) {
                effects.push({ label: 'DOUBLE AGENT', color: '#ffaa44', description: 'Primed. The next Informant the agent plays will point to a random POI instead of a real tip near you.', duration: 'Until triggered' });
              }
              if (gameState.player.lowProfileActive) {
                effects.push({ label: 'LOW PROFILE', color: '#ffaa44', description: 'No additional exposure is gained for the rest of this turn.', duration: 'Current turn' });
              }
              if (gameState.player.paradeRoutePrimed) {
                effects.push({ label: 'PARADE ROUTE', color: '#ffaa44', description: 'Next turn the agent runs into a parade. Their movement distance is halved.', duration: 'Until next agent turn' });
              }
              if (gameState.player.stopAndFriskPrimed) {
                effects.push({ label: 'STOP & FRISK', color: '#ffaa44', description: 'Local police stop the agent for questioning next turn. They cannot play Move or Shortcut cards.', duration: 'Until next agent turn' });
              }
              if (gameState.player.isInTransit && gameState.player.transitLineId) {
                const line = cityData.layers.transit.lines.find(l => l.id === gameState.player.transitLineId);
                if (line) {
                  const dir = gameState.player.transitDirection;
                  const idx = gameState.player.transitStopIndex;
                  const stop = line.stops[idx];
                  const nextIdx = dir === 'forward' ? idx + 1 : idx - 1;
                  const nextStop = nextIdx >= 0 && nextIdx < line.stops.length ? line.stops[nextIdx] : null;
                  effects.push({
                    label: `ON ${line.type.toUpperCase()}`,
                    color: line.color,
                    description: `${line.name} — ${dir} from ${stop?.name ?? '—'}. Next stop: ${nextStop?.name ?? 'endpoint (will reverse)'}.`,
                    duration: 'Until you exit',
                  });
                }
              }
              if (gameState.player.bonusApNextTurn > 0) {
                effects.push({ label: `+${gameState.player.bonusApNextTurn} AP NEXT`, color: '#ffcc44', description: 'Extra AP granted at the start of next turn.', duration: 'Next turn' });
              }
              if (gameState.player.retainedCardIds.length > 0) {
                const n = gameState.player.retainedCardIds.length;
                effects.push({ label: 'STASHED', color: '#aa88ff', description: `${n} card${n !== 1 ? 's' : ''} will stay in hand at end of turn instead of being discarded.`, duration: 'Current turn' });
              }
              if (gameState.player.adrenalineApThisTurn > 0) {
                effects.push({ label: `ADRENALINE +${gameState.player.adrenalineApThisTurn} AP`, color: '#ff8844', description: 'Extra AP gained this turn from Adrenaline.', duration: 'Current turn' });
              }
              if (gameState.player.prepBuiltThisTurn > 0) {
                const pct = Math.round(gameState.player.prepBuiltThisTurn * 100);
                effects.push({ label: `PREP +${pct}%`, color: '#ff5555', description: 'Prep bonus added this turn — boosts ambush strike and gun confrontation odds.', duration: 'Current turn' });
              }
              if (gameState.player.isElevated) {
                const levels = gameState.player.rooftopBuildingLevels;
                const heightLabel = levels != null
                  ? `${levels} floor${levels !== 1 ? 's' : ''}`
                  : 'Unknown height';
                const hasSniperOnRoof = gameState.player.items.some(i => i.id.startsWith('sniper'));
                if (hasSniperOnRoof) {
                  effects.push({ label: 'SNIPER', color: '#ff4444', description: `${heightLabel}. Long-range ambush enabled — 500m kill range.`, duration: 'Until you descend' });
                } else {
                  effects.push({ label: 'ROOFTOP', color: '#00e5ff', description: `${heightLabel}. 2× vision range, 120° cone. Invisible to ground-level agents.`, duration: 'Until you descend' });
                }
              }
              if (gameState.player.operationalIntegrityActive) {
                effects.push({ label: 'OP INTEGRITY', color: '#44cc88', description: 'Next turn: +2 AP and +3 additional cards drawn.', duration: 'Next turn' });
              }
              if (counterSurveillanceActive) {
                effects.push({ label: 'COUNTER-SURV', color: '#ffaa44', description: 'Your own signal footprint is visible on the map.', duration: 'Current turn' });
              }
              if (gameState.player.coldReadActive) {
                effects.push({ label: 'COLD READ', color: '#aa88ff', description: 'Next intel card played has improved precision.', duration: 'Current turn' });
              }
              if (gameState.player.sharpenActive) {
                effects.push({ label: 'SHARPEN', color: '#ff5555', description: '+10% Prep for every card you play (including this one).', duration: 'Current turn' });
              }
              if (gameState.player.contingencyActive > 0) {
                const cc = gameState.player.contingencyActive;
                effects.push({
                  label: cc > 1 ? `CONTINGENCY ×${cc}` : 'CONTINGENCY',
                  color: '#ffaa44',
                  description: cc > 1
                    ? `The next ${cc} Tactics cards you draw cost 0 AP.`
                    : 'The next Tactics card you draw costs 0 AP.',
                  duration: 'Until next Tactics draw',
                });
              }
              if (gameState.player.patienceActive) {
                effects.push({ label: 'PATIENCE', color: '#44cc88', description: `+1 max AP for the rest of the round (${gameState.player.maxAp} total).`, duration: 'Rest of round' });
              }
              if (gameState.player.detectedTurnsRemaining > 0) {
                effects.push({ label: 'DETECTED', color: '#ff4040', description: 'Agent has your exact position. Your signal is broadcast every turn.', duration: turnsLabel(gameState.player.detectedTurnsRemaining) });
              }
              if (gameState.player.seenAgentTurnsLeft > 0) {
                effects.push({ label: 'ENEMY SEEN', color: '#ff8844', description: 'You spotted the agent. Their last known position is visible on the map.', duration: turnsLabel(gameState.player.seenAgentTurnsLeft) });
              }
              if (gameState.player.items.some(i => i.id.startsWith('radio-scanner'))) {
                // Held-inventory passive: no countdown — active as long as the
                // Radio Scanner remains in the player's items. Dropping/selling
                // the scanner deactivates the effect. startsWith matches both
                // pickup-suffixed ids ("radio-scanner-abc123") and loadout
                // ("radio-scanner-loadout"); strict equality missed both.
                effects.push({ label: 'SCANNER', color: '#44aaff', description: 'Radio Scanner in inventory. Agent passive signals are visible on the map and amplified by 25%.', duration: 'Held' });
              }
              if (gameState.player.jammerTurnsLeft > 0) {
                // Check if player is currently inside jammer zone
                const inZone = gameState.player.jammerPosition && (() => {
                  const dLat = (gameState.player.position.lat - gameState.player.jammerPosition!.lat) * 111000;
                  const dLng = (gameState.player.position.lng - gameState.player.jammerPosition!.lng) * 111000 * Math.cos(gameState.player.position.lat * Math.PI / 180);
                  return Math.sqrt(dLat * dLat + dLng * dLng) <= gameState.player.jammerRadius;
                })();
                effects.push({
                  label: inZone ? 'JAMMED' : 'JAMMER',
                  color: inZone ? '#44cc88' : '#888',
                  description: inZone ? 'You are inside the jammer zone. No signal generated.' : 'Jammer active nearby but you are outside its radius.',
                  duration: turnsLabel(gameState.player.jammerTurnsLeft),
                });
              }
              if (effects.length === 0) return null;
              return (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  padding: '4px 8px 16px',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                }}>
                  {effects.map((fx, i) => (
                    <div
                      key={i}
                      className="status-chip"
                      style={{
                        background: `${fx.color}15`,
                        border: `1px solid ${fx.color}40`,
                        borderRadius: '4px',
                        padding: '3px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'default',
                        position: 'relative',
                      }}
                    >
                      <span style={{
                        fontFamily: theme.fonts.mono,
                        fontSize: '9px',
                        fontWeight: 700,
                        color: fx.color,
                        letterSpacing: '1px',
                      }}>
                        {fx.label}
                      </span>
                      <div className="status-chip-tooltip" style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 8px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(12, 16, 24, 0.96)',
                        border: `1px solid ${fx.color}80`,
                        borderRadius: '4px',
                        padding: '8px 10px',
                        minWidth: '180px',
                        maxWidth: '260px',
                        display: 'none',
                        zIndex: 1000,
                        pointerEvents: 'none',
                      }}>
                        <div style={{
                          fontFamily: theme.fonts.mono,
                          fontSize: '10px',
                          fontWeight: 700,
                          color: fx.color,
                          letterSpacing: '1px',
                          marginBottom: '4px',
                        }}>
                          {fx.label}
                        </div>
                        <div style={{
                          fontFamily: theme.fonts.mono,
                          fontSize: '10px',
                          color: theme.colors.textPrimary,
                          lineHeight: '1.4',
                          marginBottom: '4px',
                          whiteSpace: 'normal',
                        }}>
                          {fx.description}
                        </div>
                        <div style={{
                          fontFamily: theme.fonts.mono,
                          fontSize: '9px',
                          color: `${fx.color}cc`,
                          letterSpacing: '0.5px',
                          paddingTop: '4px',
                          borderTop: `1px solid ${fx.color}30`,
                        }}>
                          {fx.duration}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={styles.handArea}>
            {/* Virtual Scavenge cards for nearby item pickups */}
            {nearbyItems.map(pi => {
              const scavengeCard: Card = {
                id: `scavenge-${pi.id}`,
                name: 'Scavenge',
                category: 'tactics',
                apCost: 0,
                description: `Search ${pi.poi.name || (pi.poi.category.charAt(0).toUpperCase() + pi.poi.category.slice(1))} for items.`,
              };
              const pType = pi.pickupType ?? 'mixed';
              const pColor =
                pType === 'weapon' ? theme.colors.pickupWeapon :
                pType === 'gadget' ? theme.colors.pickupGadget :
                pType === 'lockpick' ? theme.colors.pickupLockpick :
                pType === 'scanner' ? theme.colors.pickupScanner :
                theme.colors.pickupMixed;
              const pLabel =
                pType === 'weapon' ? 'WEAPON' :
                pType === 'gadget' ? 'GADGET' :
                pType === 'lockpick' ? 'LOCKPICK' :
                pType === 'scanner' ? 'SCANNER' :
                'PICKUP';
              return (
                <div
                  key={scavengeCard.id}
                  className="hand-card hand-card--scavenge"
                  onClick={() => handleCardClick(scavengeCard)}
                >
                  <div style={{ ...styles.cardCost, background: pColor }}>0</div>
                  <div style={styles.cardName}>Scavenge</div>
                  <div style={{ ...styles.cardCategory, color: pColor }}>{pLabel}</div>
                  <div style={{ fontSize: '9px', color: theme.colors.textSecondary, marginTop: '2px' }}>
                    {pi.poi.name || pi.poi.category}
                  </div>
                  <div style={styles.cardCategoryIcon}>
                    <CardCategoryIcon category="tactics" size={16} />
                  </div>
                  <div className="card-tooltip">
                    <div className="card-tooltip__header">
                      <span className="card-tooltip__name">Scavenge</span>
                      <span className="card-tooltip__cost">0 AP</span>
                    </div>
                    <div className="card-tooltip__category" style={{ color: pColor }}>
                      {pType === 'weapon' ? 'WEAPON CACHE' :
                        pType === 'gadget' ? 'GADGET STATION' :
                        pType === 'lockpick' ? 'LOCKPICK DROP' :
                        pType === 'scanner' ? 'SCANNER DROP' :
                        'SUPPLY DROP'}
                    </div>
                    <div className="card-tooltip__desc">{scavengeCard.description}</div>
                  </div>
                </div>
              );
            })}
            {/* Virtual Collect Passphrase cards */}
            {nearbyPassphrases.map(pp => (
              <div
                key={pp.id}
                className="hand-card hand-card--scavenge"
                onClick={() => handleCardClick({
                  id: `passphrase-card-${pp.id}`,
                  name: 'Collect Passphrase',
                  category: 'intel',
                  apCost: 0,
                  description: `Dead drop at ${pp.poiName}. Collect to reveal passphrase.`,
                })}
              >
                <div style={{ ...styles.cardCost, background: '#ccddee', color: '#111' }}>0</div>
                <div style={styles.cardName}>Passphrase</div>
                <div style={{ ...styles.cardCategory, color: '#ccddee' }}>DEAD DROP</div>
                <div style={{ fontSize: '8px', color: '#ccddee88', marginTop: '2px' }}>
                  ● ● ● — ● ● ●
                </div>
                <div className="card-tooltip">
                  <div className="card-tooltip__header">
                    <span className="card-tooltip__name">Collect Passphrase</span>
                    <span className="card-tooltip__cost">0 AP</span>
                  </div>
                  <div className="card-tooltip__category">DEAD DROP — {pp.poiName}</div>
                  <div className="card-tooltip__desc">Collect to reveal the extraction passphrase. Need {passphrasesRequired} to extract. Generates moderate signal.</div>
                </div>
              </div>
            ))}
            {/* Virtual Enter Safehouse cards */}
            {nearbySafehouses.map(sh => {
              const exposurePct = gameState.player.exposure / tuning.exposureMax;
              const canEnter = exposurePct <= 0.5;
              return (
                <div
                  key={sh.id}
                  className={`hand-card ${canEnter ? 'hand-card--scavenge' : 'hand-card--disabled'}`}
                  onClick={() => canEnter && handleEnterSafehouse(sh.id)}
                >
                  <div style={{ ...styles.cardCost, background: '#44cc88' }}>0</div>
                  <div style={styles.cardName}>Enter Safehouse</div>
                  <div style={{ ...styles.cardCategory, color: '#44cc88' }}>SAFEHOUSE</div>
                  {!canEnter && (
                    <div style={{ fontSize: '8px', color: '#ff5050', marginTop: '2px' }}>
                      {Math.round(exposurePct * 100)}% exp (max 50%)
                    </div>
                  )}
                  <div className="card-tooltip">
                    <div className="card-tooltip__header">
                      <span className="card-tooltip__name">Enter Safehouse</span>
                      <span className="card-tooltip__cost">0 AP</span>
                    </div>
                    <div className="card-tooltip__category">SAFEHOUSE</div>
                    <div className="card-tooltip__desc">Enter a safehouse to draft rare cards. Requires exposure below 50%. You'll be hidden for up to 3 turns. Exposure resets to 0 on exit.</div>
                  </div>
                </div>
              );
            })}
            {/* Virtual Rooftop Access via lockpick */}
            {canLockpickRooftop && (() => {
              // Contingency discount: virtual tactics cards aren't in the
              // deck/hand pipeline that normally stamps _contingencyApplied
              // at draw time, so the bonus never applied to them. Apply
              // here at render: if a charge is available, force apCost 0
              // and flag the card so the click handler decrements the
              // counter the same way it does for real drawn tactics cards.
              const contingencyDiscount = gameState.player.contingencyActive > 0;
              const baseCost = 3;
              const rooftopCard: Card = contingencyDiscount
                ? {
                    id: `lockpick-rooftop-${Date.now()}`,
                    name: 'Rooftop Access',
                    category: 'tactics',
                    apCost: 0,
                    description: 'Use your Lock Pick to access a rooftop. Consumes the Lock Pick.',
                    _contingencyApplied: true,
                  }
                : {
                    id: `lockpick-rooftop-${Date.now()}`,
                    name: 'Rooftop Access',
                    category: 'tactics',
                    apCost: baseCost,
                    description: 'Use your Lock Pick to access a rooftop. Consumes the Lock Pick.',
                  };
              const canAfford = gameState.player.ap >= rooftopCard.apCost;
              return (
                <div
                  className={`hand-card ${canAfford ? 'hand-card--scavenge' : 'hand-card--disabled'}`}
                  onClick={() => canAfford && handleCardClick(rooftopCard)}
                >
                  <div style={{ ...styles.cardCost, background: '#ffaa44' }}>{rooftopCard.apCost}</div>
                  <div style={styles.cardName}>Rooftop Access</div>
                  <div style={{ ...styles.cardCategory, color: '#ffaa44' }}>LOCKPICK</div>
                  <div style={{ fontSize: '8px', color: '#ffaa4488', marginTop: '2px' }}>
                    🔑 uses Lock Pick
                    {contingencyDiscount ? ' · ⚡ CONTINGENCY' : ''}
                  </div>
                  <div className="card-tooltip">
                    <div className="card-tooltip__header">
                      <span className="card-tooltip__name">Rooftop Access (Lock Pick)</span>
                      <span className="card-tooltip__cost">{rooftopCard.apCost} AP{contingencyDiscount ? ' (Contingency)' : ''}</span>
                    </div>
                    <div className="card-tooltip__category">TACTICS — LOCKPICK</div>
                    <div className="card-tooltip__desc">Pick a lock to access a rooftop. 2× vision range, 120° cone. Sniper rifle enables long-range ambush. Consumes your Lock Pick.</div>
                  </div>
                </div>
              );
            })()}
            {/* Virtual Extract cards for nearby extraction points.
                Surface only on extraction nodes (where extract IS the round
                objective) and in practice mode. On standard Hit/Boss rounds
                the objective is kill-the-agent, so no extract offer. */}
            {(!runState || isExtractionRound) && nearbyExtractions.map(ext => {
              const exposurePct = gameState.player.exposure / tuning.exposureMax;
              const canExtract = exposurePct <= tuning.extractMaxExposurePct;
              const extractCard: Card = {
                id: `extract-${ext.id}`,
                name: 'Extract',
                category: 'tactics',
                apCost: 0,
                description: canExtract
                  ? `Extract via ${ext.name}. Exposure ${Math.round(exposurePct * 100)}% — clear to go.`
                  : `Extract via ${ext.name}. Exposure ${Math.round(exposurePct * 100)}% — too hot! Must be below ${Math.round(tuning.extractMaxExposurePct * 100)}%.`,
              };
              return (
                <div
                  key={extractCard.id}
                  className={`hand-card ${canExtract ? 'hand-card--scavenge' : 'hand-card--disabled'}`}
                  onClick={() => handleCardClick(extractCard)}
                >
                  <div style={{ ...styles.cardCost, background: canExtract ? '#44ff88' : '#ff4040' }}>0</div>
                  <div style={styles.cardName}>Extract</div>
                  <div style={{ ...styles.cardCategory, color: canExtract ? '#44ff88' : '#ff4040' }}>
                    {ext.name.toUpperCase()}
                  </div>
                  <div style={{ fontSize: '9px', color: canExtract ? '#44ff88' : '#ff6060', marginTop: '2px' }}>
                    {canExtract ? 'CLEAR' : `${Math.round(exposurePct * 100)}% EXP`}
                  </div>
                  <div style={styles.cardCategoryIcon}>
                    <CardCategoryIcon category="tactics" size={16} />
                  </div>
                  <div className="card-tooltip">
                    <div className="card-tooltip__header">
                      <span className="card-tooltip__name">Extract</span>
                      <span className="card-tooltip__cost">0 AP</span>
                    </div>
                    <div className="card-tooltip__category">EXTRACTION — {ext.name.toUpperCase()}</div>
                    <div className="card-tooltip__desc">{extractCard.description}</div>
                  </div>
                </div>
              );
            })}
            {/* Regular hand cards */}
            {gameState.player.hand.map((card, cardIdx) => {
              const isSelected = selectedCardId === card.id;
              const isPending = pendingFieldcraftIds.has(card.id);
              const isEntering = enteringCardIds.has(card.id);
              const canAfford = gameState.player.ap >= card.apCost;
              const isMovement = !!card.movementRange;
              const blockedByHiding = gameState.player.isInBuilding && isMovement;
              const blockedHotwire = card.name === 'Hotwire' && (!playerOnResidentialStreet || playerInCommercialZone || gameState.player.isInVehicle || gameState.player.isInTransit);
              // Cards that can't be used in a vehicle
              const blockedByVehicle = gameState.player.isInVehicle && (
                card.name === 'Hide Inside' || card.name === 'Shortcut' || card.name === 'Hotwire' || card.name === 'Rooftop Access'
              );
              // Cards that can't be used while on transit (same restrictions as vehicle, plus movement)
              const blockedByTransit = gameState.player.isInTransit && (
                isMovement || card.name === 'Hide Inside' || card.name === 'Shortcut' || card.name === 'Hotwire' || card.name === 'Rooftop Access'
              );
              // Taxi: like transit — no movement cards, no hiding/shortcuts/hotwire/rooftop,
              // and can't hail another taxi mid-ride.
              const blockedByTaxi = gameState.player.isInTaxi && (
                isMovement || card.name === 'Hide Inside' || card.name === 'Shortcut' || card.name === 'Hotwire' || card.name === 'Rooftop Access' || card.name === 'Taxi'
              );
              const blockedByElevated = gameState.player.isElevated && (
                card.name === 'Hide Inside' || card.name === 'Rooftop Access' || card.name === 'Hotwire'
              );
              const blockedByHidingRooftop = gameState.player.isElevated && isMovement;
              // Overwatch: block Move/Shortcut while holding an overwatch segment
              const blockedByOverwatch = !!gameState.player.overwatch && isMovement;
              // Opportunist: only playable after a movement card
              const blockedOpportunist = card.name === 'Opportunist' && !gameState.player.lastPlayedMovement;
              // Operational Integrity: only playable as first card of turn
              const blockedOpInt = card.name === 'Operational Integrity' && gameState.player.cardsPlayedThisTurn > 0;
              const blockedPatience = card.name === 'Patience' && gameState.player.cardsPlayedThisTurn > 0;
              const isPlayable = canAfford && !blockedByHiding && !blockedByHidingRooftop && !blockedByOverwatch && !isPending && !blockedHotwire && !blockedByVehicle && !blockedByTransit && !blockedByTaxi && !blockedByElevated && !blockedOpportunist && !blockedOpInt && !blockedPatience;
              return (
                <div
                  key={card.id}
                  ref={setCardRef(card.id)}
                  className={`hand-card ${isSelected ? 'hand-card--selected' : ''} ${!isPlayable ? 'hand-card--disabled' : ''} ${isEntering ? 'hand-card--entering' : ''}`}
                  style={{
                    ...(isPending ? { opacity: 0.35, pointerEvents: 'none' as const } : {}),
                    ...(isEntering ? ({ '--card-enter-delay': `${cardIdx * 0.07}s` } as React.CSSProperties) : {}),
                    ...(card.name === 'Improvised Strike' && ambushAvailable ? { borderColor: 'rgba(255, 50, 50, 0.7)', boxShadow: '0 0 8px rgba(255, 50, 50, 0.4)' } : {}),
                  }}
                  onClick={() => isPlayable && (isMovement || card.isFieldcraft || isFieldcraftEffectCard(card) || card.name === 'Counter-Surveillance' || !!card.sweepRadius || card.name === 'Hide Inside' || card.name === 'Rooftop Access' || card.name === 'Instinct' || card.name === 'Ghost' || card.name === 'Decoy' || card.name === 'Informant' || card.name === 'Spotter' || card.name === 'Hotwire' || card.name === 'Improvised Strike' || card.name === 'Wire Tap' || card.name === 'District Scan' || card.name === 'Acoustic Ping' || card.name === 'Trace Route' || card.name === 'Misdirect' || card.name === 'Taxi' || card.name === 'Disguise' || card.name === 'Malware' || card.name === 'Stage Exit' || card.name === 'Double Agent' || card.name === 'Bearing' || card.name === 'Recon Plane' || card.name === 'Stakeout' || card.name === 'Surveillance Van' || card.name === 'Traffic Analysis' || card.name === 'Low Profile' || card.name === 'Parade Route' || card.name === 'Stop and Frisk' || card.name === 'Overwatch' || card.name === 'Static' || card.name === "Dead Man's Switch" || card.name === 'Doppelgänger' || card.name === 'Doppelganger' || (card.name === 'Ping' && card.isJunk)) && handleCardClick(card)}
                >
                  <div style={styles.cardCost}>{card.apCost}</div>
                  <div style={styles.cardName}>{card.name}</div>
                  <div style={styles.cardCategory}>{card.category.toUpperCase()}</div>
                  {isMovement && <div style={styles.cardRange}>
                    {gameState.player.isInVehicle ? (card.movementRange! * 2) : card.movementRange}m
                  </div>}
                  {card.name === 'Improvised Strike' && ambushAvailable && <div style={{ position: 'absolute', top: '3px', right: '3px', background: 'rgba(255, 50, 50, 0.3)', color: '#ff5050', fontFamily: theme.fonts.mono, fontSize: '7px', fontWeight: 700, letterSpacing: '0.5px', padding: '1px 3px', borderRadius: '2px', animation: 'pulse 1.5s ease-in-out infinite' }}>AMBUSH</div>}
                  {card.isBurn && !(card.name === 'Improvised Strike' && ambushAvailable) && <div style={{ position: 'absolute', top: '3px', right: '3px', background: 'rgba(255, 80, 50, 0.25)', color: '#ff5032', fontFamily: theme.fonts.mono, fontSize: '7px', fontWeight: 700, letterSpacing: '0.5px', padding: '1px 3px', borderRadius: '2px' }}>BURN</div>}
                  {card.isChain && <div style={{ position: 'absolute', top: '3px', right: '3px', background: 'rgba(68, 170, 255, 0.25)', color: '#44aaff', fontFamily: theme.fonts.mono, fontSize: '7px', fontWeight: 700, letterSpacing: '0.5px', padding: '1px 3px', borderRadius: '2px' }}>CHAIN</div>}
                  {card.isExtractRequired && <div style={{ position: 'absolute', top: card.isBurn || card.isChain ? '14px' : '3px', right: '3px', background: 'rgba(255, 204, 68, 0.25)', color: '#ffcc44', fontFamily: theme.fonts.mono, fontSize: '7px', fontWeight: 700, letterSpacing: '0.5px', padding: '1px 3px', borderRadius: '2px' }}>EXTRACT</div>}
                  {gameState.player.retainedCardIds.includes(card.id) && <div style={{ position: 'absolute', top: card.isBurn || card.isChain || card.isExtractRequired ? '14px' : '3px', right: '3px', background: 'rgba(68, 204, 136, 0.25)', color: '#44cc88', fontFamily: theme.fonts.mono, fontSize: '7px', fontWeight: 700, letterSpacing: '0.5px', padding: '1px 3px', borderRadius: '2px' }}>STASHED</div>}
                  {/* Show car icon instead of normal category icon when in vehicle and it's a movement card */}
                  {/* Category icon — bottom-left. Car icon replaces normal icon when in vehicle on movement cards */}
                  <div style={styles.cardCategoryIcon}>
                    {isMovement && gameState.player.isInVehicle ? (
                      <img src="./Icons/Transport-car.svg" alt="car" style={{ width: '18px', height: '18px', filter: 'brightness(0) invert(0.85) sepia(1) saturate(3) hue-rotate(180deg)' }} />
                    ) : (
                      <CardCategoryIcon category={card.category} size={16} />
                    )}
                  </div>
                  {/* Tooltip */}
                  <div className="card-tooltip">
                    <div className="card-tooltip__header">
                      <span className="card-tooltip__name">{card.name}</span>
                      <span className="card-tooltip__cost">{card.apCost} AP</span>
                    </div>
                    <div className="card-tooltip__category">{card.category.toUpperCase()}</div>
                    <div className="card-tooltip__desc">{card.description}</div>
                    <div className="card-tooltip__stats">
                      {card.name === 'Malware' && (
                        <div className="card-tooltip__stat">Active copies planted: {activeMalwareCount}</div>
                      )}
                      {card.movementRange && <div className="card-tooltip__stat">Range: {card.movementRange}m</div>}
                      {card.sweepRadius && <div className="card-tooltip__stat">Scan Radius: {(card.sweepRadius / 1000).toFixed(1)}km</div>}
                      {card.signalModifier !== undefined && card.signalModifier !== 1 && (
                        <div className="card-tooltip__stat">
                          Signal: {card.signalModifier === 0 ? 'None' : `${Math.round(card.signalModifier * 100)}%`}
                        </div>
                      )}
                      {card.condition && <div className="card-tooltip__stat">{card.condition}</div>}
                      {card.isExtractRequired && (
                        <div className="card-tooltip__stat" style={{ color: '#ffcc44' }}>
                          CONDITIONAL: Collect at least 2 passphrases this round to keep this card permanently. Otherwise it is lost at round end.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>

          {/* Discard pile */}
          <div style={styles.pile} onClick={() => setPileOverlay('discard')} title="View discard pile">
            <div style={styles.pileCount}>{gameState.player.discard.length}</div>
            <div style={styles.pileLabel}>DISCARD</div>
          </div>

          {/* Turn info + End Turn */}
          <div style={styles.turnPanel}>
            {/* C-SURV toggle — only visible when counter-surveillance card is played */}
            {counterSurveillanceActive && (
              <button
                className="action-btn"
                style={{
                  ...styles.signalToggle,
                  ...(showOwnSignals ? styles.cSurvToggleActive : styles.signalToggleInactive),
                }}
                onClick={() => setShowOwnSignals(prev => !prev)}
                title={showOwnSignals ? 'Hide your signal footprint' : 'Show your signal footprint'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" style={{ marginRight: '4px' }}>
                  <circle cx="8" cy="8" r="6" fill="none" stroke={showOwnSignals ? theme.colors.playerMarker : theme.colors.textDim} strokeWidth="1.5" />
                  <circle cx="8" cy="8" r="3" fill="none" stroke={showOwnSignals ? theme.colors.playerMarker : theme.colors.textDim} strokeWidth="1" opacity="0.6" />
                  <circle cx="8" cy="8" r="1" fill={showOwnSignals ? theme.colors.playerMarker : theme.colors.textDim} />
                </svg>
                <span>C-SURV</span>
              </button>
            )}
            {/* Signals always on — toggle and age counter removed */}
            <div style={styles.turnPanelRow}>
              <div style={styles.apDisplay}>
                AP <span style={styles.apValue}>{gameState.player.ap}/{gameState.player.maxAp}</span>
              </div>
              {gameState.player.isInVehicle && (
                <div style={{
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  color: '#f0c040',
                  letterSpacing: '1px',
                }}>
                  🚗 VEHICLE
                </div>
              )}
              {gameState.player.isInTaxi && (
                <div style={{
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  color: '#ffcc33',
                  letterSpacing: '1px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <img src="./Icons/Transport-taxi.svg" alt="taxi" style={{ width: '12px', height: '12px', filter: 'brightness(0) invert(0.9) sepia(1) saturate(5) hue-rotate(5deg)' }} />
                  TAXI
                </div>
              )}
              <div style={styles.turnDisplay}>
                TURN {gameState.turn}
              </div>
              {(gameState.player.detected || gameState.player.detectedTurnsRemaining > 0) && (
                <div style={{
                  fontSize: '11px',
                  color: '#ff2020',
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  letterSpacing: '2px',
                  animation: 'pulse 1s infinite',
                }}>
                  ⚠ SEEN ({gameState.player.detectedTurnsRemaining})
                </div>
              )}
            </div>
            {gameState.player.overwatch && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(255, 80, 80, 0.15)',
                  border: '1px solid rgba(255, 80, 80, 0.45)',
                  color: '#ff5050',
                  marginBottom: '6px',
                }}
                onClick={() => setGameState(prev => ({
                  ...prev,
                  player: { ...prev.player, overwatch: null },
                }))}
              >
                <span>EXIT OVERWATCH</span>
              </button>
            )}
            {gameState.player.isInVehicle && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(240, 192, 64, 0.15)',
                  border: '1px solid rgba(240, 192, 64, 0.4)',
                  marginBottom: '6px',
                }}
                onClick={() => setGameState(prev => ({
                  ...prev,
                  player: { ...prev.player, isInVehicle: false },
                }))}
              >
                <img src="./Icons/Transport-car.svg" alt="car" style={{ width: '16px', height: '16px', marginRight: '4px', filter: 'brightness(0) invert(0.8) sepia(1) saturate(5) hue-rotate(15deg)' }} />
                <span>EXIT VEHICLE</span>
              </button>
            )}
            {gameState.player.isInTaxi && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(255, 204, 51, 0.15)',
                  border: '1px solid rgba(255, 204, 51, 0.4)',
                  marginBottom: '6px',
                }}
                onClick={handleExitTaxi}
              >
                <img src="./Icons/Transport-taxi.svg" alt="taxi" style={{ width: '16px', height: '16px', marginRight: '4px', filter: 'brightness(0) invert(0.9) sepia(1) saturate(5) hue-rotate(5deg)' }} />
                <span>EXIT TAXI</span>
              </button>
            )}
            {gameState.player.isInSafehouse && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(68, 204, 136, 0.15)',
                  border: '1px solid rgba(68, 204, 136, 0.4)',
                  marginBottom: '6px',
                }}
                onClick={handleExitSafehouse}
              >
                <span>EXIT SAFEHOUSE</span>
              </button>
            )}
            {gameState.player.isInBuilding && !gameState.player.isInSafehouse && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(68, 204, 136, 0.15)',
                  border: '1px solid rgba(68, 204, 136, 0.4)',
                  marginBottom: '6px',
                }}
                onClick={handleExitBuilding}
              >
                <span>EXIT BUILDING</span>
              </button>
            )}
            {gameState.player.isElevated && !gameState.player.isInBuilding && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(0, 229, 255, 0.15)',
                  border: '1px solid rgba(0, 229, 255, 0.4)',
                  marginBottom: '6px',
                }}
                onClick={handleExitBuilding}
              >
                <span>DESCEND</span>
              </button>
            )}
            {gameState.player.isInTransit && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(170, 136, 255, 0.15)',
                  border: '1px solid rgba(170, 136, 255, 0.4)',
                  marginBottom: '6px',
                }}
                onClick={handleExitTransit}
              >
                <span>EXIT TRANSIT</span>
              </button>
            )}
            {!gameState.player.isInTransit && nearbyBoardingOptions.length > 0 && activeTransitType !== null && (
              <button
                className="action-btn"
                style={{
                  ...styles.endTurnButton,
                  background: 'rgba(170, 136, 255, 0.15)',
                  border: '1px solid rgba(170, 136, 255, 0.4)',
                  marginBottom: '6px',
                }}
                onClick={() => setBoardTransitOpen(true)}
              >
                <span>BOARD TRANSIT</span>
              </button>
            )}
            <button
              className={`end-turn-button${endTurnFlash ? ' end-turn-button--flash' : ''}`}
              style={styles.endTurnButton}
              onClick={handleEndTurn}
            >
              <span style={styles.endTurnX}>✕</span>
              <span>END TURN</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scavenge dialog */}
      {scavengeDialog && (
        <div style={styles.pileOverlayBackdrop} onClick={handleScavengeLeave}>
          <div
            style={{ ...styles.pileOverlayPanel, width: '400px' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={styles.pileOverlayHeader}>
              <span style={{
                ...styles.pileOverlayTitle,
                color:
                  scavengeDialog.pickupType === 'weapon' ? theme.colors.pickupWeapon :
                  scavengeDialog.pickupType === 'gadget' ? theme.colors.pickupGadget :
                  scavengeDialog.pickupType === 'lockpick' ? theme.colors.pickupLockpick :
                  scavengeDialog.pickupType === 'scanner' ? theme.colors.pickupScanner :
                  theme.colors.pickupMixed,
              }}>
                {scavengeDialog.pickupType === 'weapon' ? 'WEAPON FOUND' :
                 scavengeDialog.pickupType === 'gadget' ? 'GADGET FOUND' :
                 scavengeDialog.pickupType === 'lockpick' ? 'LOCKPICK FOUND' :
                 scavengeDialog.pickupType === 'scanner' ? 'SCANNER FOUND' :
                 'ITEM FOUND'}
              </span>
              <button style={styles.pileOverlayClose} onClick={handleScavengeLeave}>✕</button>
            </div>
            <div style={{ padding: '18px', fontFamily: theme.fonts.mono }}>
              {/* Location info */}
              <div style={{ fontSize: '10px', color: theme.colors.textDim, letterSpacing: '1px', marginBottom: '4px' }}>
                {scavengeDialog.category.toUpperCase()}
                {scavengeDialog.poiName ? ` — ${scavengeDialog.poiName}` : ''}
              </div>

              {/* The item */}
              {(() => {
                const itemColor = getCategoryColor(scavengeDialog.item.category);
                return (
              <div style={{
                background: `${itemColor}10`,
                border: `1px solid ${itemColor}50`,
                borderRadius: '6px',
                padding: '14px',
                marginTop: '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ItemIcon itemId={scavengeDialog.item.id} category={scavengeDialog.item.category} size={36} />
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: itemColor }}>
                      {scavengeDialog.item.name}
                    </div>
                    <div style={{ fontSize: '9px', color: theme.colors.textDim, letterSpacing: '1px', marginTop: '2px' }}>
                      {scavengeDialog.item.category.toUpperCase()}
                      {scavengeDialog.item.apCost > 0 ? ` — ${scavengeDialog.item.apCost} AP` : ''}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '8px', lineHeight: '1.5' }}>
                  {scavengeDialog.item.description}
                </div>
              </div>
                );
              })()}

              {/* Inventory state */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '10px', color: theme.colors.textDim, letterSpacing: '1px', marginBottom: '8px' }}>
                  INVENTORY ({gameState.player.items.length}/4)
                </div>
                {gameState.player.items.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {gameState.player.items.map((item, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        background: 'rgba(30, 38, 50, 0.8)',
                        borderRadius: '4px',
                        border: `1px solid ${getCategoryColor(item.category)}30`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ItemIcon itemId={item.id} category={item.category} size={20} />
                          <span style={{ fontSize: '11px', color: getCategoryColor(item.category), fontWeight: 600 }}>
                            {item.name}
                          </span>
                          <span style={{ fontSize: '9px', color: theme.colors.textDim }}>
                            {item.category}
                          </span>
                        </div>
                        {gameState.player.items.length >= getInventoryCap(runState) && (
                          <button
                            style={{
                              background: 'rgba(255, 50, 50, 0.15)',
                              border: '1px solid rgba(255, 50, 50, 0.4)',
                              borderRadius: '3px',
                              color: '#ff5050',
                              fontSize: '9px',
                              fontFamily: theme.fonts.mono,
                              padding: '3px 8px',
                              cursor: 'pointer',
                              letterSpacing: '1px',
                            }}
                            onClick={() => handleScavengeDrop(idx)}
                          >
                            DROP
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '10px', color: theme.colors.textDim }}>Empty</div>
                )}
              </div>

              {/* Action buttons */}
              {(() => {
                const invFull = gameState.player.items.length >= getInventoryCap(runState);
                return (
              <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                <button
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: invFull ? 'rgba(60, 60, 60, 0.5)' : 'rgba(255, 221, 68, 0.15)',
                    border: `1px solid ${invFull ? theme.colors.border : 'rgba(255, 221, 68, 0.5)'}`,
                    borderRadius: '6px',
                    color: invFull ? theme.colors.textDim : '#ffdd44',
                    fontFamily: theme.fonts.mono,
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '2px',
                    cursor: invFull ? 'not-allowed' : 'pointer',
                  }}
                  onClick={handleScavengeTake}
                  disabled={invFull}
                >
                  {invFull ? 'INVENTORY FULL' : 'TAKE'}
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'rgba(30, 38, 50, 0.8)',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '6px',
                    color: theme.colors.textSecondary,
                    fontFamily: theme.fonts.mono,
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '2px',
                    cursor: 'pointer',
                  }}
                  onClick={handleScavengeLeave}
                >
                  LEAVE
                </button>
              </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Deck / Discard pile overlay */}
      {pileOverlay && (() => {
        const cards = pileOverlay === 'deck' ? gameState.player.deck : gameState.player.discard;
        const title = pileOverlay === 'deck' ? 'DRAW PILE' : 'DISCARD PILE';
        return (
          <div
            style={styles.pileOverlayBackdrop}
            onClick={() => setPileOverlay(null)}
          >
            <div
              style={styles.pileOverlayPanel}
              onClick={e => e.stopPropagation()}
            >
              <div style={styles.pileOverlayHeader}>
                <span style={styles.pileOverlayTitle}>{title} ({cards.length})</span>
                <button
                  style={styles.pileOverlayClose}
                  onClick={() => setPileOverlay(null)}
                >
                  ✕
                </button>
              </div>
              {cards.length === 0 ? (
                <div style={styles.pileOverlayEmpty}>No cards</div>
              ) : (
                <div style={styles.pileOverlayGrid}>
                  {cards.map((card, idx) => {
                    const categoryColor =
                      card.category === 'movement' ? '#66ccff'
                      : card.category === 'intel' ? '#aa88ff'
                      : card.category === 'tactics' ? '#ffaa44'
                      : card.category === 'combat' ? '#ff5555'
                      : card.category === 'fieldcraft' ? '#44cc88'
                      : '#888';
                    return (
                      <div key={`${card.id}-${idx}`} style={{ ...styles.pileOverlayCard, borderColor: `${categoryColor}30` }}>
                        <div style={{
                          ...styles.pileOverlayCardCost,
                          background: '#ffcc00',
                        }}>
                          {card.apCost}
                        </div>
                        <div style={{ ...styles.pileOverlayCardName, paddingRight: '28px' }}>
                          {card.name}
                          <span style={{ position: 'absolute' as const, top: '10px', right: '30px', opacity: 0.7 }}>
                            <CardCategoryIcon category={card.category} size={14} />
                          </span>
                        </div>
                        <div style={{
                          ...styles.pileOverlayCardCategory,
                          color: categoryColor,
                        }}>
                          {card.category.toUpperCase()}
                        </div>
                        {(card.isBurn || card.isChain || card.isExtractRequired) && (
                          <div style={{ display: 'flex', gap: '4px', marginBottom: '2px', flexWrap: 'wrap' }}>
                            {card.isBurn && <span style={{ background: 'rgba(255, 80, 50, 0.2)', color: '#ff5032', fontFamily: theme.fonts.mono, fontSize: '8px', fontWeight: 700, letterSpacing: '1px', padding: '1px 5px', borderRadius: '3px', border: '1px solid rgba(255, 80, 50, 0.3)' }}>BURN</span>}
                            {card.isChain && <span style={{ background: 'rgba(68, 170, 255, 0.2)', color: '#44aaff', fontFamily: theme.fonts.mono, fontSize: '8px', fontWeight: 700, letterSpacing: '1px', padding: '1px 5px', borderRadius: '3px', border: '1px solid rgba(68, 170, 255, 0.3)' }}>CHAIN</span>}
                            {card.isExtractRequired && <span style={{ background: 'rgba(255, 204, 68, 0.2)', color: '#ffcc44', fontFamily: theme.fonts.mono, fontSize: '8px', fontWeight: 700, letterSpacing: '1px', padding: '1px 5px', borderRadius: '3px', border: '1px solid rgba(255, 204, 68, 0.3)' }}>EXTRACT</span>}
                          </div>
                        )}
                        <div style={styles.pileOverlayCardDesc}>{card.description}</div>
                        <div style={styles.pileOverlayCardStats}>
                          {card.movementRange && <span>Range: {card.movementRange}m</span>}
                          {card.sweepRadius && <span>Scan: {(card.sweepRadius / 1000).toFixed(1)}km</span>}
                          {card.signalModifier !== undefined && card.signalModifier !== 1 && (
                            <span>Signal: {card.signalModifier === 0 ? 'None' : `${Math.round(card.signalModifier * 100)}%`}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Decoy Radius Selection Modal */}
      {decoySelectOpen && selectedCard?.name === 'Decoy' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
        }}
        onClick={() => {
          setDecoySelectOpen(false);
          setSelectedCardId(null);
        }}
        >
          <div
            style={{
              background: theme.colors.surface,
              border: `1px solid rgba(255, 170, 68, 0.4)`,
              borderRadius: '12px',
              padding: '24px 28px',
              width: '360px',
              maxWidth: '90vw',
              fontFamily: theme.fonts.mono,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              fontSize: '11px',
              letterSpacing: '3px',
              color: theme.colors.textDim,
              marginBottom: '4px',
            }}>
              TACTICS
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#ffaa44',
              marginBottom: '16px',
            }}>
              DECOY — Select Radius
            </div>
            <div style={{ fontSize: '11px', color: theme.colors.textSecondary, lineHeight: '1.5', marginBottom: '18px' }}>
              Choose the size of the fake signal. Larger radius is less precise but covers more area.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Tiny', radius: 125, desc: 'Pinpoint blip — looks like an exact sighting' },
                { label: 'Small', radius: 250, desc: 'Tight blip — looks like a precise reading' },
                { label: 'Medium', radius: 500, desc: 'Standard signal — convincing and broad' },
                { label: 'Large', radius: 800, desc: 'Wide signal — draws attention across a district' },
              ].map(opt => (
                <button
                  key={opt.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: 'rgba(255, 170, 68, 0.06)',
                    border: `1px solid rgba(255, 170, 68, 0.25)`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: theme.fonts.mono,
                    color: '#ffaa44',
                    fontSize: '13px',
                    fontWeight: 600,
                    textAlign: 'left' as const,
                  }}
                  onClick={() => {
                    setDecoyRadius(opt.radius);
                    setDecoySelectOpen(false);
                  }}
                >
                  <div>
                    <div>{opt.label} ({opt.radius}m)</div>
                    <div style={{ fontSize: '9px', color: theme.colors.textDim, fontWeight: 400, marginTop: '2px' }}>
                      {opt.desc}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <button
              style={{
                width: '100%',
                marginTop: '14px',
                padding: '10px',
                background: 'rgba(30, 38, 50, 0.8)',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: theme.fonts.mono,
                color: theme.colors.textSecondary,
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '2px',
              }}
              onClick={() => {
                setDecoySelectOpen(false);
                setSelectedCardId(null);
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* Taxi confirm modal — shows the route preview distance and lets the
          player confirm or pick a different destination. Card isn't consumed
          until confirm. */}
      {taxiPendingRoute && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
        }}
        onClick={handleTaxiCancel}
        >
          <div
            style={{
              background: theme.colors.surface,
              border: `1px solid rgba(255, 204, 51, 0.45)`,
              borderRadius: '12px',
              padding: '24px 28px',
              width: '380px',
              maxWidth: '90vw',
              fontFamily: theme.fonts.mono,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              fontSize: '11px',
              letterSpacing: '3px',
              color: theme.colors.textDim,
              marginBottom: '4px',
            }}>
              TACTICS
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#ffcc33',
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <img src="./Icons/Transport-taxi.svg" alt="taxi" style={{ width: '22px', height: '22px', filter: 'brightness(0) invert(0.9) sepia(1) saturate(5) hue-rotate(5deg)' }} />
              HAIL TAXI
            </div>
            <div style={{ fontSize: '11px', color: theme.colors.textSecondary, lineHeight: '1.6', marginBottom: '14px' }}>
              Destination: <span style={{ color: '#ffcc33' }}>{taxiPendingRoute.destinationLabel}</span>
            </div>
            <div style={{
              fontSize: '10px',
              color: theme.colors.textDim,
              lineHeight: '1.5',
              background: 'rgba(255, 204, 51, 0.05)',
              border: '1px solid rgba(255, 204, 51, 0.15)',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '16px',
            }}>
              Auto-drives <strong>{tuning.taxiSpeedPerTurn}m</strong> per turn along roads. Estimated{' '}
              <strong>{Math.max(1, Math.ceil(taxiPendingRoute.path.totalDistance / tuning.taxiSpeedPerTurn))}</strong> turn(s).
              Generates more signal than walking, less than a hotwired car. Exit any time.
              Cost: <strong>{selectedCard?.apCost ?? 3} AP</strong>.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="action-btn"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(255, 204, 51, 0.15)',
                  border: '1px solid rgba(255, 204, 51, 0.45)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: theme.fonts.mono,
                  color: '#ffcc33',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '2px',
                }}
                onClick={handleTaxiConfirm}
              >
                CONFIRM
              </button>
              <button
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(30, 38, 50, 0.8)',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: theme.fonts.mono,
                  color: theme.colors.textSecondary,
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '2px',
                }}
                onClick={handleTaxiCancel}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Combat Encounter Modal — hidden during reveal phase so the
          player can still see the agent's position on the map while the
          message overlay above the hand announces contact. The modal
          takes over once the phase auto-advances to 'action' (or
          immediately if a pre-selected weapon skips reveal). */}
      {combatEncounter && combatEncounter.phase !== 'reveal' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: theme.colors.surface,
            border: `2px solid ${
              combatEncounter.outcome === 'player-kills' ? '#44ff44'
              : combatEncounter.outcome === 'player-dies' ? '#ff3030'
              : combatEncounter.outcome === 'player-escapes' ? '#ffaa44'
              : combatEncounter.outcome === 'player-saved' ? '#ffdd44'
              : '#ff5050' // ambush — no outcome yet
            }`,
            borderRadius: '12px',
            padding: '0',
            width: '440px',
            maxWidth: '90vw',
            fontFamily: theme.fonts.mono,
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              background: combatEncounter.outcome === 'player-kills' ? 'rgba(68, 255, 68, 0.08)'
                : combatEncounter.outcome === 'player-dies' ? 'rgba(255, 48, 48, 0.08)'
                : combatEncounter.outcome === 'player-escapes' ? 'rgba(255, 170, 68, 0.08)'
                : combatEncounter.outcome === 'player-saved' ? 'rgba(255, 221, 68, 0.08)'
                : 'rgba(255, 80, 80, 0.08)',
              borderBottom: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                fontSize: '11px',
                letterSpacing: '3px',
                color: theme.colors.textDim,
                marginBottom: '4px',
              }}>
                {combatEncounter.phase === 'action'
                  ? (combatEncounter.scenario === 'ambush' ? 'AMBUSH OPPORTUNITY'
                    : combatEncounter.scenario === 'confrontation' ? 'CONFRONTATION'
                    : 'FIGHT BACK')
                  : combatEncounter.scenario === 'ambush' ? 'AMBUSH'
                  : combatEncounter.scenario === 'confrontation' ? 'CONFRONTATION'
                  : 'AMBUSHED'}
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: 700,
                color: combatEncounter.outcome === 'player-kills' ? '#44ff44'
                  : combatEncounter.outcome === 'player-dies' ? '#ff3030'
                  : combatEncounter.outcome === 'player-escapes' ? '#ffaa44'
                  : combatEncounter.outcome === 'player-saved' ? '#ffdd44'
                  : '#ff5050',
              }}>
                {combatEncounter.phase === 'action' && combatEncounter.scenario === 'ambush' ? 'You have the drop.' :
                 combatEncounter.phase === 'action' && combatEncounter.scenario === 'confrontation' ? 'Both aware. Choose your move.' :
                 combatEncounter.phase === 'action' && combatEncounter.scenario === 'ambushed' ? 'The agent jumped you. Fight back!' :
                 !combatEncounter.outcome ? 'Engaging...' :
                 combatEncounter.outcome === 'player-kills' ? 'TARGET NEUTRALIZED' :
                 combatEncounter.outcome === 'player-dies' ? 'YOU HAVE BEEN ELIMINATED' :
                 combatEncounter.outcome === 'player-escapes' ? 'NARROW ESCAPE' :
                 'SURVIVED'}
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '20px' }}>
              {combatEncounter.phase === 'action' ? (
                <div>
                  <div style={{ fontSize: '12px', color: theme.colors.textSecondary, lineHeight: '1.6', marginBottom: '8px' }}>
                    {combatEncounter.scenario === 'ambush'
                      ? 'The agent doesn\'t see you. Choose your attack:'
                      : combatEncounter.scenario === 'confrontation'
                      ? 'You\'ve both made visual contact. Choose how to engage:'
                      : 'The agent got the drop on you, but they\'re unarmed. Fight back:'}
                  </div>
                  {combatEncounter.scenario !== 'ambush' && (
                    <div style={{
                      fontSize: '10px', fontFamily: theme.fonts.mono, letterSpacing: '1px',
                      padding: '4px 8px', borderRadius: '3px', marginBottom: '12px', display: 'inline-block',
                      ...(combatEncounter.agentWeapon
                        ? { color: '#ff8888', background: 'rgba(255,50,50,0.15)', border: '1px solid rgba(255,50,50,0.3)' }
                        : { color: '#88cc88', background: 'rgba(68,204,136,0.15)', border: '1px solid rgba(68,204,136,0.3)' }),
                    }}>
                      AGENT: {combatEncounter.agentWeapon ? combatEncounter.agentWeapon.name.toUpperCase() : 'UNARMED'}
                    </div>
                  )}
                  {(() => {
                    // Compute whether the player has any viable attack option.
                    // "Viable" = a weapon that passes canAmbushWith for this
                    // position, OR Improvised Strike in hand, OR a flashbang
                    // (shown as its own escape button). If none of those, the
                    // fallback "Fight to the Death" button appears so the
                    // player always has a clickable action — encounters are
                    // never dismissable.
                    const weapons = gameState.player.items.filter(i => i.category === 'weapon');
                    const hasValidWeapon = weapons.some(w => canAmbushWith(w, gameState.player).valid);
                    // Improvised Strike is melee — unusable from a rooftop.
                    // Mirrors the rule for pistols/knives: descend to fight.
                    const hasImprovisedStrike =
                      !gameState.player.isElevated &&
                      gameState.player.hand.some(c => c.name === 'Improvised Strike');
                    const flashbang = gameState.player.items.find(i => i.id.startsWith('flashbang'));
                    const hasViableOption = hasValidWeapon || hasImprovisedStrike || !!flashbang;
                    return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Weapon items */}
                    {weapons.map(weapon => {
                      const check = canAmbushWith(weapon, gameState.player);
                      return (
                        <button
                          key={weapon.id}
                          disabled={!check.valid}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            background: check.valid ? 'rgba(255, 80, 80, 0.1)' : 'rgba(60, 60, 60, 0.3)',
                            border: `1px solid ${check.valid ? 'rgba(255, 80, 80, 0.4)' : theme.colors.border}`,
                            borderRadius: '8px',
                            cursor: check.valid ? 'pointer' : 'not-allowed',
                            fontFamily: theme.fonts.mono,
                            color: check.valid ? '#ff5050' : theme.colors.textDim,
                            fontSize: '13px',
                            fontWeight: 700,
                            textAlign: 'left' as const,
                          }}
                          onClick={() => {
                            if (!check.valid) return;
                            let encounter: CombatEncounter;
                            if (combatEncounter.scenario === 'ambush') {
                              encounter = resolveAmbush(weapon, false);
                            } else if (combatEncounter.scenario === 'ambushed') {
                              // Armed player defending against unarmed agent
                              // ambush: route to resolveAmbushed so we get
                              // the weapon-advantage +0.5 bonus + competitive
                              // roll against agent.prepBonus. Previously this
                              // fell into resolveConfrontationWithChoice whose
                              // "armed vs unarmed = auto-win no roll" branch
                              // skipped all the combat math.
                              encounter = resolveAmbushed(gameState.player, gameState.agent);
                            } else {
                              encounter = resolveConfrontationWithChoice(weapon, false, 0, gameState.player, gameState.agent);
                            }
                            setCombatEncounter(encounter);
                            if (encounter.vestExhausted || encounter.flashbangUsed) {
                              setGameState(gs => ({
                                ...gs,
                                player: { ...gs.player, items: applyCombatItemEffects(gs.player.items, encounter) },
                              }));
                            }
                            if (encounter.outcome === 'player-kills') {
                              if (killEndsRoundRef.current) {
                                setGameState(gs => ({ ...gs, roundResult: 'kill' }));
                              } else {
                                // Extraction op: agent dies but round continues.
                                setGameState(gs => ({ ...gs, agent: { ...gs.agent, isDead: true } }));
                              }
                            } else if (encounter.outcome === 'player-dies') {
                              setGameState(gs => ({ ...gs, roundResult: 'death' }));
                            }
                          }}
                        >
                          <ItemIcon itemId={weapon.id} category="weapon" size={28} />
                          <div>
                            <div>{weapon.name}</div>
                            {!check.valid && (
                              <div style={{ fontSize: '9px', color: '#ff8844', fontWeight: 400, marginTop: '2px' }}>
                                {check.reason}
                              </div>
                            )}
                          </div>
                          <div style={{ marginLeft: 'auto', fontSize: '10px', letterSpacing: '2px' }}>
                            {combatEncounter.scenario === 'ambush' ? 'AMBUSH' : 'ATTACK'}
                          </div>
                        </button>
                      );
                    })}
                    {/* Improvised Strike option if card is in hand and the
                        player is at melee range (not elevated). Rooftop
                        positions hide this option since the player can't
                        reach a ground-level agent with a hand strike. */}
                    {!gameState.player.isElevated && gameState.player.hand.some(c => c.name === 'Improvised Strike') && (
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          background: 'rgba(255, 80, 80, 0.06)',
                          border: `1px solid rgba(255, 80, 80, 0.25)`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontFamily: theme.fonts.mono,
                          color: '#ff8866',
                          fontSize: '13px',
                          fontWeight: 700,
                          textAlign: 'left' as const,
                        }}
                        onClick={() => {
                          let encounter: CombatEncounter;
                          if (combatEncounter.scenario === 'ambush') {
                            encounter = resolveAmbush(null, true, gameState.player.prepBonus, gameState.agent, gameState.player);
                          } else if (combatEncounter.scenario === 'ambushed') {
                            // Unarmed player + Improvised Strike against
                            // unarmed-agent ambush: resolveAmbushed handles
                            // the competitive roll (no weapon-advantage
                            // bonus because player is unarmed, but the
                            // Improvised Strike in hand means the player
                            // has a prep bonus baked in via prepBonus).
                            encounter = resolveAmbushed(gameState.player, gameState.agent);
                          } else {
                            encounter = resolveConfrontationWithChoice(null, true, gameState.player.prepBonus, gameState.player, gameState.agent);
                          }
                          setCombatEncounter(encounter);
                          const card = gameState.player.hand.find(c => c.name === 'Improvised Strike');
                          if (card) {
                            setGameState(gs => ({
                              ...gs,
                              player: {
                                ...gs.player,
                                ...routePlayedCard(gs.player, card),
                                ap: gs.player.ap - card.apCost,
                              },
                            }));
                          }
                          if (encounter.vestExhausted || encounter.flashbangUsed) {
                            setGameState(gs => ({
                              ...gs,
                              player: { ...gs.player, items: applyCombatItemEffects(gs.player.items, encounter) },
                            }));
                          }
                          if (encounter.outcome === 'player-kills') {
                            if (killEndsRoundRef.current) {
                              setGameState(gs => ({ ...gs, roundResult: 'kill' }));
                            } else {
                              setGameState(gs => ({ ...gs, agent: { ...gs.agent, isDead: true } }));
                            }
                          } else if (encounter.outcome === 'player-dies') {
                            setGameState(gs => ({ ...gs, roundResult: 'death' }));
                          }
                        }}
                      >
                        <CardCategoryIcon category="combat" size={28} />
                        <div>
                          <div>Improvised Strike</div>
                          <div style={{ fontSize: '9px', color: theme.colors.textDim, fontWeight: 400, marginTop: '2px' }}>
                            {Math.round(Math.min(1, gameState.player.prepBonus) * 100)}% chance — build Prep to improve
                          </div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: '10px', letterSpacing: '2px' }}>
                          {combatEncounter.scenario === 'ambush' ? 'AMBUSH' : 'STRIKE'}
                        </div>
                      </button>
                    )}
                    {/* Flashbang — always auto-escapes. Explicit button so the
                        player sees it as a valid action (especially when it's
                        their only option). Clicking routes through the unarmed
                        resolver which consumes the flashbang internally. */}
                    {flashbang && (
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          background: 'rgba(255, 221, 68, 0.10)',
                          border: '1px solid rgba(255, 221, 68, 0.4)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontFamily: theme.fonts.mono,
                          color: '#ffdd44',
                          fontSize: '13px',
                          fontWeight: 700,
                          textAlign: 'left' as const,
                        }}
                        onClick={() => {
                          // resolveConfrontationWithChoice short-circuits on
                          // hasItem(player.items, 'flashbang') and returns
                          // player-escapes with flashbangUsed=true. Same
                          // resolver works for all non-ambush scenarios.
                          const encounter = resolveConfrontationWithChoice(null, false, 0, gameState.player, gameState.agent);
                          setCombatEncounter(encounter);
                          if (encounter.vestExhausted || encounter.flashbangUsed) {
                            setGameState(gs => ({
                              ...gs,
                              player: { ...gs.player, items: applyCombatItemEffects(gs.player.items, encounter) },
                            }));
                          }
                        }}
                      >
                        <ItemIcon itemId={flashbang.id} category={flashbang.category} size={28} />
                        <div>
                          <div>Flashbang</div>
                          <div style={{ fontSize: '9px', color: theme.colors.textDim, fontWeight: 400, marginTop: '2px' }}>
                            Guaranteed escape. Consumed on use.
                          </div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: '10px', letterSpacing: '2px' }}>
                          ESCAPE
                        </div>
                      </button>
                    )}
                    {/* Last-resort fight button — shows whenever no other viable
                        option exists (no valid weapons, no Improvised Strike
                        in hand, no flashbang). Labeled "Fight to the Death"
                        because it's what you do when you're out of good
                        choices. Vest may still save the player vs pistol. */}
                    {!hasViableOption && (
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          background: 'rgba(255, 160, 80, 0.08)',
                          border: `1px solid rgba(255, 160, 80, 0.3)`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontFamily: theme.fonts.mono,
                          color: '#ffaa55',
                          fontSize: '13px',
                          fontWeight: 700,
                          textAlign: 'left' as const,
                        }}
                        onClick={() => {
                          // Route ambushed scenarios through resolveAmbushed
                          // so the unarmed-vs-unarmed competitive roll runs.
                          // resolveConfrontationWithChoice handles the other
                          // two scenarios (confrontation / ambush).
                          const encounter = combatEncounter.scenario === 'ambushed'
                            ? resolveAmbushed(gameState.player, gameState.agent)
                            : resolveConfrontationWithChoice(null, false, 0, gameState.player, gameState.agent);
                          setCombatEncounter(encounter);
                          if (encounter.vestExhausted || encounter.flashbangUsed) {
                            setGameState(gs => ({
                              ...gs,
                              player: { ...gs.player, items: applyCombatItemEffects(gs.player.items, encounter) },
                            }));
                          }
                          if (encounter.outcome === 'player-kills') {
                            if (killEndsRoundRef.current) {
                              setGameState(gs => ({ ...gs, roundResult: 'kill' }));
                            } else {
                              setGameState(gs => ({ ...gs, agent: { ...gs.agent, isDead: true } }));
                            }
                          } else if (encounter.outcome === 'player-dies') {
                            setGameState(gs => ({ ...gs, roundResult: 'death' }));
                          }
                        }}
                      >
                        <span style={{ fontSize: '24px' }}>👊</span>
                        <div>
                          <div>Fight to the Death</div>
                          <div style={{ fontSize: '9px', color: theme.colors.textDim, fontWeight: 400, marginTop: '2px' }}>
                            Unarmed — {Math.round(Math.min(0.9, gameState.player.prepBonus ?? 0) * 100)}% chance (prep-based). Vest may absorb a pistol shot.
                          </div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: '10px', letterSpacing: '2px' }}>
                          FIGHT
                        </div>
                      </button>
                    )}
                  </div>
                  );
                  })()}
                </div>
              ) : (
                /* Resolved combat — show outcome */
                <div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                    lineHeight: '1.7',
                    marginBottom: '16px',
                  }}>
                    {combatEncounter.message}
                  </div>

                  {/* Roll display — show prep stats and dice rolls */}
                  {combatEncounter.rolls && combatEncounter.rolls.length > 0 && (
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '12px 14px',
                      marginBottom: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}>
                      {/* Prep stats header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                      }}>
                        {combatEncounter.playerPrep !== undefined && (
                          <div style={{ textAlign: 'center' as const }}>
                            <div style={{ fontSize: '8px', letterSpacing: '1.5px', color: theme.colors.textDim, marginBottom: '2px' }}>
                              YOUR CHANCE
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#44aaff' }}>
                              {Math.round(combatEncounter.playerPrep * 100)}%
                            </div>
                            <div style={{ fontSize: '8px', color: theme.colors.textDim, marginTop: '2px' }}>
                              Roll under to hit
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: '10px', color: theme.colors.textDim, alignSelf: 'center' }}>vs</div>
                        {combatEncounter.agentPrep !== undefined && (
                          <div style={{ textAlign: 'center' as const }}>
                            <div style={{ fontSize: '8px', letterSpacing: '1.5px', color: theme.colors.textDim, marginBottom: '2px' }}>
                              AGENT CHANCE
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#ff5050' }}>
                              {Math.round(combatEncounter.agentPrep * 100)}%
                            </div>
                            <div style={{ fontSize: '8px', color: theme.colors.textDim, marginTop: '2px' }}>
                              Roll under to hit
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Roll log — show rounds */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <div style={{ fontSize: '9px', letterSpacing: '1px', color: '#44aaff' }}>YOU ROLLED</div>
                        <div style={{ fontSize: '9px', letterSpacing: '1px', color: theme.colors.textDim }}>ROUND</div>
                        <div style={{ fontSize: '9px', letterSpacing: '1px', color: '#ff5050' }}>AGENT ROLLED</div>
                      </div>
                      {(() => {
                        const rolls = combatEncounter.rolls!;
                        // Group into rounds of 2 (player, agent)
                        const rounds: { player?: typeof rolls[0]; agent?: typeof rolls[0] }[] = [];
                        for (let i = 0; i < rolls.length; i++) {
                          const r = rolls[i];
                          if (r.side === 'player') {
                            rounds.push({ player: r });
                          } else if (rounds.length > 0 && !rounds[rounds.length - 1].agent) {
                            rounds[rounds.length - 1].agent = r;
                          } else {
                            rounds.push({ agent: r });
                          }
                        }
                        return rounds.map((round, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 0',
                            borderBottom: idx < rounds.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                          }}>
                            {/* Player roll */}
                            <div style={{ flex: 1, textAlign: 'left' as const }}>
                              {round.player ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <div style={{
                                    width: '8px', height: '8px', borderRadius: '50%',
                                    background: round.player.success ? '#44ff44' : '#ff3030',
                                  }} />
                                  <span style={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    fontFamily: theme.fonts.mono,
                                    color: round.player.success ? '#44ff44' : '#ff3030',
                                  }}>
                                    {(round.player.roll * 100).toFixed(0)}
                                  </span>
                                  <span style={{ fontSize: '9px', color: theme.colors.textDim }}>
                                    {round.player.success ? '✓' : '✗'} need &lt;{Math.round(round.player.chance * 100)}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ fontSize: '9px', color: theme.colors.textDim }}>—</span>
                              )}
                            </div>
                            {/* Round number */}
                            <div style={{ fontSize: '9px', color: theme.colors.textDim, width: '30px', textAlign: 'center' as const }}>
                              R{idx + 1}
                            </div>
                            {/* Agent roll */}
                            <div style={{ flex: 1, textAlign: 'right' as const }}>
                              {round.agent ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                                  <span style={{ fontSize: '9px', color: theme.colors.textDim }}>
                                    need &lt;{Math.round(round.agent.chance * 100)} {round.agent.success ? '✓' : '✗'}
                                  </span>
                                  <span style={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    fontFamily: theme.fonts.mono,
                                    color: round.agent.success ? '#ff3030' : '#44ff44',
                                  }}>
                                    {(round.agent.roll * 100).toFixed(0)}
                                  </span>
                                  <div style={{
                                    width: '8px', height: '8px', borderRadius: '50%',
                                    background: round.agent.success ? '#ff3030' : '#44ff44',
                                  }} />
                                </div>
                              ) : (
                                <span style={{ fontSize: '9px', color: theme.colors.textDim }}>—</span>
                              )}
                            </div>
                          </div>
                        ));
                      })()}
                      <div style={{ fontSize: '8px', color: theme.colors.textDim, marginTop: '6px', fontStyle: 'italic' }}>
                        Roll under prep % = success. Green = good for you.
                      </div>
                    </div>
                  )}

                  {combatEncounter.vestExhausted && (
                    <div style={{ fontSize: '10px', color: '#ffdd44', marginBottom: '8px' }}>
                      ⚠ Bulletproof vest destroyed
                    </div>
                  )}
                  {combatEncounter.flashbangUsed && (
                    <div style={{ fontSize: '10px', color: '#ffaa44', marginBottom: '8px' }}>
                      ⚠ Flashbang consumed
                    </div>
                  )}
                  <button
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: combatEncounter.outcome === 'player-kills' ? 'rgba(68, 255, 68, 0.15)'
                        : combatEncounter.outcome === 'player-dies' ? 'rgba(255, 48, 48, 0.15)'
                        : 'rgba(255, 170, 68, 0.15)',
                      border: `1px solid ${
                        combatEncounter.outcome === 'player-kills' ? 'rgba(68, 255, 68, 0.4)'
                        : combatEncounter.outcome === 'player-dies' ? 'rgba(255, 48, 48, 0.4)'
                        : 'rgba(255, 170, 68, 0.4)'
                      }`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontFamily: theme.fonts.mono,
                      color: combatEncounter.outcome === 'player-kills' ? '#44ff44'
                        : combatEncounter.outcome === 'player-dies' ? '#ff3030'
                        : '#ffaa44',
                      fontSize: '13px',
                      fontWeight: 700,
                      letterSpacing: '2px',
                    }}
                    onClick={() => {
                      setCombatEncounter(null);
                      // Keep agent visible for a few turns after combat
                      setGameState(gs => ({
                        ...gs,
                        agent: {
                          ...gs.agent,
                          detectedTurnsRemaining: Math.max(gs.agent.detectedTurnsRemaining ?? 0, 3),
                        },
                      }));
                    }}
                  >
                    {combatEncounter.outcome === 'player-kills' ? 'CONTINUE'
                      : combatEncounter.outcome === 'player-dies' ? 'GAME OVER'
                      : 'CONTINUE'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fieldcraft card picker modal */}
      {pendingAction && (() => {
        const p = gameState.player;
        // Escape / click-outside closes the picker and clears the pending
        // action. The fieldcraft card has already been consumed (prePlay
        // ran before the picker opened), so cancelling just wastes it —
        // matches tabletop semantics and prevents the player from being
        // trapped in a picker they didn't mean to open.
        const cancelPicker = () => setPendingAction(null);
        switch (pendingAction.type) {
          case 'chain-discard':
            return (
              <CardPicker
                title="CHAIN — Discard 1"
                subtitle="Choose a card to discard, then draw 1."
                cards={p.hand}
                onCancel={cancelPicker}
                onPick={(card) => {
                  const result = resolveChainDiscard(gameState, card);
                  setGameState(result.newState);
                  setPendingAction(null);
                  if (result.notification) {
                    notify(result.notification, 'general');
                  }
                }}
              />
            );
          case 'adapt-discard':
            return (
              <CardPicker
                title="ADAPT — Discard 1"
                subtitle="Choose a card to discard, then draw 3."
                cards={p.hand}
                onCancel={cancelPicker}
                onPick={(card) => {
                  const result = resolveAdaptDiscard(gameState, card);
                  setGameState(result.newState);
                  setPendingAction(null);
                  if (result.notification) {
                    notify(result.notification, 'general');
                  }
                }}
              />
            );
          case 'copy-pick':
            return (
              <CardPicker
                title="COPY — Choose Card"
                subtitle="Choose a card in your hand. A duplicate is added to your discard pile."
                cards={p.hand}
                onCancel={cancelPicker}
                onPick={(card) => {
                  const result = resolveCopyPick(gameState, card);
                  setGameState(result.newState);
                  setPendingAction(null);
                  if (result.notification) {
                    notify(result.notification, 'general');
                  }
                }}
              />
            );
          case 'double-tap-pick':
            return (
              <CardPicker
                title="DOUBLE TAP — Choose Card"
                subtitle="Choose a card to duplicate. The copy costs 0 AP and burns at end of turn."
                // Exclude the Double Tap card itself from the picker so
                // the player can't accidentally select it (which would
                // create a DT copy of DT — weird recursion). DT card is
                // still in hand because prePlay is deferred until pick.
                cards={p.hand.filter(c => c.id !== pendingAction.cardPlayed.id)}
                onCancel={cancelPicker}
                onPick={(card) => {
                  // Functional setState so resolveDoubleTapPick sees the
                  // freshest state snapshot. Without this, an Improvise
                  // pick that landed in the same tick wouldn't be in the
                  // closure-captured hand, and the DT copy would be made
                  // from a stale card reference.
                  let note: string | undefined;
                  setGameState(prev => {
                    const result = resolveDoubleTapPick(prev, card, pendingAction.cardPlayed);
                    note = result.notification;
                    return result.newState;
                  });
                  setPendingAction(null);
                  if (note) notify(note, 'general');
                }}
              />
            );
          case 'recall-pick':
            return (
              <CardPicker
                title="RECALL — Choose from Discard"
                subtitle="Choose a card from your discard pile to add to your hand."
                cards={p.discard}
                onCancel={cancelPicker}
                onPick={(card) => {
                  const result = resolveRecallPick(gameState, card);
                  setGameState(result.newState);
                  setPendingAction(null);
                  if (result.notification) {
                    notify(result.notification, 'general');
                  }
                }}
              />
            );
          case 'stash-pick':
            return (
              <CardPicker
                title="STASH — Choose Card to Retain"
                subtitle="Choose a card to keep in your hand at end of turn."
                cards={p.hand}
                onCancel={cancelPicker}
                onPick={(card) => {
                  const result = resolveStashPick(gameState, card);
                  setGameState(result.newState);
                  setPendingAction(null);
                  if (result.notification) {
                    notify(result.notification, 'general');
                  }
                }}
              />
            );
          case 'field-modify-pick':
            return (
              <CardPicker
                title="FIELD MODIFY — Reduce AP Cost"
                subtitle="Choose a card to reduce its AP cost by 1 (min 0)."
                cards={p.hand}
                onCancel={cancelPicker}
                onPick={(card) => {
                  const result = resolveFieldModifyPick(gameState, card);
                  setGameState(result.newState);
                  setPendingAction(null);
                  if (result.notification) {
                    notify(result.notification, 'general');
                  }
                }}
              />
            );
          case 'overclock-pick':
            return (
              <CardPicker
                title="OVERCLOCK — Double Range"
                subtitle="Choose a movement card to double its range."
                cards={p.hand.filter(c => !!c.movementRange)}
                onCancel={cancelPicker}
                onPick={(card) => {
                  const result = resolveOverclockPick(gameState, card);
                  setGameState(result.newState);
                  setPendingAction(null);
                  if (result.notification) {
                    notify(result.notification, 'general');
                  }
                }}
              />
            );
          case 'improvise-pick':
            return (
              <CardPicker
                title="IMPROVISE — Choose 1 of 3"
                subtitle="Pick 1 card to add to your hand. The rest go back on top of your deck."
                cards={pendingAction.topCards}
                onCancel={cancelPicker}
                onPick={(card) => {
                  // Functional setState — any queued updates from the same
                  // tick (e.g. visual-contact seenAgentTurnsLeft, or
                  // concurrent item pickups) are preserved rather than
                  // being clobbered by a stale newState replacement.
                  let note: string | undefined;
                  setGameState(prev => {
                    const result = resolveImprovisePick(prev, card, pendingAction.topCards);
                    note = result.notification;
                    return result.newState;
                  });
                  setPendingAction(null);
                  if (note) notify(note, 'general');
                }}
              />
            );
          default:
            return null;
        }
      })()}

      {/* Item used animation */}
      {itemUsedAnim && (
        <div className="item-used-anim" key={itemUsedAnim.key} style={{
          top: `calc(50% + ${(itemUsedAnim.slotIndex - 1.5) * 54}px)`,
        }}>
          <div className="item-used-anim__icon">
            <img
              src={`/Icons/Items-${
                itemUsedAnim.iconId === 'radio-scanner' ? 'icons8-walkie-talkie'
                : itemUsedAnim.iconId === 'burner-phone' ? 'brner-phone'
                : itemUsedAnim.iconId
              }.svg`}
              alt={itemUsedAnim.name}
              style={{ width: '32px', height: '32px', filter: 'brightness(0) invert(1) opacity(0.8)' }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
          <div className="item-used-anim__text">{itemUsedAnim.name} used</div>
        </div>
      )}

      {/* Combat reveal overlay — a short beat before the full combat modal
          takes over. Lets the player parse where the agent is on the map
          before the screen dims out. Clickable to skip to the weapon picker. */}
      {combatEncounter?.phase === 'reveal' && (
        <div
          style={{
            ...styles.messageOverlay,
            ...styles.messageOverlayAgent,
            cursor: 'pointer',
            pointerEvents: 'auto' as const,
            animation: 'pulse 1s ease-in-out infinite',
          }}
          onClick={advanceCombatPhase}
        >
          <span style={{ ...styles.messageOverlayText, fontWeight: 700, letterSpacing: '1.5px' }}>
            {combatEncounter.revealText ?? 'Contact!'}
          </span>
          {combatEncounter.agentWeapon && (
            <span style={{
              fontSize: '10px',
              fontFamily: theme.fonts.mono,
              letterSpacing: '1px',
              padding: '2px 8px',
              borderRadius: '3px',
              background: 'rgba(255, 80, 80, 0.15)',
              border: '1px solid rgba(255, 80, 80, 0.35)',
              color: '#ffb3b3',
            }}>
              {combatEncounter.agentWeapon.name.toUpperCase()}
            </span>
          )}
        </div>
      )}

      {/* Game notification — message overlay above the hand. Variant drives
          the color (agent = red, intel = purple, general = blue). Hidden
          while a movement hint or combat reveal is showing so they don't
          fight for the same position. */}
      {gameNotification && combatEncounter?.phase !== 'reveal' && !(movementMode && movementPreview?.nearFootpath) && !(movementMode && altMovementMode && altRoutesCount > 0) && (
        <div style={{
          ...styles.messageOverlay,
          ...(gameNotification.variant === 'agent' ? styles.messageOverlayAgent :
              gameNotification.variant === 'intel' ? styles.messageOverlayIntel :
              styles.messageOverlayGeneral),
        }}>
          <span style={styles.messageOverlayText}>{gameNotification.text}</span>
        </div>
      )}

      {/* Footpath hint — generic message overlay with general (blue) variant */}
      {movementMode && !altMovementMode && movementPreview?.nearFootpath && (
        <div style={{ ...styles.messageOverlay, ...styles.messageOverlayGeneral }}>
          <kbd style={styles.messageOverlayKbd}>SHIFT</kbd>
          <span style={styles.messageOverlayText}>Hold to use footpaths</span>
        </div>
      )}

      {/* Alt movement route selection hint */}
      {movementMode && altMovementMode && altRoutesCount > 0 && (
        <div style={{ ...styles.messageOverlay, ...styles.messageOverlayGeneral }}>
          <span style={styles.messageOverlayText}>Hover a path and click to select</span>
        </div>
      )}

      {/* Recon Plane placement hint — shown while in placement mode with no pending placement */}
      {reconPlaneMode && !reconPlanePending && (
        <div style={{ ...styles.messageOverlay, ...styles.messageOverlayIntel }}>
          <span style={styles.messageOverlayText}>Click and drag to place &amp; rotate the recon strip</span>
        </div>
      )}

      {/* Stakeout placement hints */}
      {stakeoutMode && !stakeoutPendingBuilding && (
        <div style={{ ...styles.messageOverlay, ...styles.messageOverlayIntel }}>
          <span style={styles.messageOverlayText}>Click a commercial building to place your stakeout</span>
        </div>
      )}
      {stakeoutMode && stakeoutPendingBuilding && (
        <div style={{ ...styles.messageOverlay, ...styles.messageOverlayIntel }}>
          <span style={styles.messageOverlayText}>Click anywhere to set the vision cone direction</span>
        </div>
      )}

      {/* Surveillance Van placement hint — two-click flow like Move.
          Pre-origin: instruct the player to click a drivable street.
          Post-origin: show live length (red when over-budget) as the cursor
          moves, then click again to commit. */}
      {surveillanceVanMode && (
        <div style={{ ...styles.messageOverlay, ...styles.messageOverlayIntel }}>
          {!surveillanceVanPreview ? (
            <span style={styles.messageOverlayText}>
              Click a drivable street to set the van's starting point
            </span>
          ) : (
            <span style={styles.messageOverlayText}>
              Route:{' '}
              <strong style={{ color: surveillanceVanPreview.overBudget ? '#ff5050' : '#c9b8ff' }}>
                {Math.round(surveillanceVanPreview.distance)}m
              </strong>
              {' / '}
              {tuning.surveillanceVanRouteBudget}m
              {surveillanceVanPreview.overBudget ? ' — OVER BUDGET' : ' — click to commit'}
            </span>
          )}
        </div>
      )}

      {/* Recon Plane confirm banner — shown when a placement is pending. Clicking
          the map + dragging again replaces the pending with a new placement.
          The Confirm button commits; Cancel exits mode. */}
      {reconPlaneMode && reconPlanePending && (
        <div
          style={{
            ...styles.messageOverlay,
            ...styles.messageOverlayIntel,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            pointerEvents: 'auto',
          }}
        >
          <span style={styles.messageOverlayText}>
            Recon strip ready — {Math.round(reconPlanePending.angleDeg)}°. Drag to adjust, or confirm.
          </span>
          <button
            className="action-btn"
            onClick={handleReconPlaneConfirm}
            style={{
              padding: '4px 12px',
              background: 'rgba(170, 136, 255, 0.2)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'rgba(170, 136, 255, 0.5)',
              borderRadius: '4px',
              color: '#aa88ff',
              fontFamily: theme.fonts.mono,
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '1.5px',
              cursor: 'pointer',
            }}
          >
            CONFIRM
          </button>
          <button
            onClick={handleReconPlaneCancel}
            style={{
              padding: '4px 12px',
              background: 'rgba(100, 100, 100, 0.15)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'rgba(100, 100, 100, 0.4)',
              borderRadius: '4px',
              color: '#888',
              fontFamily: theme.fonts.mono,
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '1.5px',
              cursor: 'pointer',
            }}
          >
            CANCEL
          </button>
        </div>
      )}

      {/* Round result overlay — extraction success, death, or kill */}
      {/* Round-end debrief — map stays visible with paths, banner + button overlaid */}
      {gameState.roundResult && !combatEncounter && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 10000,
          fontFamily: theme.fonts.mono,
          pointerEvents: 'none',
        }}>
          {/* Top banner */}
          <div style={{
            textAlign: 'center',
            padding: '32px 40px 20px',
            pointerEvents: 'auto',
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: 700,
              letterSpacing: '4px',
              marginBottom: '8px',
              color: gameState.roundResult === 'escape' ? '#44ff88'
                : gameState.roundResult === 'kill' ? '#44ff44'
                : '#ff3030',
              textShadow: '0 2px 20px rgba(0,0,0,0.8)',
            }}>
              {gameState.roundResult === 'escape' ? 'EXTRACTED'
                : gameState.roundResult === 'kill' ? 'TARGET NEUTRALIZED'
                : 'GAME OVER'}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#ccc',
              lineHeight: '1.6',
              textShadow: '0 1px 8px rgba(0,0,0,0.9)',
            }}>
              {gameState.roundResult === 'escape'
                ? `Clean exit at ${Math.round((gameState.player.exposure / tuning.exposureMax) * 100)}% exposure.`
                : gameState.roundResult === 'kill'
                ? 'The enemy agent has been eliminated.'
                : 'You have been neutralized.'}
            </div>
            <div style={{
              fontSize: '10px',
              color: '#888',
              letterSpacing: '2px',
              marginTop: '6px',
              textShadow: '0 1px 6px rgba(0,0,0,0.9)',
            }}>
              TURN {gameState.turn} — {cityData.name.toUpperCase()}
            </div>
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex',
            gap: '20px',
            padding: '8px 16px',
            background: 'rgba(8,12,20,0.8)',
            borderRadius: '6px',
            pointerEvents: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '20px', height: '3px', background: '#44aaff', borderRadius: '2px' }} />
              <span style={{ fontSize: '9px', color: '#44aaff', letterSpacing: '1px' }}>YOUR PATH</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '20px', height: '3px', background: '#ff4444', borderRadius: '2px', borderStyle: 'dashed' }} />
              <span style={{ fontSize: '9px', color: '#ff4444', letterSpacing: '1px' }}>AGENT PATH</span>
            </div>
          </div>

          {/* Bottom button */}
          <div style={{
            padding: '20px 40px 32px',
            pointerEvents: 'auto',
          }}>
            <button
              style={{
                background: 'rgba(8, 12, 20, 0.9)',
                border: `1px solid ${gameState.roundResult === 'death' ? 'rgba(255, 48, 48, 0.5)' : 'rgba(68, 255, 136, 0.5)'}`,
                color: gameState.roundResult === 'death' ? '#ff3030' : '#44ff88',
                padding: '12px 40px',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '3px',
                cursor: 'pointer',
                fontFamily: theme.fonts.mono,
                borderRadius: '6px',
              }}
              onClick={() => {
                if (onRoundComplete && gameState.roundResult) {
                  const passphrasesCollected = gameState.passphrases.filter(p => p.collected).length;
                  const baseReward = gameState.roundResult === 'kill' ? tuning.marks.killReward
                    : gameState.roundResult === 'escape' ? tuning.marks.escapeReward
                    : 0;
                  const earnedMarks = baseReward + (passphrasesCollected * tuning.marks.passphraseReward);
                  // Ship 2: safehouse cards (flagged isExtractRequired) are
                  // retained only when the player collected ≥ 2 passphrases
                  // this round. Applies on 'kill' AND 'escape' — though
                  // extraction rounds already gate extract itself behind the
                  // same passphrase count, so extracts always satisfy this.
                  // Lose the cards from the deck AND from the summary list
                  // so the kill-summary screen is honest about what was kept.
                  const PASSPHRASES_TO_RETAIN = 2;
                  const retainSafehouseCards = passphrasesCollected >= PASSPHRASES_TO_RETAIN;
                  let playerDeck = [...gameState.player.hand, ...gameState.player.deck, ...gameState.player.discard];
                  if (!retainSafehouseCards) {
                    playerDeck = playerDeck.filter(c => !c.isExtractRequired);
                  }
                  const safehouseCards = retainSafehouseCards ? safehouseCardsPickedRef.current : [];
                  onRoundComplete(gameState.roundResult, earnedMarks, gameState.player.items, playerDeck, safehouseCards, gameState.agent.codename);
                } else if (onBack) {
                  onBack();
                }
              }}
            >
              {gameState.roundResult === 'death'
                ? (onRoundComplete ? 'CONTINUE' : 'RETURN TO MENU')
                : 'CONTINUE'}
            </button>
          </div>
        </div>
      )}

      {/* Tutorial overlay — in-game help */}
      {showTutorial && (
        <TutorialScreen overlay onBack={() => setShowTutorial(false)} />
      )}
    </div>
  );
}

// Bucket colors for passive chips — kept in sync with PassiveDraftScreen.tsx
const PASSIVE_BUCKET_COLORS: Record<PassiveBucket, string> = {
  training:    '#66ccff',
  background:  '#aa88ff',
  contacts:    '#ffaa44',
  loadout:     '#44cc88',
  perception:  '#ff5ac4',
  economy:     '#ffcc44',
  combat:      '#ff5555',
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.colors.background,
  },
  topBar: {
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '0 16px',
    background: theme.colors.surface,
    borderBottom: `1px solid ${theme.colors.border}`,
    flexShrink: 0,
  },
  passiveRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 16px',
    background: 'rgba(8, 12, 20, 0.9)',
    borderBottom: `1px solid ${theme.colors.border}`,
    flexShrink: 0,
    flexWrap: 'wrap',
    fontFamily: theme.fonts.mono,
  },
  passiveRowLabel: {
    fontSize: '8px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    fontWeight: 700,
    marginRight: '4px',
  },
  passiveChip: {
    fontSize: '9px',
    letterSpacing: '0.5px',
    fontWeight: 600,
    padding: '3px 7px',
    border: '1px solid',
    borderRadius: '3px',
    background: 'rgba(255,255,255,0.02)',
    whiteSpace: 'nowrap',
    cursor: 'help',
  },
  backButton: {
    background: 'none',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    letterSpacing: '1px',
    padding: '4px 10px',
    cursor: 'pointer',
    borderRadius: '2px',
  },
  topBarTitle: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '3px',
    color: theme.colors.textPrimary,
  },
  topBarCity: {
    fontSize: '11px',
    letterSpacing: '2px',
    color: theme.colors.accentGreen,
    fontWeight: 500,
  },
  topBarStats: {
    fontSize: '10px',
    color: theme.colors.textDim,
    letterSpacing: '1px',
    marginLeft: 'auto',
  },
  bannerStack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column' as const,
    pointerEvents: 'none',
  },
  hoverBannerInline: {
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
    background: 'rgba(12, 16, 24, 0.85)',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  movementBannerInline: {
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
    background: 'rgba(12, 16, 24, 0.92)',
    borderBottom: '1px solid rgba(68, 170, 255, 0.4)',
    borderLeft: '3px solid rgba(68, 170, 255, 0.6)',
  },
  movementBannerCard: {
    fontSize: '13px',
    letterSpacing: '1.5px',
    color: theme.colors.playerMarker,
    fontWeight: 600,
  },
  movementBannerText: {
    fontSize: '12px',
    letterSpacing: '0.5px',
    color: theme.colors.textPrimary,
  },
  movementPreviewInfo: {
    display: 'flex',
    gap: '12px',
    padding: '2px 10px',
    background: 'rgba(255, 255, 255, 0.04)',
    borderRadius: '3px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  previewStat: {
    fontSize: '10px',
    fontFamily: theme.fonts.mono,
    fontWeight: 700,
    letterSpacing: '1px',
    color: theme.colors.textSecondary,
  },
  // --- Message overlay (formerly "footpath hint overlay") ---
  // Shared container for any temporary on-screen message: game notifications,
  // movement hints (SHIFT for footpaths), alt-movement route selection.
  // Variant styles override background/border/text color based on the
  // NotificationVariant: 'agent' (red), 'intel' (purple), 'general' (blue).
  messageOverlay: {
    position: 'fixed' as const,
    bottom: '220px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 16px',
    borderRadius: '6px',
    border: '1px solid',
    maxWidth: 'min(720px, 90vw)',
    zIndex: 100,
    pointerEvents: 'none' as const,
    fontFamily: theme.fonts.mono,
  },
  messageOverlayText: {
    fontSize: '11px',
    letterSpacing: '0.5px',
    lineHeight: 1.5,
  },
  messageOverlayGeneral: {
    background: 'rgba(12, 20, 36, 0.92)',
    borderColor: 'rgba(68, 136, 255, 0.5)',
    color: '#9ac5ff',
  },
  messageOverlayAgent: {
    background: 'rgba(40, 10, 12, 0.92)',
    borderColor: 'rgba(255, 80, 80, 0.55)',
    color: '#ff9a9a',
  },
  messageOverlayIntel: {
    background: 'rgba(26, 16, 48, 0.92)',
    borderColor: 'rgba(170, 136, 255, 0.55)',
    color: '#c9b8ff',
  },
  messageOverlayKbd: {
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: '11px',
    fontFamily: theme.fonts.mono,
    fontWeight: 700,
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    borderRadius: '4px',
    letterSpacing: '1px',
    color: 'inherit',
  },
  movementBannerKeys: {
    display: 'flex',
    gap: '14px',
    marginLeft: 'auto',
  },
  kbdHint: {
    fontSize: '10px',
    color: theme.colors.textDim,
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  kbd: {
    display: 'inline-block',
    padding: '1px 5px',
    fontSize: '9px',
    fontFamily: theme.fonts.mono,
    fontWeight: 600,
    color: theme.colors.textPrimary,
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '3px',
    lineHeight: '14px',
  },
  sweepBannerInline: {
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
    background: 'rgba(221, 204, 68, 0.12)',
    borderBottom: '1px solid rgba(221, 204, 68, 0.3)',
  },
  sweepBannerCard: {
    fontSize: '13px',
    letterSpacing: '1.5px',
    color: '#ddcc44',
    fontWeight: 600,
  },
  sweepBannerText: {
    fontSize: '12px',
    letterSpacing: '0.5px',
    color: theme.colors.textPrimary,
  },
  hideBannerInline: {
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
    background: 'rgba(102, 204, 136, 0.12)',
    borderBottom: '1px solid rgba(102, 204, 136, 0.3)',
  },
  hideBannerCard: {
    fontSize: '13px',
    letterSpacing: '1.5px',
    color: '#66cc88',
    fontWeight: 600,
  },
  hideBannerText: {
    fontSize: '12px',
    letterSpacing: '0.5px',
    color: theme.colors.textPrimary,
  },
  exitBuildingButton: {
    marginLeft: 'auto',
    padding: '4px 14px',
    background: 'rgba(12, 16, 24, 0.8)',
    border: `1px solid ${theme.colors.playerMarker}`,
    borderRadius: '3px',
    color: theme.colors.playerMarker,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    cursor: 'pointer',
  },
  decoyBannerInline: {
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
    background: 'rgba(255, 170, 68, 0.12)',
    borderBottom: '1px solid rgba(255, 170, 68, 0.3)',
  },
  decoyBannerCard: {
    fontSize: '13px',
    letterSpacing: '1.5px',
    color: '#ffaa44',
    fontWeight: 600,
  },
  decoyBannerText: {
    fontSize: '12px',
    letterSpacing: '0.5px',
    color: theme.colors.textPrimary,
  },
  instinctBannerInline: {
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px',
    background: 'rgba(68, 204, 102, 0.12)',
    borderBottom: '1px solid rgba(68, 204, 102, 0.3)',
  },
  instinctBannerCard: {
    fontSize: '13px',
    letterSpacing: '1.5px',
    color: '#44cc66',
    fontWeight: 600,
  },
  instinctBannerText: {
    fontSize: '12px',
    letterSpacing: '0.5px',
    color: theme.colors.textPrimary,
  },
  movementBannerEsc: {
    fontSize: '10px',
    letterSpacing: '1px',
    color: theme.colors.textDim,
    marginLeft: 'auto',
  },
  hoverType: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    fontWeight: 600,
  },
  hoverName: {
    fontSize: '16px',
    letterSpacing: '1.5px',
    color: theme.colors.textPrimary,
    fontWeight: 500,
  },
  hoverLine: {
    fontSize: '12px',
    letterSpacing: '1px',
    color: theme.colors.textSecondary,
    fontWeight: 400,
    marginLeft: '4px',
  },
  hoverRoadClass: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '8px',
    padding: '2px 8px',
    background: 'rgba(255, 255, 255, 0.04)',
    borderRadius: '3px',
    border: `1px solid ${theme.colors.border}`,
  },
  hoverRoadClassLabel: {
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    fontFamily: theme.fonts.mono,
  },
  hoverRoadClassWeight: {
    fontSize: '10px',
    fontWeight: 700,
    fontFamily: theme.fonts.mono,
    letterSpacing: '0.5px',
    opacity: 0.8,
  },
  hoverZone: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
    padding: '2px 8px',
    background: 'rgba(255, 255, 255, 0.04)',
    borderRadius: '3px',
    border: `1px solid ${theme.colors.border}`,
  },
  hoverZoneLabel: {
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
  },
  hoverZoneMultiplier: {
    fontSize: '11px',
    fontWeight: 700,
    fontFamily: theme.fonts.mono,
    letterSpacing: '0.5px',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  // OSM attribution — top-left corner, small and muted. Required by ODbL.
  osmCredit: {
    position: 'absolute' as const,
    top: '8px',
    left: '10px',
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    letterSpacing: '0.5px',
    color: theme.colors.textDim,
    opacity: 0.55,
    textDecoration: 'none',
    zIndex: 5,
    pointerEvents: 'auto' as const,
  },
  // --- Inventory (left side) ---
  inventoryBar: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '6px',
    zIndex: 10,
    pointerEvents: 'none',
  },
  inventorySlot: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1.5px solid ${theme.colors.border}`,
    borderRadius: '6px',
    pointerEvents: 'auto' as const,
    cursor: 'default',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  inventoryTooltip: {
    position: 'absolute' as const,
    left: '56px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    padding: '8px 10px',
    minWidth: '140px',
    maxWidth: '200px',
    pointerEvents: 'none' as const,
    zIndex: 20,
    display: 'none', // shown via CSS hover
  },
  itemActionMenu: {
    position: 'absolute' as const,
    left: '56px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(12, 16, 24, 0.96)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    padding: '8px 10px',
    minWidth: '150px',
    zIndex: 25,
    pointerEvents: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  itemMenuButton: {
    padding: '5px 10px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '1px',
    cursor: 'pointer',
    textAlign: 'center' as const,
    transition: 'background 0.15s',
  },
  itemMenuButtonUse: {
    background: 'rgba(68, 170, 255, 0.25)',
    color: '#44aaff',
  },
  itemMenuButtonDisabled: {
    background: 'rgba(100, 100, 100, 0.15)',
    color: theme.colors.textDim,
    cursor: 'not-allowed',
  },
  itemMenuButtonDrop: {
    background: 'rgba(255, 80, 80, 0.2)',
    color: '#ff5050',
  },
  itemMenuButtonCancel: {
    background: 'rgba(150, 150, 150, 0.15)',
    color: theme.colors.textSecondary,
  },
  inventoryLabel: {
    fontSize: '8px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    marginTop: '2px',
  },
  // --- Bottom HUD ---
  bottomHud: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '0 16px 16px 16px',
    zIndex: 10,
    pointerEvents: 'none',
  },
  pile: {
    width: '64px',
    height: '88px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    pointerEvents: 'auto' as const,
    cursor: 'pointer',
  },
  pileCount: {
    fontSize: '22px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    letterSpacing: '1px',
  },
  pileLabel: {
    fontSize: '9px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    marginTop: '4px',
  },
  handWrapper: {
    flex: 1,
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'flex-end',
    pointerEvents: 'none' as const,
  },
  handArea: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    pointerEvents: 'none' as const,
    // Reserve hand-card height even when the hand is empty so the status
    // chips above stay pinned at the same y-position. Matches the 120px
    // card height defined in .hand-card so the reservation is exact.
    minHeight: '120px',
  },
  cardCost: {
    position: 'absolute' as const,
    top: '6px',
    left: '8px',
    fontSize: '14px',
    fontWeight: 700,
    color: theme.colors.accentYellow,
  },
  cardName: {
    fontSize: '11px',
    fontWeight: 600,
    color: theme.colors.textPrimary,
    letterSpacing: '0.5px',
    textAlign: 'center' as const,
    padding: '0 6px',
  },
  cardCategory: {
    fontSize: '8px',
    letterSpacing: '1.5px',
    color: theme.colors.textDim,
  },
  cardRange: {
    position: 'absolute' as const,
    bottom: '6px',
    right: '6px',
    fontSize: '9px',
    fontWeight: 600,
    color: theme.colors.accentBlue,
    letterSpacing: '0.5px',
  },
  cardCategoryIcon: {
    position: 'absolute' as const,
    bottom: '4px',
    left: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.9,
  },
  turnPanel: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    gap: '6px',
    pointerEvents: 'auto' as const,
  },
  turnPanelRow: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between',
  },
  apDisplay: {
    fontSize: '13px',
    letterSpacing: '1.5px',
    color: theme.colors.textSecondary,
    fontWeight: 500,
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    padding: '6px 14px',
    textAlign: 'center' as const,
  },
  apValue: {
    color: theme.colors.accentYellow,
    fontWeight: 700,
  },
  turnDisplay: {
    fontSize: '13px',
    letterSpacing: '2px',
    color: theme.colors.textSecondary,
    fontWeight: 500,
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    padding: '6px 14px',
    textAlign: 'center' as const,
  },
  marksDisplay: {
    fontSize: '11px',
    letterSpacing: '1px',
    color: theme.colors.textSecondary,
    fontWeight: 500,
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    padding: '4px 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    pointerEvents: 'auto' as const,
    marginTop: '4px',
  },
  marksValue: {
    color: theme.colors.accentGreen,
    fontWeight: 700,
  },
  marksTooltip: {
    display: 'none',
    position: 'absolute' as const,
    left: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginLeft: '8px',
    background: 'rgba(12, 16, 24, 0.98)',
    border: `1px solid ${theme.colors.accentGreen}40`,
    borderRadius: '6px',
    padding: '10px 14px',
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    width: '140px',
    whiteSpace: 'normal' as const,
    zIndex: 100,
    pointerEvents: 'none' as const,
  },
  endTurnButton: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '12px 24px',
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    color: theme.colors.textPrimary,
    fontFamily: theme.fonts.mono,
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '2px',
    cursor: 'pointer',
  },
  endTurnX: {
    fontSize: '18px',
    color: theme.colors.textSecondary,
  },
  // --- Signal toggle ---
  signalToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    padding: '5px 12px',
    borderRadius: '4px',
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  signalToggleActive: {
    background: 'rgba(255, 51, 68, 0.15)',
    border: `1px solid ${theme.colors.agentMarker}`,
    color: theme.colors.agentMarker,
  },
  cSurvToggleActive: {
    background: 'rgba(68, 170, 255, 0.15)',
    border: `1px solid ${theme.colors.playerMarker}`,
    color: theme.colors.playerMarker,
  },
  signalToggleInactive: {
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textDim,
  },
  signalSettingsButton: {
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textDim,
    padding: '4px 10px',
  },
  signalSettingsDropdown: {
    position: 'absolute' as const,
    bottom: '100%',
    right: 0,
    marginBottom: '4px',
    background: 'rgba(12, 16, 24, 0.95)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    padding: '6px 0',
    zIndex: 100,
    minWidth: '100px',
  },
  signalSettingsLabel: {
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    color: theme.colors.textDim,
    padding: '2px 10px 6px',
    borderBottom: `1px solid ${theme.colors.border}`,
    marginBottom: '2px',
  },
  signalSettingsOption: {
    display: 'block',
    width: '100%',
    padding: '5px 10px',
    background: 'none',
    border: 'none',
    fontFamily: theme.fonts.mono,
    fontSize: '11px',
    color: theme.colors.textPrimary,
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  signalSettingsOptionActive: {
    color: theme.colors.agentMarker,
    background: 'rgba(255, 51, 68, 0.1)',
  },

  // --- Test mode ---
  testModeButton: {
    marginLeft: 'auto',
    padding: '4px 12px',
    background: 'rgba(30, 38, 50, 0.8)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '1px',
    cursor: 'pointer',
  },
  testModeButtonActive: {
    background: 'rgba(255, 200, 0, 0.15)',
    borderColor: '#f0c040',
    color: '#f0c040',
  },
  testDebugPanel: {
    position: 'absolute' as const,
    top: '10px',
    right: '10px',
    width: '220px',
    background: 'rgba(12, 16, 24, 0.95)',
    border: '1px solid #f0c040',
    borderRadius: '6px',
    padding: '10px 12px',
    zIndex: 20,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    color: theme.colors.textSecondary,
    pointerEvents: 'auto' as const,
  },
  testDebugTitle: {
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '2px',
    color: '#f0c040',
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '1px solid rgba(240, 192, 64, 0.3)',
  },
  testDebugRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '2px 0',
  },
  testDebugLabel: {
    color: theme.colors.textDim,
    fontSize: '9px',
    letterSpacing: '0.5px',
  },

  // --- Pile overlay ---
  pileOverlayBackdrop: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pileOverlayPanel: {
    width: '600px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    background: 'rgba(12, 16, 24, 0.97)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  pileOverlayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: `1px solid ${theme.colors.border}`,
    flexShrink: 0,
  },
  pileOverlayTitle: {
    fontFamily: theme.fonts.mono,
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: theme.colors.textPrimary,
  },
  pileOverlayClose: {
    background: 'none',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    color: theme.colors.textSecondary,
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px 10px',
    fontFamily: theme.fonts.mono,
  },
  pileOverlayEmpty: {
    padding: '40px',
    textAlign: 'center' as const,
    color: theme.colors.textDim,
    fontFamily: theme.fonts.mono,
    fontSize: '13px',
    letterSpacing: '1px',
  },
  pileOverlayGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '10px',
    padding: '14px 18px',
    overflowY: 'auto' as const,
  },
  pileOverlayCard: {
    background: 'rgba(30, 38, 50, 0.9)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    position: 'relative' as const,
  },
  pileOverlayCardCost: {
    position: 'absolute' as const,
    top: '6px',
    right: '6px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 800,
    color: '#000',
  },
  pileOverlayCardName: {
    fontFamily: theme.fonts.mono,
    fontSize: '13px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    paddingRight: '24px',
  },
  pileOverlayCardCategory: {
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    letterSpacing: '2px',
    fontWeight: 600,
  },
  pileOverlayCardDesc: {
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    color: theme.colors.textSecondary,
    lineHeight: '1.4',
    marginTop: '4px',
  },
  pileOverlayCardStats: {
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    color: theme.colors.textDim,
    marginTop: '4px',
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
  },
};
